// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
  baseUrl:
    'http://{defaultHost}{prefix}' | 'http://{defaultHost}' | (string & {})
}

/**
 * Authorization failure: `{ error }` with a human-readable message, e.g. a premium-only feature or a filter reserved to admins.
 */
export type AccessDeniedResponse = {
  error: string
}

/**
 * One entry in a user or player's activity feed. Wraps an underlying Match, UserMatch, or Training record (identified by `activatable_type`/`activatable_id`) with feed-specific metadata — visibility, an optional personal note/title, and an optionally-linked smart watch workout.
 */
export type Activity = {
  id?: number
  /**
   * ID of the underlying record (see activatable_type)
   */
  activatable_id?: number
  /**
   * Which kind of record this activity wraps
   */
  activatable_type?: 'Match' | 'UserMatch' | 'Training'
  /**
   * Owning user's ID, if the activatable belongs to a User
   */
  user_id?: number | null
  /**
   * Owning FIT player profile's ID, if the activatable belongs to a player profile instead of a User
   */
  fit_player_profile_id?: number | null
  /**
   * Whether this entry appears in the public feed (owners always see their own regardless)
   */
  visible?: boolean
  /**
   * The owner's personal note on this entry
   */
  note?: string | null
  title?: string | null
  created_at?: string
  /**
   * Free-form summary of the underlying record, shape depends on activatable_type
   */
  details?: {
    [key: string]: unknown
  } | null
  smart_watch_workout?: SmartWatchWorkout
}

/**
 * Pagination info for GET /activities, plus a breakdown of how many activities of each kind exist in the requested scope (independent of the `type` filter and of the current page).
 */
export type ActivityMeta = {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
  /**
   * Count of Match-backed activities in scope
   */
  total_match: number
  /**
   * Count of Training-backed activities in scope
   */
  total_training: number
  /**
   * Count of UserMatch-backed activities in scope
   */
  total_user_match: number
}

/**
 * Activity streak summary for GET /activities/streak.
 */
export type ActivityStreak = {
  /**
   * Consecutive weeks up to and including the current one with at least one activity
   */
  streak_weeks: number
  /**
   * All-time count of the caller's activities, regardless of the requested date range
   */
  total: number
  /**
   * Activities within the current calendar month
   */
  this_month: number
  /**
   * Activities within the current calendar week
   */
  this_week: number
  /**
   * One entry per day within the requested `from`..`to` window that has at least one activity
   */
  days: Array<ActivityStreakDay>
}

/**
 * One day within an activity streak breakdown.
 */
export type ActivityStreakDay = {
  date: string
  /**
   * Distinct kinds of activity recorded on this day (SmartWatchWorkout appears here whenever an activity that day has a linked smart watch workout)
   */
  types: Array<'Match' | 'UserMatch' | 'Training' | 'SmartWatchWorkout'>
  /**
   * IDs of the activities recorded on this day
   */
  activity_ids: Array<number>
  /**
   * Whether any activity on this day has a linked smart watch workout
   */
  has_smart_watch: boolean
}

/**
 * Usage of an app feature: total and how many were created (logged in the app) in the last 24 hours, the last 7 days and the 7 days before.
 */
export type ActivityTrend = {
  total: number
  last_24h: number
  last_1w: number
  /**
   * Created between 14 and 7 days ago.
   */
  previous_1w: number
}

/**
 * A named ad-slot configuration entry shown in the app.
 */
export type AdsenseSlot = {
  id: number
  /**
   * Identifying name of the ad slot.
   */
  name: string
}

/**
 * FIT age category: U08-U18 (under N years), NOR/NOF (open, men/women), O30-O80 (veterans, N years and older).
 */
export const AgeCategory = {
  U08: 'U08',
  U10: 'U10',
  U12: 'U12',
  U14: 'U14',
  U16: 'U16',
  U18: 'U18',
  NOR: 'NOR',
  NOF: 'NOF',
  O30: 'O30',
  O35: 'O35',
  O40: 'O40',
  O45: 'O45',
  O50: 'O50',
  O55: 'O55',
  O60: 'O60',
  O65: 'O65',
  O70: 'O70',
  O75: 'O75',
  O80: 'O80',
} as const

/**
 * FIT age category: U08-U18 (under N years), NOR/NOF (open, men/women), O30-O80 (veterans, N years and older).
 */
export type AgeCategory = (typeof AgeCategory)[keyof typeof AgeCategory]

/**
 * Self-assessment from the amateur onboarding flow (m0/m0b). Null when the user has no amateur profile.
 */
export type AmateurProfileSummary = {
  /**
   * Answer to «Che giocatore sei?» (m0)
   */
  skill: number | null
  /**
   * Answer to «Ogni quanto giochi?» (m0b)
   */
  frequency: number | null
  /**
   * NTRP-like estimate computed by the frontend
   */
  ntrp: number | null
  level_label: string | null
  /**
   * Free-text description of the own level, max 280 characters.
   */
  level_description: string | null
  /**
   * Level label derived from `skill` (Principiante, Intermedio, Avanzato, Agonista); null when the skill has no label.
   */
  chip: string | null
} | null

/**
 * Lightweight identity of whoever authored/published a piece of content (a User, or an entity they masquerade as, e.g. a Club).
 */
export type Author = {
  /**
   * The author entity's own id (not necessarily a User id)
   */
  id: number
  name: string
  /**
   * Class name of the author entity, e.g. "User", "Club"
   */
  type: string
  thumbnail?: string | null
  /**
   * The underlying User id, when the author entity is/has one
   */
  user_id?: number | null
  /**
   * Active in the app in the last 24h
   */
  active_24h?: boolean | null
  /**
   * Active in the app in the last month
   */
  active_1m?: boolean | null
}

/**
 * A role-specific profile (teacher, trainer, shop, ...) a user has set up.
 */
export type AvailableProfile = {
  id: number
  type: string
  name: string | null
  photo: string | null
}

/**
 * Aggregate counters for the admin dashboard.
 */
export type BackofficeNumbersResponse = {
  numbers: {
    users: number
    active_users_24h: number
    active_users_1m: number
    active_users_1y: number
    users_created_last_month: number
  }
  data: {
    /**
     * Update tasks running, created in the last week.
     */
    update_tasks: number
    /**
     * Unconsumed membership codes created in the last week.
     */
    membership_codes: number
    /**
     * Pending reports created in the last week.
     */
    reports: number
    membership_codes_24h: number
    membership_codes_1w: number
    membership_codes_1m: number
    /**
     * Created between 14 and 7 days ago.
     */
    membership_codes_1w_ago: number
    /**
     * Created between 2 months and 1 month ago.
     */
    membership_codes_1m_ago: number
    messages_last_24h: number
    messages_last_1w: number
    /**
     * Numeric string (Redis cache).
     */
    visit_events_last_24h: string
    /**
     * Numeric string (Redis cache).
     */
    visit_events_last_1w: string
    new_users_last_24h: number
    new_users_last_1w: number
    /**
     * Activity counts by activatable type, as numeric strings (Redis cache). `UserMatch` counts only non-competitive user matches.
     */
    activities_by_type: {
      Training: string
      UserMatch: string
      [key: string]: string
    }
    smartwatch_workouts_count: number
    activity_trends: {
      smartwatch_workouts: ActivityTrend
      /**
       * Trainings logged by users (copies generated by recurring trainings excluded).
       */
      trainings: ActivityTrend
      friendly_matches: ActivityTrend
      /**
       * Recurring trainings: `total` = trainings with an active rule today, the windows = trainings that got a rule.
       */
      training_schedules: ActivityTrend
    }
    /**
     * Match Finder: matches organised in the app (friendly, played or not; matches entered after being played excluded).
     */
    match_finder: {
      /**
       * Users visible to nearby players (discoverable), not banned.
       */
      findable_users: number
      /**
       * Open matches not started yet.
       */
      open_now: number
      /**
       * Matches organised, by creation date.
       */
      matches: ActivityTrend
      /**
       * Matches where the creator accepted a join request, by first acceptance date.
       */
      matched: ActivityTrend
    }
  }
}

/**
 * Full user record as shown in the admin backoffice (users listing/search, premium token owner). Includes private fields (email, tokens, ban info) and the summary of every professional profile the user owns.
 */
export type BackofficeUser = {
  id: number
  email: string
  /**
   * Social login uid (Facebook/Apple/Google).
   */
  uid?: string | null
  club_id?: number | null
  first_name: string
  last_name: string
  birth_date?: string | null
  phone?: string | null
  /**
   * The Address record serialized as-is.
   */
  address?: {
    id?: number
    line_1?: string | null
    line_2?: string | null
    zip?: string | null
    city?: string | null
    province?: string | null
    country?: string | null
    raw_user_input?: string | null
    latitude?: number | null
    longitude?: number | null
    /**
     * PostGIS point as WKT, e.g. "POINT (9.19 45.46)".
     */
    lonlat?: string | null
    created_at?: string
    updated_at?: string
  } | null
  /**
   * The CarrierWave photo uploader serialized as-is: the original `url` plus one `{ url }` object per version.
   */
  photo?: {
    url?: string | null
    user?: {
      url?: string | null
    }
    club?: {
      url?: string | null
    }
    offer?: {
      url?: string | null
    }
    post?: {
      url?: string | null
    }
  }
  bio?: string | null
  preferred_surface?: Surface | null
  premium: boolean
  hidden_results: boolean
  role:
    | 'admin'
    | 'simple_user'
    | 'banner_manager'
    | 'censor_manager'
    | 'premium_user'
  banned_at?: string | null
  banned_by_id?: number | null
  ban_notes?: string | null
  confirmed_at?: string | null
  created_at: string
  updated_at: string
  full_name: string
  notifications_last_seen?: string | null
  market_condition_accepted?: boolean
  first_sign_in_at?: string | null
  /**
   * Null for users who signed up before login tracking.
   */
  signup_method: SignupMethod | null
  /**
   * Null for users who signed up before login tracking.
   */
  signup_source: LoginSource | null
  /**
   * Last login, any method. Null if none since tracking.
   */
  last_sign_in_at: string | null
  last_sign_in_method: LoginMethod | null
  last_sign_in_source: LoginSource | null
  /**
   * Last client version seen (X-Client-Version header), e.g. 3.6.4. Null if the client never sent it.
   */
  client_version: string | null
  /**
   * Last client platform seen (X-Client-Platform header).
   */
  client_platform: ClientPlatform | null
  /**
   * Client version used to sign up.
   */
  signup_client_version: string | null
  signup_client_platform: ClientPlatform | null
  marketing_communication_check?: boolean
  tennistalker_communication_check?: boolean
  /**
   * Notification preference flags (jsonb), keyed by notification kind.
   */
  notification_settings?: {
    [key: string]: boolean
  }
  primary_segment?: 'pro' | 'agonista' | 'amatoriale' | 'fan'
  /**
   * Students count of the user's teacher profile, if any.
   */
  ambassador_students?: number | null
  premium_expiration?: string | null
  is_banned: boolean
  /**
   * Name of the admin who banned the user.
   */
  banned_by?: string | null
  /**
   * The linked FitPlayerProfile record serialized as-is (all columns).
   */
  fit_player_profile?: {
    id?: number
    user_id?: number | null
    fit_player_id?: string | null
    anagrafica_code?: string | null
    first_name?: string
    last_name?: string
    full_name?: string
    birth_date?: string | null
    gender?: string | null
    age_category?: AgeCategory | null
    qualification?: string | null
    club_id?: number | null
    address_id?: number | null
    address_string_from_updater?: string | null
    region?: string | null
    province?: string | null
    ita?: boolean | null
    active?: boolean
    hidden?: boolean | null
    certificate_expiration?: string | null
    current_elo?: number | null
    elo_matches_count?: number | null
    created_at?: string
    updated_at?: string
  } | null
  /**
   * True if the email is confirmed or the user signed up with a social login.
   */
  activated: boolean
  fit_player?: {
    /**
     * The FIT id from the latest profile request if any, else the profile's own fit_player_id.
     */
    fit_player_id?: string | null
  } | null
  nutritionist?: {
    id?: number
    qualifications?: string | null
    professional_info?: string | null
  } | null
  mental_coach?: {
    id?: number
    qualifications?: string | null
    professional_info?: string | null
  } | null
  teacher?: {
    id?: number
    qualification?: string | null
    professional_info?: string | null
  } | null
  trainer?: {
    id?: number
    qualification?: string | null
    professional_info?: string | null
  } | null
  shop_profile?: {
    id?: number
    name?: string | null
    website?: string | null
    info?: string | null
    address?: string | null
  } | null
  club_manager?: {
    id?: number
    photo?: string | null
    club?: {
      id?: number
      fit_club_id?: string | null
      name?: string
      legal_address?: string | null
      region?: string | null
      province?: string | null
      phone?: string | null
    }
  } | null
}

export type Block = {
  success: boolean
}

/**
 * A manually-added bonus applied to a player's ranking for a given year (see also RankInfo#user_added, the aggregated total of these).
 */
export type Bonus = {
  id: number
  year: number
  name?: string | null
  /**
   * Bonus points, serialized as a decimal string.
   */
  value: string
}

/**
 * A paginated list of Bonus entries.
 */
export type BonusListResponse = {
  page: number
  total_pages: number
  bonuses: Array<Bonus>
}

/**
 * The result of creating a Bonus.
 */
export type BonusWithOutcomeResponse = {
  success: boolean
  bonus: Bonus
}

/**
 * The next free ranking "cardinality" (progressive counter) for the current year, to be passed to POST /cardinality/save.
 */
export type CardinalityResponse = {
  /**
   * Current year.
   */
  year: number
  /**
   * Max cardinality saved this year + 1 (1 if none).
   */
  cardinality: number
}

/**
 * Response for GET /fit-player-profiles/{id}/chart_extract: a slice of the ranking list centered on the requested player, used to render the "ranking chart" excerpt on their profile page.
 */
export type ChartExtractResponse = {
  /**
   * A contiguous slice of the ranking list around the requested player.
   */
  chart_extract: Array<PlayerWithPosition>
}

/**
 * Platform the client declared in the X-Client-Platform header: the native app on ios / android, or web.
 */
export const ClientPlatform = {
  IOS: 'ios',
  ANDROID: 'android',
  WEB: 'web',
} as const

/**
 * Platform the client declared in the X-Client-Platform header: the native app on ios / android, or web.
 */
export type ClientPlatform =
  (typeof ClientPlatform)[keyof typeof ClientPlatform]

/**
 * A tennis club, as returned by list/search endpoints (see also the fuller single-club shape returned by GET /clubs/{id}).
 */
export type Club = {
  id: number
  name: string
  /**
   * Number of registered members
   */
  members_counter?: number
  phone?: string | null
  /**
   * Region (FIT code), e.g. 'LAZ'
   */
  region?: string | null
  /**
   * Italian province code, e.g. 'Roma'
   */
  province?: string | null
  /**
   * Formatted address of the club courts
   */
  fields_address_display_info?: string | null
  /**
   * Whether the club has an active club manager
   */
  managed?: boolean
  active?: boolean
  /**
   * Tennis school classification, if the club runs one
   */
  school_typology?: string | null
}

/**
 * A request from a user to become (or their existing standing as) the manager of a club.
 */
export type ClubManagerProfile = {
  id: number
  first_name?: string | null
  last_name?: string | null
  phone?: string | null
  email: string
  /**
   * Request status. `pending` until an admin approves or rejects it.
   */
  status: 'pending' | 'approved' | 'rejected' | null
  /**
   * Free-text admin notes left when approving/rejecting.
   */
  notes: string | null
  /**
   * The club this manager request is for.
   */
  club: {
    id?: number
    /**
     * Official FIT club identifier, if linked.
     */
    fit_club_id?: string | null
    name?: string
    legal_address?: string | null
    region?: string | null
    province?: string | null
    phone?: string | null
  }
}

/**
 * Either the ClubManagerProfileResponse success shape, or a ValidationErrorResponse (this operation answers 200 for both the happy path and a validation failure).
 */
export type ClubManagerProfileCreateResponse =
  ClubManagerProfileResponse | ValidationErrorResponse

/**
 * club_manager_profiles/_masqueraded: a club manager acting as their club.
 */
export type ClubManagerProfileMasqueraded = {
  /**
   * The managed club id (not the profile id).
   */
  id: number
  name: string
  type: 'club_manager_profile'
  thumbnail?: string | null
}

/**
 * Response wrapping a single club manager profile request.
 */
export type ClubManagerProfileResponse = {
  success: boolean
  /**
   * A request from a user to become (or their existing standing as) the manager of a club.
   */
  club_manager_profile: {
    id: number
    first_name?: string | null
    last_name?: string | null
    phone?: string | null
    email: string
    /**
     * Request status. `pending` until an admin approves or rejects it.
     */
    status: 'pending' | 'approved' | 'rejected' | null
    /**
     * Free-text admin notes left when approving/rejecting.
     */
    notes: string | null
    /**
     * The club this manager request is for.
     */
    club: {
      id?: number
      /**
       * Official FIT club identifier, if linked.
       */
      fit_club_id?: string | null
      name?: string
      legal_address?: string | null
      region?: string | null
      province?: string | null
      phone?: string | null
    }
  }
}

/**
 * Paginated list of club manager profile requests.
 */
export type ClubManagerProfilesIndexResponse = {
  total_pages: number
  club_manager_profiles: Array<{
    id: number
    first_name?: string | null
    last_name?: string | null
    phone?: string | null
    email: string
    /**
     * Request status. `pending` until an admin approves or rejects it.
     */
    status: 'pending' | 'approved' | 'rejected' | null
    /**
     * Free-text admin notes left when approving/rejecting.
     */
    notes: string | null
    /**
     * The club this manager request is for.
     */
    club: {
      id?: number
      /**
       * Official FIT club identifier, if linked.
       */
      fit_club_id?: string | null
      name?: string
      legal_address?: string | null
      region?: string | null
      province?: string | null
      phone?: string | null
    }
  }>
}

/**
 * A club mate of the target player, with the matches they've played against each other.
 */
export type ClubMateData = {
  opponent: ConnectionOpponentInfo
  matches: Array<SimulatorMatch>
}

/**
 * A minimal club stub embedded in another resource (e.g. a player's club affiliation), rather than the full Club shape.
 */
export type ClubPreview = {
  id?: number
  name?: string
  /**
   * FIT federation club identifier
   */
  fit_club_id?: string | null
  region?: string | null
  province?: string | null
} | null

/**
 * Club summary embedded in a club_manager_profile.
 */
export type ClubPreviewFull = {
  id: number
  fit_club_id?: string | null
  name: string | null
  legal_address?: string | null
  region?: string | null
  province?: string | null
  phone?: string | null
}

/**
 * A comment on a post. `children` holds one level of replies (comments whose `parent_id` points to this one).
 */
export type Comment = {
  id: number
  content: string
  /**
   * created_at, as a unix timestamp
   */
  date: number
  /**
   * Discriminator used when reporting this item
   */
  reportable_type: 'comment'
  /**
   * Whether the requesting user is the author
   */
  is_author: boolean
  likes: number
  liked_by_current_user: boolean
  author: PlayerMasqueraded | Author
  /**
   * Direct replies to this comment
   */
  children: Array<Comment>
}

/**
 * Either the CommentWithOutcomeResponse success shape, or a ValidationErrorResponse (this operation answers 200 for both the happy path and a validation failure).
 */
export type CommentMutationResponse =
  CommentWithOutcomeResponse | ValidationErrorResponse

/**
 * Response envelope wrapping a single comment together with a success flag.
 */
export type CommentWithOutcomeResponse = {
  success: boolean
  comment: Comment
}

/**
 * An opponent that both the viewer and the target player have faced, with each side's matches against that opponent.
 */
export type CommonOpponentData = {
  opponent: ConnectionOpponentInfo
  /**
   * The target player's matches against this opponent.
   */
  target_matches: Array<SimulatorMatch>
  /**
   * The viewer's matches against this opponent.
   */
  my_matches: Array<SimulatorMatch>
}

/**
 * A competition: the overall club event that groups one or more Tournaments (each a single gender/category/surface bracket).
 */
export type Competition = {
  id: number
  /**
   * FIT federation competition identifier
   */
  fit_competition_id: string
  name: string
  club_id?: number | null
  club_name?: string | null
  /**
   * Start date
   */
  from: string
  /**
   * End date
   */
  to: string
  /**
   * FIT's newer-format competition identifier
   */
  new_fit_competition_id?: string | null
  /**
   * Whether this is a championship event
   */
  championship: boolean
  created_at: string
  updated_at: string
}

/**
 * A competition's gender, as used by Tournament and SavedCompetitionSearch.
 */
export const CompetitionGender = {
  MALE: 'male',
  FEMALE: 'female',
  MIXED: 'mixed',
} as const

/**
 * A competition's gender, as used by Tournament and SavedCompetitionSearch.
 */
export type CompetitionGender =
  (typeof CompetitionGender)[keyof typeof CompetitionGender]

/**
 * A minimal competition stub embedded in another resource (e.g. a club's list of upcoming competitions), rather than the full Competition shape.
 */
export type CompetitionPreview = {
  id: number
  name: string
  from: string
  to: string
}

/**
 * Response of GET /competitions/{id}: a competition plus its club and the (reduced) tournament brackets held within it.
 */
export type CompetitionResponse = {
  id: number
  fit_competition_id: string
  new_fit_competition_id?: string | null
  name: string
  from?: string | null
  to?: string | null
  championship?: boolean | null
  club: ClubPreview | null
  tournaments: Array<TournamentPreview>
  /**
   * Championships only: the clubs playing the team matches, home or away, by name. Empty for the other competitions.
   */
  teams: Array<{
    id: number
    name: string
    /**
     * FIT region code, e.g. MAR
     */
    region: string
    /**
     * Province code, e.g. AN
     */
    province: string
  }>
}

/**
 * A competition result shape returned by POST /competitions/advanced-search when filtered by gender or by region+status — includes each tournament's optional `evaluation` (computed difficulty metrics, only present when `with_eval` was requested).
 */
export type CompetitionSearch = {
  id: number
  name: string
  address?: string | null
  dates?: string | null
  new_fit_competition_id?: string | null
  club: {
    id?: number
    name?: string
    region?: string | null
    province?: string | null
  } | null
  tournaments: Array<{
    id?: number
    name?: string
    gender?: Gender | null
    surface?: Surface | null
    rankings_category?: number | null
    age_category_raw?: AgeCategory | null
    sign_up_order?: string | null
    average_rating?: number | null
    players_count?: number | null
    max_number_partecipants?: number | null
    court_type?: CourtType | null
    evaluation?: {
      absolute_level?: number | null
      danger_index?: number | null
      personal_difficulty?: number | null
      confidence_score?: number | null
      analyzed_players_count?: number | null
      total_players_count?: number | null
    } | null
  }>
}

/**
 * A lightweight opponent stub used inside connection data (head-to-head, common opponents, club mates).
 */
export type ConnectionOpponentInfo = {
  id: number
  full_name: string
  rank: PlainRank | null
  thumbnail: string | null
}

/**
 * Response for GET /fit-player-profiles/{id}/connections_reduced: the same relationship as ConnectionsResponse, but as counts only (used for a summary badge rather than the full connections list).
 */
export type ConnectionsReducedResponse = {
  h2h_matches: number
  common_opponents_count: number
  common_opponents_matches: number
  club_mates_count: number
  club_mates_matches: number
}

/**
 * Response for GET /fit-player-profiles/{id}/connections: how the viewer relates to the target player — head-to-head record, opponents they have in common, and shared club mates. Used to surface a "how you know this player" section on their profile.
 */
export type ConnectionsResponse = {
  /**
   * Matches directly between the viewer and the target player.
   */
  h2h: Array<SimulatorMatch>
  common_opponents: Array<CommonOpponentData>
  club_mates: Array<ClubMateData>
}

/**
 * The type of court a tournament/draw is played on.
 */
export const CourtType = {
  INDOOR: 'indoor',
  OUTDOOR: 'outdoor',
  OTHER_TYPE: 'other_type',
} as const

/**
 * The type of court a tournament/draw is played on.
 */
export type CourtType = (typeof CourtType)[keyof typeof CourtType]

/**
 * A promotional banner (image + click-through URL), typically assigned to one or more placements via PositionedBanner.
 */
export type CustomBanner = {
  id: number
  /**
   * Banner image URL.
   */
  image: string | null
  /**
   * URL opened when the banner is tapped.
   */
  destination: string | null
}

/**
 * Writable custom banner fields. Can be sent flat (as here) or nested under a `custom_banner` key.
 */
export type CustomBannerInput = {
  /**
   * Banner image, as a base64 data URI (`data:image/png;base64,...`).
   */
  image?: string
  /**
   * URL opened when the banner is tapped.
   */
  destination?: string
}

/**
 * Either the CustomBannerWithOutcomeResponse success shape, or a ValidationErrorResponse (this operation answers 200 for both the happy path and a validation failure).
 */
export type CustomBannerMutationResponse =
  CustomBannerWithOutcomeResponse | ValidationErrorResponse

/**
 * A CustomBanner record serialized as-is (all columns, image as the CarrierWave uploader `{ url }`), as embedded in PositionedBannerResponse.
 */
export type CustomBannerRecord = {
  id: number
  destination?: string | null
  image?: {
    url?: string | null
  }
  positioned_banner_id?: number | null
  created_at?: string | null
  updated_at?: string | null
}

/**
 * Response wrapping a created/updated/deleted custom banner.
 */
export type CustomBannerWithOutcomeResponse = {
  success: boolean
  custom_banner: CustomBanner
}

/**
 * Paginated (20 per page) list of custom banners, most recent first.
 */
export type CustomBannersIndexResponse = {
  data: Array<CustomBanner>
  page: number
  total_pages: number
}

/**
 * A metric of a daily report source, from the catalog shared by the job, the MCP `daily_report` tool and this API.
 */
export type DailyReportMetric = {
  /**
   * Stable key used in `values` and `summary`.
   */
  key: string
  /**
   * Italian label for the backoffice.
   */
  label: string
  unit: 'count' | 'money' | 'seconds' | 'minutes' | 'hours' | 'percent'
  /**
   * How several days are combined (week/month rows and `summary`): `sum` adds up the days, `last` takes the last day with data (totals, active users), `avg` averages the days with data (durations, rates).
   */
  aggregation: 'sum' | 'last' | 'avg'
  /**
   * ISO currency of `money` metrics.
   */
  currency: string | null
  description: string | null
}

/**
 * The history of every daily report source for the same period.
 */
export type DailyReportOverviewResponse = {
  /**
   * One per source, in the catalog order.
   */
  reports: Array<DailyReportResponse>
}

/**
 * Daily report history of one source, read from the database.
 */
export type DailyReportResponse = {
  source: DailyReportSourceKey
  label: string
  period: {
    from: string
    to: string
    days: number
    granularity: 'day' | 'week' | 'month' | 'year'
  }
  metrics: Array<DailyReportMetric>
  /**
   * Only rows with data, oldest first.
   */
  rows: Array<DailyReportRow>
  summary: DailyReportValues
  /**
   * The period of the same length that ends the day before `period.from`.
   */
  previous_period: {
    from: string
    to: string
  }
  /**
   * Summary of `previous_period` with the same rules, for the changes.
   */
  previous_summary: DailyReportValues
  days_with_data: number
  /**
   * Days of the period without data, up to the last day the job has already collected (two days ago).
   */
  missing_days: number
  /**
   * Last time a row of the period was written.
   */
  last_collected_at: string | null
}

export type DailyReportRow = {
  /**
   * Day, or first day of the week (Monday)/month/year.
   */
  date: string
  /**
   * Days with data in the row.
   */
  days: number
  values: DailyReportValues
}

export type DailyReportSource = {
  key: DailyReportSourceKey
  label: string
  metrics: Array<DailyReportMetric>
}

/**
 * Daily report source: one scraper of DailyReportJob (one tab of the sheet).
 */
export const DailyReportSourceKey = {
  BACKOFFICE: 'backoffice',
  GA4: 'ga4',
  ADMOB: 'admob',
  YOUTUBE: 'youtube',
  RECHARGE: 'recharge',
  RENEWALS: 'renewals',
  META: 'meta',
  WORDPRESS: 'wordpress',
  SPOTIFY: 'spotify',
  TIKTOK: 'tiktok',
  LINKEDIN: 'linkedin',
} as const

/**
 * Daily report source: one scraper of DailyReportJob (one tab of the sheet).
 */
export type DailyReportSourceKey =
  (typeof DailyReportSourceKey)[keyof typeof DailyReportSourceKey]

export type DailyReportSourcesResponse = {
  sources: Array<DailyReportSource>
}

/**
 * Metric key => value. Null when no day of the row has that metric.
 */
export type DailyReportValues = {
  [key: string]: number | null
}

/**
 * One elimination bracket within a Tournament (e.g. the main draw, or a qualifying draw). Its matches make up the games played in this bracket.
 */
export type Draw = {
  id: number
  /**
   * FIT federation draw identifier
   */
  fit_draw_id: string
  tournament_id: number
  name: string
  /**
   * Number of entered players
   */
  subcribers_count?: number | null
  /**
   * Number of direct-entry slots
   */
  entries_count?: number | null
  /**
   * Number of qualifier/wildcard slots
   */
  outer_count?: number | null
  /**
   * Number of seeded players
   */
  head_series_count?: number | null
  /**
   * Highest round number reached so far
   */
  last_round?: number | null
  /**
   * Total number of rounds in the bracket
   */
  round_count?: number | null
  /**
   * FitPlayerProfile id of the champion
   */
  winner_id?: number | null
  /**
   * Ranking assigned to a "lucky loser" reinstated into the draw despite an early exit
   */
  best_loser_ranking: number
  created_at: string
  updated_at: string
  draw_type: string
  home_club_id?: number | null
  away_club_id?: number | null
  score?: string | null
  match_date?: string | null
  ball_type?: string | null
  surface?: Surface | null
}

/**
 * The parent competition embedded in a Draw's payload: the minimal stub plus its dates and organizing club.
 */
export type DrawCompetition = {
  id: number
  name: string
  fit_competition_id?: string | null
  new_fit_competition_id?: string | null
  /**
   * dd/mm/yyyy, the last day
   */
  date: string
  championship?: boolean | null
  /**
   * dd/mm/yyyy
   */
  from: string | null
  /**
   * dd/mm/yyyy
   */
  to: string | null
  /**
   * The organizing club; null for team championships.
   */
  club: ClubPreview | null
}

/**
 * Response of GET /draws/{id} and the draw-mutation endpoints (delete-matches, change-winner, change-best-loser-ranking): the full draw plus its matches and parent tournament/competition.
 */
export type DrawResponse = {
  id: number
  fit_draw_id?: string | null
  name: string
  subcribers_count?: number | null
  entries_count?: number | null
  outer_count?: number | null
  head_series_count?: number | null
  last_round?: number | null
  round_count?: number | null
  best_loser_ranking?: number | null
  score?: string | null
  match_date?: string | null
  surface?: Surface | null
  ball_type?: string | null
  home_club?: SearchClubPreview | null
  away_club?: SearchClubPreview | null
  winner?: InlineSearchPlayer | null
  tournament: PreviewTournament
  competition: DrawCompetition
  matches: Array<MatchResponse>
}

/**
 * A minimal draw stub embedded in another resource, rather than the full Draw/DrawResponse shape.
 */
export type DrawSummary = {
  id: number
  name: string
  fit_draw_id: string
  match_date?: string | null
  surface?: Surface | null
}

/**
 * GET /edit-profile/profiles response: the current user's role-specific profiles. Every slot is null when the user has no such profile.
 */
export type EditProfileProfilesResponse = {
  fit_player: {
    fit_player_id?: string | null
  } | null
  nutritionist: {
    id?: number
    qualifications?: string | null
    professional_info?: string | null
  } | null
  mental_coach: {
    id?: number
    qualifications?: string | null
    professional_info?: string | null
  } | null
  teacher: {
    id?: number
    qualification?: string | null
    professional_info?: string | null
  } | null
  trainer: {
    id?: number
    qualification?: string | null
    professional_info?: string | null
  } | null
  shop_profile: {
    id?: number
    name?: string | null
    website?: string | null
    info?: string | null
    address?: string | null
  } | null
  club_manager: {
    id?: number
    photo?: string | null
    club?: ClubPreviewFull
  } | null
}

/**
 * What every mutating /edit-profile action returns: the updated user, plus optional feedback about a FIT card lookup performed as a side effect.
 */
export type EditProfileUserResponse = {
  success: boolean
  user: User
  /**
   * Present when the request touched fit_player_profile / a FIT card number.
   */
  fit_card_feedback?: {
    status?: string | null
    message?: string | null
  } | null
}

/**
 * A skill endorsement tally used as a signal in the match simulation.
 */
export type EndorsementInfo = {
  id: number
  /**
   * The endorsed skill's name
   */
  name: string
  /**
   * Number of endorsements received for this skill
   */
  points: number
}

/**
 * Either the SuccessResponse success shape, or a ValidationErrorResponse (this operation answers 200 for both the happy path and a validation failure).
 */
export type EndorsementMutationResponse =
  SuccessResponse | ValidationErrorResponse

/**
 * A skill/trait a user can endorse another player for (e.g. "dritto potente"), and how many endorsement points it's worth.
 */
export type EndorsementType = {
  id: number
  name: string
  points: number
  /**
   * Present when embedded in an endorsement's response: the ID of that Endorsement record.
   */
  endorsement_id?: number | null
}

/**
 * The masqueraded shape of a club, competition or tournament.
 */
export type EntityMasqueraded = {
  id: number
  name: string
  type: 'club' | 'competition' | 'tournament'
}

/**
 * The generic `{ success: false, errors: ... }` failure shape used across the API — for
 * auth failures, not-found, or any other error that isn't a structured field-validation
 * failure. `errors` is either a flat array of human-readable messages, or (less commonly)
 * a hash of field name → array of messages for that field, depending on the endpoint. See
 * ValidationErrorResponse for the dedicated shape most create/update validation failures
 * use instead.
 */
export type ErrorResponse = {
  success?: boolean
  errors?:
    | Array<string>
    | {
        [key: string]: Array<string>
      }
}

/**
 * An extra entry of the app hamburger menu, managed from the backoffice.
 */
export type ExtraMenuItem = {
  id: number
  /**
   * Text shown in the menu
   */
  label: string
  /**
   * App path starting with `/` (opened inside the app) or an external http(s) URL (opened like the external links of notifications)
   */
  url: string
}

/**
 * Backoffice view of an extra menu item, inactive ones included.
 */
export type ExtraMenuItemAdmin = {
  id: number
  label: string
  url: string
  /**
   * Ascending menu order
   */
  position: number
  /**
   * Only active items reach the app
   */
  active: boolean
  created_at: string
  updated_at: string
}

export type ExtraMenuItemAdminIndexResponse = {
  /**
   * All items in menu order
   */
  data: Array<ExtraMenuItemAdmin>
}

export type ExtraMenuItemAdminResponse = {
  success: boolean
  extra_menu_item: ExtraMenuItemAdmin
}

/**
 * Create/update payload. On update only the given keys change.
 */
export type ExtraMenuItemInput = {
  label?: string
  /**
   * App path starting with `/` or an http(s) URL
   */
  url?: string
  position?: number
  active?: boolean
}

export type ExtraMenuItemsResponse = {
  /**
   * Active items in menu order; empty when there are none
   */
  items: Array<ExtraMenuItem>
}

/**
 * A paginated list of matches for the current user's feed.
 */
export type FeedMatchesResponse = {
  page: number
  total_pages: number
  matches: Array<MatchResponse>
}

/**
 * A paginated list of marketplace offers for the current user's feed.
 */
export type FeedOffersResponse = {
  page: number
  total_pages: number
  offers: Array<OfferSearch>
}

/**
 * A FIT-registered player. Distinct from User: a FitPlayerProfile may or may not be linked to an app account (`user_id`).
 */
export type FitPlayerProfile = {
  id: number
  /**
   * The player's ID in the FIT federation system.
   */
  fit_player_id?: string | null
  /**
   * The app User this profile is linked to, if any.
   */
  user_id?: number | null
  first_name: string
  last_name: string
  full_name: string
  birth_date?: string | null
  gender?: Gender
  region: string
  province: string
  age_category?: AgeCategory | null
  /**
   * Expiry date of the medical fitness certificate.
   */
  certificate_expiration?: string | null
  club_id?: number | null
  /**
   * Whether the player opted out of appearing in searches/rankings listings.
   */
  hidden?: boolean
  /**
   * Unique FIT registry code for this player.
   */
  anagrafica_code?: string | null
  created_at: string
  updated_at: string
}

/**
 * A single row in the filtered/paginated POST /fit-player-profiles/advanced-search results.
 */
export type FitPlayerProfileAdvancedSearch = {
  id: number
  full_name: string
  /**
   * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `full_name` (surname first). May be blank on some legacy records: fall back to `full_name`.
   */
  first_name: string
  /**
   * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
   */
  last_name: string
  rank: PlainRank
  category?: AgeCategory | null
  /**
   * Whether this FIT profile is already linked to an app User account.
   */
  user_exists: boolean
  thumbnail?: string | null
  premium?: boolean | null
  premium_expiration?: string | null
  active: boolean
  club?: ClubPreview | null
  region: string
  province: string
}

/**
 * A member of a club's roster, as embedded in club-related listings.
 */
export type FitPlayerProfileClubMember = {
  id: number
  name: string
  /**
   * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`.
   */
  first_name: string
  /**
   * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
   */
  last_name: string
  /**
   * Member role/profile type, e.g. "player" or "teacher".
   */
  type: string
  thumbnail?: string | null
  premium?: boolean | null
  premium_expiration?: string | null
  /**
   * Whether this member is linked to an app User account.
   */
  user_exists?: boolean
  category?: AgeCategory | null
  rank?: PlainRank
  /**
   * Coaching qualification, present only for teacher/ trainer profile types.
   */
  qualification?: string | null
}

/**
 * A user's request to claim/link an official FIT player profile, with its approval outcome.
 */
export type FitPlayerProfileRequest = {
  id: number
  user: UserPreview
  fit_player_profile: FitPlayerProfileSearch
  /**
   * FIT federation id declared by the user in the request.
   */
  fit_player_id?: string | null
  /**
   * Unix timestamp
   */
  created_at: number
  status: 'pending' | 'approved' | 'rejected'
  /**
   * Unix timestamp
   */
  approved_at?: number | null
  /**
   * Unix timestamp
   */
  rejected_at?: number | null
  approval_notes?: string | null
  rejectal_notes?: string | null
}

/**
 * Paginated (100 per page) list of FIT player profile requests.
 */
export type FitPlayerProfileRequestsIndexResponse = {
  page: number
  total_pages: number
  data: Array<FitPlayerProfileRequest>
}

/**
 * The main "show" payload for a FitPlayerProfile: combines the FIT registry record with app-side aggregates (rank, recent matches, active competitions, social state).
 */
export type FitPlayerProfileResponse = {
  id: number
  /**
   * FIT membership card number.
   */
  card_number?: string | null
  /**
   * Unique FIT registry code.
   */
  anagrafica_code?: string | null
  user_id?: number | null
  name: string
  /**
   * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`.
   */
  first_name: string
  /**
   * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
   */
  last_name: string
  rank: PlainRank
  category?: AgeCategory | null
  thumbnail?: string | null
  /**
   * Total profile page views.
   */
  views: number
  gender?: Gender | null
  /**
   * Whether the player is currently FIT-active (has a valid membership for the current year).
   */
  active: boolean
  /**
   * Whether the player is Italian (as opposed to a foreign player registered with FIT).
   */
  ita: boolean
  /**
   * Whether the linked User has a premium subscription.
   */
  premium: boolean | null
  premium_expiration?: string | null
  /**
   * Whether the requesting user follows this profile.
   */
  followed: boolean
  /**
   * Polymorphic type discriminator for follow/like/report endpoints, always "FitPlayerProfile" here.
   */
  followable_type: string
  fit_player_profile_id: number
  shop_profile_id?: number | null
  age: number
  email?: string | null
  /**
   * The player's last matches, true where won, most recent last — used to render a win/loss streak strip.
   */
  state_of_form?: Array<boolean>
  profiles: {
    teacher?: boolean
    trainer?: boolean
    mental_coach?: boolean
    nutritionist?: boolean
    shop?: boolean
  }
  club?: {
    id?: number
    name?: string
    region?: string
    province?: string
  } | null
  matches: {
    won?: number
    lost?: number
    won_percentage?: number
  }
  /**
   * Simulated/projected ranking figures for the current fit_year, computed by FitPointsCalculator.
   */
  info: {
    /**
     * Simulated FIT points for the current year (results not yet officially published).
     */
    fit_points?: number
    simulated_rank?: PlainRank
    /**
     * How many more FIT points the player needs to be promoted to the next rank.
     */
    points_to_rank_switch?: number
    /**
     * Rank positions gained (positive) or lost (negative) since the year's first ranking.
     */
    first_rank_difference?: number
  }
  last_matches: Array<MatchPreview>
  /**
   * The player's best-ever rank and the year it was achieved.
   */
  max_rank: {
    rank?: PlainRank
    year?: number
  }
  active_competitions: Array<CompetitionPreview>
  /**
   * Whether the year-in-review "wrapped" feature is available for this profile (see GET /{id}/wrapped).
   */
  enable_wrapped: boolean
  last_match_year?: number
  /**
   * The current FIT season year (the FIT season does not align to the calendar year).
   */
  fit_year?: number
}

/**
 * A single row in the lightweight typeahead-style GET /fit-player-profiles/search results.
 */
export type FitPlayerProfileSearch = {
  id: number
  name: string
  /**
   * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`.
   */
  first_name: string
  /**
   * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
   */
  last_name: string
  rank: PlainRank
  category?: AgeCategory | null
  /**
   * Whether the profile has a real (10-character) FIT federation id (`FitPlayerProfile#real_player_id?`). A flag, not the id itself.
   */
  real_player_id: boolean
}

/**
 * A follow relationship: the author (implicit, the current user) follows a polymorphic `followable` entity (e.g. a User or a FitPlayerProfile).
 */
export type Follow = {
  followable_id: number
  /**
   * Class of the followed entity, e.g. "User" or "FitPlayerProfile".
   */
  followable_type: string
}

/**
 * The result of creating a Follow.
 */
export type FollowResponse = {
  success: boolean
  follow: Follow
}

/**
 * The followers/following lists for a profile.
 */
export type FollowersListResponse = {
  followers: Array<UserFollowers>
  following: Array<PlayerMasqueraded | Author>
}

/**
 * A player's gender, as registered with FIT.
 */
export const Gender = { MALE: 'male', FEMALE: 'female' } as const

/**
 * A player's gender, as registered with FIT.
 */
export type Gender = (typeof Gender)[keyof typeof Gender]

/**
 * Clubs by name or by the full name of their region/province ("lombardia", "milano"), active clubs first, then most similar. Null when the type was not requested with `types`.
 */
export type GlobalSearchClubs = {
  /**
   * Matches of this type across all pages
   */
  total_count: number
  page: number
  total_pages: number
  results: Array<SearchClub>
} | null

/**
 * Competitions by name, club name or the full name of the club region/province, most similar first, then most recent. Null when the type was not requested with `types`.
 */
export type GlobalSearchCompetitions = {
  /**
   * Matches of this type across all pages
   */
  total_count: number
  page: number
  total_pages: number
  results: Array<SearchCompetition>
} | null

/**
 * FIT players by first and last name, in the players search order (active card holders first, then by ranking). Null when the type was not requested with `types`.
 */
export type GlobalSearchPlayers = {
  /**
   * Matches of this type across all pages
   */
  total_count: number
  page: number
  total_pages: number
  results: Array<SearchPlayer>
} | null

/**
 * User posts by content, newest first, without those of banned users. Null when the type was not requested with `types`.
 */
export type GlobalSearchPosts = {
  /**
   * Matches of this type across all pages
   */
  total_count: number
  page: number
  total_pages: number
  results: Array<SearchPost>
} | null

/**
 * GET /search results, one section per type. Text is matched ignoring case, accents and apostrophes ("d amico", "D'AMICO" and "damico" are the same); every word of the query must match.
 */
export type GlobalSearchResponse = {
  /**
   * The query as received
   */
  query: string
  players: GlobalSearchPlayers
  users: GlobalSearchUsers
  clubs: GlobalSearchClubs
  competitions: GlobalSearchCompetitions
  posts: GlobalSearchPosts
}

/**
 * Users by full name, nickname or the name of their linked FIT player profile (hidden profiles excluded), most similar first. Null when the type was not requested with `types`.
 */
export type GlobalSearchUsers = {
  /**
   * Matches of this type across all pages
   */
  total_count: number
  page: number
  total_pages: number
  results: Array<SearchOtherProfile>
} | null

/**
 * An image to attach to an offer or post. On create only `url` is read; on update, an entry with an `id` is kept as-is and `_destroy: true` removes it.
 */
export type ImageInput = {
  /**
   * Existing image id; omit when adding a new image
   */
  id?: number | null
  /**
   * Image URL, used when adding a new image
   */
  url?: string | null
  /**
   * Set true to remove this image (update only)
   */
  _destroy?: boolean
}

/**
 * A club as shown in the home screen's lightweight "type-ahead" search (POST /home/inline-search), a smaller shape than the dedicated search tabs.
 */
export type InlineSearchClub = {
  id: number
  name: string | null
  members_counter?: number | null
  fields_address_display_info?: string | null
  phone?: string | null
  region?: string | null
  province?: string | null
  managed: boolean
  active?: boolean | null
}

/**
 * A competition as shown in the home screen's "type-ahead" inline search.
 */
export type InlineSearchCompetition = {
  id: number
  name?: string | null
  club_name?: string | null
  dates?: string | null
  new_fit_competition_id?: string | null
}

/**
 * A reduced FitPlayerProfile shape reused across many endpoints (inline search, draw entries, tournament participant lists, etc.) wherever a lightweight player reference is enough.
 */
export type InlineSearchPlayer = {
  id: number
  category: AgeCategory | null
  name: string | null
  full_name: string | null
  /**
   * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`.
   */
  first_name: string
  /**
   * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
   */
  last_name: string
  rank: PlainRank | null
  club_name: string | null
  user_exists: boolean
  thumbnail: string | null
  premium: boolean | null
  premium_expiration: string | null
  active: boolean
  region: string | null
  province: string | null
}

/**
 * Response of POST /home/inline-search-players: the players-only variant of the inline search, used where the UI only needs player results.
 */
export type InlineSearchPlayersResponse = {
  results: {
    players: Array<InlineSearchPlayer>
  }
}

/**
 * Response of POST /home/inline-search: all four categories at once, for the home screen quick-search widget.
 */
export type InlineSearchResponse = {
  results: {
    players: Array<InlineSearchPlayer>
    clubs: Array<InlineSearchClub>
    competitions: Array<InlineSearchCompetition>
    other_profiles: Array<InlineSearchUser>
  }
}

/**
 * A non-player (professional-role) profile as shown in the home screen's "type-ahead" inline search.
 */
export type InlineSearchUser = {
  id: number
  name: string | null
  player_id?: number | null
  teacher: boolean
  trainer: boolean
  mental_coach: boolean
  nutritionist: boolean
  shop: boolean
  player: boolean
  thumbnail?: string | null
}

/**
 * Identity of the logged-in user for the Intercom Messenger (Messenger Security). Pass `jwt` to the SDK (`intercom_user_jwt` on the web, `setUserJwt` in the app) together with the user id, and ask for a new one after `expires_at`.
 */
export type IntercomMessengerToken = {
  /**
   * HS256 JWT with the claims `user_id` (TennisTalker user id as a string), `email` and `exp`, signed with the workspace Messenger secret.
   */
  jwt: string
  /**
   * When the JWT stops being accepted.
   */
  expires_at: string
  /**
   * Contact attributes to pass to the Messenger as they are, with the same names and values the backend writes on tickets (INTERCOM_CONTACT_ATTRIBUTE_MAP), e.g. `tt_user_id`, `player_url`, `premium`, `premium_expiration` (Unix seconds). Empty when the map is not set.
   */
  attributes: {
    [key: string]: string | number | boolean | null
  }
}

/**
 * Returned by POST /update-tasks/run_async_job once the job is enqueued.
 */
export type JobEnqueuedResponse = {
  message: string
  success: boolean
}

/**
 * Public view of a live event: branding, game settings and state.
 */
export type LiveEvent = {
  id: number
  /**
   * URL identifier (`/evento/{slug}`)
   */
  slug: string
  name: string
  brand_name: string | null
  description: string | null
  /**
   * Active and within starts_at/ends_at: games can be started
   */
  open: boolean
  starts_at: string | null
  ends_at: string | null
  /**
   * Big screen on: live games go through the queue
   */
  screen_enabled: boolean
  /**
   * Live games need the `live_key` printed in the QR
   */
  live_key_required: boolean
  /**
   * Seconds the called player has to start
   */
  queue_call_timeout_seconds: number
  game_type: 'wall_rally'
  game_config: LiveEventGameConfig
  theme: LiveEventTheme
  /**
   * Brand logo URL, drawn on the wall; null = TennisTalker mark
   */
  logo: string | null
}

/**
 * Backoffice view of a live event: the public fields plus secrets and raw settings.
 */
export type LiveEventAdmin = LiveEvent & {
  /**
   * Visible to users
   */
  active: boolean
  /**
   * Static key embedded in the official QR
   */
  live_key: string | null
  /**
   * Only the stored overrides (game_config has defaults merged in)
   */
  raw_game_config: {
    [key: string]: unknown
  }
  /**
   * Only the stored overrides (theme has the TennisTalker defaults merged in)
   */
  raw_theme: LiveEventThemeInput
  created_at: string
  updated_at: string
}

export type LiveEventAdminIndexResponse = {
  data: Array<LiveEventAdmin>
  page: number
  total_pages: number
}

export type LiveEventAdminResponse = {
  success: boolean
  live_event: LiveEventAdmin
}

export type LiveEventAdminSessionResponse = {
  success: boolean
  session: LiveEventGameSessionAdmin
}

export type LiveEventAdminSessionsResponse = {
  data: Array<LiveEventGameSessionAdmin>
  page: number
  total_pages: number
}

/**
 * The caller's best valid score per mode, null if never played that mode.
 */
export type LiveEventBestScores = {
  live: number | null
  home: number | null
}

export type LiveEventDetailResponse = {
  success: boolean
  live_event: LiveEvent
  /**
   * The caller's queued/called/playing session, null when none or logged out
   */
  active_session: LiveEventGameSession | null
  /**
   * Null when logged out
   */
  best: LiveEventBestScores | null
}

/**
 * Difficulty settings of the "Il muro" game. The event stores only overrides; responses always carry the full set (defaults merged in).
 */
export type LiveEventGameConfig = {
  /**
   * Misses allowed before game over
   */
  lives: number
  /**
   * Initial ball speed multiplier (1 = normal)
   */
  base_speed: number
  /**
   * Speed increase per hit (0.05 = +5% per hit)
   */
  speed_step: number
  /**
   * Maximum ball speed multiplier
   */
  max_speed: number
  /**
   * Initial racket width, fraction of the field
   */
  racket_width: number
  /**
   * Minimum racket width, fraction of the field
   */
  min_racket_width: number
  /**
   * A bonus target appears on the wall every N hits
   */
  target_every_hits: number
  /**
   * Extra points when the ball hits a target
   */
  target_points: number
}

/**
 * `live` = competitive game started from the official QR code (main leaderboard); `home` = practice game started from the app (secondary leaderboard).
 */
export const LiveEventGameMode = { HOME: 'home', LIVE: 'live' } as const

/**
 * `live` = competitive game started from the official QR code (main leaderboard); `home` = practice game started from the app (secondary leaderboard).
 */
export type LiveEventGameMode =
  (typeof LiveEventGameMode)[keyof typeof LiveEventGameMode]

/**
 * A game (or a place in the big-screen queue) of the caller.
 */
export type LiveEventGameSession = {
  id: number
  live_event_id: number
  mode: LiveEventGameMode
  status: LiveEventSessionStatus
  /**
   * This game is (or will be) mirrored on the big screen
   */
  on_screen: boolean
  /**
   * Random seed of the game engine
   */
  seed: number
  /**
   * 1 = next to play; null when not queued
   */
  queue_position: number | null
  /**
   * Deadline to start when called for the big screen
   */
  call_expires_at: string | null
  score: number | null
  hits: number | null
  bonus_hits: number | null
  level: number | null
  duration_ms: number | null
  queued_at: string | null
  started_at: string | null
  finished_at: string | null
  /**
   * Cancelled by an admin: excluded from leaderboards
   */
  invalidated: boolean
}

/**
 * Backoffice view of a game session, with moderation fields and the player.
 */
export type LiveEventGameSessionAdmin = LiveEventGameSession & {
  /**
   * `suspicious: true` when the reported score is inconsistent with hits/duration
   */
  stats: {
    [key: string]: unknown
  }
  called_at: string | null
  last_seen_at: string | null
  invalidated_at: string | null
  invalidated_reason: string | null
  invalidated_by_id: number | null
  /**
   * Soft-deleted
   */
  deleted: boolean
  user: {
    id: number
    name: string
    email: string
  }
}

/**
 * Create/update payload. The logo is a base64 data URI; `remove_logo` deletes it.
 */
export type LiveEventInput = {
  name?: string
  slug?: string
  brand_name?: string | null
  description?: string | null
  active?: boolean
  starts_at?: string | null
  ends_at?: string | null
  live_key?: string | null
  queue_call_timeout_seconds?: number
  game_type?: 'wall_rally'
  game_config?: {
    [key: string]: number
  }
  theme?: LiveEventThemeInput
  /**
   * base64 data URI
   */
  logo?: string
  remove_logo?: boolean
}

export type LiveEventLeaderboard = {
  success: boolean
  mode: LiveEventGameMode
  /**
   * Distinct players with a valid score
   */
  players_count: number
  data: Array<LiveEventLeaderboardRow>
  /**
   * The caller's row (also when outside `data`); null when logged out or never played
   */
  me: LiveEventLeaderboardRow | null
}

/**
 * A player's best valid score in one mode (ties: the earliest wins).
 */
export type LiveEventLeaderboardRow = {
  position: number
  session_id: number
  score: number
  level: number | null
  finished_at: string
  user: LiveEventUser
}

export type LiveEventQueueEntry = {
  /**
   * 1 = next
   */
  position: number
  session_id: number
  user: LiveEventUser | null
}

/**
 * Big screen state: who holds the screen and the first 10 players in the queue.
 */
export type LiveEventQueueResponse = {
  success: boolean
  screen_enabled: boolean
  current: LiveEventQueueSession | null
  queue_length: number
  queue: Array<LiveEventQueueEntry>
}

/**
 * The game currently holding the big screen (called or playing).
 */
export type LiveEventQueueSession = {
  id: number
  mode: LiveEventGameMode
  status: LiveEventSessionStatus
  on_screen: boolean
  seed: number
  queue_position: number | null
  call_expires_at: string | null
  score: number | null
  user: LiveEventUser | null
}

export type LiveEventSessionResponse = {
  success: boolean
  session: LiveEventGameSession
}

/**
 * queued → called (your turn, on the big screen) → playing → finished. `expired`: missed the turn or lost connection; `cancelled`: left the queue or replaced.
 */
export const LiveEventSessionStatus = {
  QUEUED: 'queued',
  CALLED: 'called',
  PLAYING: 'playing',
  FINISHED: 'finished',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
} as const

/**
 * queued → called (your turn, on the big screen) → playing → finished. `expired`: missed the turn or lost connection; `cancelled`: left the queue or replaced.
 */
export type LiveEventSessionStatus =
  (typeof LiveEventSessionStatus)[keyof typeof LiveEventSessionStatus]

/**
 * Brand theme of the event, with the TennisTalker defaults merged in (lime, deep olive court, iris targets): a branded event changes at most the logo and these three colours.
 */
export type LiveEventTheme = {
  /**
   * Buttons, highlights and racket (#rrggbb)
   */
  primary_color: string
  /**
   * Court and dark surfaces (#rrggbb)
   */
  secondary_color: string
  /**
   * Bonus targets on the wall (#rrggbb)
   */
  accent_color: string
  /**
   * Defaults to the event name
   */
  title?: string
  subtitle?: string
}

/**
 * Theme overrides. Colours are #rrggbb; a blank or missing colour falls back to the TennisTalker default. Unknown keys are dropped.
 */
export type LiveEventThemeInput = {
  primary_color?: string
  secondary_color?: string
  accent_color?: string
  title?: string
  subtitle?: string
}

/**
 * Player identity shown on leaderboards and on the big screen.
 */
export type LiveEventUser = {
  id: number
  name: string
  first_name: string
  photo: string | null
}

/**
 * How a session was opened: email/password, a social login, the email confirmation link (`email_confirmation`) or the admin backoffice login.
 */
export const LoginMethod = {
  PASSWORD: 'password',
  APPLE: 'apple',
  GOOGLE: 'google',
  FACEBOOK: 'facebook',
  FACEBOOK_LIMITED: 'facebook_limited',
  EMAIL_CONFIRMATION: 'email_confirmation',
  BACKOFFICE: 'backoffice',
} as const

/**
 * How a session was opened: email/password, a social login, the email confirmation link (`email_confirmation`) or the admin backoffice login.
 */
export type LoginMethod = (typeof LoginMethod)[keyof typeof LoginMethod]

/**
 * Where a sign-up or login happened: `web` (routes under /api) or the native app (routes under /app).
 */
export const LoginSource = { WEB: 'web', APP: 'app' } as const

/**
 * Where a sign-up or login happened: `web` (routes under /api) or the native app (routes under /app).
 */
export type LoginSource = (typeof LoginSource)[keyof typeof LoginSource]

/**
 * Any polymorphic author/recipient rendered through its class's `_masqueraded` partial. Discriminate on `type`: `user` (MessagesBackofficeUser), `fit_player_profile` (PlayerMasqueraded), `club_manager_profile`, the professional profiles, or club/competition/tournament.
 */
export type MasqueradedEntity =
  | MessagesBackofficeUser
  | PlayerMasqueraded
  | ClubManagerProfileMasqueraded
  | ProfessionalProfileMasqueraded
  | EntityMasqueraded

/**
 * An officially recorded match within a Draw. first_winner/first_loser are the singles (or doubles pair 1) players; second_winner/second_loser are the doubles partners, null for singles matches.
 */
export type Match = {
  id: number
  draw_id: number
  /**
   * Round number within the draw
   */
  round_id?: number | null
  /**
   * FitPlayerProfile id of the winner
   */
  first_winner_id: number
  /**
   * Winner doubles partner FitPlayerProfile id
   */
  second_winner_id?: number | null
  /**
   * FitPlayerProfile id of the loser
   */
  first_loser_id: number
  /**
   * Loser doubles partner FitPlayerProfile id
   */
  second_loser_id?: number | null
  /**
   * Public score, e.g. '6-4 6-2'
   */
  result: string
  /**
   * Score as recorded internally (may include retirement notation etc.)
   */
  internal_result: string
  /**
   * Ranking-points weight of this match
   */
  weight: number
  /**
   * External FIT match identifier
   */
  fit_match_id?: string | null
  /**
   * Whether the loser retired mid-match
   */
  retire?: boolean | null
  /**
   * Whether a player was a no-show
   */
  absent?: boolean | null
  /**
   * Whether a doubles match counts toward ranking points
   */
  double_valid_for_rankings: boolean
  /**
   * Whether this record was manually edited
   */
  modified: boolean
  created_at: string
  updated_at: string
}

/**
 * Either the MatchUpdateResponse success shape, or a ValidationErrorResponse (this operation answers 200 for both the happy path and a validation failure).
 */
export type MatchCreateResponse = MatchUpdateResponse | ValidationErrorResponse

/**
 * Detail of a match to organise, from the point of view of the viewer.
 */
export type MatchFinder = {
  viewer_role: 'creator' | 'participant' | 'requester' | 'visitor'
  seats: Array<MatchFinderSeat>
  /**
   * A request is live (visible to everyone).
   */
  request_pending: boolean
  /**
   * The seat the live request is for, for every viewer. Null without one.
   */
  pending_seat: 'teammate' | 'opponent' | 'second_opponent' | null
  request: MatchFinderRequest | null
  can_request: boolean
  can_withdraw_request: boolean
  can_answer_request: boolean
  can_edit: boolean
  can_cancel: boolean
  can_leave: boolean
  /**
   * The invited side of an upcoming proposed match (opponent or second opponent, not the challenger teammate): POST .../accept or .../decline.
   */
  can_answer_invite: boolean
  /**
   * Any player of a full match that has started and has no result: PUT .../result.
   */
  can_add_result: boolean
  /**
   * Any player of a played match until the result is confirmed (then admins only): PUT .../result replaces it (last version wins) and sends it back to the others to confirm.
   */
  can_edit_result: boolean
  /**
   * A player other than the last submitter, while unconfirmed: PUT .../confirm or .../reject.
   */
  can_confirm_result: boolean
  /**
   * Who entered or corrected the result last (played only).
   */
  result_submitted_by_user_id: number | null
  /**
   * Version of the result: ISO 8601 with microseconds, a string on purpose. Send it back unchanged to PUT .../confirm and .../reject.
   */
  result_submitted_at: string | null
  /**
   * When the result confirms itself if nobody answers (24h after the last submission, applied hourly). Null once confirmed or rejected.
   */
  result_auto_confirms_at: string | null
  reminder_enabled: boolean
  celebrated: boolean
  creator_is_member: boolean
  /**
   * Only in the open list: club = within 300 m of the viewer club courts.
   */
  section: 'club' | 'nearby' | null
  /**
   * Open list only: from the sent lat/lng, else from the profile city when radius_km is sent, else from the viewer club.
   */
  distance_km: number | null
  /**
   * True when the court is not booked yet and a distance is present.
   */
  distance_approx: boolean
}

/**
 * The live seat request. Only the creator and the requester receive it.
 */
export type MatchFinderRequest = {
  seat: 'teammate' | 'opponent' | 'second_opponent'
  /**
   * ISO 8601 with microseconds, a string on purpose (a JS Date drops them). Send it back unchanged, with player.user_id, to POST .../request/accept and .../request/reject.
   */
  requested_at: string
  expires_at: string
  starts_at?: string | null
  ends_at?: string | null
  /**
   * Free text the requester sent with the request (max 280).
   */
  note?: string | null
  player: SeatPlayer
}

export type MatchFinderSeat = {
  seat: 'author' | 'teammate' | 'opponent' | 'second_opponent'
  team: 1 | 2
  /**
   * null for a free seat.
   */
  player: SeatPlayer | null
}

/**
 * A minimal match stub embedded in another resource, from the perspective of a specific player ('has_won' relative to them).
 */
export type MatchPreview = {
  id: number
  result?: string | null
  /**
   * Whether the subject player won this match
   */
  has_won?: boolean | null
  first_opponent?: PlayerMasqueraded
  second_opponent?: PlayerMasqueraded | null
}

/**
 * A match as returned by GET/POST/PUT /matches — includes its parent draw/tournament/competition and computed per-caller permissions.
 */
export type MatchResponse = {
  id: number
  /**
   * Round number within the draw
   */
  round_id?: number | null
  /**
   * Ranking-points weight of this match
   */
  weight: number
  result?: string | null
  /**
   * Unix timestamp of the match
   */
  date: number
  /**
   * Whether the current caller may edit this match
   */
  is_editable: boolean
  /**
   * Whether the current caller may delete this match
   */
  is_deletable: boolean
  is_played: boolean
  /**
   * Resource-type discriminator
   */
  type: 'match'
  draw: DrawSummary
  tournament: PreviewTournament
  competition: PreviewCompetition
  first_winner: PlayerMasqueraded
  second_winner: PlayerMasqueraded | null
  first_loser: PlayerMasqueraded
  second_loser: PlayerMasqueraded | null
}

/**
 * The match shape returned by GET /matches (list): a flatter, inline variant of MatchResponse with its own draw/tournament/competition/player sub-shapes rather than shared $refs.
 */
export type MatchSearch = {
  id: number
  /**
   * Round number within the draw
   */
  round_id?: number | null
  result?: string | null
  draw: {
    id: number
    name: string
    fit_draw_id?: string | null
    match_date?: string | null
    surface?: Surface | null
  }
  tournament: {
    id: number
    name: string
    fit_tournament_id?: string | null
    new_fit_tournament_id?: string | null
    age_category?: AgeCategory | null
    rankings_category?: number | null
    surface?: Surface | null
    court_type?: CourtType | null
    /**
     * Whether the caller follows the tournament.
     */
    followed?: boolean
    followable_type?: 'tournament'
  }
  competition: {
    id: number
    name: string
    fit_competition_id?: string | null
    new_fit_competition_id?: string | null
    date: string
    championship: boolean
  }
  first_winner: {
    id: number
    name?: string | null
    /**
     * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`. Null for hidden players; absent when the entry is not a FIT player.
     */
    first_name?: string | null
    /**
     * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
     */
    last_name?: string | null
    type: string
    club?: {
      id?: number
      name?: string
      region?: string | null
      province?: string | null
    } | null
    user_exists: boolean
    premium?: boolean | null
    premium_expiration?: string | null
    thumbnail?: string | null
    rank?: PlainRank | null
    category?: AgeCategory | null
    region?: string | null
    province?: string | null
    active_24h?: boolean | null
    active_1m?: boolean | null
  } | null
  second_winner: {
    id: number
    name?: string | null
    /**
     * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`. Null for hidden players; absent when the entry is not a FIT player.
     */
    first_name?: string | null
    /**
     * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
     */
    last_name?: string | null
    type: string
    club?: {
      id?: number
      name?: string
      region?: string | null
      province?: string | null
    } | null
    user_exists: boolean
    premium?: boolean | null
    premium_expiration?: string | null
    thumbnail?: string | null
    rank?: PlainRank | null
    category?: AgeCategory | null
    region?: string | null
    province?: string | null
    active_24h?: boolean | null
    active_1m?: boolean | null
  } | null
  first_loser: {
    id: number
    name?: string | null
    /**
     * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`. Null for hidden players; absent when the entry is not a FIT player.
     */
    first_name?: string | null
    /**
     * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
     */
    last_name?: string | null
    type: string
    club?: {
      id?: number
      name?: string
      region?: string | null
      province?: string | null
    } | null
    user_exists: boolean
    premium?: boolean | null
    premium_expiration?: string | null
    thumbnail?: string | null
    rank?: PlainRank | null
    category?: AgeCategory | null
    region?: string | null
    province?: string | null
    active_24h?: boolean | null
    active_1m?: boolean | null
  } | null
  second_loser: {
    id: number
    name?: string | null
    /**
     * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`. Null for hidden players; absent when the entry is not a FIT player.
     */
    first_name?: string | null
    /**
     * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
     */
    last_name?: string | null
    type: string
    club?: {
      id?: number
      name?: string
      region?: string | null
      province?: string | null
    } | null
    user_exists: boolean
    premium?: boolean | null
    premium_expiration?: string | null
    thumbnail?: string | null
    rank?: PlainRank | null
    category?: AgeCategory | null
    region?: string | null
    province?: string | null
    active_24h?: boolean | null
    active_1m?: boolean | null
  } | null
}

/**
 * The filters that were applied to this simulation run.
 */
export type MatchSimulatorOptions = {
  surface?: string | null
  competition_type?: string | null
  court_type?: string | null
}

/**
 * Response of GET /fit-player-profiles/{id}/match-simulator: predicts the outcome of a hypothetical match between the profile and a chosen opponent, based on both players' historical stats.
 */
export type MatchSimulatorResponse = {
  year: number
  player: PlayerInfo
  opponent: PlayerInfo
  simulation_result: SimulationResult
  ranking_stats_player: RankingStatsDetail
  ranking_stats_opponent: RankingStatsDetail
  evaluation_ratio: number
  /**
   * Overall computed strength rating
   */
  evaluation_player: number
  /**
   * Overall computed strength rating
   */
  evaluation_opponent: number
  endorsement_player: Array<EndorsementInfo>
  endorsement_opponent: Array<EndorsementInfo>
  strengths: Array<string>
  weaknesses: Array<string>
  options: MatchSimulatorOptions
}

/**
 * Response of PUT/DELETE /matches/{id} and PUT /matches/{id}/result.
 */
export type MatchUpdateResponse = {
  success: boolean
  match: MatchResponse
}

/**
 * An identified membership checkout. Send the user to `checkout_url` with a plain link (on native, in the system browser): the backend creates the Shopify cart and redirects there.
 */
export type MembershipCheckoutResponse = {
  /**
   * Opaque reference of the checkout (`ck_…`).
   */
  ref: string
  /**
   * Absolute URL of `GET /premium/checkout/{ref}` on this backend.
   */
  checkout_url: string
  /**
   * After this the link falls back to the plain Recharge checkout.
   */
  expires_at: string
}

/**
 * Either `{ success: true }`, or `{ success: false, errors: [...] }` when `email` or `id` is missing (both answered with 200).
 */
export type MembershipEmailResponse = SuccessResponse | ErrorResponse

export type MessageCreateErrorResponse =
  | {
      success: boolean
      error: 'blocked'
    }
  | ErrorResponse

/**
 * A single message as shown in the admin backoffice listing.
 */
export type MessagesBackofficeMessage = {
  id: number
  content: string
  created_at: string
  read_at?: string | null
  /**
   * Set when a recipient blocked this message.
   */
  blocked_at?: string | null
  /**
   * Computed from blocked_at.
   */
  is_blocked: boolean
  author?: MasqueradedEntity | null
  recipient?: MasqueradedEntity | null
}

/**
 * Paginated (Kaminari) list of every message on the platform, for admin moderation.
 */
export type MessagesBackofficeResponse = {
  messages: Array<MessagesBackofficeMessage>
  meta: {
    current_page: number
    next_page?: number | null
    prev_page?: number | null
    total_pages: number
    total_count: number
  }
}

/**
 * users/_masqueraded: a User as author/recipient (type `user`).
 */
export type MessagesBackofficeUser = {
  id: number
  name: string
  type: 'user'
  thumbnail?: string | null
  user_id?: number
  active_24h?: boolean
  active_1m?: boolean
}

/**
 * Minimal reference to the blocked message.
 */
export type MessagesBlock = {
  id: number
}

/**
 * Response for PUT /messages/{id}/block.
 */
export type MessagesBlockResponse = {
  success: boolean
}

/**
 * A conversation thread identifier: the profile and its counterpart.
 */
export type MessagesConversation = {
  id: number
  name: string
  type: string
  counterpart_id: number
  counterpart_type: string
}

/**
 * A single message within a conversation.
 */
export type MessagesConversationItem = {
  id: number
  content: string
  /**
   * Unix timestamp
   */
  created_at: number
  /**
   * True if the other party sent this message.
   */
  is_counterpart: boolean
  no_response?: boolean | null
  /**
   * Unix timestamp: when the recipient read it (opened the conversation). Null while unread. Messages older than the read tracking were marked read in bulk.
   */
  read_at?: number | null
}

/**
 * GET /messages/conversation response: the message history, oldest first.
 */
export type MessagesConversationResponse = Array<MessagesConversationItem>

/**
 * Minimal reference to a profile someone has messaged with.
 */
export type MessagesCounterpart = {
  id: number
  name: string
  type: string
}

/**
 * One entry in the messages-counterparts list: the last message exchanged with a given counterpart profile.
 */
export type MessagesCounterpartItem = {
  last_message: string
  counterpart: {
    id: number
    name: string
    /**
     * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`. Null for hidden players; absent when the entry is not a FIT player.
     */
    first_name?: string | null
    /**
     * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
     */
    last_name?: string | null
    type: string
    club?: {
      id?: number
      name?: string
      region?: string
      province?: string
    }
    user_exists?: boolean
    premium?: boolean
    premium_expiration?: string
    /**
     * Null when the profile has no photo.
     */
    thumbnail?: string | null
    rank?: string
    category?: string
    region?: string
    province?: string
    active_24h?: boolean
    active_1m?: boolean
  }
  is_blocked: boolean
}

/**
 * GET /users/messages-counterparts response: every distinct profile the caller has exchanged messages with, most recent first, deduplicated.
 */
export type MessagesCounterpartResponse = Array<MessagesCounterpartItem>

/**
 * A club returned by the nearby-search endpoint, with its distance from the queried point.
 */
export type NearbyClub = {
  id: number
  name: string | null
  /**
   * FIT federation club identifier
   */
  fit_club_id: string | null
  address: string | null
  /**
   * Great-circle distance from the search point
   */
  distance_km: number
  lat: number | null
  lng: number | null
}

/**
 * Response of GET /clubs/nearby: up to 20 active clubs within the radius, closest first.
 */
export type NearbyClubsResponse = {
  /**
   * Echoes back the coordinates that were searched
   */
  search_point: {
    lat: number
    lng: number
  }
  /**
   * The radius that was applied
   */
  radius_km: number
  results: Array<NearbyClub>
}

export type NearbyPlayer = SeatPlayer & {
  /**
   * FIT age category, null without a FIT profile.
   */
  category?: AgeCategory | null
  gender?: 'male' | 'female' | null
  city?: string | null
  /**
   * Rounded to the km
   */
  distance_km: number
  /**
   * Latest activity: the list is ordered by it, then by distance.
   */
  last_played_at?: string | null
}

/**
 * Discoverable players near the caller. `locked`: the caller is not discoverable, so `players` is empty and `count` only says how many to blur.
 */
export type NearbyPlayersResponse = {
  success: boolean
  locked: boolean
  /**
   * Locked: all the findable players in the radius. Otherwise the size of `players` (at most 20).
   */
  count: number
  players: Array<NearbyPlayer>
}

/**
 * Request body for creating a post.
 */
export type NewPost = {
  /**
   * ID of the entity posting (e.g., User ID, Club ID)
   */
  masqueraded_id: number
  /**
   * Type of the entity posting (e.g., "User", "Club")
   */
  masqueraded_type: string
  /**
   * ID of the post being shared
   */
  shared_post_id?: number | null
  as_tennistalker?: boolean
  video?: string | null
  /**
   * Optional when a shareable is attached
   */
  content?: string
  /**
   * Type of the entity being shared in the post
   */
  shareable_type?: null | 'Activity'
  /**
   * ID of the entity being shared in the post
   */
  shareable_id?: number | null
  images?: Array<NewPostImage>
}

/**
 * An image attached to a post being created or updated. Omit `id` to add a new image; pass an existing `id` with `_destroy: true` to remove it on update.
 */
export type NewPostImage = {
  /**
   * Existing image id (update only)
   */
  id?: number | null
  /**
   * Required when adding a new image
   */
  url?: string | null
  /**
   * Set true to remove this image (update only)
   */
  _destroy?: boolean
}

/**
 * An in-app notification delivered to a user (new follower, comment, ranking update, etc.).
 */
export type Notification = {
  id: number
  /**
   * Rendered notification text
   */
  content?: string | null
  /**
   * Deep link the app should open when this is tapped
   */
  destination_url?: string | null
  thumbnail?: string | null
  /**
   * Unix timestamp
   */
  created_at: number
  /**
   * True if not yet marked as read
   */
  new_one: boolean
}

/**
 * Per-category notification toggles of a user, as the backend applies them (a toggle never saved gets its default). `magazine_newsletter_opt_in` is the magazine newsletter consent, off until the user turns it on.
 */
export type NotificationSettings = {
  comments_followed: boolean
  like_comments: boolean
  like_posts: boolean
  magazine_newsletter_opt_in: boolean
  matches_club_mates: boolean
  matches_followed: boolean
  new_ranking: boolean
  posts_followed: boolean
  saved_search: boolean
}

/**
 * A marketplace offer (private listing or shop listing), as persisted.
 */
export type Offer = {
  id: number
  slug: string
  /**
   * Who the offer is posted as
   */
  masqueraded_type: 'User' | 'ShopProfile'
  /**
   * id of the User or ShopProfile posting the offer
   */
  masqueraded_id: number
  /**
   * id of the User who actually created the offer
   */
  author_id: number
  /**
   * OfferCategory id
   */
  category_id: number
  title: string
  content: string
  /**
   * Truncated preview of content
   */
  excerpt?: string | null
  price: number
  /**
   * Discounted price, if on sale
   */
  sale_price?: number | null
  /**
   * End date of the sale, if any
   */
  sale_end?: string | null
  second_hand: boolean
  destination?: string | null
  /**
   * Sort order; higher weight sorts first
   */
  weight: number
  /**
   * Moderation/visibility status, internal enum
   */
  status: number
  created_at: string
  updated_at: string
}

/**
 * A category used to classify marketplace offers, with its nested children (public shape — see OfferCategoryBackoffice for the admin listing).
 */
export type OfferCategory = {
  id: number
  name: string
  /**
   * Nested child categories, if this is a top-level category
   */
  children: Array<OfferCategory>
}

/**
 * Admin-only representation of an offer category, with moderation fields not present in the public OfferCategory shape.
 */
export type OfferCategoryBackoffice = {
  id: number
  name: string
  description?: string | null
  /**
   * Parent category id, absent for top-level categories
   */
  parent_id?: number | null
  /**
   * Sort order; higher weight sorts first
   */
  weight: number
  /**
   * Count of offers currently in this category
   */
  offers_number: number
  children: Array<OfferCategoryBackoffice>
}

/**
 * Envelope returned by the offer category create/update/destroy endpoints.
 */
export type OfferCategoryResponse = {
  success: boolean
  offer_category: OfferCategory
}

/**
 * A single offer as returned by GET /offers/{id}, with the seller and category expanded for display.
 */
export type OfferResponse = {
  id: number
  title: string
  slug: string
  /**
   * Truncated preview of content
   */
  excerpt?: string | null
  content: string
  price: number
  second_hand: boolean
  /**
   * True if the seller is a shop rather than an individual user
   */
  seller_shop: boolean
  /**
   * Unix timestamp the offer was created
   */
  date: number
  category: {
    id: number
    name: string
    parent?: {
      id?: number
      name?: string
      parent?: {
        [key: string]: unknown
      } | null
    } | null
  }
  images: Array<{
    id: number
    url: string
  }>
  /**
   * The User or ShopProfile the offer is posted as (see Offer#masqueraded_type).
   */
  seller: {
    id: number
    name: string
    type: 'user' | 'shop_profile'
    thumbnail?: string | null
    user_id?: number | null
  }
  /**
   * True if the current user is this offer's author. Note: an admin may still delete any offer regardless of this flag.
   */
  is_deletable: boolean
}

/**
 * The lighter offer shape used by list/search endpoints (search, privates, shops) — a single primary photo instead of the full images array.
 */
export type OfferSearch = {
  id: number
  title: string
  slug: string
  /**
   * Truncated preview of content
   */
  excerpt?: string | null
  price: number
  second_hand: boolean
  /**
   * True if the seller is a shop rather than an individual user
   */
  seller_shop: boolean
  /**
   * The User or ShopProfile the offer is posted as.
   */
  seller: {
    id: number
    name: string
    type: 'user' | 'shop_profile'
    thumbnail?: string | null
    user_id?: number | null
  }
  category: {
    id: number
    name: string
    parent?: {
      id?: number
      name?: string
      parent?: {
        [key: string]: unknown
      } | null
    } | null
  }
  /**
   * URL of the offer's primary image, if any
   */
  photo?: string | null
  /**
   * Unix timestamp the offer was created
   */
  date: number
}

/**
 * A FIT rank, from "4.NC" (unranked) up to "1.1" (best).
 */
export const PlainRank = {
  '4_NC': '4.NC',
  '4_6': '4.6',
  '4_5': '4.5',
  '4_4': '4.4',
  '4_3': '4.3',
  '4_2': '4.2',
  '4_1': '4.1',
  '3_5': '3.5',
  '3_4': '3.4',
  '3_3': '3.3',
  '3_2': '3.2',
  '3_1': '3.1',
  '2_8': '2.8',
  '2_7': '2.7',
  '2_6': '2.6',
  '2_5': '2.5',
  '2_4': '2.4',
  '2_3': '2.3',
  '2_2': '2.2',
  '2_1': '2.1',
  '1_20': '1.20',
  '1_19': '1.19',
  '1_18': '1.18',
  '1_17': '1.17',
  '1_16': '1.16',
  '1_15': '1.15',
  '1_14': '1.14',
  '1_13': '1.13',
  '1_12': '1.12',
  '1_11': '1.11',
  '1_10': '1.10',
  '1_9': '1.9',
  '1_8': '1.8',
  '1_7': '1.7',
  '1_6': '1.6',
  '1_5': '1.5',
  '1_4': '1.4',
  '1_3': '1.3',
  '1_2': '1.2',
  '1_1': '1.1',
} as const

/**
 * A FIT rank, from "4.NC" (unranked) up to "1.1" (best).
 */
export type PlainRank = (typeof PlainRank)[keyof typeof PlainRank]

/**
 * A FIT rank ("4.NC" unranked up to "1.1" best), or "ND" when no ranking data is available for the requested period.
 */
export const PlainRankWithNd = {
  '4_NC': '4.NC',
  '4_6': '4.6',
  '4_5': '4.5',
  '4_4': '4.4',
  '4_3': '4.3',
  '4_2': '4.2',
  '4_1': '4.1',
  '3_5': '3.5',
  '3_4': '3.4',
  '3_3': '3.3',
  '3_2': '3.2',
  '3_1': '3.1',
  '2_8': '2.8',
  '2_7': '2.7',
  '2_6': '2.6',
  '2_5': '2.5',
  '2_4': '2.4',
  '2_3': '2.3',
  '2_2': '2.2',
  '2_1': '2.1',
  '1_20': '1.20',
  '1_19': '1.19',
  '1_18': '1.18',
  '1_17': '1.17',
  '1_16': '1.16',
  '1_15': '1.15',
  '1_14': '1.14',
  '1_13': '1.13',
  '1_12': '1.12',
  '1_11': '1.11',
  '1_10': '1.10',
  '1_9': '1.9',
  '1_8': '1.8',
  '1_7': '1.7',
  '1_6': '1.6',
  '1_5': '1.5',
  '1_4': '1.4',
  '1_3': '1.3',
  '1_2': '1.2',
  '1_1': '1.1',
  ND: 'ND',
} as const

/**
 * A FIT rank ("4.NC" unranked up to "1.1" best), or "ND" when no ranking data is available for the requested period.
 */
export type PlainRankWithNd =
  (typeof PlainRankWithNd)[keyof typeof PlainRankWithNd]

/**
 * A paginated page of a player's posts (`feed` and `posts` carry the same data, for different client versions).
 */
export type PlayerFeedResponse = {
  page: number
  total_pages: number
  feed: Array<Post>
  posts: Array<Post>
}

/**
 * A player's summary as shown in the match simulator (player or opponent side).
 */
export type PlayerInfo = {
  id: number
  full_name: string
  photo?: string
  rank: string
  club: string
  category: string
  max_rank: string
  win_loss_year: {
    wins?: number
    losses?: number
  }
  win_loss_total: {
    wins?: number
    losses?: number
  }
}

/**
 * A lightweight, "masqueraded" (identity-abstracted) representation of a FitPlayerProfile or User, used wherever an author/actor is shown without needing their full profile (feeds, followers lists, comments, etc.).
 */
export type PlayerMasqueraded = {
  id: number
  name: string
  /**
   * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`. Null for hidden players; absent for a non-tracked opponent (type "fit_player_profile_not_tracked").
   */
  first_name?: string | null
  /**
   * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
   */
  last_name?: string | null
  /**
   * The masqueraded entity's class, e.g. "FitPlayerProfile" or "User".
   */
  type: string
  club?: {
    id?: number
    name?: string
    region?: string | null
    province?: string | null
  } | null
  user_exists: boolean
  premium?: boolean | null
  premium_expiration?: string | null
  thumbnail?: string | null
  rank?: PlainRank
  category?: AgeCategory | null
  region?: string | null
  province?: string | null
  /**
   * Whether the player's simulated rank for the current year is better than their official rank.
   */
  simulated_rank_higher?: boolean
  /**
   * Whether the underlying User was active in the last 24 hours.
   */
  active_24h?: boolean | null
  /**
   * Whether the underlying User was active in the last month.
   */
  active_1m?: boolean | null
}

/**
 * A search result row for GET /fit-player-profiles/search-for-merge, used by admins to find the duplicate profile they want to merge into another one (see PUT .../merge/...): the inline search row plus the FIT registry codes.
 */
export type PlayerMergeSearch = InlineSearchPlayer & {
  /**
   * FIT membership card number.
   */
  card_number: string | null
  /**
   * Unique FIT registry code.
   */
  anagrafica_code: string | null
}

/**
 * Recent visitors to a FitPlayerProfile page.
 */
export type PlayerVisitorsResponse = {
  visitors: Array<PlayerMasqueraded | Author>
}

/**
 * A player row annotated with its ranking-list position, used by the chart_extract endpoint to show the players immediately above/below a given player in the standings.
 */
export type PlayerWithPosition = {
  id: number
  rank?: PlainRank
  /**
   * Position in the ranking list (cardinality).
   */
  number?: number | null
  category?: AgeCategory | null
  name: string | null
  /**
   * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`.
   */
  first_name: string
  /**
   * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
   */
  last_name: string
  thumbnail?: string | null
}

/**
 * The write shape for a PositionedBanner: assigns one or more bannerable records (currently CustomBanner) to a named placement ("slot") in the app.
 */
export type PositionedBanner = {
  id: number
  /**
   * Placement identifier, e.g. 'home_top'.
   */
  slot: string
  /**
   * Polymorphic type, currently always 'CustomBanner'.
   */
  bannerable_type: string
  /**
   * IDs of the CustomBanner records assigned to this slot.
   */
  bannerable_id?: Array<number>
  created_at: string
  updated_at: string
}

/**
 * Response for GET /positioned-banners/{slot}/all: every banner assigned to the slot, unlike the single-slot endpoint which picks one at random.
 */
export type PositionedBannerAllResponse = {
  slot: string
  type: string
  banners: Array<{
    image_url?: string | null
    destination?: string | null
  }>
}

/**
 * Writable PositionedBanner fields. Can be sent flat (as here) or nested under a `positioned_banner` key.
 */
export type PositionedBannerInput = {
  /**
   * Placement identifier, e.g. 'home_top'. Unique.
   */
  slot: string
  /**
   * 'TENNISTALKER' (CustomBanner) or 'ADSENSE' (AdsenseSlot); any other value fails validation.
   */
  bannerable_type: 'TENNISTALKER' | 'ADSENSE'
  /**
   * IDs of the CustomBanner records to assign to this slot (replaces the existing assignment on update).
   */
  bannerable_id?: Array<number>
}

/**
 * The read shape for a PositionedBanner, expanded with the resolved banner content (image/destination) for the slot.
 */
export type PositionedBannerResponse = {
  id: number
  slot: string
  bannerable_id?: number | null
  type: string
  adsense_slot?: string | null
  image_url?: string | null
  destination?: string | null
  custom_banners?: Array<CustomBannerRecord> | null
}

/**
 * Response for GET /positioned-banners/{slot}: a single, randomly-picked banner for the slot (when it holds more than one).
 */
export type PositionedBannerSlotResponse = {
  slot: string
  type: string
  image_url?: string | null
  destination?: string | null
}

/**
 * A post in the social feed: user-authored content, an official TennisTalker/blog article (when `is_news` is true), or a repost of another post/activity (when `shared_post`/`shareable` is present).
 */
export type Post = {
  id: number
  /**
   * News posts only
   */
  title?: string | null
  content: string
  /**
   * Truncated preview of `content`
   */
  excerpt: string
  /**
   * News posts only, unix timestamp
   */
  published_at?: number | null
  /**
   * created_at, as a unix timestamp
   */
  date?: number
  /**
   * Visual theme id used by the app to render this post
   */
  theme?: number | null
  click_count?: number | null
  /**
   * External link this post points to
   */
  destination?: string | null
  /**
   * True when published on behalf of the official TennisTalker account, not the real author
   */
  as_tennistalker?: boolean | null
  /**
   * Discriminator used when reporting this item
   */
  reportable_type?: 'post'
  /**
   * Discriminator used by heterogeneous feed endpoints that mix posts with other item types
   */
  feed_item_type?: 'post'
  /**
   * Whether the requesting user is the author
   */
  is_author?: boolean
  /**
   * News posts only, e.g. the RSS feed name
   */
  source_name?: string | null
  url?: string
  /**
   * First <img> src scraped out of content; news posts only
   */
  image_url?: string | null
  /**
   * The actual User who created the post, as opposed to `author`, which reflects the masqueraded identity
   */
  real_author?: UserPreview
  video?: string | null
  /**
   * True for official news/blog articles
   */
  is_news?: boolean
  /**
   * News posts only
   */
  news_link?: string | null
  author: PlayerMasqueraded | Author
  images: Array<PostImage>
  comments: Array<Comment>
  /**
   * A simplified Post object that is being shared.
   */
  shared_post?: {
    id?: number
    content?: string
    author?: Author
  } | null
  /**
   * Type of the entity shared in the post
   */
  shareable_type?: null | 'Activity'
  /**
   * Payload of the shared Activity when shareable_type is present.
   */
  shareable?: {
    [key: string]: unknown
  } | null
  likes: number
  liked_by_current_user: boolean
}

/**
 * An image attached to a post, as returned in responses.
 */
export type PostImage = {
  id: number
  url: string
}

/**
 * Response envelope wrapping a single post together with a success flag.
 */
export type PostWithOutcomeResponse = {
  success: boolean
  post: Post
}

/**
 * A paginated list of posts.
 */
export type PostsResponse = {
  page: number
  total_pages: number
  posts: Array<Post>
}

/**
 * Premium status of the current user, to refresh the saved user without logging in again.
 */
export type PremiumStatusResponse = {
  premium: boolean
  premium_expiration: string | null
  role:
    | 'admin'
    | 'simple_user'
    | 'banner_manager'
    | 'censor_manager'
    | 'premium_user'
}

/**
 * A premium membership code, typically generated by a Shopify membership purchase, with the user who redeemed it (if any).
 */
export type PremiumToken = {
  id: number
  /**
   * Redeemable code: random for new codes, `<TOKEN_SALT><id>` for older ones. Show this instead of building it from the id.
   */
  code: string
  shopify_order_id?: string | null
  /**
   * Whether the code has already been redeemed.
   */
  consumed: boolean
  /**
   * Email the membership email is sent to.
   */
  email?: string | null
  /**
   * Membership duration, in days.
   */
  duration?: number | null
  created_at: string
  user: BackofficeUser | null
}

/**
 * Writable premium token fields. Can be sent flat (as here) or nested under a `premium_token` key.
 */
export type PremiumTokenInput = {
  /**
   * User this token belongs to, if known
   */
  user_id?: number | null
  /**
   * Shopify order that generated this token
   */
  shopify_order_id?: string
  /**
   * Whether the token has already been redeemed
   */
  consumed?: boolean
  /**
   * Email the token/membership email will be sent to
   */
  email?: string
  /**
   * Membership duration granted by this token, in days
   */
  duration?: number
}

/**
 * Response wrapping a created/updated/deleted premium token.
 */
export type PremiumTokenWithOutcomeResponse = {
  success: boolean
  premium_token: PremiumToken
}

/**
 * Paginated (20 per page) list of premium tokens, most recent first.
 */
export type PremiumTokensIndexResponse = {
  data: Array<PremiumToken>
  page: number
  total_pages: number
}

/**
 * The minimal competition stub embedded in a Match or Draw response.
 */
export type PreviewCompetition = {
  id: number
  name: string
  fit_competition_id?: string | null
  new_fit_competition_id?: string | null
  /**
   * dd/mm/yyyy
   */
  date: string
  championship?: boolean | null
}

/**
 * The minimal tournament stub embedded in a Match or Draw response — see Schemas::TournamentPreview for the fuller listing shape.
 */
export type PreviewTournament = {
  id: number
  name: string
  fit_tournament_id?: string | null
  new_fit_tournament_id?: string | null
  age_category?: AgeCategory | null
  rankings_category?: number | null
  surface?: Surface | null
  court_type?: CourtType | null
  followed: boolean
  followable_type: 'tournament'
}

/**
 * Segment derived from the profiles the user has (User#primary_segment): pro if any work profile exists, else agonista with a linked FIT card, else amatoriale with an amateur profile, else fan. Never stored, always computed.
 */
export const PrimarySegment = {
  PRO: 'pro',
  AGONISTA: 'agonista',
  AMATORIALE: 'amatoriale',
  FAN: 'fan',
} as const

/**
 * Segment derived from the profiles the user has (User#primary_segment): pro if any work profile exists, else agonista with a linked FIT card, else amatoriale with an amateur profile, else fan. Never stored, always computed.
 */
export type PrimarySegment =
  (typeof PrimarySegment)[keyof typeof PrimarySegment]

/**
 * The masqueraded shape of a professional profile (teacher, trainer, nutritionist, mental coach, shop).
 */
export type ProfessionalProfileMasqueraded = {
  /**
   * The profile id.
   */
  id: number
  name: string
  /**
   * The owning user id.
   */
  user_id: number
  type:
    | 'teacher_profile'
    | 'trainer_profile'
    | 'nutritionist_profile'
    | 'mental_coach_profile'
    | 'shop_profile'
}

/**
 * The full breakdown produced by FitPointsCalculator: how a player's simulated FIT ranking for a year was computed from their matches plus various bonus categories. Most fields are internal calculator bookkeeping, exposed as-is for transparency/debugging rather than because every field is meant to be rendered in the app.
 */
export type RankInfo = {
  /**
   * Total simulated FIT points for the year.
   */
  fit_points?: number
  rank?: PlainRank
  actual_rank?: PlainRank
  wins?: number
  losses?: number
  /**
   * Ranking coefficient used to weigh match results by opponent strength.
   */
  coefficient?: number
  /**
   * How many of the best results count toward the player's points.
   */
  first_n_matches?: number
  simulated_rank?: PlainRank
  /**
   * Points still needed to be promoted to the next rank.
   */
  switch_rank_points?: number
  /**
   * Sum of all bonus-category points (tab, bnl, vet, reg, ita, mgp, no_lose, user_added).
   */
  total_bonuses?: number
  /**
   * IDs of the matches counted toward first_n_matches.
   */
  first_n_matches_ids?: Array<string>
  points_n_matches?: number
  base_won_considered?: number
  tournaments_won?: Array<TournamentWon>
  /**
   * Points needed for the next rank up.
   */
  rank_upper_band?: number
  /**
   * Points at/below which the player would drop a rank.
   */
  rank_lower_band?: number
  /**
   * Rank positions gained/lost since the year's first ranking.
   */
  first_rank_difference?: number
  year_first_rank?: PlainRank
  /**
   * FIT points after applying any point-reduction penalties.
   */
  reduced_fit_points?: number
  won_percentage?: number
  /**
   * Bonus points from the two best tournament draws played ("tabellone" bonus).
   */
  tab?: number
  /**
   * Bonus points from BNL-affiliated tournaments.
   */
  bnl?: number
  /**
   * Bonus points from veteran/senior category tournaments.
   */
  vet?: number
  /**
   * Bonus points from regional championships.
   */
  reg?: number
  /**
   * Bonus points from national ("Italiani") championships.
   */
  ita?: number
  /**
   * Bonus points from Master Gran Prix veteran tournaments.
   */
  mgp?: number
  /**
   * Bonus for an undefeated season by a wide enough margin.
   */
  no_lose?: number
  /**
   * Manually added bonus points (see the Bonuses endpoints).
   */
  user_added?: number
  doubles_points?: number
  /**
   * Wins against equal or higher-ranked opponents.
   */
  wins_non_positive_distance?: number
  /**
   * Walkover wins (opponent no-show).
   */
  wins_without_playing?: number
  lost_one?: boolean
  not_played?: number
  /**
   * Per-match point breakdown.
   */
  matches_points?: Array<{
    id?: string
    points?: number
    reduced_points?: boolean
  }>
  /**
   * IDs of draws flagged as "rodeo" (reduced-point) tournaments.
   */
  draw_rodeos_ids?: Array<string>
  /**
   * Numeric index of the previous rank (see PlainRank ordering).
   */
  last_plain_rank?: number
  wins_zero_points?: number
  /**
   * Numeric index of the year's first rank.
   */
  first_plain_rank?: number
  /**
   * Numeric index of the simulated rank.
   */
  simulated_plain_rank?: number
  /**
   * Raw base-table entry used as the starting point for the calculation.
   */
  current_capital?: {
    [key: string]: unknown
  } | null
}

/**
 * One player's historical match record, broken down by opponent rank relative to their own, by match length, by championship type, in the head-to-head, and by court/surface.
 */
export type RankingStatsDetail = {
  same_rank?: WinLossStats
  one_rank_higher?: WinLossStats
  two_plus_ranks_higher?: WinLossStats
  one_rank_lower?: WinLossStats
  two_plus_ranks_lower?: WinLossStats
  two_sets?: WinLossStats
  three_sets?: WinLossStats
  championship_stats?: {
    a_squadre?: WinLossStats
    torneo?: WinLossStats
  }
  h2h?: WinLossStats
  court_stats?: {
    [key: string]: WinLossStats
  }
  court_type_stats?: {
    [key: string]: WinLossStats
  }
}

/**
 * A moderation flag filed by a user against a Post, Comment, User, or UserMatch.
 */
export type Report = {
  id: number
  /**
   * The reporting user's stated reason.
   */
  notes?: string | null
  /**
   * 'pending' until an admin closes it.
   */
  status: 'pending' | 'closed'
  /**
   * Note left by the admin who closed the report.
   */
  admin_notes?: string | null
  /**
   * Unix timestamp
   */
  created_at: number
  /**
   * The user who filed the report.
   */
  author: {
    id?: number
    name?: string
  }
  /**
   * The reported record (a Post, Comment, User, or UserMatch, discriminated by `type`), or null if it has since been deleted.
   */
  reportable?:
    | ReportablePost
    | ReportableComment
    | ReportableUser
    | ReportableUserMatch
    | null
}

/**
 * Either the ReportResponse success shape, or a ValidationErrorResponse (this operation answers 200 for both the happy path and a validation failure).
 */
export type ReportCreateResponse = ReportResponse | ValidationErrorResponse

/**
 * Response wrapping a single report.
 */
export type ReportResponse = {
  success: boolean
  report: Report
}

/**
 * A reported Comment.
 */
export type ReportableComment = {
  id: number
  content?: string | null
  type: 'comment'
  author: MasqueradedEntity
}

/**
 * A reported Post.
 */
export type ReportablePost = {
  id: number
  content?: string | null
  type: 'post'
  /**
   * Public web URL of the post.
   */
  url: string
  author: MasqueradedEntity
}

/**
 * A reported User (`content` is their email).
 */
export type ReportableUser = {
  id: number
  /**
   * The reported user's email.
   */
  content: string
  type: 'user'
  /**
   * Public web URL of the user profile.
   */
  url: string
}

/**
 * A reported UserMatch (`content` is opponent name and result).
 */
export type ReportableUserMatch = {
  id: number
  content: string
  type: 'user_match'
  /**
   * Public web URL of the match.
   */
  url: string
}

/**
 * Paginated list of reports.
 */
export type ReportsIndexResponse = {
  page: number
  total_pages: number
  data: Array<Report>
}

/**
 * A user's single saved competition search filter (at most one per user), used to match new competitions against for push-notification alerts.
 */
export type SavedCompetitionSearch = {
  id: number
  user_id: number
  championship?: boolean | null
  age_category?: string | null
  rankings_category?: string | null
  gender?: CompetitionGender | null
  region?: string | null
  province: Array<string>
  created_at: string
  updated_at: string
}

/**
 * Returned with 404/500 when there is no saved search to return/delete.
 */
export type SavedCompetitionSearchErrorResponse = {
  success: boolean
  error: string
}

/**
 * A club result on the Clubs tab of the global search.
 */
export type SearchClub = {
  id: number
  name: string
  members_counter?: number | null
  phone?: string | null
  region?: string | null
  province?: string | null
  fields_address_display_info?: string | null
  managed: boolean
  active?: boolean | null
  school_typology?: string | null
}

/**
 * A minimal club stub embedded in a search result.
 */
export type SearchClubPreview = {
  id: number
  name: string
  fit_club_id?: string | null
}

/**
 * A single tab's paginated results from the global search, plus results_numbers badge counts for the other tabs.
 */
export type SearchClubsResponse = {
  page: number
  total_pages: number
  results: Array<SearchClub>
  results_numbers: SearchResultsNumbers
}

/**
 * A competition result on the Competitions tab of the global search.
 */
export type SearchCompetition = {
  id: number
  name: string
  address?: string | null
  dates?: string | null
  new_fit_competition_id?: string | null
  club: SearchClubPreview | null
  tournaments: Array<{
    [key: string]: unknown
  }>
}

/**
 * A single tab's paginated results from the global search, plus results_numbers badge counts for the other tabs.
 */
export type SearchCompetitionsResponse = {
  page: number
  total_pages: number
  results: Array<SearchCompetition>
  results_numbers: SearchResultsNumbers
}

/**
 * A FIT card with a teacher qualification that is not linked to any user.
 */
export type SearchFitTeacher = {
  /**
   * FitPlayerProfile id (teacher_signed_id FitPlayerProfile_<id>)
   */
  id: number
  name: string | null
  full_name: string | null
  first_name: string
  last_name: string
  /**
   * FIT teacher qualification, e.g. "IS1"
   */
  qualification: string
  rank: PlainRank | null
  category: AgeCategory | null
  club_name: string | null
  active: boolean
  region: string | null
  province: string | null
}

/**
 * Response of POST /search/fit_teachers.
 */
export type SearchFitTeachersResponse = {
  results: Array<SearchFitTeacher>
}

/**
 * A user result of the "other profiles" search tab, with a flag per professional profile. Teachers with a FIT card qualification have a teacher profile too, so they show up as `teacher: true`.
 */
export type SearchOtherProfile = {
  id: number
  name: string | null
  /**
   * Linked FitPlayerProfile id, if any
   */
  player_id: number | null
  /**
   * Whether the user has a teacher profile
   */
  teacher: boolean
  trainer: boolean
  mental_coach: boolean
  nutritionist: boolean
  shop: boolean
  /**
   * Whether this user also has a FitPlayerProfile
   */
  player: boolean
  thumbnail?: string | null
  premium: boolean
  premium_expiration: string | null
  /**
   * Rank of the linked FitPlayerProfile, null without one
   */
  rank: PlainRank | null
  /**
   * Age category of the linked FitPlayerProfile, null without one
   */
  category: AgeCategory | null
  /**
   * Club of the linked FitPlayerProfile
   */
  club_name: string | null
  /**
   * Region of the linked FitPlayerProfile (its club first)
   */
  region: string | null
  /**
   * Province of the linked FitPlayerProfile (its club first)
   */
  province: string | null
}

/**
 * A single tab's paginated results from the global search, plus results_numbers badge counts for the other tabs.
 */
export type SearchOtherProfilesResponse = {
  page: number
  total_pages: number
  results: Array<SearchOtherProfile>
  results_numbers: SearchResultsNumbers
  profiles_counters: SearchProfilesCounters
}

/**
 * A player result on the Players tab of the global search.
 */
export type SearchPlayer = {
  id: number
  full_name: string | null
  /**
   * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `full_name` (surname first). May be blank on some legacy records: fall back to `full_name`.
   */
  first_name: string
  /**
   * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
   */
  last_name: string
  rank?: PlainRank | null
  category?: AgeCategory | null
  user_exists: boolean
  thumbnail?: string | null
  premium?: boolean | null
  premium_expiration?: string | null
  active?: boolean | null
  club: SearchClubPreview | null
  region: string | null
  province: string | null
}

/**
 * A single tab's paginated results from the global search, plus results_numbers badge counts for the other tabs.
 */
export type SearchPlayersResponse = {
  page: number
  total_pages: number
  results: Array<SearchPlayer>
  results_numbers: SearchResultsNumbers
}

/**
 * A user post matched by the global search (news, magazine articles, official TennisTalker posts and posts of banned users are never returned).
 */
export type SearchPost = {
  id: number
  content: string
  /**
   * Truncated preview of `content`
   */
  excerpt: string
  /**
   * created_at, as a unix timestamp
   */
  date: number
  url: string
  /**
   * First image of the post, if any
   */
  image_url: string | null
  /**
   * The identity the post was published as
   */
  author: PlayerMasqueraded | Author
}

/**
 * Per-professional-type match counts, shown only on the "other profiles" tab.
 */
export type SearchProfilesCounters = {
  talker: number
  fit_player_profile: number
  teacher_profile: number
  trainer_profile: number
  mental_coach_profile: number
  nutritionist_profile: number
  shop_profile: number
}

/**
 * Match counts across all four search tabs, returned alongside the current tab's results so the UI can badge the others without extra requests.
 */
export type SearchResultsNumbers = {
  players: number
  clubs: number
  competitions: number
  other_profiles: number
}

/**
 * Who sits in a seat. A name-only seat carries just `name`.
 */
export type SeatPlayer = {
  user_id?: number | null
  fit_player_profile_id?: number | null
  name: string
  first_name?: string | null
  last_name?: string | null
  thumbnail?: string | null
  /**
   * FIT rank ("4.1"). Null without a FIT profile and for an unranked one (4.NC): the card then shows chip.
   */
  rank?: string | null
  /**
   * Declared amateur level label, for every user who set it.
   */
  chip?: string | null
  /**
   * Declared free text, for every user who set it.
   */
  level_description?: string | null
  /**
   * Same as search users. Null for a seat without a user.
   */
  premium?: boolean | null
  /**
   * Same as search users. Null for a seat without a user.
   */
  premium_expiration?: string | null
}

/**
 * Public profile page for a shop-type user, with its active offers. The `managed`/`followed` flags reflect the caller when X-Api-Key is sent; both are false for an anonymous request.
 */
export type ShopProfile = {
  id: number
  name: string | null
  website: string | null
  info: string | null
  address: string | null
  /**
   * True if the current user manages this shop
   */
  managed: boolean
  /**
   * True if the current user follows this shop
   */
  followed: boolean
  followable_type: string
  offers: Array<OfferSearch>
}

/**
 * How the user signed up: email/password registration or a social login.
 */
export const SignupMethod = {
  PASSWORD: 'password',
  APPLE: 'apple',
  GOOGLE: 'google',
  FACEBOOK: 'facebook',
  FACEBOOK_LIMITED: 'facebook_limited',
} as const

/**
 * How the user signed up: email/password registration or a social login.
 */
export type SignupMethod = (typeof SignupMethod)[keyof typeof SignupMethod]

/**
 * The bare `{ error: "..." }` failure shape.
 */
export type SimpleErrorResponse = {
  error: string
}

/**
 * The predicted outcome of a hypothetical player-vs-opponent match, computed from both players' historical stats.
 */
export type SimulationResult = {
  /**
   * Computed strength score for the player
   */
  player_score: number
  /**
   * Computed strength score for the opponent
   */
  opponent_score: number
  /**
   * player_score / opponent_score
   */
  ratio: number
  /**
   * Outcome verdict, from the player's perspective: 'sfavorevole' (unfavorable) through 'favorevole' (favorable)
   */
  prediction:
    | 'sfavorevole'
    | 'leggermente_sfavorevole'
    | 'equilibrato'
    | 'leggermente_favorevole'
    | 'favorevole'
  surface_advantage?: SurfaceAdvantage | null
}

/**
 * A match as shown in the ranking simulator, from the perspective of the player being simulated ('has_won' is relative to them). Includes the fit points that match contributed (or would contribute).
 */
export type SimulatorMatch = {
  id?: number
  /**
   * Round number within the draw
   */
  round_id?: number | null
  result?: string | null
  /**
   * Whether the subject player won this match
   */
  has_won?: boolean
  is_double?: boolean
  /**
   * FIT ranking points this match actually contributed
   */
  fit_points?: number | null
  type?: string
  is_editable?: boolean
  is_deletable?: boolean
  /**
   * Unix timestamp of the match
   */
  date?: number | null
  /**
   * The draw's name
   */
  draw?: string | null
  draw_obj?: DrawSummary | null
  tournament?: {
    id?: number
    name?: string
    fit_tournament_id?: string | null
    new_fit_tournament_id?: string | null
    age_category?: AgeCategory | null
    rankings_category?: number | null
    surface?: Surface | null
  } | null
  competition?: {
    id?: number
    name?: string
    fit_competition_id?: string | null
    new_fit_competition_id?: string | null
    date?: string
    championship?: boolean
  } | null
  first_opponent?: {
    id?: number | null
    name?: string
    /**
     * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`. Null for hidden players; absent when the entry is not a FIT player.
     */
    first_name?: string | null
    /**
     * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
     */
    last_name?: string | null
    type?: string
    club?: ClubPreview | null
    user_exists?: boolean | null
    premium?: boolean | null
    premium_expiration?: string | null
    thumbnail?: string | null
    rank?: PlainRank | null
    category?: AgeCategory | null
    region?: string | null
    province?: string | null
    simulated_rank_higher?: boolean | null
  } | null
  second_opponent?: {
    id?: number | null
    name?: string
    /**
     * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`. Null for hidden players; absent when the entry is not a FIT player.
     */
    first_name?: string | null
    /**
     * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
     */
    last_name?: string | null
    type?: string
    club?: ClubPreview | null
    user_exists?: boolean | null
    premium?: boolean | null
    premium_expiration?: string | null
    thumbnail?: string | null
    rank?: PlainRank | null
    category?: AgeCategory | null
    region?: string | null
    province?: string | null
  } | null
  partner?: {
    id?: number | null
    name?: string
    /**
     * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`. Null for hidden players; absent when the entry is not a FIT player.
     */
    first_name?: string | null
    /**
     * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
     */
    last_name?: string | null
    type?: string
    club?: ClubPreview | null
    user_exists?: boolean | null
    premium?: boolean | null
    premium_expiration?: string | null
    thumbnail?: string | null
    rank?: PlainRank | null
    category?: AgeCategory | null
    region?: string | null
    province?: string | null
  } | null
  /**
   * Whether this match counts toward the player's best-N ranking calculation for the period
   */
  is_in_best_matches?: boolean
  /**
   * Simulated/recalculated points for this match, as opposed to `fit_points` (the actual recorded value)
   */
  calculated_fit_points?: number | null
}

/**
 * Response of GET /fit-player-profiles/{id}/simulator: the "what if" ranking simulator — recomputes a player's projected rank from a selectable set of matches, letting them explore hypothetical results.
 */
export type SimulatorResponse = {
  current_year: number
  age?: number | null
  /**
   * Years with enough match history to run a simulation
   */
  available_years: Array<number>
  rank_info: RankInfo
  /**
   * Ranks the player could plausibly reach, offered as simulation targets
   */
  simulatable_ranks: Array<PlainRank>
  /**
   * The rank produced by the currently selected simulation
   */
  simulated_rank?: PlainRank | null
  competitions_number: number
  /**
   * Whether this uses the extended (larger match pool) simulation mode
   */
  is_super_simulation: boolean
  matches: Array<SimulatorMatch>
}

/**
 * A workout synced in from a connected smart watch / fitness tracker (HealthKit on iOS, equivalent on Android). Starts life `pending` and is later either linked to one of the user's Activity feed entries (`associated`) or discarded (`dismissed`).
 */
export type SmartWatchWorkout = {
  id?: number
  /**
   * Device-generated workout ID, unique per user+platform (used to dedupe re-syncs)
   */
  platform_id?: string
  platform?: 'ios' | 'android'
  /**
   * Device-reported workout kind, e.g. "Tennis", "Running"
   */
  workout_type?: string
  start_date?: string
  end_date?: string
  /**
   * Duration in seconds
   */
  duration?: number
  /**
   * Distance in meters
   */
  total_distance?: number | null
  /**
   * Energy in kcal
   */
  total_energy_burned?: number | null
  /**
   * pending = awaiting the user's decision; associated = linked to activity_id; dismissed = discarded
   */
  status?: 'pending' | 'associated' | 'dismissed'
  /**
   * The Activity this workout is linked to, once associated
   */
  activity_id?: number | null
  /**
   * When the device reported syncing this workout
   */
  synced_at?: string
  created_at?: string
  heart_rate_stats?: {
    /**
     * bpm
     */
    min?: number | null
    /**
     * bpm
     */
    max?: number | null
    /**
     * bpm
     */
    avg?: number | null
  } | null
}

/**
 * An Instagram or TikTok post as published on the Later link in bio.
 */
export type SocialFeedPost = {
  /**
   * Post caption, possibly empty
   */
  caption: string
  /**
   * https link the post points to on Later (episode, article…). Posts without a link on Later are left out
   */
  link_url: string
  /**
   * https cover image on the Later CDN
   */
  image_url: string
  /**
   * Later media type, e.g. video, image, carousel
   */
  media_type: string | null
  /**
   * Publication time (UTC)
   */
  posted_at: string | null
}

/**
 * Latest posts per network, newest first (at most 12 each). Cached for 15 minutes; when Later is unreachable the last good copy is served, and both lists are empty when there has never been one.
 */
export type SocialFeedResponse = {
  instagram: Array<SocialFeedPost>
  tiktok: Array<SocialFeedPost>
  /**
   * When the posts were read from Later; null when the feed is empty
   */
  fetched_at: string | null
}

/**
 * The bare `{ success: true }` shape several endpoints answer with when there's no other data to return.
 */
export type SuccessResponse = {
  success: boolean
}

/**
 * Court/playing surface type.
 */
export const Surface = {
  CLAY_COURT: 'clay_court',
  SYNTHETIC: 'synthetic',
  GRASS_COURT: 'grass_court',
  SYNTHETIC_GRASS: 'synthetic_grass',
  HARD_COURT: 'hard_court',
  SYNTHETIC_CLAY: 'synthetic_clay',
  OTHER: 'other',
} as const

/**
 * Court/playing surface type.
 */
export type Surface = (typeof Surface)[keyof typeof Surface]

/**
 * Compares both players' win rates on the match's surface to say who has the edge.
 */
export type SurfaceAdvantage = {
  surface?: string
  player_wins?: number
  player_losses?: number
  player_win_rate?: number
  opponent_wins?: number
  opponent_losses?: number
  opponent_win_rate?: number
  advantage?: 'player' | 'opponent' | 'neutral'
}

/**
 * A reduced Competition shape embedded in a Tournament's payload (the parent competition, without its own tournament list).
 */
export type TournamentCompetition = {
  id: number
  name: string
  fit_competition_id: string
  new_fit_competition_id: string | null
  championship?: boolean | null
  from?: string
  to?: string
  /**
   * The organizing club; null for team championships.
   */
  club: ClubPreview | null
}

/**
 * A reduced Draw shape embedded in a tournament listing — see GET /draws/{id} for the fuller bracket detail (matches, best-loser ranking, etc).
 */
export type TournamentDraw = {
  id: number
  name: string
  /**
   * FIT federation draw identifier
   */
  fit_draw_id?: string | null
  /**
   * Number of entered players
   */
  subcribers_count?: number | null
  score?: string | null
  match_date?: string | null
  winner?: InlineSearchPlayer
}

/**
 * A tournament (one gender/category/surface bracket of a Competition) as embedded in a club or competition listing.
 */
export type TournamentPreview = {
  id: number
  /**
   * FIT federation tournament identifier
   */
  fit_tournament_id: string
  new_fit_tournament_id?: string | null
  name: string
  age_category: AgeCategory | null
  /**
   * FIT rankings category (1 = Open, higher = lower skill tiers)
   */
  rankings_category: number | null
  players_count: number | null
  max_number_partecipants: number | null
  surface: Surface | null
  court_type: CourtType | null
  /**
   * 'entry_order' or 'rank_order' — how entrants are seeded
   */
  sign_up_order: string | null
  /**
   * Whether the caller follows this tournament
   */
  followed: boolean
  followable_type: 'tournament'
  draws: Array<{
    [key: string]: unknown
  }>
  /**
   * Computed field strength metric (only present when requested via with_eval)
   */
  absolute_level?: number | null
  /**
   * Computed upset-risk metric (only present via with_eval)
   */
  danger_index?: number | null
  /**
   * Computed difficulty relative to the caller (only present via with_eval, when authenticated)
   */
  personal_difficulty?: number | null
  analyzed_players_count?: number | null
  /**
   * Confidence of the evaluation metrics above, 0-1
   */
  confidence_score?: number | null
}

/**
 * A single title in a fit player profile's "tournaments won" history.
 */
export type TournamentWon = {
  tournament_id?: number
  tournament_name?: string
  best_ranking?: PlainRank
  competition_name?: string
}

/**
 * A training session organized by its author. Can act as a recurring template: see TrainingSchedule for how a training generates repeat occurrences.
 */
export type Training = {
  id?: number
  date?: string
  /**
   * Local start time, "HH:MM" (24h)
   */
  start_time?: string | null
  location_name?: string | null
  club_id?: number | null
  /**
   * Duration in minutes
   */
  duration?: number | null
  indoor?: boolean
  surface?: string
  intensity?: 'very_light' | 'light' | 'moderate' | 'hard' | 'very_hard' | null
  /**
   * Free-text training focus areas (e.g. "Tecnica", "Fisico")
   */
  focus?: Array<string>
  created_at?: string
  /**
   * The training's teacher: a linked User/FitPlayerProfile, or a free-text name (type "String", id null). Null if no teacher was set at all.
   */
  teacher?: {
    id?: number | null
    type?: 'User' | 'FitPlayerProfile' | 'String'
    full_name?: string
    thumbnail?: string | null
  } | null
  /**
   * The user who created this training
   */
  author?: {
    id?: number
    name?: string
    thumbnail?: string | null
  }
  /**
   * Users and/or fit player profiles taking part in this training. A participant with no linked record (type "String", id null) is a free-text name.
   */
  participants?: Array<{
    id?: number | null
    type?: 'User' | 'FitPlayerProfile' | 'String'
    name?: string
    /**
     * FIT registry given name (usually upper case), only when type is FitPlayerProfile. Show first_name + last_name rather than `name` when present.
     */
    first_name?: string | null
    /**
     * FIT registry surname (usually upper case), only when type is FitPlayerProfile.
     */
    last_name?: string | null
    thumbnail?: string | null
    /**
     * Whether the participant has a TennisTalker account (a User, or a FitPlayerProfile claimed by one). False for free-text names.
     */
    user_exists?: boolean
  }>
}

/**
 * A recurring weekly rule attached to a template Training, used to auto-generate repeat occurrences of it on the given day of week within the optional start_date/end_date window.
 */
export type TrainingSchedule = {
  id: number
  training_id: number
  /**
   * 0 = Sunday, 1 = Monday, ..., 6 = Saturday
   */
  day_of_week: number
  /**
   * YYYY-MM-DD format
   */
  start_date: string | null
  /**
   * YYYY-MM-DD format
   */
  end_date: string | null
  active: boolean
  created_at: string
  updated_at: string
  /**
   * A stripped-down summary of the Training this schedule repeats — see the Training schema for the full record
   */
  template_training: {
    id: number
    club_id: number | null
    /**
     * Duration in minutes
     */
    duration: number | null
    intensity: 'very_light' | 'light' | 'moderate' | 'hard' | 'very_hard' | null
    location_name: string | null
    /**
     * Local start time, "HH:MM" (24h)
     */
    start_time: string | null
  }
}

/**
 * A brand present in the TWE catalogue, with how many available products it has.
 */
export type TweBrand = {
  /**
   * Da usare cosi' com'e' nel filtro `brand` di /api/twe-products
   */
  name: string
  /**
   * Prodotti disponibili del marchio
   */
  products_count: number
}

/**
 * List of TWE catalogue brands, in alphabetical order.
 */
export type TweBrandsResponse = {
  brands: Array<TweBrand>
}

/**
 * List of TWE catalogue categories.
 */
export type TweCategoriesResponse = {
  categories: Array<TweCategory>
}

/**
 * A TWE catalogue category, used to filter the product grid.
 */
export type TweCategory = {
  id: number
  name: string
  slug: string
}

/**
 * Clicks per day (Europe/Rome), one entry for every day of the window, days without clicks included with 0. Without date_from the window starts at the first click.
 */
export type TweDailyClicks = Array<{
  /**
   * Day (YYYY-MM-DD)
   */
  date: string
  /**
   * Clicks on that day
   */
  clicks_count: number
}>

/**
 * "Consigliati per te" carousel: the products picked for the caller, plus the segment (profiled/registered/anonymous) the frontend uses to drive its CTA.
 */
export type TweForMeResponse = {
  /**
   * Profilazione del chiamante, guida la CTA del frontend
   */
  segment: 'profiled' | 'registered' | 'anonymous'
  products: Array<TweProductPreview>
}

/**
 * Same shape as TweProductsResponse but without pagination: the most-clicked products ranking, with each product's click count attached.
 */
export type TweMostClickedResponse = {
  products: Array<
    TweProductPreview & {
      /**
       * Numero di click registrati
       */
      clicks_count: number
    }
  >
  /**
   * Totals over the whole filtered window, independent of `limit` — clicked or not, every product matching the filters counts toward catalogue_products_count.
   */
  summary: {
    /**
     * Total clicks in the filtered window
     */
    clicks_count: number
    /**
     * Distinct products clicked in the filtered window
     */
    products_count: number
    /**
     * Distinct users who clicked in the filtered window
     */
    users_count: number
    /**
     * Products matching the filters, clicked or not
     */
    catalogue_products_count: number
  }
  daily: TweDailyClicks
}

/**
 * Full Tennis Warehouse Europe product detail, including all its variants.
 */
export type TweProduct = {
  id: number
  /**
   * Codice articolo TWE
   */
  han: string
  title: string
  /**
   * Assente per ~22% dei prodotti: manca nel feed TWE
   */
  description?: string | null
  brand?: string | null
  gender: 'unisex' | 'uomo' | 'donna'
  /**
   * Scheda prodotto su tenniswarehouse-europe.com, con tracking TWE
   */
  url: string
  /**
   * URL remoti del CDN TWE, da 1 a 10 per prodotto
   */
  images: Array<string>
  min_price: number
  max_price: number
  /**
   * Prezzo pieno della variante piu' economica, se in saldo
   */
  former_price?: number | null
  /**
   * true se almeno una variante e' in saldo
   */
  on_sale: boolean
  /**
   * TWE consente il coupon sulla variante piu' economica
   */
  discountable: boolean
  /**
   * min_price col coupon premium (-15%); null se non scontabile
   */
  premium_price?: number | null
  /**
   * Flag `newItem` del feed TWE. Attenzione: e' true per ~75% del catalogo, non e' un segnale di novita' affidabile
   */
  new_item: boolean
  /**
   * Selezione curata da TWE: ~20 prodotti sui 3300 del feed
   */
  featured: boolean
  categories: Array<TweCategory>
  /**
   * Solo le varianti disponibili
   */
  variants: Array<TweVariant>
}

/**
 * Clicks of one TWE product in the window, total and per day.
 */
export type TweProductDailyClicksResponse = {
  /**
   * Total clicks in the window
   */
  clicks_count: number
  daily: TweDailyClicks
}

/**
 * Grid/list representation of a TWE product (no variants), used by the catalogue listing, search, and carousel endpoints.
 */
export type TweProductPreview = {
  id: number
  /**
   * Codice articolo TWE
   */
  han: string
  title: string
  /**
   * Assente per ~22% dei prodotti: manca nel feed TWE
   */
  description?: string | null
  brand?: string | null
  gender: 'unisex' | 'uomo' | 'donna'
  /**
   * Scheda prodotto su tenniswarehouse-europe.com, con tracking TWE
   */
  url: string
  /**
   * Prima immagine del prodotto
   */
  photo?: string | null
  /**
   * Prezzo della variante piu' economica
   */
  min_price: number
  /**
   * Prezzo della variante piu' cara; uguale a min_price per il 98,6% dei prodotti
   */
  max_price: number
  /**
   * Prezzo pieno della variante piu' economica, se in saldo
   */
  former_price?: number | null
  /**
   * true se almeno una variante e' in saldo
   */
  on_sale: boolean
  /**
   * TWE consente il coupon sulla variante piu' economica
   */
  discountable: boolean
  /**
   * min_price col coupon premium (-15%); null se non scontabile
   */
  premium_price?: number | null
  /**
   * Flag `newItem` del feed TWE.
   */
  new_item: boolean
  /**
   * Selezione curata da TWE.
   */
  featured: boolean
  categories: Array<TweCategory>
}

/**
 * Paginated list of TWE product previews.
 */
export type TweProductsResponse = {
  products: Array<TweProductPreview>
  total_pages: number
  page: number
}

/**
 * A single purchasable size/color combination of a TweProduct.
 */
export type TweVariant = {
  id: number
  /**
   * Codice articolo TWE, univoco
   */
  sku: string
  /**
   * Puo' mancare: nel feed alcuni EAN valgono "0"
   */
  ean?: string | null
  size?: string | null
  color?: string | null
  price: number
  /**
   * Prezzo pieno, presente solo se in saldo
   */
  former_price?: number | null
  /**
   * true se il feed manda un formerPrice
   */
  on_sale: boolean
  /**
   * Sconto in percentuale, se in saldo
   */
  discount?: number | null
  /**
   * TWE consente il coupon su questa variante. Indipendente da on_sale: un articolo puo' essere scontabile senza essere in saldo e viceversa
   */
  discountable: boolean
  /**
   * Prezzo con il coupon premium (-15%) applicato sopra a price. null quando discountable e' false
   */
  premium_price?: number | null
  /**
   * Sconto complessivo in percentuale dal prezzo pieno al prezzo premium
   */
  premium_discount?: number | null
  delivery_costs: number
}

/**
 * Premium renewals expected in the period: charges queued on Recharge and app users whose premium expires.
 */
export type UpcomingRenewalsResponse = {
  period: {
    from: string
    /**
     * Inclusive.
     */
    to: string
    days: number
  }
  /**
   * Charges queued on Recharge in the period (live from its API): the renewals that will run unless the subscriber cancels, skips or the payment fails. null when Recharge is not configured or its API failed (see `notes`).
   */
  recharge: {
    queued_charges: number
    /**
     * Total of the queued charges, EUR.
     */
    amount: number
    /**
     * Distinct customer emails.
     */
    customers: number
    /**
     * Customers whose email matches an app user or a purchase.
     */
    customers_linked_to_users: number
    by_price: Array<{
      price: number
      count: number
      amount: number
    }>
    /**
     * Only days with queued charges, oldest first.
     */
    by_day: Array<{
      date: string
      count: number
      amount: number
    }>
    /**
     * Each queued charge by the premium of the app user matched by email: why the charges differ from the expiring premium users.
     */
    by_user_premium: {
      /**
       * The user premium expires in the period.
       */
      expiring_in_period: number
      /**
       * Of expiring_in_period, those expiring on the charge day.
       */
      expiring_same_day: number
      /**
       * Premium active beyond the period (or without expiration): code redeemed days after the payment, stacked purchases.
       */
      expiring_after_period: number
      /**
       * User without an active premium (code never redeemed, premium already lost).
       */
      not_premium: number
      /**
       * Email not matching any app user.
       */
      no_user: number
    }
  } | null
  /**
   * App users with premium active today that expires in the period (service accounts excluded).
   */
  membership: {
    expiring_premium_users: number
    /**
     * With at least one paid order.
     */
    with_paid_purchase: number
    /**
     * Gifts, trials, manual codes only.
     */
    only_free_codes: number
    /**
     * Every day of the period, oldest first.
     */
    by_day: Array<{
      date: string
      count: number
    }>
    /**
     * Matched by email with a queued charge. null without Recharge.
     */
    with_queued_charge: number | null
    /**
     * Will lose premium unless they buy again. null without Recharge.
     */
    without_queued_charge: number | null
    without_queued_charge_paid: number | null
    /**
     * A sample of their user ids.
     */
    without_queued_charge_ids: Array<number> | null
  }
  /**
   * Data limits.
   */
  notes: Array<string>
}

/**
 * Una riga del feed Aggiornamenti.
 */
export type UpdateNotification = {
  id: number
  kind: UpdateNotificationKind
  /**
   * Titolo; null sulle notifiche vecchie che non lo hanno
   */
  title: string | null
  /**
   * Testo della notifica
   */
  content: string | null
  /**
   * Dove porta il tocco: path interno o URL esterno; null se non porta da nessuna parte
   */
  destination_url: string | null
  /**
   * Foto di chi ha agito (nuovo follower, commento, nuovo post); null se non c'è o per le notifiche create prima che venisse salvato l'autore
   */
  thumbnail: string | null
  /**
   * Unix timestamp
   */
  created_at: number
  /**
   * False finché la riga è «da leggere»
   */
  read: boolean
  /**
   * Preferenza che «Non ricevere più notifiche come questa» spegne; null se non disponibile
   */
  mutable_setting:
    'comments_followed' | 'posts_followed' | 'saved_search' | null
  cta: UpdateNotificationCta | null
}

/**
 * Azione diretta della riga (per ora mai presente).
 */
export type UpdateNotificationCta = {
  label: string
  url: string
}

/**
 * Tipo della notifica: sceglie l'icona. `followed_post` è il nuovo post di chi segui, `follower` il nuovo follower; `other` per i tipi sconosciuti.
 */
export const UpdateNotificationKind = {
  COMMENT: 'comment',
  COMMENT_THREAD: 'comment_thread',
  FOLLOWER: 'follower',
  FOLLOWED_POST: 'followed_post',
  MESSAGE: 'message',
  USER_MATCH: 'user_match',
  FIT_MATCH: 'fit_match',
  TOURNAMENTS: 'tournaments',
  GENERAL: 'general',
  TRAINING: 'training',
  RANKING: 'ranking',
  OTHER: 'other',
} as const

/**
 * Tipo della notifica: sceglie l'icona. `followed_post` è il nuovo post di chi segui, `follower` il nuovo follower; `other` per i tipi sconosciuti.
 */
export type UpdateNotificationKind =
  (typeof UpdateNotificationKind)[keyof typeof UpdateNotificationKind]

/**
 * Preferenza spenta da «Non ricevere più notifiche come questa».
 */
export type UpdateNotificationMuted = {
  setting: 'comments_followed' | 'posts_followed' | 'saved_search'
  enabled: boolean
}

/**
 * La notifica aggiornata dopo letta o non letta, con i contatori.
 */
export type UpdateNotificationWithCounters = {
  notification: UpdateNotification
  counters: UpdatesCounters
}

/**
 * Una pagina del feed, dalla più recente. `next_cursor` va passato come `before_id` per la pagina successiva; null quando non ce ne sono altre.
 */
export type UpdateNotificationsPage = {
  notifications: Array<UpdateNotification>
  next_cursor: number | null
}

/**
 * A maintenance job (data import from an uploaded file, feed sync, ...).
 */
export type UpdateTask = {
  id: number
  /**
   * Stored filename of the uploaded file.
   */
  file?: string | null
  name?: string | null
  status: 'pending' | 'running' | 'success' | 'failed'
  /**
   * Execution log.
   */
  log?: string | null
  /**
   * Unix timestamp (created_at)
   */
  upload_date: number
  /**
   * Unix timestamp (updated_at)
   */
  last_modification_date: number
  /**
   * Download URL of the uploaded import file.
   */
  file_url: string | null
  /**
   * Job that created the task and is enqueued again on rerun (null for import files and tasks created before reruns existed).
   */
  job_class: string | null
  /**
   * Arguments of the job run, ActiveJob-serialized (plain values as they are).
   */
  job_arguments: Array<unknown> | null
  /**
   * Parameter files on S3 among the job arguments. A file the job already deleted answers 404.
   */
  parameter_files: Array<{
    /**
     * S3 key in the jobs parameters bucket.
     */
    key: string
    /**
     * Download link valid for 1 hour (null when S3 is not configured).
     */
    url: string | null
  }>
  /**
   * Whether POST /update-tasks/{id}/rerun can launch it again.
   */
  rerunnable: boolean
}

/**
 * Either the UpdateTaskWithOutcomeResponse success shape, or a ValidationErrorResponse (this operation answers 200 for both the happy path and a validation failure).
 */
export type UpdateTaskCreateResponse =
  UpdateTaskWithOutcomeResponse | ValidationErrorResponse

/**
 * Paginated list of the TWE products touched by an update task.
 */
export type UpdateTaskTweProductsResponse = {
  /**
   * Echo of the `page` param (0 when omitted).
   */
  page: number
  total_pages: number
  twe_products: Array<
    TweProductPreview & {
      updated_at: string
      available: boolean
    }
  >
}

/**
 * Response wrapping a freshly created update task.
 */
export type UpdateTaskWithOutcomeResponse = {
  success: boolean
  update_task: UpdateTask
}

/**
 * Paginated (20 per page) list of update tasks, most recent first.
 */
export type UpdateTasksIndexResponse = {
  page: number
  total_pages: number
  update_tasks: Array<UpdateTask>
}

/**
 * Una conversazione della scheda Messaggi.
 */
export type UpdatesConversation = {
  /**
   * Il mio profilo in questa conversazione: è il mittente delle risposte.
   */
  profile: {
    id: number
    type:
      'user' | 'fit_player_profile' | 'club_manager_profile' | 'shop_profile'
    /**
     * Nome del circolo o del negozio, solo per chi gestisce più profili («come …»)
     */
    label: string | null
  }
  /**
   * L'interlocutore, con lo stesso formato di messages-counterparts. Un utente con il giocatore collegato compare come giocatore.
   */
  counterpart: {
    /**
     * Id da mostrare e per il link al profilo. Per i circoli è l'id del club, non del profilo: per scrivere e aprire la conversazione usa sempre `counterpart_ref`.
     */
    id: number
    name: string
    type: string
    thumbnail?: string | null
    /**
     * Classifica FIT, solo per i giocatori
     */
    rank?: PlainRank | null
    /**
     * Circolo del giocatore, solo per i giocatori
     */
    club?: {
      id?: number
      name?: string
    } | null
    /**
     * Solo per gli utenti e i negozi
     */
    user_id?: number | null
    /**
     * Attivo nelle ultime 24 ore; null per giocatori senza utente
     */
    active_24h?: boolean | null
    /**
     * Attivo nell'ultimo mese; null per giocatori senza utente
     */
    active_1m?: boolean | null
  }
  counterpart_ref: UpdatesProfileRef
  last_message: string
  /**
   * Unix timestamp
   */
  last_message_at: number
  /**
   * True se l'ultimo messaggio l'ho scritto io
   */
  last_message_mine: boolean
  /**
   * Messaggi ricevuti non letti
   */
  unread_count: number
  is_blocked: boolean
  /**
   * False per i giocatori non rivendicati e per l'account di servizio TennisTalker
   */
  can_reply: boolean
}

/**
 * Una pagina di conversazioni. `next_cursor` va passato come `before`; null alla fine.
 */
export type UpdatesConversationsPage = {
  conversations: Array<UpdatesConversation>
  next_cursor: number | null
}

/**
 * Contatori della pagina Aggiornamenti. `notifications`: notifiche non viste, messaggi esclusi. `conversations`: conversazioni con almeno un messaggio non letto. `total`: la loro somma, per la campanella e il badge dell'app.
 */
export type UpdatesCounters = {
  notifications: number
  conversations: number
  total: number
}

/**
 * Riferimento a un profilo (id del profilo, anche per i circoli). È quello da usare in POST /messages e in GET /messages/conversation (counterpart_type/counterpart_id).
 */
export type UpdatesProfileRef = {
  id: number
  type: 'user' | 'fit_player_profile' | 'club_manager_profile' | 'shop_profile'
}

/**
 * A user account, as returned by most user-facing endpoints. Notably excludes password_digest and reset_password_token, but does include confirmation_token.
 */
export type User = {
  id: number
  email: string
  first_name: string | null
  last_name: string | null
  birth_date: string | null
  phone: string | null
  bio: string | null
  racket_brand: string | null
  shoes_brand: string | null
  clothing_brand: string | null
  lefthanded: boolean | null
  one_handed_backhand: boolean | null
  ig_id: string | null
  fb_id: string | null
  nickname: string | null
  preferred_surface: string | null
  club_id: number | null
  club_name: string | null
  /**
   * Non-nil while the account is unconfirmed; used by the sign-up and account-confirmation flows.
   */
  confirmation_token: string | null
  premium: boolean | null
  /**
   * When premium status lapses; null for non-premium or non-expiring premium.
   */
  premium_expiration: string | null
  discoverable: boolean | null
  /**
   * Gender declared by the user (Match Finder); null until set.
   */
  gender: Gender | null
  /**
   * Profile steps still missing before the user can use the Match Finder, in the order the app asks them. `level` and `frequency` are only asked to users without a FIT card.
   */
  match_finder_missing: Array<
    'city' | 'gender' | 'level' | 'frequency' | 'visibility'
  >
  /**
   * URL of the profile photo
   */
  thumbnail: string | null
  fit_player_profile_id: number | null
  shop_profile_id: number | null
  address: string | null
  /**
   * Role-specific profiles this user has set up (a subset of the booleans in `profiles`, with more detail per entry).
   */
  available_profiles: Array<AvailableProfile>
  notification_settings: NotificationSettings
  notifications_for_comments: boolean | null
  notifications_for_posts: boolean | null
  marketing_communication_check: boolean | null
  tennistalker_communication_check: boolean | null
  /**
   * Present only when this user has a linked fit player profile.
   */
  player?: {
    id: number
    name: string
    /**
     * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`.
     */
    first_name: string
    /**
     * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
     */
    last_name: string
    rank: PlainRank
    category: AgeCategory | null
  } | null
  primary_segment: PrimarySegment
  amateur_profile: AmateurProfileSummary
  /**
   * Which role-specific profiles this user has set up (each just a presence flag — see available_profiles for the actual profile data).
   */
  profiles: {
    teacher: boolean
    trainer: boolean
    mental_coach: boolean
    nutritionist: boolean
    shop: boolean
    amateur: boolean
  }
}

/**
 * Activity counters of a user. Followers and received messages include the linked FIT player profile; `user_matches_played` counts the played matches of the user and of that profile.
 */
export type UserActivity = {
  posts: UserActivityCounter
  comments: UserActivityCounter
  likes_given: UserActivityCounter
  /**
   * Likes on the posts of the user.
   */
  likes_received: UserActivityCounter
  followers: UserActivityCounter
  following: UserActivityCounter
  messages_sent: UserActivityCounter
  messages_received: UserActivityCounter
  user_matches_played: UserActivityCounter
  /**
   * Match Finder matches created by the user.
   */
  match_finder_organised: UserActivityCounter
  /**
   * Match Finder matches of others where the user has a seat.
   */
  match_finder_joined: UserActivityCounter
  trainings: UserActivityCounter
  training_participations: UserActivityCounter
  /**
   * Entries of the activity diary (trainings, matches, workouts).
   */
  diary_activities: UserActivityCounter
  smart_watch_workouts: UserActivityCounter
  /**
   * Marketplace offers.
   */
  offers: UserActivityCounter
  reports_sent: UserActivityCounter
  /**
   * Reports about the user profile.
   */
  reports_received: UserActivityCounter
  blocks_given: UserActivityCounter
  blocks_received: UserActivityCounter
  /**
   * FIT player pages the user opened.
   */
  fit_profile_visits_made: UserActivityCounter
  /**
   * Visits to the linked FIT player page.
   */
  fit_profile_visits_received: UserActivityCounter
  live_event_games: UserActivityCounter
  twe_product_clicks: UserActivityCounter
  /**
   * Notifications received.
   */
  notifications: UserActivityCounter
}

/**
 * One activity metric of a user.
 */
export type UserActivityCounter = {
  total: number
  /**
   * Created in the last 30 days.
   */
  last_30_days: number
  /**
   * Latest one, null if none.
   */
  last_at: string | null
}

/**
 * Full admin card of one user, for the user dialog of the backoffice.
 */
export type UserAdminDetailsResponse = {
  user: UserAdvancedSearchResult
  activity: UserActivity
  /**
   * Match Finder matches where the user has a seat, by status.
   */
  match_finder_statuses: {
    played: number
    full: number
    open: number
    cancelled: number
    proposed: number
  }
  membership: {
    codes: Array<UserMembershipCode>
    subscriptions: Array<UserMembershipSubscription>
    /**
     * Shopify checkouts opened from the app.
     */
    checkouts: {
      total: number
      /**
       * Ended with an order.
       */
      used: number
      last_at: string | null
    }
  }
  /**
   * Requests to link a FIT card, oldest first.
   */
  fit_card_requests: Array<{
    id: number
    status: 'pending' | 'approved' | 'rejected'
    fit_player_id: string | null
    fit_player_profile_id: number
    player_name: string | null
    created_at: string
    approved_at: string | null
    rejected_at: string | null
    notes: string | null
    /**
     * Created afterwards for an older link.
     */
    backfilled: boolean
  }>
  /**
   * Current sessions (devices). Sessions are deleted on logout, premium activation and FIT card link.
   */
  sessions: {
    count: number
    first_at: string | null
    last_active_at: string | null
    list: Array<{
      id: number
      created_at: string
      last_active_at: string
      expired_at: string | null
      auth_method: LoginMethod | null
      source: LoginSource | null
      client_platform: ClientPlatform | null
      client_version: string | null
      user_agent: string | null
    }>
  }
  /**
   * The linked FIT player page as the site shows it (the visit is not recorded), plus `hidden`. Null without a linked profile.
   */
  fit_player:
    | (FitPlayerProfileResponse & {
        /**
         * Hidden from the app on request.
         */
        hidden: boolean
      })
    | null
}

/**
 * A notification received by the user, including messages and deleted ones.
 */
export type UserAdminNotification = {
  id: number
  /**
   * Noticed notifier, e.g. `UserMatchNotifier::Notification`.
   */
  type: string
  kind: UpdateNotificationKind
  title: string | null
  content: string | null
  destination_url: string | null
  created_at: string
  seen_at: string | null
  read_at: string | null
  /**
   * Deleted by the user from the updates page.
   */
  deleted_at: string | null
}

export type UserAdminNotificationsResponse = {
  /**
   * Counts over every notification of the user (not just the page, ignoring `type`).
   */
  counts: {
    total: number
    /**
     * Not deleted and not read.
     */
    unread: number
    /**
     * Not deleted, not seen and not read.
     */
    unseen: number
    deleted: number
    /**
     * Notifications per notifier type.
     */
    by_type: {
      [key: string]: number
    }
  }
  /**
   * `before_id` of the next page; null on the last.
   */
  next_cursor: number | null
  notifications: Array<UserAdminNotification>
}

/**
 * Private account data of a search result, only returned to admins.
 */
export type UserAdvancedSearchAdminInfo = {
  email: string
  role: UserRole
  /**
   * Active premium: flag set and expiration empty or in the future.
   */
  premium: boolean
  /**
   * Raw `premium` column.
   */
  premium_flag: boolean
  /**
   * Null when the user has no expiration.
   */
  premium_expiration: string | null
  /**
   * Consumed membership codes.
   */
  premium_purchases_count: number
  /**
   * Shopify orders of the membership codes the user owns.
   */
  shopify_order_ids: Array<string>
  confirmed_at: string | null
  banned: boolean
  banned_at: string | null
  service: boolean
  created_at: string
  first_sign_in_at: string | null
  /**
   * Last API call made with one of the current sessions.
   */
  last_active_at: string | null
  login_method: 'social' | 'password'
  /**
   * Platforms with a registered push token.
   */
  platforms: Array<'ios' | 'android' | 'web'>
  /**
   * Null for users who signed up before login tracking.
   */
  signup_method: SignupMethod | null
  /**
   * Null for users who signed up before login tracking.
   */
  signup_source: LoginSource | null
  /**
   * Last login, any method. Null if none since tracking.
   */
  last_sign_in_at: string | null
  last_sign_in_method: LoginMethod | null
  last_sign_in_source: LoginSource | null
  /**
   * Last client version seen (X-Client-Version header), e.g. 3.6.4. Null if the client never sent it.
   */
  client_version: string | null
  /**
   * Last client platform seen (X-Client-Platform header).
   */
  client_platform: ClientPlatform | null
  /**
   * Client version used to sign up.
   */
  signup_client_version: string | null
  signup_client_platform: ClientPlatform | null
  birth_date: string | null
  /**
   * FIT card number.
   */
  fit_player_id: string | null
  /**
   * Latest approved FIT link request; null for links made before the request flow.
   */
  fit_linked_at: string | null
  marketing_communication_check: boolean
  tennistalker_communication_check: boolean
}

/**
 * Filter options for the user advanced search. Admin-only lists are empty for other callers.
 */
export type UserAdvancedSearchFilters = {
  /**
   * Whether the caller can use the admin filters.
   */
  admin: boolean
  segments: Array<UserSegment>
  professional_profiles: Array<string>
  genders: Array<Gender>
  age_categories: Array<AgeCategory>
  ranks: Array<PlainRank>
  preferred_surfaces: Array<Surface>
  /**
   * Sorts the caller can use.
   */
  sorts: Array<string>
  /**
   * Filters that need an active Club Membership.
   */
  premium_filters: Array<string>
  admin_filters: Array<string>
  roles: Array<UserRole>
  platforms: Array<string>
  login_methods: Array<string>
  /**
   * Values of `last_sign_in_methods` (plus `not_tracked`).
   */
  auth_methods: Array<LoginMethod>
  /**
   * Values of `signup_methods` (plus `not_tracked`).
   */
  signup_methods: Array<SignupMethod>
  /**
   * Values of the `*_sources` filters.
   */
  login_sources: Array<LoginSource>
  /**
   * Values of `client_platforms` (plus `not_tracked`).
   */
  client_platforms: Array<ClientPlatform>
  /**
   * Client versions in use (values of `client_versions` and `client_version_below`), newest first. Empty for non-admins.
   */
  client_versions: Array<UserClientVersion>
  /**
   * Date login tracking started: older sign-ups, logins and sessions are `not_tracked`. Null for non-admins.
   */
  login_tracked_since: string | null
}

/**
 * The FIT player profile linked to a search result.
 */
export type UserAdvancedSearchFitPlayer = {
  id: number
  full_name: string
  gender: Gender | null
  age_category: AgeCategory | null
  active: boolean
  /**
   * Latest official rank (or the one of `rank_year`); null if unranked.
   */
  rank: PlainRank | null
  club: {
    id: number
    name: string
    region: string
    province: string
  } | null
}

/**
 * Filters for the user advanced search. Every filter is optional and they are combined with AND. Filters marked **(premium)** need an active Club Membership (403 otherwise), as in the FIT player search. Filters marked **(admin)** are reserved to admins: any other caller using one gets 403. FIT filters only match users with a linked FIT player profile.
 */
export type UserAdvancedSearchRequest = {
  /**
   * Free text on full name and nickname. For admins it also matches email (partial), user ID, FIT card number (current profile or any link request), Shopify order ID, the email a membership code was bought with, and the membership code itself.
   */
  query?: string
  gender?: Gender
  /**
   * (premium) Region of the FIT player's club.
   */
  region?: Array<string>
  /**
   * (premium) Province code of the FIT player's club.
   */
  province?: Array<string>
  /**
   * IDs of the FIT player's club.
   */
  club_ids?: Array<number>
  /**
   * (premium) FIT age category.
   */
  age_categories?: Array<AgeCategory>
  /**
   * (premium) FIT rank: the latest official one, or the last one of `rank_year` when given.
   */
  ranks?: Array<PlainRank>
  /**
   * Ranking year used by `ranks`, the `rank` sort and the `rank` returned for each result. Defaults to the latest ranking.
   */
  rank_year?: number
  /**
   * Has a linked FIT player profile.
   */
  has_fit_player_profile?: boolean
  /**
   * The FIT player profile is active (tesserato).
   */
  fit_active?: boolean
  segments?: Array<UserSegment>
  /**
   * Owns every one of the given professional profiles.
   */
  professional_profiles?: Array<
    | 'teacher'
    | 'trainer'
    | 'nutritionist'
    | 'mental_coach'
    | 'club_manager'
    | 'shop'
  >
  preferred_surfaces?: Array<Surface>
  lefthanded?: boolean
  one_handed_backhand?: boolean
  /**
   * Latitude; with `lng`, filters by user address distance.
   */
  lat?: number
  lng?: number
  /**
   * Radius in meters (default 20000).
   */
  radius?: number
  /**
   * (admin) User IDs, e.g. a list uploaded from a CSV.
   */
  ids?: Array<number>
  /**
   * (admin) Exact emails.
   */
  emails?: Array<string>
  /**
   * (admin) Active premium: premium flag set and expiration empty or in the future.
   */
  premium?: boolean
  /**
   * (admin)
   */
  premium_expiration_from?: string
  /**
   * (admin) Inclusive.
   */
  premium_expiration_to?: string
  /**
   * (admin) Minimum number of consumed membership codes.
   */
  premium_purchases_min?: number
  /**
   * (admin) Email confirmed (`confirmed_at` set).
   */
  confirmed?: boolean
  /**
   * (admin) Confirmed account: `confirmed_at` set OR a social `uid` (social logins never confirm the email).
   */
  account_confirmed?: boolean
  /**
   * (admin)
   */
  banned?: boolean
  /**
   * (admin)
   */
  roles?: Array<UserRole>
  /**
   * (admin) Sign-up date from.
   */
  created_from?: string
  /**
   * (admin) Sign-up date to, inclusive.
   */
  created_to?: string
  /**
   * (admin) Logged in at least once.
   */
  logged_in?: boolean
  /**
   * (admin) Used the API with a session in the window.
   */
  last_active_from?: string
  /**
   * (admin) Inclusive.
   */
  last_active_to?: string
  /**
   * (admin) Has a push token registered for one of the platforms.
   */
  platforms?: Array<'ios' | 'android' | 'web'>
  /**
   * (admin) `social` = Apple/Google/Facebook uid present.
   */
  login_method?: 'social' | 'password'
  /**
   * (admin) How the user signed up; `not_tracked` = before login tracking (column null).
   */
  signup_methods?: Array<
    | 'password'
    | 'apple'
    | 'google'
    | 'facebook'
    | 'facebook_limited'
    | 'not_tracked'
  >
  /**
   * (admin) Where the user signed up: web or native app.
   */
  signup_sources?: Array<'web' | 'app' | 'not_tracked'>
  /**
   * (admin) Method of the last login; `not_tracked` = no login since tracking started.
   */
  last_sign_in_methods?: Array<
    | 'password'
    | 'apple'
    | 'google'
    | 'facebook'
    | 'facebook_limited'
    | 'email_confirmation'
    | 'backoffice'
    | 'not_tracked'
  >
  /**
   * (admin) Where the last login happened.
   */
  last_sign_in_sources?: Array<'web' | 'app' | 'not_tracked'>
  /**
   * (admin) Last login from this date.
   */
  last_sign_in_from?: string
  /**
   * (admin) Inclusive.
   */
  last_sign_in_to?: string
  /**
   * (admin) Platform of the last client seen; `not_tracked` = never sent.
   */
  client_platforms?: Array<'ios' | 'android' | 'web' | 'not_tracked'>
  /**
   * (admin) Exact last client version seen, e.g. 3.6.4.
   */
  client_versions?: Array<string>
  /**
   * (admin) Last client version seen older than this (numeric comparison, e.g. 3.10.0 > 3.9.2).
   */
  client_version_below?: string
  /**
   * (admin) Has at least one current session opened from web or from the app.
   */
  session_sources?: Array<'web' | 'app'>
  /**
   * (admin) A FIT link request was approved from this date. Only links made through the request flow have a date.
   */
  fit_linked_from?: string
  /**
   * (admin) Inclusive.
   */
  fit_linked_to?: string
  /**
   * (admin) Minimum age, from the FIT birth date or else the profile one; placeholders (2000-01-01, 1999-12-31), dates before 1920 and future dates count as unknown.
   */
  age_min?: number
  /**
   * (admin) Maximum age (inclusive).
   */
  age_max?: number
  /**
   * (admin) Has a valid birth date.
   */
  age_available?: boolean
  /**
   * (admin)
   */
  marketing_communication?: boolean
  /**
   * (admin)
   */
  tennistalker_communication?: boolean
  /**
   * (admin) Internal service accounts.
   */
  service?: boolean
  /**
   * (admin)
   */
  hidden_results?: boolean
  /**
   * (admin) Email bounced.
   */
  bounced?: boolean
  /**
   * (admin) Effective discoverability: `true` = not opted out (includes users who never chose), `false` = opted out.
   */
  discoverable?: boolean
  /**
   * (admin) Exact value of the `discoverable` column: `null` = the user never chose.
   */
  discoverable_value?: 'true' | 'false' | 'null'
  /**
   * (admin) Has (true) or has not (false) uploaded at least one user match, in `features_from`..`features_to` when given.
   */
  has_user_matches?: boolean
  /**
   * (admin) Restricts `has_user_matches` to competitive (true) or friendly (false) matches; alone it implies `has_user_matches: true`.
   */
  user_matches_competitive?: boolean
  /**
   * (admin) Has logged at least one training (by training date, in the features period when given).
   */
  has_trainings?: boolean
  /**
   * (admin) Has synced at least one smartwatch workout (by start date, in the features period when given).
   */
  has_smartwatch_workouts?: boolean
  /**
   * (admin) Start of the period for the `has_*` feature filters (match/training date, workout start).
   */
  features_from?: string
  /**
   * (admin) Inclusive.
   */
  features_to?: string
  /**
   * `name` (default for users) and `rank` are public; the others are admin only (default for admins: `id`). `relevance` orders by how close the user (or linked FIT profile) name is to `query` (`id` without a query). Nulls always last.
   */
  sort?:
    | 'name'
    | 'rank'
    | 'id'
    | 'created_at'
    | 'premium_expiration'
    | 'last_active_at'
    | 'first_sign_in_at'
    | 'last_sign_in_at'
    | 'relevance'
  /**
   * Default `asc` for `name`, `desc` otherwise.
   */
  direction?: 'asc' | 'desc'
  /**
   * (admin) Also return counts over the whole filtered set.
   */
  with_summary?: boolean
  page?: number
  /**
   * Page size, default 20, max 100 (50 for non-admins).
   */
  limit?: number
}

/**
 * Paginated user advanced search results.
 */
export type UserAdvancedSearchResponse = {
  page: number
  total_pages: number
  total_count: number
  users: Array<UserAdvancedSearchResult>
  /**
   * Only when an admin sends `with_summary: true`.
   */
  summary: UserAdvancedSearchSummary | null
}

/**
 * A user in the advanced search results. `admin` and `backoffice_user` are only filled for admins.
 */
export type UserAdvancedSearchResult = {
  id: number
  name: string
  nickname: string | null
  /**
   * Photo URL (user version).
   */
  photo: string | null
  primary_segment: UserSegment
  preferred_surface: Surface | null
  fit_player_profile: UserAdvancedSearchFitPlayer | null
  admin: UserAdvancedSearchAdminInfo | null
  /**
   * Admins only: the full backoffice record (same shape as GET /users), so the backoffice can reuse its row actions and edit dialog.
   */
  backoffice_user: BackofficeUser | null
}

/**
 * Counts over the whole filtered set (not just the page), the base for percentages and the premium rate of a segment.
 */
export type UserAdvancedSearchSummary = {
  total_count: number
  /**
   * Users with active premium.
   */
  premium_count: number
  /**
   * Users with a linked FIT profile.
   */
  fit_player_profile_count: number
  /**
   * Users with confirmed email.
   */
  confirmed_count: number
  /**
   * Users with a confirmed account: email confirmed or social uid.
   */
  account_confirmed_count: number
  banned_count: number
}

/**
 * A client version in use: users whose last client seen is this platform and version.
 */
export type UserClientVersion = {
  /**
   * Null when the client sent the version without the platform.
   */
  platform: ClientPlatform | null
  version: string
  users: number
}

/**
 * One entry in a followers list — either a User or a FitPlayerProfile following the target profile.
 */
export type UserFollowers = {
  id: number
  name: string
  /**
   * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`. Null when the follower has no linked FIT profile.
   */
  first_name: string | null
  /**
   * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
   */
  last_name: string | null
  /**
   * Class name of the follower ("User" or "FitPlayerProfile")
   */
  type: string
  thumbnail?: string | null
  user_id?: number | null
  fit_player_profile_id?: number | null
}

/**
 * A user-reported (unofficial) match result, pending confirmation by the opponent.
 */
export type UserMatch = {
  id: number
  author_id?: number | null
  /**
   * 'User' or 'FitPlayerProfile'
   */
  authorable_type?: string | null
  authorable_id?: number | null
  opponent_id?: number | null
  /**
   * 'User' or 'FitPlayerProfile'
   */
  opponentable_type?: string | null
  opponentable_id?: number | null
  /**
   * Free-text name when the opponent has no linked profile.
   */
  opponent_name?: string | null
  opponent_rank_string?: PlainRank | null
  opponent_plain_rank: number
  date: string
  confirmed_at?: string | null
  rejected_at?: string | null
  result: string
  tournament?: string | null
  board?: string | null
  won?: boolean | null
  competitive?: boolean
  teammate_id?: number | null
  teammateable_type?: string | null
  teammateable_id?: number | null
  second_opponent_id?: number | null
  second_opponentable_type?: string | null
  second_opponentable_id?: number | null
  teammate_name?: string | null
  second_opponent_name?: string | null
  is_a_double_eligible_for_calculation: boolean
  location_name?: string | null
  duration?: number | null
  indoor?: boolean
  surface?: string | null
  created_at: string
  updated_at: string
}

/**
 * Why a request/accept/reject/leave/result call was refused.
 */
export type UserMatchActionError = {
  success: false
  /**
   * time_conflict (409): the requester already has an overlapping match with an exact time (created or seated, open or full). started (422): leave, accept or decline after the start. result_changed (409): confirm lost the race against a correction of the result. not_started (422): result of an organised match before its start.
   */
  error:
    | 'forbidden'
    | 'own_match'
    | 'blocked'
    | 'gender_mismatch'
    | 'profile_incomplete'
    | 'unknown_seat'
    | 'invalid_slot'
    | 'already_seated'
    | 'not_requester'
    | 'no_request'
    | 'not_seated'
    | 'expired'
    | 'not_open'
    | 'spot_taken'
    | 'request_pending'
    | 'request_changed'
    | 'time_conflict'
    | 'started'
    | 'not_playable'
    | 'not_editable'
    | 'invalid_params'
    | 'result_changed'
    | 'not_started'
  /**
   * Profile fields still to fill (profile_incomplete).
   */
  missing?: Array<'city' | 'gender' | 'level' | 'frequency' | 'visibility'>
  /**
   * Current status of the match (not_open, not_playable).
   */
  status?: 'played' | 'full' | 'open' | 'cancelled' | 'proposed'
  /**
   * Seats still free (spot_taken).
   */
  free_seats?: Array<string>
}

/**
 * Where a match to organise is played.
 */
export type UserMatchAddress = {
  id: number
  /**
   * The place as the organiser typed it: show this.
   */
  label: string | null
  city?: string | null
  province?: string | null
  latitude: number | null
  longitude: number | null
}

/**
 * Either the UserMatchWithOutcomeResponse success shape, or a ValidationErrorResponse (this operation answers 200 for both the happy path and a validation failure).
 */
export type UserMatchCreateResponse =
  UserMatchWithOutcomeResponse | ValidationErrorResponse

/**
 * Paginated list of UserMatch entries.
 */
export type UserMatchListResponse = {
  page: number
  total_pages: number
  matches: Array<UserMatchResponse>
}

/**
 * Matches to organise involving the current user (not paginated).
 */
export type UserMatchMineResponse = {
  success: boolean
  user_matches: Array<UserMatchResponse>
}

/**
 * Paginated open matches the current user can join (20 per page).
 */
export type UserMatchOpenListResponse = {
  success: boolean
  user_matches: Array<UserMatchResponse>
  page: number
  total_pages: number
}

/**
 * One participant slot (author/opponent/teammate/second_opponent) in a UserMatch.
 */
export type UserMatchParticipant = {
  score: Array<string>
  id?: number
  name: string | null
  /**
   * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`. Only for a FIT player (real_player true); absent otherwise.
   */
  first_name?: string | null
  /**
   * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
   */
  last_name?: string | null
  real_player: boolean
  thumbnail?: string | null
  /**
   * Whether the participant has a TennisTalker account (a User, or a FitPlayerProfile claimed by one). False for free-text names.
   */
  user_exists: boolean
}

/**
 * Serialized UserMatch as returned by the API (not the raw record columns).
 */
export type UserMatchResponse = {
  id: number
  /**
   * dd/mm/yyyy
   */
  date: string
  tournament?: string | null
  board?: string | null
  result: string
  won?: boolean | null
  not_played: boolean
  is_pending: boolean
  is_confirmed: boolean
  is_rejected: boolean
  is_editable: boolean
  is_played: boolean
  type: 'user_match'
  opponent_rank?: string | null
  competitive: boolean
  is_a_double_eligible_for_calculation: boolean
  location_name?: string | null
  duration?: number | null
  indoor: boolean
  surface?: string | null
  /**
   * played: a result (the only status that counts for FIT points, feeds and Elo). open: looking for players. proposed: a challenge waiting for the invited player. full: every seat taken, waiting for the result. cancelled: cancelled by the organiser or a declined challenge.
   */
  status: 'played' | 'full' | 'open' | 'cancelled' | 'proposed'
  /**
   * Seats still free, to pass as `seat` to POST /request.
   */
  free_seats: Array<'teammate' | 'opponent' | 'second_opponent'>
  starts_at?: string | null
  ends_at?: string | null
  /**
   * exact: starts_at is the start time. band: a time of day window.
   */
  time_type?: 'exact' | 'band' | null
  doubles: boolean
  court_booked: boolean
  cancel_reason?: string | null
  address: UserMatchAddress | null
  author: UserMatchParticipant
  opponent: UserMatchParticipant
  teammate?: UserMatchParticipant | null
  second_opponent?: UserMatchParticipant | null
  notes: string | null
  /**
   * null for a match that is not organised (no starts_at), and in lists that are not about organising: the activity feed, /users/user-matches and GET /user-matches.
   */
  match_finder: MatchFinder | null
}

/**
 * Fields of a match to organise, accepted on create and update.
 */
export type UserMatchSchedulingParams = {
  /**
   * Required for open/proposed; must be in the future. Derives `date`.
   */
  starts_at?: string | null
  /**
   * After starts_at.
   */
  ends_at?: string | null
  time_type?: 'exact' | 'band' | null
  doubles?: boolean | null
  court_booked?: boolean | null
  /**
   * Latitude of the place.
   */
  lat_input?: number | null
  /**
   * Longitude of the place.
   */
  lng_input?: number | null
  /**
   * The place as typed by the user.
   */
  address_user_input?: string | null
  /**
   * Free text from the organiser, shown on the match; ignored on a played match.
   */
  notes?: string | null
}

/**
 * Response wrapping a single UserMatch after a create/update/confirm/reject action.
 */
export type UserMatchWithOutcomeResponse = {
  success: boolean
  user_match: UserMatchResponse
}

/**
 * A membership code (PremiumToken) owned by the user.
 */
export type UserMembershipCode = {
  id: number
  code: string
  plan: 'annual' | 'monthly_legacy' | 'other'
  duration_days: number
  /**
   * Bought with a Shopify order.
   */
  paid: boolean
  shopify_order_id: string | null
  email: string | null
  activated: boolean
  created_at: string
  /**
   * Automatic activation rule applied.
   */
  activation_rule: string | null
  shadow_activation_rule: string | null
  shadow_user_id: number | null
}

/**
 * A Recharge subscription of the user.
 */
export type UserMembershipSubscription = {
  id: number
  status: 'active' | 'cancelled' | 'expired'
  next_charge_at: string | null
  /**
   * Last charge failed, Recharge is retrying.
   */
  in_recovery: boolean
  failure_cause: string | null
  card_expires_on: string | null
  card_payment_type: string | null
  linked_via: string
  recharge_subscription_id: number | null
  first_shopify_order_id: string | null
  created_at: string
}

/**
 * Minimal public identity (id + display name) for a user, used wherever a full profile would be unnecessary.
 */
export type UserPreview = {
  id: number
  name: string
}

/**
 * A User record serialized as-is (every column except password_digest).
 */
export type UserRecord = {
  id: number
  email: string
  first_name: string
  last_name: string
  full_name?: string
  nickname?: string | null
  uid?: string | null
  fb_id?: string | null
  ig_id?: string | null
  ac_id?: string | null
  address_id?: number | null
  club_id?: number | null
  birth_date?: string | null
  phone?: string | null
  bio?: string | null
  photo?: {
    url?: string | null
    user?: {
      url?: string | null
    }
    club?: {
      url?: string | null
    }
    offer?: {
      url?: string | null
    }
    post?: {
      url?: string | null
    }
  }
  preferred_surface?: Surface | null
  role?:
    | 'admin'
    | 'simple_user'
    | 'banner_manager'
    | 'censor_manager'
    | 'premium_user'
  premium?: boolean
  premium_expiration?: string | null
  hidden_results?: boolean
  discoverable?: boolean | null
  lefthanded?: boolean | null
  one_handed_backhand?: boolean | null
  racket_brand?: string | null
  shoes_brand?: string | null
  clothing_brand?: string | null
  service?: boolean
  bounced?: boolean
  email_notifications?: boolean
  notifications_for_comments?: boolean
  notifications_for_posts?: boolean
  notification_settings?: {
    [key: string]: boolean
  }
  notifications_last_seen?: string | null
  market_condition_accepted?: boolean
  marketing_communication_check?: boolean
  tennistalker_communication_check?: boolean
  banned_at?: string | null
  banned_by_id?: number | null
  ban_notes?: string | null
  confirmation_token?: string | null
  confirmed_at?: string | null
  reset_password_token?: string | null
  first_sign_in_at?: string | null
  /**
   * Null for users who signed up before login tracking.
   */
  signup_method?: SignupMethod | null
  /**
   * Null for users who signed up before login tracking.
   */
  signup_source?: LoginSource | null
  /**
   * Last login, any method. Null if none since tracking.
   */
  last_sign_in_at?: string | null
  last_sign_in_method?: LoginMethod | null
  last_sign_in_source?: LoginSource | null
  /**
   * Last client version seen (X-Client-Version header), e.g. 3.6.4. Null if the client never sent it.
   */
  client_version?: string | null
  /**
   * Last client platform seen (X-Client-Platform header).
   */
  client_platform?: ClientPlatform | null
  /**
   * Client version used to sign up.
   */
  signup_client_version?: string | null
  signup_client_platform?: ClientPlatform | null
  created_at?: string
  updated_at?: string
}

/**
 * User role (authorization level).
 */
export const UserRole = {
  ADMIN: 'admin',
  SIMPLE_USER: 'simple_user',
  BANNER_MANAGER: 'banner_manager',
  CENSOR_MANAGER: 'censor_manager',
  PREMIUM_USER: 'premium_user',
} as const

/**
 * User role (authorization level).
 */
export type UserRole = (typeof UserRole)[keyof typeof UserRole]

/**
 * The user's segment, derived from the profiles they own (not declared): `pro` (any professional profile) > `agonista` (linked FIT player profile) > `amatoriale` (onboarding amateur profile) > `fan` (none of the above).
 */
export const UserSegment = {
  PRO: 'pro',
  AGONISTA: 'agonista',
  AMATORIALE: 'amatoriale',
  FAN: 'fan',
} as const

/**
 * The user's segment, derived from the profiles they own (not declared): `pro` (any professional profile) > `agonista` (linked FIT player profile) > `amatoriale` (onboarding amateur profile) > `fan` (none of the above).
 */
export type UserSegment = (typeof UserSegment)[keyof typeof UserSegment]

/**
 * The `user` payload returned by login/registration endpoints: a User plus the newly-issued session `token` to use as X-Api-Key on subsequent calls.
 */
export type UserSession = {
  id: number
  first_name: string | null
  last_name: string | null
  name: string | null
  email: string
  role: string | null
  birth_date: string | null
  phone: string | null
  photo_url: string | null
  thumbnail: string | null
  confirmation_token: string | null
  /**
   * When the session token expires
   */
  token_expiration: string | null
  /**
   * Session token — send this as the X-Api-Key header on subsequent requests
   */
  token: string | null
  available_profiles: Array<AvailableProfile>
  fit_player_profile_id: number | null
  shop_profile_id: number | null
  notification_settings: NotificationSettings
  notifications_for_comments: boolean | null
  notifications_for_posts: boolean | null
  market_condition_accepted: boolean | null
  bio: string | null
  premium: boolean
  premium_expiration: string | null
  racket_brand: string | null
  shoes_brand: string | null
  clothing_brand: string | null
  lefthanded: boolean | null
  one_handed_backhand: boolean | null
  ig_id: string | null
  fb_id: string | null
  nickname: string | null
  preferred_surface: string | null
  club_id: number | null
  club_name: string | null
  address: string | null
  marketing_communication_check: boolean | null
  tennistalker_communication_check: boolean | null
  discoverable: boolean | null
  /**
   * Gender declared by the user (Match Finder); null until set.
   */
  gender: Gender | null
  /**
   * Profile steps still missing before the user can use the Match Finder, in the order the app asks them. `level` and `frequency` are only asked to users without a FIT card.
   */
  match_finder_missing: Array<
    'city' | 'gender' | 'level' | 'frequency' | 'visibility'
  >
  lat?: number | null
  lng?: number | null
  player?: {
    id: number
    name: string
    /**
     * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`.
     */
    first_name: string
    /**
     * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
     */
    last_name: string
    category: AgeCategory | null
    rank: PlainRank
    gender: Gender | null
  } | null
  profiles: {
    teacher: boolean
    trainer: boolean
    mental_coach: boolean
    nutritionist: boolean
    shop: boolean
    amateur: boolean
  }
  primary_segment: PrimarySegment
  amateur_profile: AmateurProfileSummary
}

/**
 * Something the user did. Labels are up to the client; `detail` and `data` are raw data (names, results, excerpts, enums) that depend on `kind`.
 */
export type UserTimelineEvent = {
  /**
   * `login` = a session still existing (logout, premium activation and FIT card link delete them); `match_finder_*` = organised matches; `user_match` = played matches of the user or of the linked FIT player; `message_sent` = a direct message, `detail` = its whole text (up to 4000 characters), `data` has the recipient and read/deleted/blocked.
   */
  kind:
    | 'signup'
    | 'login'
    | 'ban'
    | 'membership_code'
    | 'subscription'
    | 'checkout'
    | 'fit_request'
    | 'post'
    | 'comment'
    | 'message_sent'
    | 'follow'
    | 'user_match'
    | 'match_finder_created'
    | 'match_finder_joined'
    | 'training'
    | 'training_participation'
    | 'workout'
    | 'offer'
    | 'report_sent'
    | 'report_received'
    | 'block'
    | 'live_event_game'
  at: string
  /**
   * Main text: excerpt, name, result, code...
   */
  detail: string | null
  /**
   * Model of the record, when there is one.
   */
  record_type: string | null
  record_id: number | null
  /**
   * Other fields of the kind (method, source, client, status, plan...).
   */
  data: {
    [key: string]: unknown
  }
}

export type UserTimelineResponse = {
  events: Array<UserTimelineEvent>
  /**
   * `before` of the next page; null on the last.
   */
  next_before: string | null
}

/**
 * Outcome of PATCH /users/{id}: the (possibly unsaved) user record, plus `errors` (full messages) when validation failed.
 */
export type UserUpdateResponse = {
  success: boolean
  user: UserRecord
  errors?: Array<string>
}

/**
 * GET /users/{id} response: a user's public profile as seen by another user, including whether the caller follows them and their role-specific profile details.
 */
export type UserWithProfile = {
  id: number
  pseudonymized_id?: string
  user_id: number | null
  name: string
  bio?: string | null
  racket_brand?: string | null
  shoes_brand?: string | null
  clothing_brand?: string | null
  lefthanded?: boolean | null
  one_handed_backhand?: boolean | null
  ig_id?: string | null
  fb_id?: string | null
  nickname?: string | null
  preferred_surface?: string | null
  thumbnail?: string | null
  fit_player_profile_id: number | null
  shop_profile_id?: number | null
  premium: boolean
  premium_expiration?: string | null
  /**
   * Whether the current caller follows this profile
   */
  followed: boolean
  /**
   * Class name to use when following this profile ("User" or "FitPlayerProfile")
   */
  followable_type: string
  player?: {
    id: number
    name: string
    /**
     * Given name(s) from the FIT registry, usually upper case ("LORENZO"). Show first_name + last_name rather than `name` (surname first). May be blank on some legacy records: fall back to `name`.
     */
    first_name: string
    /**
     * Surname from the FIT registry, usually upper case ("MUSETTI"). See first_name.
     */
    last_name: string
    category: AgeCategory | null
    rank: PlainRank
    gender: Gender | null
  } | null
  profiles: {
    teacher: boolean
    trainer: boolean
    mental_coach: boolean
    nutritionist: boolean
    shop: boolean
  }
  nutritionist?: {
    id?: number
    qualifications?: string | null
    professional_info?: string | null
  } | null
  mental_coach?: {
    id?: number
    qualifications?: string | null
    professional_info?: string | null
  } | null
  teacher?: {
    id?: number
    qualification?: string | null
    professional_info?: string | null
  } | null
  trainer?: {
    id?: number
    qualification?: string | null
    professional_info?: string | null
  } | null
  shop?: {
    id?: number
    name?: string
    website?: string | null
    info?: string | null
    address?: string | null
  } | null
  club_manager_profile?: {
    id?: number
    name?: string
    thumbnail?: string | null
    type?: string
  } | null
  active_24h?: boolean
  active_1m?: boolean
  rank?: PlainRank | null
  category?: AgeCategory | null
}

/**
 * Paginated (Kaminari) list of users for the admin backoffice.
 */
export type UsersIndexResponse = {
  page: number
  total_pages: number
  users: Array<BackofficeUser>
}

/**
 * Either the paginated UsersIndexResponse, or a bare empty array when `q` is blank.
 */
export type UsersSearchResponse = UsersIndexResponse | Array<unknown>

/**
 * Failure shape used by most create/update actions when a model fails validation: `errors` is a hash keyed by attribute name, each value an array of human-readable messages for that attribute (ActiveModel-style).
 */
export type ValidationErrorResponse = {
  success: boolean
  errors: {
    [key: string]: Array<string>
  }
}

/**
 * A wins/losses tally for one historical breakdown bucket.
 */
export type WinLossStats = {
  wins: number
  losses: number
}

/**
 * Response for GET /fit-player-profiles/{id}/wrapped: a "year in review" summary of a player's season (Spotify-Wrapped-style), gated behind `enable_wrapped` on the profile.
 */
export type WrappedResponse = {
  year: number
  /**
   * Whether the profile's linked user has a premium subscription (may unlock extra wrapped content client-side).
   */
  premium: boolean
  rankStartYear: PlainRank
  rankEndYear: PlainRank
  won: number
  lost: number
  won_percentage: number
  /**
   * The player's best rank reached during the year.
   */
  max_rank: {
    rank?: PlainRank
    year?: number
  }
  category?: AgeCategory | null
  /**
   * Position within the age category's ranking.
   */
  category_rank?: number | null
  region?: string | null
  /**
   * Position within the region's ranking.
   */
  region_rank?: number | null
  province?: string | null
  /**
   * Position within the province's ranking.
   */
  province_rank?: number | null
}

export type GetActivitiesData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    /**
     * Page number (20 per page)
     */
    page?: number
    /**
     * Show this user's activity feed instead of the caller's (their hidden activities are excluded unless it is the caller)
     */
    user_id?: number
    /**
     * Show this FIT player profile's activity feed instead of the caller's (hidden activities excluded unless it is the caller's own profile)
     */
    player_id?: number
    /**
     * Restrict the feed to activities backed by this kind of record:
     * * `match`
     * * `user_match`
     * * `training`
     *
     */
    type?: string
  }
  url: '/activities'
}

export type GetActivitiesResponses = {
  /**
   * activities filtered by player_id
   */
  200: {
    activities: Array<Activity>
    meta: ActivityMeta
  }
}

export type GetActivitiesResponse =
  GetActivitiesResponses[keyof GetActivitiesResponses]

export type DeleteActivitiesByIdData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path: {
    id: number
  }
  query?: never
  url: '/activities/{id}'
}

export type DeleteActivitiesByIdResponses = {
  /**
   * activity deleted
   */
  200: {
    success?: boolean
    activity?: Activity
  }
}

export type DeleteActivitiesByIdResponse =
  DeleteActivitiesByIdResponses[keyof DeleteActivitiesByIdResponses]

export type GetActivitiesByIdData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path: {
    id: number
  }
  query?: never
  url: '/activities/{id}'
}

export type GetActivitiesByIdErrors = {
  /**
   * activity not found
   */
  404: unknown
}

export type GetActivitiesByIdResponses = {
  /**
   * activity found
   */
  200: {
    success?: boolean
    activity?: Activity
  }
}

export type GetActivitiesByIdResponse =
  GetActivitiesByIdResponses[keyof GetActivitiesByIdResponses]

export type PatchActivitiesByIdData = {
  body?: {
    activity: {
      /**
       * Personal note on this activity entry
       */
      note?: string | null
      /**
       * Whether this activity appears in the public feed
       */
      visible?: boolean
    }
  }
  headers: {
    'X-Api-Key': string
  }
  path: {
    id: number
  }
  query?: never
  url: '/activities/{id}'
}

export type PatchActivitiesByIdErrors = {
  /**
   * activity not found or belongs to another user
   */
  404: unknown
}

export type PatchActivitiesByIdResponses = {
  /**
   * activity updated
   */
  200: {
    success?: boolean
    activity?: Activity
  }
}

export type PatchActivitiesByIdResponse =
  PatchActivitiesByIdResponses[keyof PatchActivitiesByIdResponses]

export type GetActivitiesStreakData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    /**
     * YYYY-MM-DD inclusive lower bound (default: 365 days before `to`)
     */
    from?: string
    /**
     * YYYY-MM-DD inclusive upper bound (default: today)
     */
    to?: string
  }
  url: '/activities/streak'
}

export type GetActivitiesStreakErrors = {
  /**
   * range too large
   */
  422: unknown
}

export type GetActivitiesStreakResponses = {
  /**
   * streak data
   */
  200: ActivityStreak
}

export type GetActivitiesStreakResponse =
  GetActivitiesStreakResponses[keyof GetActivitiesStreakResponses]

export type GetAdminLiveEventsData = {
  body?: never
  path?: never
  query?: {
    /**
     * Page (1-based)
     */
    page?: number
  }
  url: '/admin/live-events'
}

export type GetAdminLiveEventsErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
}

export type GetAdminLiveEventsError =
  GetAdminLiveEventsErrors[keyof GetAdminLiveEventsErrors]

export type GetAdminLiveEventsResponses = {
  /**
   * events
   */
  200: LiveEventAdminIndexResponse
}

export type GetAdminLiveEventsResponse =
  GetAdminLiveEventsResponses[keyof GetAdminLiveEventsResponses]

export type PostAdminLiveEventsData = {
  body?: {
    live_event: LiveEventInput & {
      [key: string]: unknown
    }
  }
  path?: never
  query?: never
  url: '/admin/live-events'
}

export type PostAdminLiveEventsErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * validation failed
   */
  422: ValidationErrorResponse
}

export type PostAdminLiveEventsError =
  PostAdminLiveEventsErrors[keyof PostAdminLiveEventsErrors]

export type PostAdminLiveEventsResponses = {
  /**
   * event created
   */
  200: LiveEventAdminResponse
}

export type PostAdminLiveEventsResponse =
  PostAdminLiveEventsResponses[keyof PostAdminLiveEventsResponses]

export type DeleteAdminLiveEventsByIdData = {
  body?: never
  path: {
    /**
     * Live event ID
     */
    id: number
  }
  query?: never
  url: '/admin/live-events/{id}'
}

export type DeleteAdminLiveEventsByIdErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * event not found
   */
  404: unknown
}

export type DeleteAdminLiveEventsByIdError =
  DeleteAdminLiveEventsByIdErrors[keyof DeleteAdminLiveEventsByIdErrors]

export type DeleteAdminLiveEventsByIdResponses = {
  /**
   * event deleted
   */
  200: LiveEventAdminResponse
}

export type DeleteAdminLiveEventsByIdResponse =
  DeleteAdminLiveEventsByIdResponses[keyof DeleteAdminLiveEventsByIdResponses]

export type GetAdminLiveEventsByIdData = {
  body?: never
  path: {
    /**
     * Live event ID
     */
    id: number
  }
  query?: never
  url: '/admin/live-events/{id}'
}

export type GetAdminLiveEventsByIdErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * event not found
   */
  404: unknown
}

export type GetAdminLiveEventsByIdError =
  GetAdminLiveEventsByIdErrors[keyof GetAdminLiveEventsByIdErrors]

export type GetAdminLiveEventsByIdResponses = {
  /**
   * event
   */
  200: LiveEventAdminResponse
}

export type GetAdminLiveEventsByIdResponse =
  GetAdminLiveEventsByIdResponses[keyof GetAdminLiveEventsByIdResponses]

export type PutAdminLiveEventsByIdData = {
  body?: {
    live_event: LiveEventInput
  }
  path: {
    /**
     * Live event ID
     */
    id: number
  }
  query?: never
  url: '/admin/live-events/{id}'
}

export type PutAdminLiveEventsByIdErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * event not found
   */
  404: unknown
  /**
   * validation failed
   */
  422: ValidationErrorResponse
}

export type PutAdminLiveEventsByIdError =
  PutAdminLiveEventsByIdErrors[keyof PutAdminLiveEventsByIdErrors]

export type PutAdminLiveEventsByIdResponses = {
  /**
   * event updated
   */
  200: LiveEventAdminResponse
}

export type PutAdminLiveEventsByIdResponse =
  PutAdminLiveEventsByIdResponses[keyof PutAdminLiveEventsByIdResponses]

export type PutAdminLiveEventsByIdScreenData = {
  body?: {
    enabled: boolean
  }
  path: {
    /**
     * Live event ID
     */
    id: number
  }
  query?: never
  url: '/admin/live-events/{id}/screen'
}

export type PutAdminLiveEventsByIdScreenErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * event not found
   */
  404: unknown
}

export type PutAdminLiveEventsByIdScreenError =
  PutAdminLiveEventsByIdScreenErrors[keyof PutAdminLiveEventsByIdScreenErrors]

export type PutAdminLiveEventsByIdScreenResponses = {
  /**
   * screen updated
   */
  200: LiveEventAdminResponse
}

export type PutAdminLiveEventsByIdScreenResponse =
  PutAdminLiveEventsByIdScreenResponses[keyof PutAdminLiveEventsByIdScreenResponses]

export type GetAdminLiveEventsByIdQueueData = {
  body?: never
  path: {
    /**
     * Live event ID
     */
    id: number
  }
  query?: never
  url: '/admin/live-events/{id}/queue'
}

export type GetAdminLiveEventsByIdQueueErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * event not found
   */
  404: unknown
}

export type GetAdminLiveEventsByIdQueueError =
  GetAdminLiveEventsByIdQueueErrors[keyof GetAdminLiveEventsByIdQueueErrors]

export type GetAdminLiveEventsByIdQueueResponses = {
  /**
   * queue
   */
  200: LiveEventQueueResponse
}

export type GetAdminLiveEventsByIdQueueResponse =
  GetAdminLiveEventsByIdQueueResponses[keyof GetAdminLiveEventsByIdQueueResponses]

export type GetAdminLiveEventsByIdSessionsData = {
  body?: never
  path: {
    /**
     * Live event ID
     */
    id: number
  }
  query?: {
    mode?: LiveEventGameMode
    status?: LiveEventSessionStatus
    user_id?: number
    /**
     * Only games flagged `stats.suspicious`
     */
    suspicious?: boolean
    /**
     * Only games invalidated by an admin
     */
    invalidated?: boolean
    /**
     * Include soft-deleted games
     */
    with_deleted?: boolean
    /**
     * Page (1-based)
     */
    page?: number
  }
  url: '/admin/live-events/{id}/sessions'
}

export type GetAdminLiveEventsByIdSessionsErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * event not found
   */
  404: unknown
}

export type GetAdminLiveEventsByIdSessionsError =
  GetAdminLiveEventsByIdSessionsErrors[keyof GetAdminLiveEventsByIdSessionsErrors]

export type GetAdminLiveEventsByIdSessionsResponses = {
  /**
   * sessions
   */
  200: LiveEventAdminSessionsResponse
}

export type GetAdminLiveEventsByIdSessionsResponse =
  GetAdminLiveEventsByIdSessionsResponses[keyof GetAdminLiveEventsByIdSessionsResponses]

export type PutAdminLiveEventsByIdSessionsBySessionIdInvalidateData = {
  body?: {
    reason?: string
  }
  path: {
    /**
     * Live event ID
     */
    id: number
    /**
     * Game session ID
     */
    session_id: number
  }
  query?: never
  url: '/admin/live-events/{id}/sessions/{session_id}/invalidate'
}

export type PutAdminLiveEventsByIdSessionsBySessionIdInvalidateErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * game not found
   */
  404: unknown
}

export type PutAdminLiveEventsByIdSessionsBySessionIdInvalidateError =
  PutAdminLiveEventsByIdSessionsBySessionIdInvalidateErrors[keyof PutAdminLiveEventsByIdSessionsBySessionIdInvalidateErrors]

export type PutAdminLiveEventsByIdSessionsBySessionIdInvalidateResponses = {
  /**
   * game invalidated
   */
  200: LiveEventAdminSessionResponse
}

export type PutAdminLiveEventsByIdSessionsBySessionIdInvalidateResponse =
  PutAdminLiveEventsByIdSessionsBySessionIdInvalidateResponses[keyof PutAdminLiveEventsByIdSessionsBySessionIdInvalidateResponses]

export type PutAdminLiveEventsByIdSessionsBySessionIdRestoreData = {
  body?: never
  path: {
    /**
     * Live event ID
     */
    id: number
    /**
     * Game session ID
     */
    session_id: number
  }
  query?: never
  url: '/admin/live-events/{id}/sessions/{session_id}/restore'
}

export type PutAdminLiveEventsByIdSessionsBySessionIdRestoreErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * game not found
   */
  404: unknown
}

export type PutAdminLiveEventsByIdSessionsBySessionIdRestoreError =
  PutAdminLiveEventsByIdSessionsBySessionIdRestoreErrors[keyof PutAdminLiveEventsByIdSessionsBySessionIdRestoreErrors]

export type PutAdminLiveEventsByIdSessionsBySessionIdRestoreResponses = {
  /**
   * game restored
   */
  200: LiveEventAdminSessionResponse
}

export type PutAdminLiveEventsByIdSessionsBySessionIdRestoreResponse =
  PutAdminLiveEventsByIdSessionsBySessionIdRestoreResponses[keyof PutAdminLiveEventsByIdSessionsBySessionIdRestoreResponses]

export type DeleteAdminLiveEventsByIdSessionsBySessionIdData = {
  body?: never
  path: {
    /**
     * Live event ID
     */
    id: number
    /**
     * Game session ID
     */
    session_id: number
  }
  query?: never
  url: '/admin/live-events/{id}/sessions/{session_id}'
}

export type DeleteAdminLiveEventsByIdSessionsBySessionIdErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * game not found
   */
  404: unknown
}

export type DeleteAdminLiveEventsByIdSessionsBySessionIdError =
  DeleteAdminLiveEventsByIdSessionsBySessionIdErrors[keyof DeleteAdminLiveEventsByIdSessionsBySessionIdErrors]

export type DeleteAdminLiveEventsByIdSessionsBySessionIdResponses = {
  /**
   * game deleted
   */
  200: LiveEventAdminSessionResponse
}

export type DeleteAdminLiveEventsByIdSessionsBySessionIdResponse =
  DeleteAdminLiveEventsByIdSessionsBySessionIdResponses[keyof DeleteAdminLiveEventsByIdSessionsBySessionIdResponses]

export type GetAdminLiveEventsByIdLeaderboardData = {
  body?: never
  path: {
    /**
     * Live event ID
     */
    id: number
  }
  query?: {
    /**
     * Default `live`
     */
    mode?: LiveEventGameMode
    /**
     * Rows, 1..200
     */
    limit?: number
  }
  url: '/admin/live-events/{id}/leaderboard'
}

export type GetAdminLiveEventsByIdLeaderboardErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * event not found
   */
  404: unknown
}

export type GetAdminLiveEventsByIdLeaderboardError =
  GetAdminLiveEventsByIdLeaderboardErrors[keyof GetAdminLiveEventsByIdLeaderboardErrors]

export type GetAdminLiveEventsByIdLeaderboardResponses = {
  /**
   * leaderboard
   */
  200: LiveEventLeaderboard
}

export type GetAdminLiveEventsByIdLeaderboardResponse =
  GetAdminLiveEventsByIdLeaderboardResponses[keyof GetAdminLiveEventsByIdLeaderboardResponses]

export type GetAdsenseSlotsAllData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/adsense_slots/all'
}

export type GetAdsenseSlotsAllResponses = {
  /**
   * lista completa
   */
  200: Array<AdsenseSlot>
}

export type GetAdsenseSlotsAllResponse =
  GetAdsenseSlotsAllResponses[keyof GetAdsenseSlotsAllResponses]

export type GetAdsenseSlotsData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    /**
     * Numero pagina (default 1)
     */
    page?: number
  }
  url: '/adsense_slots'
}

export type GetAdsenseSlotsResponses = {
  /**
   * lista paginata
   */
  200: {
    data: Array<AdsenseSlot>
    page: number
    total_pages: number
  }
}

export type GetAdsenseSlotsResponse =
  GetAdsenseSlotsResponses[keyof GetAdsenseSlotsResponses]

export type PostAdsenseSlotsData = {
  body?: {
    adsense_slot?: {
      /**
       * Nome identificativo dello slot
       */
      name?: string
    }
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/adsense_slots'
}

export type PostAdsenseSlotsErrors = {
  /**
   * utente non staff
   */
  401: unknown
}

export type PostAdsenseSlotsResponses = {
  /**
   * parametri non validi
   */
  200: ValidationErrorResponse
}

export type PostAdsenseSlotsResponse =
  PostAdsenseSlotsResponses[keyof PostAdsenseSlotsResponses]

export type DeleteAdsenseSlotsByIdData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path: {
    /**
     * ID AdsenseSlot
     */
    id: number
  }
  query?: never
  url: '/adsense_slots/{id}'
}

export type DeleteAdsenseSlotsByIdErrors = {
  /**
   * adsense slot non trovato
   */
  404: unknown
}

export type DeleteAdsenseSlotsByIdResponses = {
  /**
   * adsense slot eliminato
   */
  200: {
    success: boolean
    adsense_slot: AdsenseSlot
  }
}

export type DeleteAdsenseSlotsByIdResponse =
  DeleteAdsenseSlotsByIdResponses[keyof DeleteAdsenseSlotsByIdResponses]

export type GetAdsenseSlotsByIdData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path: {
    /**
     * ID AdsenseSlot
     */
    id: number
  }
  query?: never
  url: '/adsense_slots/{id}'
}

export type GetAdsenseSlotsByIdErrors = {
  /**
   * adsense slot non trovato
   */
  404: unknown
}

export type GetAdsenseSlotsByIdResponses = {
  /**
   * adsense slot trovato
   */
  200: AdsenseSlot
}

export type GetAdsenseSlotsByIdResponse =
  GetAdsenseSlotsByIdResponses[keyof GetAdsenseSlotsByIdResponses]

export type PatchAdsenseSlotsByIdData = {
  body?: {
    adsense_slot?: {
      name?: string
    }
  }
  headers: {
    'X-Api-Key': string
  }
  path: {
    /**
     * ID AdsenseSlot
     */
    id: number
  }
  query?: never
  url: '/adsense_slots/{id}'
}

export type PatchAdsenseSlotsByIdResponses = {
  /**
   * parametri non validi
   */
  200: ValidationErrorResponse
}

export type PatchAdsenseSlotsByIdResponse =
  PatchAdsenseSlotsByIdResponses[keyof PatchAdsenseSlotsByIdResponses]

export type DeleteSessionsByIdData = {
  body?: never
  path: {
    /**
     * Session token to destroy
     */
    id: string
  }
  query?: never
  url: '/sessions/{id}'
}

export type DeleteSessionsByIdResponses = {
  /**
   * caller without a valid session
   */
  200: {
    success?: boolean
  }
}

export type DeleteSessionsByIdResponse =
  DeleteSessionsByIdResponses[keyof DeleteSessionsByIdResponses]

export type PostSessionsFacebookData = {
  body?: {
    token: string
    uid: string
    email: string
    first_name?: string
    last_name?: string
    photo?: string
  }
  path?: never
  query?: never
  url: '/sessions/facebook'
}

export type PostSessionsFacebookResponses = {
  /**
   * Facebook account without email rejected
   */
  200: {
    success?: boolean
    user?: {
      id?: number
      email?: string
      first_name?: string
      last_name?: string
    }
  }
  /**
   * user logged in
   */
  201: {
    success?: boolean
    user?: UserSession
  }
}

export type PostSessionsFacebookResponse =
  PostSessionsFacebookResponses[keyof PostSessionsFacebookResponses]

export type PostSessionsFacebookLimitedData = {
  body?: {
    token: string
  }
  path?: never
  query?: never
  url: '/sessions/facebook_limited'
}

export type PostSessionsFacebookLimitedResponses = {
  /**
   * expired token rejected
   */
  200: {
    success?: boolean
    errors?: Array<string>
  }
  /**
   * user logged in
   */
  201: {
    success?: boolean
    user?: UserSession
  }
}

export type PostSessionsFacebookLimitedResponse =
  PostSessionsFacebookLimitedResponses[keyof PostSessionsFacebookLimitedResponses]

export type PostSessionsAppleWebAuthData = {
  body?: {
    authorization: {
      code?: string
      state?: string
      id_token?: string
    }
    user?: {
      name?: {
        firstName?: string | null
        lastName?: string | null
      }
      email?: string | null
    }
  }
  path?: never
  query?: never
  url: '/sessions/apple-web-auth'
}

export type PostSessionsAppleWebAuthResponses = {
  /**
   * forged token rejected
   */
  200: {
    success?: boolean
    errors?: Array<string>
  }
  /**
   * user logged in
   */
  201: {
    success?: boolean
    user?: UserSession
  }
}

export type PostSessionsAppleWebAuthResponse =
  PostSessionsAppleWebAuthResponses[keyof PostSessionsAppleWebAuthResponses]

export type PostSessionsBackofficeData = {
  body?: {
    email: string
    password: string
  }
  path?: never
  query?: never
  url: '/sessions/backoffice'
}

export type PostSessionsBackofficeResponses = {
  /**
   * invalid backoffice credentials
   */
  200: {
    success?: boolean
    user?: UserSession
    errors?: Array<string>
  }
}

export type PostSessionsBackofficeResponse =
  PostSessionsBackofficeResponses[keyof PostSessionsBackofficeResponses]

export type GetFakeRouteToTestLoginCheckData = {
  body?: never
  path?: never
  query?: never
  url: '/fake-route-to-test-login-check'
}

export type GetFakeRouteToTestLoginCheckErrors = {
  /**
   * token found for user but it is expired
   */
  401: unknown
}

export type GetFakeRouteToTestLoginCheckResponses = {
  /**
   * valid token found for user
   */
  200: {
    /**
     * id of the User resolved from the session token
     */
    current_user_id?: number
  }
}

export type GetFakeRouteToTestLoginCheckResponse =
  GetFakeRouteToTestLoginCheckResponses[keyof GetFakeRouteToTestLoginCheckResponses]

export type PostBlocksData = {
  body?: {
    target_id: number
  }
  path?: never
  query?: never
  url: '/blocks'
}

export type PostBlocksErrors = {
  /**
   * not logged in
   */
  401: unknown
  /**
   * target does not exist
   */
  404: unknown
  /**
   * cannot block yourself
   */
  422: unknown
}

export type PostBlocksResponses = {
  /**
   * blocking twice is idempotent
   */
  201: Block
}

export type PostBlocksResponse = PostBlocksResponses[keyof PostBlocksResponses]

export type DeleteBlocksByIdData = {
  body?: never
  path: {
    /**
     * The blocked user id, not the block record id
     */
    id: number
  }
  query?: never
  url: '/blocks/{id}'
}

export type DeleteBlocksByIdResponses = {
  /**
   * does not remove somebody elses block
   */
  200: unknown
}

export type GetClubManagerProfilesData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    /**
     * Page number (default 1, 20 per page)
     */
    page?: number
  }
  url: '/club-manager-profiles'
}

export type GetClubManagerProfilesErrors = {
  /**
   * not staff
   */
  401: unknown
}

export type GetClubManagerProfilesResponses = {
  /**
   * club manager profiles found
   */
  200: ClubManagerProfilesIndexResponse
}

export type GetClubManagerProfilesResponse =
  GetClubManagerProfilesResponses[keyof GetClubManagerProfilesResponses]

export type PostClubManagerProfilesData = {
  body?: {
    club_manager_profile?: {
      /**
       * ID of the club to request management of
       */
      club_id?: number
    }
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/club-manager-profiles'
}

export type PostClubManagerProfilesResponses = {
  /**
   * invalid club id returns validation errors
   */
  200: ClubManagerProfileCreateResponse
}

export type PostClubManagerProfilesResponse =
  PostClubManagerProfilesResponses[keyof PostClubManagerProfilesResponses]

export type PutClubManagerProfilesByIdApproveData = {
  body?: {
    /**
     * Approval notes, stored on the request
     */
    notes?: string
  }
  headers: {
    'X-Api-Key': string
  }
  path: {
    /**
     * ClubManagerProfile request ID
     */
    id: number
  }
  query?: never
  url: '/club-manager-profiles/{id}/approve'
}

export type PutClubManagerProfilesByIdApproveErrors = {
  /**
   * approver is not an admin
   */
  401: unknown
  /**
   * request not found
   */
  404: unknown
}

export type PutClubManagerProfilesByIdApproveResponses = {
  /**
   * request approved
   */
  200: ClubManagerProfileResponse
}

export type PutClubManagerProfilesByIdApproveResponse =
  PutClubManagerProfilesByIdApproveResponses[keyof PutClubManagerProfilesByIdApproveResponses]

export type PutClubManagerProfilesByIdRejectData = {
  body?: {
    /**
     * Rejection notes, stored on the request
     */
    notes?: string
  }
  headers: {
    'X-Api-Key': string
  }
  path: {
    /**
     * ClubManagerProfile request ID
     */
    id: number
  }
  query?: never
  url: '/club-manager-profiles/{id}/reject'
}

export type PutClubManagerProfilesByIdRejectErrors = {
  /**
   * rejecter is not an admin
   */
  401: unknown
  /**
   * request not found
   */
  404: unknown
}

export type PutClubManagerProfilesByIdRejectResponses = {
  /**
   * request rejected
   */
  200: ClubManagerProfileResponse
}

export type PutClubManagerProfilesByIdRejectResponse =
  PutClubManagerProfilesByIdRejectResponses[keyof PutClubManagerProfilesByIdRejectResponses]

export type PostClubsAdvancedSearchData = {
  body?: {
    /**
     * Page number (0-indexed), default 0
     */
    page?: number
    /**
     * Results per page, default 20
     */
    limit?: number
    /**
     * Free-text match against club name
     */
    q?: string
    /**
     * Region (FIT code), e.g. 'LAZ'
     */
    region?: string
    /**
     * One or more Italian province codes to filter by
     */
    province?: Array<string>
    /**
     * Latitude for the nearby filter
     */
    lat?: number
    /**
     * Longitude for the nearby filter
     */
    lng?: number
    /**
     * Nearby search radius in meters, default 20000 (20km)
     */
    radius?: number
  }
  path?: never
  query?: never
  url: '/clubs/advanced-search'
}

export type PostClubsAdvancedSearchResponses = {
  /**
   * advanced search results
   */
  200: {
    page?: number
    total_pages?: number
    results_numbers?: number
    results?: Array<Club>
  }
}

export type PostClubsAdvancedSearchResponse =
  PostClubsAdvancedSearchResponses[keyof PostClubsAdvancedSearchResponses]

export type GetClubsNearbyData = {
  body?: never
  path?: never
  query: {
    /**
     * Latitude of the search point
     */
    lat: number
    /**
     * Longitude of the search point
     */
    lng: number
    /**
     * Search radius in km, default 20
     */
    radius?: number
  }
  url: '/clubs/nearby'
}

export type GetClubsNearbyErrors = {
  /**
   * coordinate mancanti o non valide
   */
  400: {
    error: string
  }
}

export type GetClubsNearbyError =
  GetClubsNearbyErrors[keyof GetClubsNearbyErrors]

export type GetClubsNearbyResponses = {
  /**
   * nearby clubs found
   */
  200: NearbyClubsResponse
}

export type GetClubsNearbyResponse =
  GetClubsNearbyResponses[keyof GetClubsNearbyResponses]

export type GetClubsByIdData = {
  body?: never
  headers?: {
    /**
     * Optional session token; personalizes followed/managed flags
     */
    'X-Api-Key'?: string
  }
  path: {
    id: number
  }
  query?: {
    /**
     * Only the first N (1-100) members, competitions, draws and followers. The *_counter fields always give the totals; teachers are never limited.
     */
    limit?: number
  }
  url: '/clubs/{id}'
}

export type GetClubsByIdErrors = {
  /**
   * club not found
   */
  404: unknown
}

export type GetClubsByIdResponses = {
  /**
   * club found
   */
  200: {
    id?: number
    name?: string
    members_counter?: number
    phone?: string | null
    region?: string | null
    province?: string | null
    legal_address?: string | null
    fields_address?: string | null
    mail_address?: string | null
    other_address?: string | null
    thumbnail?: string | null
    managed?: boolean
    club_manager_profile_id?: number | null
    followed?: boolean
    followable_type?: string
    followers?: Array<UserFollowers>
    members?: Array<FitPlayerProfileClubMember>
    competitions?: Array<{
      id?: number
      name?: string
      from?: string | null
      to?: string | null
    }>
    teachers?: Array<FitPlayerProfileClubMember>
    draws?: Array<CompetitionPreview>
    school_typology?: string | null
    active?: boolean
    /**
     * All the competitions, also beyond `limit`
     */
    competitions_counter: number
    /**
     * All the draws, also beyond `limit`
     */
    draws_counter: number
    /**
     * All the followers, also beyond `limit`
     */
    followers_counter: number
  }
}

export type GetClubsByIdResponse =
  GetClubsByIdResponses[keyof GetClubsByIdResponses]

export type GetClubsByIdFeedData = {
  body?: never
  headers?: {
    /**
     * Optional session token
     */
    'X-Api-Key'?: string
  }
  path: {
    id: string
  }
  query?: {
    /**
     * Page number, 0-indexed
     */
    page?: number
  }
  url: '/clubs/{id}/feed'
}

export type GetClubsByIdFeedErrors = {
  /**
   * club not found
   */
  404: unknown
}

export type GetClubsByIdFeedResponses = {
  /**
   * club feed found
   */
  200: PlayerFeedResponse
}

export type GetClubsByIdFeedResponse =
  GetClubsByIdFeedResponses[keyof GetClubsByIdFeedResponses]

export type GetClubsIdByFitClubIdByFitClubIdData = {
  body?: never
  path: {
    /**
     * The club's FIT club id
     */
    fit_club_id: string
  }
  query?: never
  url: '/clubs/id-by-fit-club-id/{fit_club_id}'
}

export type GetClubsIdByFitClubIdByFitClubIdErrors = {
  /**
   * club not found
   */
  404: unknown
}

export type GetClubsIdByFitClubIdByFitClubIdResponses = {
  /**
   * club id found
   */
  200: {
    id?: number
  }
}

export type GetClubsIdByFitClubIdByFitClubIdResponse =
  GetClubsIdByFitClubIdByFitClubIdResponses[keyof GetClubsIdByFitClubIdByFitClubIdResponses]

export type PostCommentsByIdLikeData = {
  body?: never
  path: {
    /**
     * Comment ID
     */
    id: number
  }
  query?: never
  url: '/comments/{id}/like'
}

export type PostCommentsByIdLikeErrors = {
  /**
   * unauthorized
   */
  401: unknown
  /**
   * comment not found
   */
  404: unknown
}

export type PostCommentsByIdLikeResponses = {
  /**
   * comment liked
   */
  200: CommentWithOutcomeResponse
}

export type PostCommentsByIdLikeResponse =
  PostCommentsByIdLikeResponses[keyof PostCommentsByIdLikeResponses]

export type DeleteCommentsByIdUnlikeData = {
  body?: never
  path: {
    /**
     * Comment ID
     */
    id: number
  }
  query?: never
  url: '/comments/{id}/unlike'
}

export type DeleteCommentsByIdUnlikeErrors = {
  /**
   * unauthorized
   */
  401: unknown
  /**
   * comment not found
   */
  404: unknown
}

export type DeleteCommentsByIdUnlikeResponses = {
  /**
   * comment unliked
   */
  200: CommentWithOutcomeResponse
}

export type DeleteCommentsByIdUnlikeResponse =
  DeleteCommentsByIdUnlikeResponses[keyof DeleteCommentsByIdUnlikeResponses]

export type GetCompetitionsByIdData = {
  body?: never
  headers?: {
    /**
     * Optional API key for user context
     */
    'X-Api-Key'?: string
  }
  path: {
    /**
     * Competition ID
     */
    id: number
  }
  query?: {
    /**
     * Include tournament average rating (Radar Tornei: Membership + linked FIT profile, ignored otherwise)
     */
    with_eval?: boolean
  }
  url: '/competitions/{id}'
}

export type GetCompetitionsByIdErrors = {
  /**
   * competition not found
   */
  404: unknown
}

export type GetCompetitionsByIdResponses = {
  /**
   * competition found with authentication
   */
  200: CompetitionResponse
}

export type GetCompetitionsByIdResponse =
  GetCompetitionsByIdResponses[keyof GetCompetitionsByIdResponses]

export type GetCompetitionsSearchData = {
  body?: never
  path?: never
  query?: {
    /**
     * Search query
     */
    query?: string
  }
  url: '/competitions/search'
}

export type GetCompetitionsSearchResponses = {
  /**
   * empty results when no query
   */
  200: Array<SearchCompetition>
}

export type GetCompetitionsSearchResponse =
  GetCompetitionsSearchResponses[keyof GetCompetitionsSearchResponses]

export type PostCompetitionsAdvancedSearchData = {
  body?: {
    /**
     * Page number (default: 1)
     */
    page?: number
    /**
     * Results per page (default: 5)
     */
    limit?: number
    /**
     * Search query
     */
    query?: string
    /**
     * Tournament gender filter
     */
    gender?: string
    /**
     * Region filter
     */
    region?: string
    /**
     * Province filter
     */
    province?: Array<string>
    /**
     * Championship filter
     */
    championship?: string
    /**
     * Competition status
     */
    status?: 'completed' | 'running' | 'scheduled'
    /**
     * Start date filter
     */
    date_from?: string
    /**
     * End date filter
     */
    date_to?: string
    /**
     * Rankings category
     */
    rankings_category?: 'open' | 'seconda' | 'terza' | 'quarta'
    /**
     * Age category
     */
    age_category?: 'open' | 'under' | 'over'
    /**
     * Court surface filter
     */
    surface?: Surface
    /**
     * Sign up order filter
     */
    sign_up_order?: number
    /**
     * Latitude for nearby search
     */
    lat?: number
    /**
     * Longitude for nearby search
     */
    lng?: number
    /**
     * Radius in kilometers for nearby search
     */
    radius?: number
    /**
     * Include tournament average rating (Radar Tornei: Membership + linked FIT profile, ignored otherwise)
     */
    with_eval?: boolean
  }
  path?: never
  query?: never
  url: '/competitions/advanced-search'
}

export type PostCompetitionsAdvancedSearchResponses = {
  /**
   * empty advanced search results
   */
  200: {
    page?: number
    total_pages?: number
    results_numbers?: number
    results?: Array<CompetitionSearch>
  }
}

export type PostCompetitionsAdvancedSearchResponse =
  PostCompetitionsAdvancedSearchResponses[keyof PostCompetitionsAdvancedSearchResponses]

export type GetDailyReportsData = {
  body?: never
  path?: never
  query: {
    source: DailyReportSourceKey
    /**
     * Period start
     */
    from?: string
    /**
     * Period end, inclusive
     */
    to?: string
    preset?:
      | 'last_7_days'
      | 'last_30_days'
      | 'last_90_days'
      | 'current_month'
      | 'current_year'
      | 'previous_month'
      | 'previous_year'
    granularity?: 'day' | 'week' | 'month' | 'year'
  }
  url: '/daily-reports'
}

export type GetDailyReportsErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * unknown source or invalid period
   */
  422: ErrorResponse
}

export type GetDailyReportsError =
  GetDailyReportsErrors[keyof GetDailyReportsErrors]

export type GetDailyReportsResponses = {
  /**
   * monthly rows
   */
  200: DailyReportResponse
}

export type GetDailyReportsResponse =
  GetDailyReportsResponses[keyof GetDailyReportsResponses]

export type GetDailyReportsOverviewData = {
  body?: never
  path?: never
  query?: {
    /**
     * Period start
     */
    from?: string
    /**
     * Period end, inclusive
     */
    to?: string
    preset?:
      | 'last_7_days'
      | 'last_30_days'
      | 'last_90_days'
      | 'current_month'
      | 'current_year'
      | 'previous_month'
      | 'previous_year'
    granularity?: 'day' | 'week' | 'month' | 'year'
  }
  url: '/daily-reports/overview'
}

export type GetDailyReportsOverviewErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * invalid period
   */
  422: ErrorResponse
}

export type GetDailyReportsOverviewError =
  GetDailyReportsOverviewErrors[keyof GetDailyReportsOverviewErrors]

export type GetDailyReportsOverviewResponses = {
  /**
   * every source
   */
  200: DailyReportOverviewResponse
}

export type GetDailyReportsOverviewResponse =
  GetDailyReportsOverviewResponses[keyof GetDailyReportsOverviewResponses]

export type GetDailyReportsUpcomingRenewalsData = {
  body?: never
  path?: never
  query?: {
    /**
     * Period start, default today
     */
    from?: string
    /**
     * Period end, inclusive, default from + 7 days
     */
    to?: string
  }
  url: '/daily-reports/upcoming-renewals'
}

export type GetDailyReportsUpcomingRenewalsErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * period too long
   */
  422: ErrorResponse
}

export type GetDailyReportsUpcomingRenewalsError =
  GetDailyReportsUpcomingRenewalsErrors[keyof GetDailyReportsUpcomingRenewalsErrors]

export type GetDailyReportsUpcomingRenewalsResponses = {
  /**
   * without Recharge
   */
  200: UpcomingRenewalsResponse
}

export type GetDailyReportsUpcomingRenewalsResponse =
  GetDailyReportsUpcomingRenewalsResponses[keyof GetDailyReportsUpcomingRenewalsResponses]

export type GetDailyReportsSourcesData = {
  body?: never
  path?: never
  query?: never
  url: '/daily-reports/sources'
}

export type GetDailyReportsSourcesErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
}

export type GetDailyReportsSourcesError =
  GetDailyReportsSourcesErrors[keyof GetDailyReportsSourcesErrors]

export type GetDailyReportsSourcesResponses = {
  /**
   * catalog
   */
  200: DailyReportSourcesResponse
}

export type GetDailyReportsSourcesResponse =
  GetDailyReportsSourcesResponses[keyof GetDailyReportsSourcesResponses]

export type DeleteDrawsByIdData = {
  body?: never
  headers: {
    /**
     * API key of an admin user
     */
    'X-Api-Key': string
  }
  path: {
    /**
     * Draw ID
     */
    id: number
  }
  query?: never
  url: '/draws/{id}'
}

export type DeleteDrawsByIdErrors = {
  /**
   * draw not found
   */
  404: unknown
}

export type DeleteDrawsByIdResponses = {
  /**
   * draw deleted successfully
   */
  200: {
    success?: boolean
  }
}

export type DeleteDrawsByIdResponse =
  DeleteDrawsByIdResponses[keyof DeleteDrawsByIdResponses]

export type GetDrawsByIdData = {
  body?: never
  headers?: {
    /**
     * Optional API key for user context
     */
    'X-Api-Key'?: string
  }
  path: {
    /**
     * Draw ID
     */
    id: number
  }
  query?: never
  url: '/draws/{id}'
}

export type GetDrawsByIdErrors = {
  /**
   * draw not found
   */
  404: unknown
}

export type GetDrawsByIdResponses = {
  /**
   * draw found with authentication
   */
  200: DrawResponse
}

export type GetDrawsByIdResponse =
  GetDrawsByIdResponses[keyof GetDrawsByIdResponses]

export type PutDrawsByIdDeleteMatchesData = {
  body?: never
  headers: {
    /**
     * API key of an admin user
     */
    'X-Api-Key': string
  }
  path: {
    /**
     * Draw ID
     */
    id: number
  }
  query?: never
  url: '/draws/{id}/delete-matches'
}

export type PutDrawsByIdDeleteMatchesErrors = {
  /**
   * draw not found
   */
  404: unknown
}

export type PutDrawsByIdDeleteMatchesResponses = {
  /**
   * matches deleted successfully
   */
  200: {
    success?: boolean
    draw?: DrawResponse
  }
}

export type PutDrawsByIdDeleteMatchesResponse =
  PutDrawsByIdDeleteMatchesResponses[keyof PutDrawsByIdDeleteMatchesResponses]

export type PutDrawsByIdChangeWinnerData = {
  body?: {
    /**
     * Player ID to set as winner (omit/blank to remove it)
     */
    winner_id?: number | null
  }
  headers: {
    /**
     * API key for authentication
     */
    'X-Api-Key': string
  }
  path: {
    /**
     * Draw ID
     */
    id: number
  }
  query?: never
  url: '/draws/{id}/change-winner'
}

export type PutDrawsByIdChangeWinnerErrors = {
  /**
   * not an admin
   */
  401: unknown
  /**
   * draw or player not found
   */
  404: unknown
}

export type PutDrawsByIdChangeWinnerResponses = {
  /**
   * winner removed when winner_id is blank
   */
  200: {
    success?: boolean
    draw?: DrawResponse
  }
}

export type PutDrawsByIdChangeWinnerResponse =
  PutDrawsByIdChangeWinnerResponses[keyof PutDrawsByIdChangeWinnerResponses]

export type PutDrawsByIdChangeBestLoserRankingData = {
  body: {
    /**
     * Best loser ranking value
     */
    best_loser_ranking: string
  }
  headers: {
    /**
     * API key for authentication
     */
    'X-Api-Key': string
  }
  path: {
    /**
     * Draw ID
     */
    id: number
  }
  query?: never
  url: '/draws/{id}/change-best-loser-ranking'
}

export type PutDrawsByIdChangeBestLoserRankingErrors = {
  /**
   * draw not found
   */
  404: unknown
}

export type PutDrawsByIdChangeBestLoserRankingResponses = {
  /**
   * best loser ranking changed successfully
   */
  200: {
    success?: boolean
    draw?: DrawResponse
  }
}

export type PutDrawsByIdChangeBestLoserRankingResponse =
  PutDrawsByIdChangeBestLoserRankingResponses[keyof PutDrawsByIdChangeBestLoserRankingResponses]

export type GetEditProfileProfilesData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/edit-profile/profiles'
}

export type GetEditProfileProfilesErrors = {
  /**
   * unauthorized
   */
  401: unknown
}

export type GetEditProfileProfilesResponses = {
  /**
   * profiles found
   */
  200: EditProfileProfilesResponse
}

export type GetEditProfileProfilesResponse =
  GetEditProfileProfilesResponses[keyof GetEditProfileProfilesResponses]

export type PutEditProfileGeneralInfoData = {
  body?: {
    first_name?: string
    last_name?: string
    birth_date?: string
    phone?: string
    address_user_input?: string
    lat_input?: number | string | null
    lng_input?: number | string | null
    club_id?: number | null
    bio?: string
    preferred_surface?: string
    racket_brand?: string
    shoes_brand?: string
    clothing_brand?: string
    lefthanded?: boolean
    one_handed_backhand?: boolean
    ig_id?: string
    fb_id?: string
    nickname?: string
    photo?: string
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/edit-profile/general-info'
}

export type PutEditProfileGeneralInfoErrors = {
  /**
   * unauthorized
   */
  401: unknown
}

export type PutEditProfileGeneralInfoResponses = {
  /**
   * general info updated
   */
  200: EditProfileUserResponse
}

export type PutEditProfileGeneralInfoResponse =
  PutEditProfileGeneralInfoResponses[keyof PutEditProfileGeneralInfoResponses]

export type PutEditProfileAccountInfoData = {
  body?: {
    email?: string
    password?: string
    password_confirmation?: string
    notifications_for_comments?: boolean
    notifications_for_posts?: boolean
    marketing_communication_check?: boolean
    tennistalker_communication_check?: boolean
    discoverable?: boolean
    gender?: Gender
    notification_settings?: {
      comments_followed?: boolean
      like_comments?: boolean
      like_posts?: boolean
      magazine_newsletter_opt_in?: boolean
      matches_club_mates?: boolean
      matches_followed?: boolean
      new_ranking?: boolean
      posts_followed?: boolean
      saved_search?: boolean
    }
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/edit-profile/account-info'
}

export type PutEditProfileAccountInfoErrors = {
  /**
   * unauthorized
   */
  401: unknown
}

export type PutEditProfileAccountInfoResponses = {
  /**
   * account info updated
   */
  200: EditProfileUserResponse
}

export type PutEditProfileAccountInfoResponse =
  PutEditProfileAccountInfoResponses[keyof PutEditProfileAccountInfoResponses]

export type PutEditProfileProfilesInfoData = {
  body?: {
    fit_player_profile?: {
      fit_card_number?: string
      selected_player?: string
    }
    teacher_profile?: {
      qualification?: string
      professional_info?: string
    }
    trainer_profile?: {
      qualification?: string
      professional_info?: string
    }
    nutritionist_profile?: {
      qualifications?: string
      professional_info?: string
    }
    mental_coach_profile?: {
      qualifications?: string
      professional_info?: string
    }
    club_manager_profile?: {
      fit_club_id?: string
      photo?: string
    }
    shop_profile?: {
      name?: string
      address?: string
      website?: string
      info?: string
    }
    /**
     * Self-assessment of «Gioco a tennis» (onboarding m0/m0b): creates or updates the amateur profile. Only the keys sent are changed.
     */
    amateur_profile?: {
      skill?: number
      frequency?: number
      ntrp?: number
      level_label?: string
      level_description?: string
      first_match_intent?: 'smartwatch' | 'manual' | 'later'
    }
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/edit-profile/profiles-info'
}

export type PutEditProfileProfilesInfoErrors = {
  /**
   * unauthorized
   */
  401: unknown
  /**
   * FIT card sent by a user without first name, last name or birth date
   */
  422: ValidationErrorResponse
}

export type PutEditProfileProfilesInfoError =
  PutEditProfileProfilesInfoErrors[keyof PutEditProfileProfilesInfoErrors]

export type PutEditProfileProfilesInfoResponses = {
  /**
   * profiles info updated
   */
  200: EditProfileUserResponse
}

export type PutEditProfileProfilesInfoResponse =
  PutEditProfileProfilesInfoResponses[keyof PutEditProfileProfilesInfoResponses]

export type PutEditProfileAppTokenInfoData = {
  body?: {
    subscription_token: string
    os: string
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/edit-profile/app-token-info'
}

export type PutEditProfileAppTokenInfoErrors = {
  /**
   * unauthorized
   */
  401: unknown
  /**
   * bad request
   */
  500: unknown
}

export type PutEditProfileAppTokenInfoResponses = {
  /**
   * app token info updated
   */
  200: EditProfileUserResponse
}

export type PutEditProfileAppTokenInfoResponse =
  PutEditProfileAppTokenInfoResponses[keyof PutEditProfileAppTokenInfoResponses]

export type PutEditProfileDeleteProfileData = {
  body?: {
    /**
     * Which secondary profile to delete
     */
    profile:
      | 'teacher_profile'
      | 'trainer_profile'
      | 'nutritionist_profile'
      | 'mental_coach_profile'
      | 'club_manager_profile'
      | 'shop_profile'
      | 'amateur_profile'
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/edit-profile/delete-profile'
}

export type PutEditProfileDeleteProfileErrors = {
  /**
   * unauthorized
   */
  401: unknown
  /**
   * profile not found
   */
  404: unknown
  /**
   * amateur profile kept while a FIT card is linked
   */
  422: ErrorResponse
}

export type PutEditProfileDeleteProfileError =
  PutEditProfileDeleteProfileErrors[keyof PutEditProfileDeleteProfileErrors]

export type PutEditProfileDeleteProfileResponses = {
  /**
   * profile deleted
   */
  200: EditProfileUserResponse
}

export type PutEditProfileDeleteProfileResponse =
  PutEditProfileDeleteProfileResponses[keyof PutEditProfileDeleteProfileResponses]

export type PutEditProfileRemovePlayerProfileData = {
  body?: {
    amateur_profile?: {
      skill?: number
      frequency?: number
      ntrp?: number
      level_label?: string
    }
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/edit-profile/remove-player-profile'
}

export type PutEditProfileRemovePlayerProfileErrors = {
  /**
   * unauthorized
   */
  401: unknown
  /**
   * player profile not found
   */
  404: unknown
  /**
   * malformed self-assessment: refused, never a 500
   */
  422: ErrorResponse
}

export type PutEditProfileRemovePlayerProfileError =
  PutEditProfileRemovePlayerProfileErrors[keyof PutEditProfileRemovePlayerProfileErrors]

export type PutEditProfileRemovePlayerProfileResponses = {
  /**
   * player profile removed
   */
  200: EditProfileUserResponse
}

export type PutEditProfileRemovePlayerProfileResponse =
  PutEditProfileRemovePlayerProfileResponses[keyof PutEditProfileRemovePlayerProfileResponses]

export type PutEditProfileRemovePlayerProfileAssociationsData = {
  body?: {
    /**
     * FIT card number identifying the player profile to unlink
     */
    fit_card_number: string
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/edit-profile/remove-player-profile-associations'
}

export type PutEditProfileRemovePlayerProfileAssociationsErrors = {
  /**
   * unauthorized
   */
  401: unknown
}

export type PutEditProfileRemovePlayerProfileAssociationsResponses = {
  /**
   * player profile associations removed
   */
  200: {
    success?: boolean
  }
  /**
   * player profile not found
   */
  204: void
}

export type PutEditProfileRemovePlayerProfileAssociationsResponse =
  PutEditProfileRemovePlayerProfileAssociationsResponses[keyof PutEditProfileRemovePlayerProfileAssociationsResponses]

export type GetExtraMenuItemsData = {
  body?: never
  path?: never
  query?: never
  url: '/extra-menu-items'
}

export type GetExtraMenuItemsResponses = {
  /**
   * active items
   */
  200: ExtraMenuItemsResponse
}

export type GetExtraMenuItemsResponse =
  GetExtraMenuItemsResponses[keyof GetExtraMenuItemsResponses]

export type GetAdminExtraMenuItemsData = {
  body?: never
  path?: never
  query?: never
  url: '/admin/extra-menu-items'
}

export type GetAdminExtraMenuItemsErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
}

export type GetAdminExtraMenuItemsError =
  GetAdminExtraMenuItemsErrors[keyof GetAdminExtraMenuItemsErrors]

export type GetAdminExtraMenuItemsResponses = {
  /**
   * items
   */
  200: ExtraMenuItemAdminIndexResponse
}

export type GetAdminExtraMenuItemsResponse =
  GetAdminExtraMenuItemsResponses[keyof GetAdminExtraMenuItemsResponses]

export type PostAdminExtraMenuItemsData = {
  body?: {
    extra_menu_item: ExtraMenuItemInput & {
      [key: string]: unknown
    }
  }
  path?: never
  query?: never
  url: '/admin/extra-menu-items'
}

export type PostAdminExtraMenuItemsErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * validation failed
   */
  422: ValidationErrorResponse
}

export type PostAdminExtraMenuItemsError =
  PostAdminExtraMenuItemsErrors[keyof PostAdminExtraMenuItemsErrors]

export type PostAdminExtraMenuItemsResponses = {
  /**
   * item created
   */
  200: ExtraMenuItemAdminResponse
}

export type PostAdminExtraMenuItemsResponse =
  PostAdminExtraMenuItemsResponses[keyof PostAdminExtraMenuItemsResponses]

export type DeleteAdminExtraMenuItemsByIdData = {
  body?: never
  path: {
    /**
     * Extra menu item ID
     */
    id: number
  }
  query?: never
  url: '/admin/extra-menu-items/{id}'
}

export type DeleteAdminExtraMenuItemsByIdErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * item not found
   */
  404: unknown
}

export type DeleteAdminExtraMenuItemsByIdError =
  DeleteAdminExtraMenuItemsByIdErrors[keyof DeleteAdminExtraMenuItemsByIdErrors]

export type DeleteAdminExtraMenuItemsByIdResponses = {
  /**
   * item deleted
   */
  200: ExtraMenuItemAdminResponse
}

export type DeleteAdminExtraMenuItemsByIdResponse =
  DeleteAdminExtraMenuItemsByIdResponses[keyof DeleteAdminExtraMenuItemsByIdResponses]

export type PutAdminExtraMenuItemsByIdData = {
  body?: {
    extra_menu_item: ExtraMenuItemInput
  }
  path: {
    /**
     * Extra menu item ID
     */
    id: number
  }
  query?: never
  url: '/admin/extra-menu-items/{id}'
}

export type PutAdminExtraMenuItemsByIdErrors = {
  /**
   * not an admin
   */
  401: AccessDeniedResponse
  /**
   * item not found
   */
  404: unknown
  /**
   * validation failed
   */
  422: ValidationErrorResponse
}

export type PutAdminExtraMenuItemsByIdError =
  PutAdminExtraMenuItemsByIdErrors[keyof PutAdminExtraMenuItemsByIdErrors]

export type PutAdminExtraMenuItemsByIdResponses = {
  /**
   * item updated
   */
  200: ExtraMenuItemAdminResponse
}

export type PutAdminExtraMenuItemsByIdResponse =
  PutAdminExtraMenuItemsByIdResponses[keyof PutAdminExtraMenuItemsByIdResponses]

export type GetFitPlayerProfilesByIdData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: never
  url: '/fit-player-profiles/{id}'
}

export type GetFitPlayerProfilesByIdErrors = {
  /**
   * fit player profile not found
   */
  404: unknown
}

export type GetFitPlayerProfilesByIdResponses = {
  /**
   * fit player profile found
   */
  200: FitPlayerProfileResponse
}

export type GetFitPlayerProfilesByIdResponse =
  GetFitPlayerProfilesByIdResponses[keyof GetFitPlayerProfilesByIdResponses]

export type GetFitPlayerProfilesByIdWrappedData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: never
  url: '/fit-player-profiles/{id}/wrapped'
}

export type GetFitPlayerProfilesByIdWrappedResponses = {
  /**
   * wrapped data
   */
  200: WrappedResponse
}

export type GetFitPlayerProfilesByIdWrappedResponse =
  GetFitPlayerProfilesByIdWrappedResponses[keyof GetFitPlayerProfilesByIdWrappedResponses]

export type GetFitPlayerProfilesSearchData = {
  body?: never
  path?: never
  query: {
    /**
     * Free-text search on the player full name
     */
    query: string
  }
  url: '/fit-player-profiles/search'
}

export type GetFitPlayerProfilesSearchResponses = {
  /**
   * empty search results
   */
  200: Array<FitPlayerProfileSearch>
}

export type GetFitPlayerProfilesSearchResponse =
  GetFitPlayerProfilesSearchResponses[keyof GetFitPlayerProfilesSearchResponses]

export type PostFitPlayerProfilesAdvancedSearchData = {
  body?: {
    /**
     * Free-text search on the player full name
     */
    query?: string
    gender?: 'male' | 'female'
    region?: string
    province?: Array<string>
    age_categories?: Array<string>
    /**
     * PlainRank values to filter by (premium)
     */
    ranks?: Array<string>
    page?: number
    /**
     * Order by the simulated rank instead of the official one (premium)
     */
    simulatedRankOrder?: boolean
  }
  path?: never
  query?: never
  url: '/fit-player-profiles/advanced-search'
}

export type PostFitPlayerProfilesAdvancedSearchErrors = {
  /**
   * unauthorized - premium feature required
   */
  403: unknown
}

export type PostFitPlayerProfilesAdvancedSearchResponses = {
  /**
   * advanced search results
   */
  200: {
    page?: number
    total_pages?: number
    results?: Array<FitPlayerProfileAdvancedSearch>
    results_numbers?: number
    active_players_count?: number
  }
}

export type PostFitPlayerProfilesAdvancedSearchResponse =
  PostFitPlayerProfilesAdvancedSearchResponses[keyof PostFitPlayerProfilesAdvancedSearchResponses]

export type GetFitPlayerProfilesAdvancedSearchFiltersData = {
  body?: never
  path?: never
  query?: never
  url: '/fit-player-profiles/advanced-search/filters'
}

export type GetFitPlayerProfilesAdvancedSearchFiltersResponses = {
  /**
   * filter options
   */
  200: {
    age_categories?: Array<AgeCategory>
    ranks?: Array<PlainRank>
  }
}

export type GetFitPlayerProfilesAdvancedSearchFiltersResponse =
  GetFitPlayerProfilesAdvancedSearchFiltersResponses[keyof GetFitPlayerProfilesAdvancedSearchFiltersResponses]

export type GetFitPlayerProfilesByIdRankData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: {
    /**
     * Defaults to the current FIT season year
     */
    year?: number
  }
  url: '/fit-player-profiles/{id}/rank'
}

export type GetFitPlayerProfilesByIdRankResponses = {
  /**
   * player rank
   */
  200: {
    rank?: PlainRank
  }
}

export type GetFitPlayerProfilesByIdRankResponse =
  GetFitPlayerProfilesByIdRankResponses[keyof GetFitPlayerProfilesByIdRankResponses]

export type GetFitPlayerProfilesByIdStatsData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: {
    /**
     * Defaults to the current FIT season year
     */
    year?: number
  }
  url: '/fit-player-profiles/{id}/stats'
}

export type GetFitPlayerProfilesByIdStatsResponses = {
  /**
   * player statistics
   */
  200: {
    rank_chart?: {
      steps?: number
      data?: Array<Array<unknown>>
    }
    competitions_counter?: number
    matches_per_tournament?: number | null
    matches_per_competition?: number | null
    ranks?: {
      [key: string]: unknown
    } | null
    state_of_form?: Array<boolean>
    year?: number
    available_years?: Array<number>
    ranking_stats?: {
      same_rank?: {
        wins?: number
        losses?: number
      }
      one_rank_higher?: {
        wins?: number
        losses?: number
      }
      two_plus_ranks_higher?: {
        wins?: number
        losses?: number
      }
      one_rank_lower?: {
        wins?: number
        losses?: number
      }
      two_plus_ranks_lower?: {
        wins?: number
        losses?: number
      }
      not_played?: {
        wins?: number
        losses?: number
      }
      two_sets?: {
        wins?: number
        losses?: number
      }
      three_sets?: {
        wins?: number
        losses?: number
      }
      championship?: {
        wins?: number
        losses?: number
      }
      competition?: {
        wins?: number
        losses?: number
      }
      court_stats?: {
        clay_court?: {
          wins?: number
          losses?: number
        }
        synthetic?: {
          wins?: number
          losses?: number
        }
      }
      court_type_stats?: {
        indoor?: {
          wins?: number
          losses?: number
        }
        outdoor?: {
          wins?: number
          losses?: number
        }
        other_type?: {
          wins?: number
          losses?: number
        }
      }
    }
    distance_from_best?: {
      best_rank?: PlainRank
      best_rank_year?: number
      distance?: number
    }
    last_competition?: {
      id?: number
      fit_competition_id?: string
      name?: string
    } | null
  }
}

export type GetFitPlayerProfilesByIdStatsResponse =
  GetFitPlayerProfilesByIdStatsResponses[keyof GetFitPlayerProfilesByIdStatsResponses]

export type GetFitPlayerProfilesByIdSimulatorData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: {
    /**
     * Defaults to the current FIT season year
     */
    year?: number
    /**
     * Hypothetical starting PlainRank to simulate from (premium)
     */
    simulated_rank?: string
    /**
     * Run the fuller simulation mode (premium)
     */
    is_super_simulation?: boolean
    /**
     * Simulates an extra match against this player
     */
    opponent_id?: number
    /**
     * Outcome of the simulated match against opponent_id
     */
    won?: boolean
  }
  url: '/fit-player-profiles/{id}/simulator'
}

export type GetFitPlayerProfilesByIdSimulatorErrors = {
  /**
   * unauthorized - premium feature required
   */
  403: unknown
}

export type GetFitPlayerProfilesByIdSimulatorResponses = {
  /**
   * simulator data
   */
  200: SimulatorResponse
}

export type GetFitPlayerProfilesByIdSimulatorResponse =
  GetFitPlayerProfilesByIdSimulatorResponses[keyof GetFitPlayerProfilesByIdSimulatorResponses]

export type GetFitPlayerProfilesByIdHarmonizationData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: {
    /**
     * Defaults to the current FIT season year
     */
    year?: number
  }
  url: '/fit-player-profiles/{id}/harmonization'
}

export type GetFitPlayerProfilesByIdHarmonizationErrors = {
  /**
   * unauthorized - premium feature required
   */
  403: unknown
}

export type GetFitPlayerProfilesByIdHarmonizationResponses = {
  /**
   * harmonization data
   */
  200: {
    rank?: PlainRankWithNd | null
    total_players?: number | null
    position?: number | null
    upper_rank_threshold?: number | null
    lower_rank_threshold?: number | null
  }
}

export type GetFitPlayerProfilesByIdHarmonizationResponse =
  GetFitPlayerProfilesByIdHarmonizationResponses[keyof GetFitPlayerProfilesByIdHarmonizationResponses]

export type GetFitPlayerProfilesByIdEndorsementsData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: never
  url: '/fit-player-profiles/{id}/endorsements'
}

export type GetFitPlayerProfilesByIdEndorsementsResponses = {
  /**
   * endorsements data
   */
  200: Array<EndorsementType>
}

export type GetFitPlayerProfilesByIdEndorsementsResponse =
  GetFitPlayerProfilesByIdEndorsementsResponses[keyof GetFitPlayerProfilesByIdEndorsementsResponses]

export type GetFitPlayerProfilesByIdVisitsData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: never
  url: '/fit-player-profiles/{id}/visits'
}

export type GetFitPlayerProfilesByIdVisitsResponses = {
  /**
   * visit count
   */
  200: {
    visits?: number
  }
}

export type GetFitPlayerProfilesByIdVisitsResponse =
  GetFitPlayerProfilesByIdVisitsResponses[keyof GetFitPlayerProfilesByIdVisitsResponses]

export type GetFitPlayerProfilesByIdFeedData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: {
    /**
     * Defaults to page 1
     */
    page?: number
  }
  url: '/fit-player-profiles/{id}/feed'
}

export type GetFitPlayerProfilesByIdFeedResponses = {
  /**
   * player feed
   */
  200: PlayerFeedResponse
}

export type GetFitPlayerProfilesByIdFeedResponse =
  GetFitPlayerProfilesByIdFeedResponses[keyof GetFitPlayerProfilesByIdFeedResponses]

export type GetFitPlayerProfilesByIdVisitorsData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: never
  url: '/fit-player-profiles/{id}/visitors'
}

export type GetFitPlayerProfilesByIdVisitorsErrors = {
  /**
   * not staff
   */
  401: unknown
}

export type GetFitPlayerProfilesByIdVisitorsResponses = {
  /**
   * player visitors
   */
  200: PlayerVisitorsResponse
}

export type GetFitPlayerProfilesByIdVisitorsResponse =
  GetFitPlayerProfilesByIdVisitorsResponses[keyof GetFitPlayerProfilesByIdVisitorsResponses]

export type GetFitPlayerProfilesFollowersListByIdData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: never
  url: '/fit-player-profiles/followers_list/{id}'
}

export type GetFitPlayerProfilesFollowersListByIdResponses = {
  /**
   * followers list
   */
  200: FollowersListResponse
}

export type GetFitPlayerProfilesFollowersListByIdResponse =
  GetFitPlayerProfilesFollowersListByIdResponses[keyof GetFitPlayerProfilesFollowersListByIdResponses]

export type GetFitPlayerProfilesByIdUserInfoData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: never
  url: '/fit-player-profiles/{id}/user-info'
}

export type GetFitPlayerProfilesByIdUserInfoResponses = {
  /**
   * empty response when no user
   */
  200: UserWithProfile
}

export type GetFitPlayerProfilesByIdUserInfoResponse =
  GetFitPlayerProfilesByIdUserInfoResponses[keyof GetFitPlayerProfilesByIdUserInfoResponses]

export type GetFitPlayerProfilesByIdReducedData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: never
  url: '/fit-player-profiles/{id}/reduced'
}

export type GetFitPlayerProfilesByIdReducedErrors = {
  /**
   * hidden profile
   */
  404: unknown
}

export type GetFitPlayerProfilesByIdReducedResponses = {
  /**
   * player profile, no visit recorded
   */
  200: FitPlayerProfileResponse
}

export type GetFitPlayerProfilesByIdReducedResponse =
  GetFitPlayerProfilesByIdReducedResponses[keyof GetFitPlayerProfilesByIdReducedResponses]

export type GetFitPlayerProfilesMostVisitedData = {
  body?: never
  path?: never
  query?: never
  url: '/fit-player-profiles/most_visited'
}

export type GetFitPlayerProfilesMostVisitedResponses = {
  /**
   * most visited players
   */
  200: {
    views?: {
      [key: string]: unknown
    }
    players?: Array<{
      [key: string]: unknown
    }>
  }
}

export type GetFitPlayerProfilesMostVisitedResponse =
  GetFitPlayerProfilesMostVisitedResponses[keyof GetFitPlayerProfilesMostVisitedResponses]

export type PostFitPlayerProfilesCheckFitCardNumberData = {
  body?: {
    /**
     * FIT card number (non-digit characters are stripped server-side before lookup)
     */
    fit_card_number?: string
  }
  path?: never
  query?: never
  url: '/fit-player-profiles/check-fit-card-number'
}

export type PostFitPlayerProfilesCheckFitCardNumberResponses = {
  /**
   * card check result
   */
  200: {
    success?: boolean
    error?: string | null
    reassign_old_card?: boolean | null
    remove_associations?: boolean | null
    pick_player?: boolean | null
  }
}

export type PostFitPlayerProfilesCheckFitCardNumberResponse =
  PostFitPlayerProfilesCheckFitCardNumberResponses[keyof PostFitPlayerProfilesCheckFitCardNumberResponses]

export type GetFitPlayerProfilesCardToIdByCardNumberData = {
  body?: never
  path: {
    /**
     * FIT card number (fit_player_id)
     */
    card_number: string
  }
  query?: never
  url: '/fit-player-profiles/card-to-id/{card_number}'
}

export type GetFitPlayerProfilesCardToIdByCardNumberErrors = {
  /**
   * player not found
   */
  404: unknown
}

export type GetFitPlayerProfilesCardToIdByCardNumberResponses = {
  /**
   * player ID found
   */
  200: {
    id?: number
  }
}

export type GetFitPlayerProfilesCardToIdByCardNumberResponse =
  GetFitPlayerProfilesCardToIdByCardNumberResponses[keyof GetFitPlayerProfilesCardToIdByCardNumberResponses]

export type GetFitPlayerProfilesSearchForMergeData = {
  body?: never
  path?: never
  query?: {
    /**
     * Search term
     */
    q?: string
  }
  url: '/fit-player-profiles/search-for-merge'
}

export type GetFitPlayerProfilesSearchForMergeErrors = {
  /**
   * not staff
   */
  401: unknown
}

export type GetFitPlayerProfilesSearchForMergeResponses = {
  /**
   * empty results
   */
  200: Array<PlayerMergeSearch>
}

export type GetFitPlayerProfilesSearchForMergeResponse =
  GetFitPlayerProfilesSearchForMergeResponses[keyof GetFitPlayerProfilesSearchForMergeResponses]

export type PutFitPlayerProfilesMergeBySourcePlayerIdByDestinationPlayerIdData =
  {
    body?: never
    path: {
      /**
       * FitPlayerProfile ID to merge away and destroy
       */
      source_player_id: number
      /**
       * FitPlayerProfile ID to keep and merge data into
       */
      destination_player_id: number
    }
    query?: never
    url: '/fit-player-profiles/merge/{source_player_id}/{destination_player_id}'
  }

export type PutFitPlayerProfilesMergeBySourcePlayerIdByDestinationPlayerIdErrors =
  {
    /**
     * not an admin
     */
    401: unknown
  }

export type PutFitPlayerProfilesMergeBySourcePlayerIdByDestinationPlayerIdResponses =
  {
    /**
     * merge failed
     */
    200: {
      success?: boolean
      errors?: Array<string>
    }
  }

export type PutFitPlayerProfilesMergeBySourcePlayerIdByDestinationPlayerIdResponse =
  PutFitPlayerProfilesMergeBySourcePlayerIdByDestinationPlayerIdResponses[keyof PutFitPlayerProfilesMergeBySourcePlayerIdByDestinationPlayerIdResponses]

export type GetFitPlayerProfilesByIdStrengthsAndWeaknessesData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: {
    /**
     * Defaults to the current FIT season year
     */
    year?: number
    /**
     * Filter by court surface (see Surface schema)
     */
    surface?: string
  }
  url: '/fit-player-profiles/{id}/strengths-and-weaknesses'
}

export type GetFitPlayerProfilesByIdStrengthsAndWeaknessesErrors = {
  /**
   * unauthorized - premium feature required
   */
  403: {
    error?: string
  }
}

export type GetFitPlayerProfilesByIdStrengthsAndWeaknessesError =
  GetFitPlayerProfilesByIdStrengthsAndWeaknessesErrors[keyof GetFitPlayerProfilesByIdStrengthsAndWeaknessesErrors]

export type GetFitPlayerProfilesByIdStrengthsAndWeaknessesResponses = {
  /**
   * strengths and weaknesses data
   */
  200: {
    strengths?: Array<string>
    weaknesses?: Array<string>
  }
}

export type GetFitPlayerProfilesByIdStrengthsAndWeaknessesResponse =
  GetFitPlayerProfilesByIdStrengthsAndWeaknessesResponses[keyof GetFitPlayerProfilesByIdStrengthsAndWeaknessesResponses]

export type GetFitPlayerProfilesByIdChartExtractData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: never
  url: '/fit-player-profiles/{id}/chart_extract'
}

export type GetFitPlayerProfilesByIdChartExtractErrors = {
  /**
   * unauthorized - premium feature required
   */
  403: unknown
}

export type GetFitPlayerProfilesByIdChartExtractResponses = {
  /**
   * chart extract data
   */
  200: ChartExtractResponse
}

export type GetFitPlayerProfilesByIdChartExtractResponse =
  GetFitPlayerProfilesByIdChartExtractResponses[keyof GetFitPlayerProfilesByIdChartExtractResponses]

export type GetFitPlayerProfilesByIdEvaluationData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: {
    /**
     * Evaluate from this hypothetical PlainRank instead of the player's actual last rank
     */
    simulated_rank?: string
  }
  url: '/fit-player-profiles/{id}/evaluation'
}

export type GetFitPlayerProfilesByIdEvaluationResponses = {
  /**
   * evaluation data
   */
  200: {
    evaluation?: number
  }
}

export type GetFitPlayerProfilesByIdEvaluationResponse =
  GetFitPlayerProfilesByIdEvaluationResponses[keyof GetFitPlayerProfilesByIdEvaluationResponses]

export type GetFitPlayerProfilesByIdRanksWithYearData = {
  body?: never
  path: {
    /**
     * FitPlayerProfile ID
     */
    id: number
  }
  query?: {
    /**
     * Defaults to the current FIT season year
     */
    year?: number
  }
  url: '/fit-player-profiles/{id}/ranks-with-year'
}

export type GetFitPlayerProfilesByIdRanksWithYearErrors = {
  /**
   * unauthorized - premium feature required
   */
  403: unknown
}

export type GetFitPlayerProfilesByIdRanksWithYearResponses = {
  /**
   * ranks with year data
   */
  200: {
    ranks?: {
      national: number | string
      national_category: number | string
      regional: number | string
      regional_category: number | string
      provincial: number | string
      provincial_category: number | string
      club: number | string
    }
    year?: number
  }
}

export type GetFitPlayerProfilesByIdRanksWithYearResponse =
  GetFitPlayerProfilesByIdRanksWithYearResponses[keyof GetFitPlayerProfilesByIdRanksWithYearResponses]

export type GetHomeData = {
  body?: never
  headers?: {
    'X-Api-Key'?: string
  }
  path?: never
  query?: never
  url: '/home'
}

export type GetHomeResponses = {
  /**
   * home feed without authentication
   */
  200: {
    success?: boolean
    posts?: Array<{
      [key: string]: unknown
    }>
    magazine?: Array<{
      [key: string]: unknown
    }>
  }
}

export type GetHomeResponse = GetHomeResponses[keyof GetHomeResponses]

export type GetHomeNumbersData = {
  body?: never
  path?: never
  query?: never
  url: '/home/numbers'
}

export type GetHomeNumbersResponses = {
  /**
   * numbers found
   */
  200: {
    numbers?: {
      matches?: string
      players?: string
      competitions?: string
    }
  }
}

export type GetHomeNumbersResponse =
  GetHomeNumbersResponses[keyof GetHomeNumbersResponses]

export type GetHomeMatchesNumbersData = {
  body?: never
  path?: never
  query?: never
  url: '/home/matches-numbers'
}

export type GetHomeMatchesNumbersResponses = {
  /**
   * matches numbers found
   */
  200: {
    this_year?: string
    this_week?: string
  }
}

export type GetHomeMatchesNumbersResponse =
  GetHomeMatchesNumbersResponses[keyof GetHomeMatchesNumbersResponses]

export type GetHomeNotificationsData = {
  body?: never
  path?: never
  query?: never
  url: '/home/notifications'
}

export type GetHomeNotificationsResponses = {
  /**
   * notifications count
   */
  200: {
    counter: number
    message_counter: number
  }
}

export type GetHomeNotificationsResponse =
  GetHomeNotificationsResponses[keyof GetHomeNotificationsResponses]

export type GetHomeBackofficeNumbersData = {
  body?: never
  path?: never
  query?: never
  url: '/home/backoffice_numbers'
}

export type GetHomeBackofficeNumbersErrors = {
  /**
   * not an admin
   */
  401: unknown
}

export type GetHomeBackofficeNumbersResponses = {
  /**
   * backoffice numbers
   */
  200: BackofficeNumbersResponse
}

export type GetHomeBackofficeNumbersResponse =
  GetHomeBackofficeNumbersResponses[keyof GetHomeBackofficeNumbersResponses]

export type PostHomeInlineSearchData = {
  body?: {
    /**
     * Search query
     */
    q: string
    /**
     * Number of results per category
     */
    limit?: number
  }
  path?: never
  query?: never
  url: '/home/inline-search'
}

export type PostHomeInlineSearchResponses = {
  /**
   * empty search results
   */
  200: InlineSearchResponse
}

export type PostHomeInlineSearchResponse =
  PostHomeInlineSearchResponses[keyof PostHomeInlineSearchResponses]

export type PostHomeInlineSearchPlayersData = {
  body?: {
    /**
     * Search query
     */
    q: string
    /**
     * Number of results
     */
    limit?: number
  }
  path?: never
  query?: never
  url: '/home/inline-search-players'
}

export type PostHomeInlineSearchPlayersResponses = {
  /**
   * player search results
   */
  200: InlineSearchPlayersResponse
}

export type PostHomeInlineSearchPlayersResponse =
  PostHomeInlineSearchPlayersResponses[keyof PostHomeInlineSearchPlayersResponses]

export type PostHomeInvitationData = {
  body?: {
    email_to_invite: string
  }
  path?: never
  query?: never
  url: '/home/invitation'
}

export type PostHomeInvitationResponses = {
  /**
   * email already exists
   */
  200: {
    success?: boolean
    message?: string
  }
}

export type PostHomeInvitationResponse =
  PostHomeInvitationResponses[keyof PostHomeInvitationResponses]

export type GetHomeRateData = {
  body?: never
  path?: never
  query?: never
  url: '/home/rate'
}

export type GetHomeRateResponses = {
  /**
   * app rate
   */
  200: {
    rate?: number
  }
}

export type GetHomeRateResponse =
  GetHomeRateResponses[keyof GetHomeRateResponses]

export type PostHomeSendAllNotificationsData = {
  body?: {
    /**
     * Notification title
     */
    title: string
    /**
     * Notification content
     */
    content: string
    /**
     * Optional destination URL
     */
    destination_url?: string | null
  }
  path?: never
  query?: never
  url: '/home/send_all_notifications'
}

export type PostHomeSendAllNotificationsResponses = {
  /**
   * missing title or content
   */
  200: {
    success?: boolean
    error?: string
  }
}

export type PostHomeSendAllNotificationsResponse =
  PostHomeSendAllNotificationsResponses[keyof PostHomeSendAllNotificationsResponses]

export type GetIntercomMessengerTokenData = {
  body?: never
  path?: never
  query?: never
  url: '/intercom/messenger-token'
}

export type GetIntercomMessengerTokenErrors = {
  /**
   * senza X-Api-Key
   */
  401: unknown
  /**
   * Messenger non configurato (manca INTERCOM_MESSENGER_SECRET)
   */
  503: ErrorResponse
}

export type GetIntercomMessengerTokenError =
  GetIntercomMessengerTokenErrors[keyof GetIntercomMessengerTokenErrors]

export type GetIntercomMessengerTokenResponses = {
  /**
   * token firmato
   */
  200: IntercomMessengerToken
}

export type GetIntercomMessengerTokenResponse =
  GetIntercomMessengerTokenResponses[keyof GetIntercomMessengerTokenResponses]

export type PostPostsByIdLikeData = {
  body?: never
  path: {
    /**
     * Post ID
     */
    id: number
  }
  query?: never
  url: '/posts/{id}/like'
}

export type PostPostsByIdLikeErrors = {
  /**
   * unauthorized
   */
  401: unknown
  /**
   * post not found
   */
  404: unknown
}

export type PostPostsByIdLikeResponses = {
  /**
   * post liked
   */
  200: PostWithOutcomeResponse
}

export type PostPostsByIdLikeResponse =
  PostPostsByIdLikeResponses[keyof PostPostsByIdLikeResponses]

export type DeletePostsByIdUnlikeData = {
  body?: never
  path: {
    /**
     * Post ID
     */
    id: number
  }
  query?: never
  url: '/posts/{id}/unlike'
}

export type DeletePostsByIdUnlikeErrors = {
  /**
   * unauthorized
   */
  401: unknown
  /**
   * post not found
   */
  404: unknown
}

export type DeletePostsByIdUnlikeResponses = {
  /**
   * post unliked
   */
  200: PostWithOutcomeResponse
}

export type DeletePostsByIdUnlikeResponse =
  DeletePostsByIdUnlikeResponses[keyof DeletePostsByIdUnlikeResponses]

export type GetLiveEventsBySlugData = {
  body?: never
  headers?: {
    /**
     * Optional session token
     */
    'X-Api-Key'?: string
  }
  path: {
    /**
     * Event slug
     */
    slug: string
  }
  query?: never
  url: '/live-events/{slug}'
}

export type GetLiveEventsBySlugErrors = {
  /**
   * event missing or not active
   */
  404: unknown
}

export type GetLiveEventsBySlugResponses = {
  /**
   * event found (anonymous)
   */
  200: LiveEventDetailResponse
}

export type GetLiveEventsBySlugResponse =
  GetLiveEventsBySlugResponses[keyof GetLiveEventsBySlugResponses]

export type GetLiveEventsBySlugLeaderboardData = {
  body?: never
  headers?: {
    /**
     * Optional session token
     */
    'X-Api-Key'?: string
  }
  path: {
    /**
     * Event slug
     */
    slug: string
  }
  query?: {
    /**
     * Default `live`
     */
    mode?: LiveEventGameMode
    /**
     * Rows to return, 1..200 (default 50)
     */
    limit?: number
  }
  url: '/live-events/{slug}/leaderboard'
}

export type GetLiveEventsBySlugLeaderboardErrors = {
  /**
   * event missing or not active
   */
  404: unknown
}

export type GetLiveEventsBySlugLeaderboardResponses = {
  /**
   * leaderboard
   */
  200: LiveEventLeaderboard
}

export type GetLiveEventsBySlugLeaderboardResponse =
  GetLiveEventsBySlugLeaderboardResponses[keyof GetLiveEventsBySlugLeaderboardResponses]

export type PostLiveEventsBySlugSessionsData = {
  body?: {
    mode: LiveEventGameMode
    /**
     * Static key embedded in the official QR
     */
    live_key?: string
  }
  path: {
    /**
     * Event slug
     */
    slug: string
  }
  query?: never
  url: '/live-events/{slug}/sessions'
}

export type PostLiveEventsBySlugSessionsErrors = {
  /**
   * missing or invalid session token
   */
  401: unknown
  /**
   * wrong QR key for a live game
   */
  403: ErrorResponse
  /**
   * event missing or not active
   */
  404: unknown
  /**
   * event closed (outside starts_at/ends_at)
   */
  422: ErrorResponse
}

export type PostLiveEventsBySlugSessionsError =
  PostLiveEventsBySlugSessionsErrors[keyof PostLiveEventsBySlugSessionsErrors]

export type PostLiveEventsBySlugSessionsResponses = {
  /**
   * live game queued behind the player on screen
   */
  200: LiveEventSessionResponse
}

export type PostLiveEventsBySlugSessionsResponse =
  PostLiveEventsBySlugSessionsResponses[keyof PostLiveEventsBySlugSessionsResponses]

export type GetLiveEventsBySlugSessionsByIdData = {
  body?: never
  path: {
    /**
     * Event slug
     */
    slug: string
    /**
     * Game session ID
     */
    id: number
  }
  query?: never
  url: '/live-events/{slug}/sessions/{id}'
}

export type GetLiveEventsBySlugSessionsByIdErrors = {
  /**
   * missing or invalid session token
   */
  401: unknown
  /**
   * someone else's game
   */
  404: unknown
}

export type GetLiveEventsBySlugSessionsByIdResponses = {
  /**
   * game found
   */
  200: LiveEventSessionResponse
}

export type GetLiveEventsBySlugSessionsByIdResponse =
  GetLiveEventsBySlugSessionsByIdResponses[keyof GetLiveEventsBySlugSessionsByIdResponses]

export type PostLiveEventsBySlugSessionsByIdStartData = {
  body?: never
  path: {
    /**
     * Event slug
     */
    slug: string
    /**
     * Game session ID
     */
    id: number
  }
  query?: never
  url: '/live-events/{slug}/sessions/{id}/start'
}

export type PostLiveEventsBySlugSessionsByIdStartErrors = {
  /**
   * missing or invalid session token
   */
  401: unknown
  /**
   * someone else's game
   */
  404: unknown
  /**
   * not your turn yet
   */
  422: ErrorResponse
}

export type PostLiveEventsBySlugSessionsByIdStartError =
  PostLiveEventsBySlugSessionsByIdStartErrors[keyof PostLiveEventsBySlugSessionsByIdStartErrors]

export type PostLiveEventsBySlugSessionsByIdStartResponses = {
  /**
   * game started
   */
  200: LiveEventSessionResponse
}

export type PostLiveEventsBySlugSessionsByIdStartResponse =
  PostLiveEventsBySlugSessionsByIdStartResponses[keyof PostLiveEventsBySlugSessionsByIdStartResponses]

export type PostLiveEventsBySlugSessionsByIdFinishData = {
  body?: {
    score: number
    /**
     * Balls returned
     */
    hits?: number
    /**
     * Targets hit
     */
    bonus_hits?: number
    /**
     * Level reached
     */
    level?: number
    /**
     * Game duration; defaults to server time
     */
    duration_ms?: number
  }
  path: {
    /**
     * Event slug
     */
    slug: string
    /**
     * Game session ID
     */
    id: number
  }
  query?: never
  url: '/live-events/{slug}/sessions/{id}/finish'
}

export type PostLiveEventsBySlugSessionsByIdFinishErrors = {
  /**
   * missing or invalid session token
   */
  401: unknown
  /**
   * someone else's game
   */
  404: unknown
  /**
   * game not in progress (not_playing) or out-of-range result (invalid_result)
   */
  422: ErrorResponse
}

export type PostLiveEventsBySlugSessionsByIdFinishError =
  PostLiveEventsBySlugSessionsByIdFinishErrors[keyof PostLiveEventsBySlugSessionsByIdFinishErrors]

export type PostLiveEventsBySlugSessionsByIdFinishResponses = {
  /**
   * score saved
   */
  200: LiveEventSessionResponse
}

export type PostLiveEventsBySlugSessionsByIdFinishResponse =
  PostLiveEventsBySlugSessionsByIdFinishResponses[keyof PostLiveEventsBySlugSessionsByIdFinishResponses]

export type PostLiveEventsBySlugSessionsByIdCancelData = {
  body?: never
  path: {
    /**
     * Event slug
     */
    slug: string
    /**
     * Game session ID
     */
    id: number
  }
  query?: never
  url: '/live-events/{slug}/sessions/{id}/cancel'
}

export type PostLiveEventsBySlugSessionsByIdCancelErrors = {
  /**
   * missing or invalid session token
   */
  401: unknown
  /**
   * someone else's game
   */
  404: unknown
}

export type PostLiveEventsBySlugSessionsByIdCancelResponses = {
  /**
   * game cancelled
   */
  200: LiveEventSessionResponse
}

export type PostLiveEventsBySlugSessionsByIdCancelResponse =
  PostLiveEventsBySlugSessionsByIdCancelResponses[keyof PostLiveEventsBySlugSessionsByIdCancelResponses]

export type GetMatchesData = {
  body?: never
  headers: {
    /**
     * API key for authentication
     */
    'X-Api-Key': string
  }
  path?: never
  query?: {
    /**
     * Page number for pagination
     */
    page?: number
  }
  url: '/matches'
}

export type GetMatchesResponses = {
  /**
   * matches found
   */
  200: {
    total_pages: number
    matches: Array<MatchSearch>
  }
}

export type GetMatchesResponse = GetMatchesResponses[keyof GetMatchesResponses]

export type PostMatchesData = {
  body?: {
    /**
     * The draw (bracket) this match belongs to
     */
    draw_id: number
    /**
     * Round number within the draw
     */
    round_id?: number | null
    /**
     * FitPlayerProfile id of the winner
     */
    first_winner_id: number
    /**
     * Winner doubles partner FitPlayerProfile id
     */
    second_winner_id?: number | null
    /**
     * FitPlayerProfile id of the loser
     */
    first_loser_id: number
    /**
     * Loser doubles partner FitPlayerProfile id
     */
    second_loser_id?: number | null
    /**
     * Public score, e.g. '6-4 6-2'
     */
    result?: string
    /**
     * Score as recorded internally (may include retirement notation etc.)
     */
    internal_result?: string
    /**
     * Ranking-points weight of this match
     */
    weight?: number
    /**
     * Whether the loser retired mid-match
     */
    retire?: boolean
    /**
     * External FIT match identifier, if any
     */
    fit_match_id?: string
    /**
     * Whether this record was manually edited
     */
    modified?: boolean
  }
  headers: {
    /**
     * API key of an admin/banner_manager/censor_manager user
     */
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/matches'
}

export type PostMatchesResponses = {
  /**
   * validation errors returned
   */
  200: MatchCreateResponse
}

export type PostMatchesResponse =
  PostMatchesResponses[keyof PostMatchesResponses]

export type DeleteMatchesByIdData = {
  body?: never
  headers: {
    /**
     * API key of an admin/banner_manager/censor_manager user
     */
    'X-Api-Key': string
  }
  path: {
    /**
     * Match ID
     */
    id: number
  }
  query?: never
  url: '/matches/{id}'
}

export type DeleteMatchesByIdErrors = {
  /**
   * match not found
   */
  404: unknown
}

export type DeleteMatchesByIdResponses = {
  /**
   * match deleted successfully
   */
  200: MatchUpdateResponse
}

export type DeleteMatchesByIdResponse =
  DeleteMatchesByIdResponses[keyof DeleteMatchesByIdResponses]

export type GetMatchesByIdData = {
  body?: never
  headers: {
    /**
     * API key for authentication
     */
    'X-Api-Key': string
  }
  path: {
    /**
     * Match ID
     */
    id: number
  }
  query?: never
  url: '/matches/{id}'
}

export type GetMatchesByIdErrors = {
  /**
   * match not found
   */
  404: unknown
}

export type GetMatchesByIdResponses = {
  /**
   * match found
   */
  200: MatchResponse
}

export type GetMatchesByIdResponse =
  GetMatchesByIdResponses[keyof GetMatchesByIdResponses]

export type PutMatchesByIdData = {
  body?: {
    draw_id?: number
    round_id?: number | null
    first_winner_id?: number
    second_winner_id?: number | null
    first_loser_id?: number
    second_loser_id?: number | null
    result?: string
    internal_result?: string
    weight?: number
    retire?: boolean
    fit_match_id?: string
    modified?: boolean
  }
  headers: {
    /**
     * API key of an admin/banner_manager/censor_manager user
     */
    'X-Api-Key': string
  }
  path: {
    /**
     * Match ID
     */
    id: number
  }
  query?: never
  url: '/matches/{id}'
}

export type PutMatchesByIdErrors = {
  /**
   * match not found
   */
  404: unknown
}

export type PutMatchesByIdResponses = {
  /**
   * match updated successfully
   */
  200: MatchUpdateResponse
}

export type PutMatchesByIdResponse =
  PutMatchesByIdResponses[keyof PutMatchesByIdResponses]

export type PutMatchesByIdResultData = {
  body: {
    /**
     * Match result
     */
    result: string
    /**
     * Whether the match was played
     */
    is_played?: boolean
  }
  headers: {
    /**
     * API key of a user allowed to edit this match
     */
    'X-Api-Key': string
  }
  path: {
    /**
     * Match ID
     */
    id: number
  }
  query?: never
  url: '/matches/{id}/result'
}

export type PutMatchesByIdResultErrors = {
  /**
   * match not found
   */
  404: unknown
}

export type PutMatchesByIdResultResponses = {
  /**
   * match result updated successfully
   */
  200: MatchUpdateResponse
}

export type PutMatchesByIdResultResponse =
  PutMatchesByIdResultResponses[keyof PutMatchesByIdResultResponses]

export type PostPremiumCheckoutData = {
  body?: never
  path?: never
  query?: never
  url: '/premium/checkout'
}

export type PostPremiumCheckoutErrors = {
  /**
   * X-Api-Key mancante
   */
  401: unknown
}

export type PostPremiumCheckoutResponses = {
  /**
   * checkout creato
   */
  200: MembershipCheckoutResponse
}

export type PostPremiumCheckoutResponse =
  PostPremiumCheckoutResponses[keyof PostPremiumCheckoutResponses]

export type GetPremiumCheckoutByRefData = {
  body?: never
  path: {
    ref: string
  }
  query?: never
  url: '/premium/checkout/{ref}'
}

export type GetPremiumPaymentMethodByRefData = {
  body?: never
  path: {
    ref: string
  }
  query?: never
  url: '/premium/payment_method/{ref}'
}

export type GetPremiumStatusData = {
  body?: never
  path?: never
  query?: never
  url: '/premium/status'
}

export type GetPremiumStatusErrors = {
  /**
   * X-Api-Key mancante
   */
  401: unknown
}

export type GetPremiumStatusResponses = {
  /**
   * stato restituito
   */
  200: PremiumStatusResponse
}

export type GetPremiumStatusResponse =
  GetPremiumStatusResponses[keyof GetPremiumStatusResponses]

export type GetOfferCategoriesData = {
  body?: never
  path?: never
  query?: never
  url: '/offer-categories'
}

export type GetOfferCategoriesResponses = {
  /**
   * offer categories found
   */
  200: Array<OfferCategory>
}

export type GetOfferCategoriesResponse =
  GetOfferCategoriesResponses[keyof GetOfferCategoriesResponses]

export type PostOfferCategoriesData = {
  body?: {
    offer_category?: {
      name?: string
      description?: string
      /**
       * Set to nest this category under another one; omit for a top-level category
       */
      parent_id?: number
      /**
       * Sort order; higher weight sorts first
       */
      weight?: number
    }
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/offer-categories'
}

export type PostOfferCategoriesErrors = {
  /**
   * unauthorized
   */
  401: unknown
}

export type PostOfferCategoriesResponses = {
  /**
   * missing name returns validation errors
   */
  200: ValidationErrorResponse
  /**
   * offer category created
   */
  201: OfferCategoryResponse
}

export type PostOfferCategoriesResponse =
  PostOfferCategoriesResponses[keyof PostOfferCategoriesResponses]

export type DeleteOfferCategoriesByIdData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path: {
    id: number
  }
  query?: never
  url: '/offer-categories/{id}'
}

export type DeleteOfferCategoriesByIdErrors = {
  /**
   * category has children and cannot be resolved as a leaf category
   */
  404: unknown
}

export type DeleteOfferCategoriesByIdResponses = {
  /**
   * offer category deleted
   */
  200: OfferCategoryResponse
}

export type DeleteOfferCategoriesByIdResponse =
  DeleteOfferCategoriesByIdResponses[keyof DeleteOfferCategoriesByIdResponses]

export type PatchOfferCategoriesByIdData = {
  body?: {
    offer_category?: {
      name?: string
    }
  }
  headers: {
    'X-Api-Key': string
  }
  path: {
    id: number
  }
  query?: never
  url: '/offer-categories/{id}'
}

export type PatchOfferCategoriesByIdResponses = {
  /**
   * offer category updated
   */
  200: OfferCategoryResponse
}

export type PatchOfferCategoriesByIdResponse =
  PatchOfferCategoriesByIdResponses[keyof PatchOfferCategoriesByIdResponses]

export type GetOfferCategoriesChildrenData = {
  body?: never
  path?: never
  query?: never
  url: '/offer-categories/children'
}

export type GetOfferCategoriesChildrenResponses = {
  /**
   * child categories found
   */
  200: Array<OfferCategory>
}

export type GetOfferCategoriesChildrenResponse =
  GetOfferCategoriesChildrenResponses[keyof GetOfferCategoriesChildrenResponses]

export type GetOfferCategoriesBackofficeData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/offer-categories/backoffice'
}

export type GetOfferCategoriesBackofficeResponses = {
  /**
   * offer categories found
   */
  200: Array<OfferCategoryBackoffice>
}

export type GetOfferCategoriesBackofficeResponse =
  GetOfferCategoriesBackofficeResponses[keyof GetOfferCategoriesBackofficeResponses]

export type DeleteOffersByIdData = {
  body?: never
  headers?: {
    /**
     * API Key for authentication
     */
    'X-Api-Key'?: string
  }
  path: {
    /**
     * Offer ID
     */
    id: number
  }
  query?: never
  url: '/offers/{id}'
}

export type DeleteOffersByIdErrors = {
  /**
   * unauthorized
   */
  401: unknown
  /**
   * offer not found
   */
  404: unknown
}

export type DeleteOffersByIdResponses = {
  /**
   * offer deleted
   */
  200: {
    success: boolean
    offer: OfferResponse
  }
}

export type DeleteOffersByIdResponse =
  DeleteOffersByIdResponses[keyof DeleteOffersByIdResponses]

export type GetOffersByIdData = {
  body?: never
  headers: {
    /**
     * API key for authentication
     */
    'X-Api-Key': string
  }
  path: {
    /**
     * Offer ID
     */
    id: number
  }
  query?: never
  url: '/offers/{id}'
}

export type GetOffersByIdErrors = {
  /**
   * offer not found
   */
  404: unknown
}

export type GetOffersByIdResponses = {
  /**
   * offer found
   */
  200: OfferResponse
}

export type GetOffersByIdResponse =
  GetOffersByIdResponses[keyof GetOffersByIdResponses]

export type PatchOffersByIdData = {
  body?: {
    masqueraded_type?: 'User' | 'ShopProfile'
    masqueraded_id?: number
    category_id?: number
    title?: string
    content?: string
    price?: number
    sale_price?: number | null
    sale_end?: string | null
    destination?: string | null
    weight?: number | null
    status?: number | null
    second_hand?: boolean | null
    images?: Array<ImageInput>
  }
  headers?: {
    /**
     * API Key for authentication
     */
    'X-Api-Key'?: string
  }
  path: {
    /**
     * Offer ID
     */
    id: number
  }
  query?: never
  url: '/offers/{id}'
}

export type PatchOffersByIdErrors = {
  /**
   * unauthorized
   */
  401: unknown
  /**
   * offer not found
   */
  404: unknown
}

export type PatchOffersByIdResponses = {
  /**
   * offer updated
   */
  200: {
    success: boolean
    offer: OfferResponse
  }
}

export type PatchOffersByIdResponse =
  PatchOffersByIdResponses[keyof PatchOffersByIdResponses]

export type PutOffersByIdData = {
  body?: {
    masqueraded_type?: 'User' | 'ShopProfile'
    masqueraded_id?: number
    category_id?: number
    title?: string
    content?: string
    price?: number
    sale_price?: number | null
    sale_end?: string | null
    destination?: string | null
    weight?: number | null
    status?: number | null
    second_hand?: boolean | null
    images?: Array<ImageInput>
  }
  headers?: {
    /**
     * API Key for authentication
     */
    'X-Api-Key'?: string
  }
  path: {
    /**
     * Offer ID
     */
    id: number
  }
  query?: never
  url: '/offers/{id}'
}

export type PutOffersByIdResponses = {
  /**
   * offer updated
   */
  200: {
    success: boolean
    offer: OfferResponse
  }
}

export type PutOffersByIdResponse =
  PutOffersByIdResponses[keyof PutOffersByIdResponses]

export type PostOffersSearchData = {
  body?: {
    /**
     * Search query
     */
    q?: string
    /**
     * Category ID filter
     */
    category_id?: number
    /**
     * Second hand filter
     */
    second_hand?: boolean
    /**
     * Page number
     */
    page?: number
    /**
     * Results per page
     */
    limit?: number
  }
  path?: never
  query?: never
  url: '/offers/search'
}

export type PostOffersSearchResponses = {
  /**
   * empty search results
   */
  200: {
    offers: Array<OfferSearch>
    total_pages: number
    page: number
  }
}

export type PostOffersSearchResponse =
  PostOffersSearchResponses[keyof PostOffersSearchResponses]

export type GetOffersPrivatesData = {
  body?: never
  headers?: {
    /**
     * API Key for authentication
     */
    'X-Api-Key'?: string
  }
  path?: never
  query?: never
  url: '/offers/privates'
}

export type GetOffersPrivatesErrors = {
  /**
   * unauthorized
   */
  401: unknown
}

export type GetOffersPrivatesResponses = {
  /**
   * private offers found
   */
  200: Array<OfferSearch>
}

export type GetOffersPrivatesResponse =
  GetOffersPrivatesResponses[keyof GetOffersPrivatesResponses]

export type GetOffersShopsData = {
  body?: never
  headers: {
    /**
     * API key for authentication
     */
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/offers/shops'
}

export type GetOffersShopsResponses = {
  /**
   * shop offers found
   */
  200: Array<OfferSearch>
}

export type GetOffersShopsResponse =
  GetOffersShopsResponses[keyof GetOffersShopsResponses]

export type PostOffersData = {
  body?: {
    /**
     * Who the offer is posted as
     */
    masqueraded_type: 'User' | 'ShopProfile'
    /**
     * id of the User or ShopProfile posting the offer
     */
    masqueraded_id: number
    /**
     * OfferCategory id
     */
    category_id: number
    title: string
    content: string
    price: number
    /**
     * Discounted price, if on sale
     */
    sale_price?: number | null
    /**
     * End date of the sale, if any
     */
    sale_end?: string | null
    destination?: string | null
    /**
     * Sort weight; higher shows first
     */
    weight?: number | null
    status?: number | null
    /**
     * Whether the item is second-hand
     */
    second_hand?: boolean | null
    images?: Array<ImageInput>
  }
  headers?: {
    /**
     * API Key for authentication
     */
    'X-Api-Key'?: string
  }
  path?: never
  query?: never
  url: '/offers'
}

export type PostOffersErrors = {
  /**
   * unauthorized
   */
  401: unknown
}

export type PostOffersResponses = {
  /**
   * invalid parameters
   */
  200: ErrorResponse
  /**
   * offer created
   */
  201: {
    success: boolean
    offer: OfferResponse
  }
}

export type PostOffersResponse = PostOffersResponses[keyof PostOffersResponses]

export type GetPostsData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query: {
    /**
     * Masqueraded profile class, e.g. "User"
     */
    type: string
    /**
     * ID of the masqueraded profile (must be managed by the caller)
     */
    id: number
    /**
     * Page number, 10 results per page (default 1)
     */
    page?: number
  }
  url: '/posts'
}

export type GetPostsErrors = {
  /**
   * unauthorized to manage the given profile
   */
  401: unknown
}

export type GetPostsResponses = {
  /**
   * posts found
   */
  200: PostsResponse
}

export type GetPostsResponse = GetPostsResponses[keyof GetPostsResponses]

export type PostPostsData = {
  body?: NewPost
  path?: never
  query?: never
  url: '/posts'
}

export type PostPostsErrors = {
  /**
   * unauthorized
   */
  401: unknown
}

export type PostPostsResponses = {
  /**
   * post created
   */
  200: {
    success?: boolean
    post?: Post
  }
}

export type PostPostsResponse = PostPostsResponses[keyof PostPostsResponses]

export type GetPostsLastBlogData = {
  body?: never
  path?: never
  query?: never
  url: '/posts/last_blog'
}

export type GetPostsLastBlogResponses = {
  /**
   * latest blog post found
   */
  200: Post
}

export type GetPostsLastBlogResponse =
  GetPostsLastBlogResponses[keyof GetPostsLastBlogResponses]

export type DeletePostsByIdData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path: {
    id: number
  }
  query?: never
  url: '/posts/{id}'
}

export type DeletePostsByIdErrors = {
  /**
   * post not found
   */
  404: unknown
}

export type DeletePostsByIdResponses = {
  /**
   * post deleted
   */
  200: PostWithOutcomeResponse
}

export type DeletePostsByIdResponse =
  DeletePostsByIdResponses[keyof DeletePostsByIdResponses]

export type GetPostsByIdData = {
  body?: never
  path: {
    id: number
  }
  query?: never
  url: '/posts/{id}'
}

export type GetPostsByIdErrors = {
  /**
   * post not found
   */
  404: unknown
}

export type GetPostsByIdResponses = {
  /**
   * post found
   */
  200: Post
}

export type GetPostsByIdResponse =
  GetPostsByIdResponses[keyof GetPostsByIdResponses]

export type PatchPostsByIdData = {
  body?: {
    post?: {
      content?: string
      video?: string | null
    }
    images?: Array<ImageInput>
  }
  headers: {
    'X-Api-Key': string
  }
  path: {
    id: number
  }
  query?: never
  url: '/posts/{id}'
}

export type PatchPostsByIdErrors = {
  /**
   * unauthorized
   */
  401: unknown
  /**
   * post of another author
   */
  404: unknown
}

export type PatchPostsByIdResponses = {
  /**
   * post updated
   */
  200: PostWithOutcomeResponse
}

export type PatchPostsByIdResponse =
  PatchPostsByIdResponses[keyof PatchPostsByIdResponses]

export type PostRechargeWebhookData = {
  body?: {
    [key: string]: unknown
  }
  headers: {
    /**
     * Webhook topic, e.g. charge/failed
     */
    'X-Recharge-Topic': string
    /**
     * Hex SHA-256 of the API client secret + raw request body
     */
    'X-Recharge-Hmac-Sha256': string
  }
  path?: never
  query?: never
  url: '/recharge/webhook'
}

export type PostRechargeWebhookErrors = {
  /**
   * firma mancante o errata
   */
  401: unknown
}

export type PostRechargeWebhookResponses = {
  /**
   * evento registrato (o ignorato)
   */
  200: SuccessResponse
}

export type PostRechargeWebhookResponse =
  PostRechargeWebhookResponses[keyof PostRechargeWebhookResponses]

export type PostUsersSignUpData = {
  body?: {
    email: string
    password: string
    password_confirmation: string
  }
  path?: never
  query?: never
  url: '/users/sign-up'
}

export type PostUsersSignUpResponses = {
  /**
   * validation errors
   */
  200: {
    success: boolean
    user?: User
    errors: {
      [key: string]: unknown
    }
  }
}

export type PostUsersSignUpResponse =
  PostUsersSignUpResponses[keyof PostUsersSignUpResponses]

export type PostUsersSignUpAdditionalInfoData = {
  body?: {
    confirmation_token: string
    first_name?: string
    last_name?: string
    birth_date?: string
    phone?: string
    bio?: string
    marketing_communication_check?: boolean
    tennistalker_communication_check?: boolean
    preferred_surface?: string
    photo?: string
    address_user_input?: string
    lat_input?: number | string | null
    lng_input?: number | string | null
    racket_brand?: string
    shoes_brand?: string
    clothing_brand?: string
    lefthanded?: boolean
    one_handed_backhand?: boolean
    ig_id?: string
    fb_id?: string
    nickname?: string
    discoverable?: boolean
    club_id?: number | null
  }
  path?: never
  query?: never
  url: '/users/sign-up/additional-info'
}

export type PostUsersSignUpAdditionalInfoErrors = {
  /**
   * no valid session and no confirmation_token
   */
  401: unknown
  /**
   * user not found
   */
  404: unknown
}

export type PostUsersSignUpAdditionalInfoResponses = {
  /**
   * additional info updated successfully
   */
  200: {
    success: boolean
    user: User
  }
}

export type PostUsersSignUpAdditionalInfoResponse =
  PostUsersSignUpAdditionalInfoResponses[keyof PostUsersSignUpAdditionalInfoResponses]

export type PostUsersCompleteProfileData = {
  body?: {
    token: string
    fit_player_profile?: {
      fit_card_number?: string
      selected_player?: string
    }
    teacher_profile?: {
      qualification?: string
      professional_info?: string
      students_count?: number
    }
    amateur_profile?: {
      skill?: number
      frequency?: number
      ntrp?: number
      level_label?: string
      first_match_intent?: 'smartwatch' | 'manual' | 'later'
    }
    trainer_profile?: {
      qualification?: string
      professional_info?: string
    }
    nutritionist_profile?: {
      qualifications?: string
      professional_info?: string
    }
    mental_coach_profile?: {
      qualifications?: string
      professional_info?: string
    }
    club_manager_profile?: {
      fit_club_id?: string
      photo?: string
    }
    shop_profile?: {
      name?: string
      address?: string
      website?: string
      info?: string
    }
  }
  path?: never
  query?: never
  url: '/users/complete-profile'
}

export type PostUsersCompleteProfileErrors = {
  /**
   * no valid session and no token
   */
  401: unknown
  /**
   * user not found
   */
  404: unknown
  /**
   * FIT card sent by a user without first name, last name or birth date
   */
  422: ValidationErrorResponse
}

export type PostUsersCompleteProfileError =
  PostUsersCompleteProfileErrors[keyof PostUsersCompleteProfileErrors]

export type PostUsersCompleteProfileResponses = {
  /**
   * profile completed successfully
   */
  200: {
    success: boolean
    user: User
    fit_card_feedback?: {
      status?: string
      message?: string
    }
  }
}

export type PostUsersCompleteProfileResponse =
  PostUsersCompleteProfileResponses[keyof PostUsersCompleteProfileResponses]

export type GetReportsData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    /**
     * Page number (default 1, 20 per page)
     */
    page?: number
  }
  url: '/reports'
}

export type GetReportsErrors = {
  /**
   * not an admin
   */
  401: unknown
}

export type GetReportsResponses = {
  /**
   * reports found
   */
  200: ReportsIndexResponse
}

export type GetReportsResponse = GetReportsResponses[keyof GetReportsResponses]

export type PostReportsData = {
  body?: {
    report?: {
      /**
       * Post, Comment, User or UserMatch, any case (user_match works too)
       */
      reportable_type: string
      /**
       * ID of the reported record
       */
      reportable_id: number
      /**
       * Optional: defaults to `other` (older app builds send only notes).
       */
      reason?:
        'inappropriate' | 'harassment' | 'fake_profile' | 'no_show' | 'other'
      /**
       * Free-text details
       */
      notes: string
    }
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/reports'
}

export type PostReportsResponses = {
  /**
   * missing reportable returns validation errors
   */
  200: ReportCreateResponse
}

export type PostReportsResponse =
  PostReportsResponses[keyof PostReportsResponses]

export type GetReportsByIdData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path: {
    /**
     * Report ID
     */
    id: number
  }
  query?: never
  url: '/reports/{id}'
}

export type GetReportsByIdResponses = {
  /**
   * report found
   */
  200: Report
}

export type GetReportsByIdResponse =
  GetReportsByIdResponses[keyof GetReportsByIdResponses]

export type PatchReportsByIdData = {
  body?: {
    /**
     * Note recorded alongside the closed report
     */
    admin_notes?: string
  }
  headers: {
    'X-Api-Key': string
  }
  path: {
    /**
     * Report ID
     */
    id: number
  }
  query?: never
  url: '/reports/{id}'
}

export type PatchReportsByIdErrors = {
  /**
   * not an admin
   */
  401: unknown
  /**
   * report not found
   */
  404: unknown
}

export type PatchReportsByIdResponses = {
  /**
   * report updated
   */
  200: ReportResponse
}

export type PatchReportsByIdResponse =
  PatchReportsByIdResponses[keyof PatchReportsByIdResponses]

export type DeleteReportsByIdDeleteTargetData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path: {
    /**
     * Report ID
     */
    id: number
  }
  query?: never
  url: '/reports/{id}/delete-target'
}

export type DeleteReportsByIdDeleteTargetErrors = {
  /**
   * a reported match cannot be deleted
   */
  422: unknown
}

export type DeleteReportsByIdDeleteTargetResponses = {
  /**
   * reported content deleted
   */
  200: SuccessResponse
}

export type DeleteReportsByIdDeleteTargetResponse =
  DeleteReportsByIdDeleteTargetResponses[keyof DeleteReportsByIdDeleteTargetResponses]

export type GetReportsPendingData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/reports/pending'
}

export type GetReportsPendingResponses = {
  /**
   * pending reports found
   */
  200: ReportsIndexResponse
}

export type GetReportsPendingResponse =
  GetReportsPendingResponses[keyof GetReportsPendingResponses]

export type GetReportsClosedData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/reports/closed'
}

export type GetReportsClosedResponses = {
  /**
   * closed reports found
   */
  200: ReportsIndexResponse
}

export type GetReportsClosedResponse =
  GetReportsClosedResponses[keyof GetReportsClosedResponses]

export type GetSearchData = {
  body?: never
  path?: never
  query: {
    /**
     * Search text, at least 2 letters or digits
     */
    q: string
    /**
     * Comma-separated sections to compute (players, users, clubs, competitions, posts); default all. The others are null.
     */
    types?: string
    /**
     * Page number of every section, 1-indexed (default 1)
     */
    page?: number
    /**
     * Results per section (default 5, max 30)
     */
    per?: number
  }
  url: '/search'
}

export type GetSearchErrors = {
  /**
   * Query troppo corta
   */
  422: ErrorResponse
}

export type GetSearchError = GetSearchErrors[keyof GetSearchErrors]

export type GetSearchResponses = {
  /**
   * Solo le sezioni richieste
   */
  200: GlobalSearchResponse
}

export type GetSearchResponse = GetSearchResponses[keyof GetSearchResponses]

export type PostSearchPlayersData = {
  body?: {
    /**
     * Search query
     */
    q: string
    /**
     * Page number, 1-indexed
     */
    page?: number
  }
  path?: never
  query?: never
  url: '/search/players'
}

export type PostSearchPlayersResponses = {
  /**
   * Giocatori trovati
   */
  200: SearchPlayersResponse
}

export type PostSearchPlayersResponse =
  PostSearchPlayersResponses[keyof PostSearchPlayersResponses]

export type PostSearchClubsData = {
  body?: {
    /**
     * Search query
     */
    q: string
    /**
     * Page number, 1-indexed
     */
    page?: number
  }
  path?: never
  query?: never
  url: '/search/clubs'
}

export type PostSearchClubsResponses = {
  /**
   * Club trovati
   */
  200: SearchClubsResponse
}

export type PostSearchClubsResponse =
  PostSearchClubsResponses[keyof PostSearchClubsResponses]

export type PostSearchCompetitionsData = {
  body?: {
    /**
     * Search query
     */
    q: string
    /**
     * Page number, 1-indexed
     */
    page?: number
  }
  path?: never
  query?: never
  url: '/search/competitions'
}

export type PostSearchCompetitionsResponses = {
  /**
   * Competizioni trovate
   */
  200: SearchCompetitionsResponse
}

export type PostSearchCompetitionsResponse =
  PostSearchCompetitionsResponses[keyof PostSearchCompetitionsResponses]

export type PostSearchOtherProfilesData = {
  body?: {
    /**
     * Search query
     */
    q: string
    /**
     * Page number, 1-indexed
     */
    page?: number
    /**
     * Restrict to one professional profile type, e.g. 'teacher_profile' (omit for all types)
     */
    profile_filter?: string | null
  }
  path?: never
  query?: never
  url: '/search/other_profiles'
}

export type PostSearchOtherProfilesResponses = {
  /**
   * Profili trovati
   */
  200: SearchOtherProfilesResponse
}

export type PostSearchOtherProfilesResponse =
  PostSearchOtherProfilesResponses[keyof PostSearchOtherProfilesResponses]

export type PostSearchFitTeachersData = {
  body?: {
    /**
     * Search query
     */
    q: string
    /**
     * Number of results (default 10, max 25)
     */
    limit?: number
  }
  path?: never
  query?: never
  url: '/search/fit_teachers'
}

export type PostSearchFitTeachersResponses = {
  /**
   * Nessun risultato con q vuota
   */
  200: SearchFitTeachersResponse
}

export type PostSearchFitTeachersResponse =
  PostSearchFitTeachersResponses[keyof PostSearchFitTeachersResponses]

export type PostSessionsData = {
  body?: {
    email: string
    password: string
  }
  path?: never
  query?: never
  url: '/sessions'
}

export type PostSessionsResponses = {
  /**
   * account not confirmed
   */
  200: {
    success?: boolean
    user?: UserSession
    errors?: Array<string>
  }
}

export type PostSessionsResponse =
  PostSessionsResponses[keyof PostSessionsResponses]

export type PostSessionsGoogleAuthData = {
  body?: {
    token: string
    uid: string
    email: string
    first_name?: string
    last_name?: string
    photo?: string
  }
  path?: never
  query?: never
  url: '/sessions/google-auth'
}

export type PostSessionsGoogleAuthResponses = {
  /**
   * invalid Google token
   */
  200: ErrorResponse
  /**
   * user logged in
   */
  201: {
    success?: boolean
    user?: UserSession
  }
}

export type PostSessionsGoogleAuthResponse =
  PostSessionsGoogleAuthResponses[keyof PostSessionsGoogleAuthResponses]

export type GetShopProfilesByIdData = {
  body?: never
  headers?: {
    /**
     * Opzionale: se presente valorizza managed/followed per l'utente corrente
     */
    'X-Api-Key'?: string
  }
  path: {
    id: number
  }
  query?: never
  url: '/shop-profiles/{id}'
}

export type GetShopProfilesByIdErrors = {
  /**
   * profilo negozio non trovato
   */
  404: unknown
}

export type GetShopProfilesByIdResponses = {
  /**
   * profilo negozio trovato con utente autenticato
   */
  200: ShopProfile
}

export type GetShopProfilesByIdResponse =
  GetShopProfilesByIdResponses[keyof GetShopProfilesByIdResponses]

export type PostShopifyOrderData = {
  body?: never
  headers: {
    /**
     * HMAC-SHA256 of the raw request body, signed with the shared Shopify webhook secret
     */
    'X-Shopify-Hmac-Sha256': string
  }
  path?: never
  query?: never
  url: '/shopify/order'
}

export type PostShopifyOrderErrors = {
  /**
   * Invalid webhook
   */
  401: unknown
}

export type PostShopifyOrderResponses = {
  /**
   * Webhook processed successfully
   */
  200: unknown
}

export type GetSmartWatchWorkoutsData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    /**
     * Page number (100 per page)
     */
    page?: number
    /**
     * Filter by status: pending | associated | dismissed
     */
    status?: string
  }
  url: '/smart_watch_workouts'
}

export type GetSmartWatchWorkoutsResponses = {
  /**
   * workouts found
   */
  200: Array<SmartWatchWorkout>
}

export type GetSmartWatchWorkoutsResponse =
  GetSmartWatchWorkoutsResponses[keyof GetSmartWatchWorkoutsResponses]

export type PostSmartWatchWorkoutsData = {
  body?: {
    smart_watch_workout: {
      /**
       * Device-generated workout ID (e.g. HealthKit UUID)
       */
      platform_id: string
      platform: 'ios' | 'android'
      /**
       * e.g. Tennis, Running
       */
      workout_type: string
      start_date: string
      end_date: string
      /**
       * Duration in seconds
       */
      duration: number
      /**
       * Meters
       */
      total_distance?: number
      /**
       * kcal
       */
      total_energy_burned?: number
      /**
       * bpm
       */
      heart_rate_min?: number
      /**
       * bpm
       */
      heart_rate_max?: number
      /**
       * bpm
       */
      heart_rate_avg?: number
      synced_at: string
    }
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/smart_watch_workouts'
}

export type PostSmartWatchWorkoutsErrors = {
  /**
   * invalid data
   */
  422: {
    success?: boolean
    errors?: Array<string>
  }
}

export type PostSmartWatchWorkoutsError =
  PostSmartWatchWorkoutsErrors[keyof PostSmartWatchWorkoutsErrors]

export type PostSmartWatchWorkoutsResponses = {
  /**
   * workout created
   */
  201: {
    success?: boolean
    smart_watch_workout?: SmartWatchWorkout
  }
}

export type PostSmartWatchWorkoutsResponse =
  PostSmartWatchWorkoutsResponses[keyof PostSmartWatchWorkoutsResponses]

export type PostSmartWatchWorkoutsBulkData = {
  body?: {
    /**
     * Duplicate platform_ids for the same user are silently skipped
     */
    workouts: Array<{
      platform_id: string
      platform: 'ios' | 'android'
      workout_type: string
      start_date: string
      end_date: string
      duration: number
      total_distance?: number
      total_energy_burned?: number
      heart_rate_min?: number
      heart_rate_max?: number
      heart_rate_avg?: number
      synced_at: string
    }>
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/smart_watch_workouts/bulk'
}

export type PostSmartWatchWorkoutsBulkResponses = {
  /**
   * workouts imported (duplicates skipped)
   */
  201: {
    success?: boolean
  }
}

export type PostSmartWatchWorkoutsBulkResponse =
  PostSmartWatchWorkoutsBulkResponses[keyof PostSmartWatchWorkoutsBulkResponses]

export type DeleteSmartWatchWorkoutsByIdData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path: {
    id: number
  }
  query?: never
  url: '/smart_watch_workouts/{id}'
}

export type DeleteSmartWatchWorkoutsByIdErrors = {
  /**
   * workout not found
   */
  404: unknown
}

export type DeleteSmartWatchWorkoutsByIdResponses = {
  /**
   * workout deleted
   */
  200: {
    success?: boolean
  }
}

export type DeleteSmartWatchWorkoutsByIdResponse =
  DeleteSmartWatchWorkoutsByIdResponses[keyof DeleteSmartWatchWorkoutsByIdResponses]

export type GetSmartWatchWorkoutsByIdData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path: {
    id: number
  }
  query?: never
  url: '/smart_watch_workouts/{id}'
}

export type GetSmartWatchWorkoutsByIdErrors = {
  /**
   * workout not found
   */
  404: unknown
}

export type GetSmartWatchWorkoutsByIdResponses = {
  /**
   * workout found
   */
  200: {
    success?: boolean
    smart_watch_workout?: SmartWatchWorkout
  }
}

export type GetSmartWatchWorkoutsByIdResponse =
  GetSmartWatchWorkoutsByIdResponses[keyof GetSmartWatchWorkoutsByIdResponses]

export type PatchSmartWatchWorkoutsByIdData = {
  body?: {
    smart_watch_workout: {
      /**
       * associated requires activity_id; dismissed clears the link
       */
      status?: 'associated' | 'dismissed'
      /**
       * ID of the Activity to link. Must belong to the current user.
       */
      activity_id?: number | null
    }
  }
  headers: {
    'X-Api-Key': string
  }
  path: {
    id: number
  }
  query?: never
  url: '/smart_watch_workouts/{id}'
}

export type PatchSmartWatchWorkoutsByIdErrors = {
  /**
   * workout not found
   */
  404: unknown
  /**
   * validation error (e.g. activity belongs to another user)
   */
  422: {
    success?: boolean
    errors?: Array<string>
  }
}

export type PatchSmartWatchWorkoutsByIdError =
  PatchSmartWatchWorkoutsByIdErrors[keyof PatchSmartWatchWorkoutsByIdErrors]

export type PatchSmartWatchWorkoutsByIdResponses = {
  /**
   * workout updated
   */
  200: {
    success?: boolean
    smart_watch_workout?: SmartWatchWorkout
  }
}

export type PatchSmartWatchWorkoutsByIdResponse =
  PatchSmartWatchWorkoutsByIdResponses[keyof PatchSmartWatchWorkoutsByIdResponses]

export type GetSocialFeedData = {
  body?: never
  path?: never
  query?: never
  url: '/social-feed'
}

export type GetSocialFeedResponses = {
  /**
   * feed returned
   */
  200: SocialFeedResponse
}

export type GetSocialFeedResponse =
  GetSocialFeedResponses[keyof GetSocialFeedResponses]

export type GetTournamentsByIdData = {
  body?: never
  headers?: {
    /**
     * Optional API key for user context
     */
    'X-Api-Key'?: string
  }
  path: {
    /**
     * Tournament ID
     */
    id: number
  }
  query?: {
    /**
     * Include tournament metrics (Radar Tornei: Membership + linked FIT profile, ignored otherwise)
     */
    with_eval?: boolean
  }
  url: '/tournaments/{id}'
}

export type GetTournamentsByIdErrors = {
  /**
   * tournament not found
   */
  404: unknown
}

export type GetTournamentsByIdResponses = {
  /**
   * tournament with no winner
   */
  200: {
    id: number
    name: string
    fit_tournament_id: string
    players_count?: number | null
    age_category?: string | null
    rankings_category?: number | null
    gender?: string | null
    new_fit_tournament_id?: string | null
    min_number_partecipants?: number | null
    max_number_partecipants?: number | null
    court_number?: number | null
    ball_type?: string | null
    surface?: Surface | null
    court_type?: CourtType | null
    sign_up_order?: string | null
    followed?: boolean
    followable_type?: 'tournament'
    competition: TournamentCompetition
    list_participants?: Array<InlineSearchPlayer>
    participants_by_rank?: {
      [key: string]: Array<InlineSearchPlayer>
    }
    draws: Array<TournamentDraw>
    absolute_level?: number | null
    danger_index?: number | null
    personal_difficulty?: number | null
    analyzed_players_count?: number | null
    confidence_score?: number | null
    number_of_double_match?: number | null
    number_of_single_match?: number | null
    number_of_points_for_single_win?: number | null
    phase_type?: number | null
  }
}

export type GetTournamentsByIdResponse =
  GetTournamentsByIdResponses[keyof GetTournamentsByIdResponses]

export type PostTrainingsByTrainingIdTrainingSchedulesData = {
  body?: {
    training_schedule?: {
      /**
       * 0=Sunday, 1=Monday... 6=Saturday
       */
      day_of_week: number
      start_date?: string | null
      end_date?: string | null
      active?: boolean
    }
  }
  path: {
    /**
     * ID of the training this schedule repeats. Must belong to the caller.
     */
    training_id: number
  }
  query?: never
  url: '/trainings/{training_id}/training_schedules'
}

export type PostTrainingsByTrainingIdTrainingSchedulesErrors = {
  /**
   * unprocessable content (e.g., duplicate day)
   */
  422: {
    success?: boolean
    errors?: Array<string>
  }
}

export type PostTrainingsByTrainingIdTrainingSchedulesError =
  PostTrainingsByTrainingIdTrainingSchedulesErrors[keyof PostTrainingsByTrainingIdTrainingSchedulesErrors]

export type PostTrainingsByTrainingIdTrainingSchedulesResponses = {
  /**
   * training schedule created
   */
  201: TrainingSchedule
}

export type PostTrainingsByTrainingIdTrainingSchedulesResponse =
  PostTrainingsByTrainingIdTrainingSchedulesResponses[keyof PostTrainingsByTrainingIdTrainingSchedulesResponses]

export type GetTrainingSchedulesData = {
  body?: never
  path?: never
  query?: never
  url: '/training_schedules'
}

export type GetTrainingSchedulesResponses = {
  /**
   * list of schedules
   */
  200: Array<TrainingSchedule>
}

export type GetTrainingSchedulesResponse =
  GetTrainingSchedulesResponses[keyof GetTrainingSchedulesResponses]

export type DeleteTrainingSchedulesByIdData = {
  body?: never
  path: {
    id: number
  }
  query?: never
  url: '/training_schedules/{id}'
}

export type DeleteTrainingSchedulesByIdErrors = {
  /**
   * schedule not found
   */
  404: unknown
}

export type DeleteTrainingSchedulesByIdResponses = {
  /**
   * schedule deactivated successfully
   */
  204: void
}

export type DeleteTrainingSchedulesByIdResponse =
  DeleteTrainingSchedulesByIdResponses[keyof DeleteTrainingSchedulesByIdResponses]

export type PatchTrainingSchedulesByIdData = {
  body?: {
    training_schedule?: {
      day_of_week?: number
      start_date?: string | null
      end_date?: string | null
      active?: boolean
    }
  }
  path: {
    id: number
  }
  query?: never
  url: '/training_schedules/{id}'
}

export type PatchTrainingSchedulesByIdErrors = {
  /**
   * schedule not found or belongs to someone else
   */
  404: unknown
}

export type PatchTrainingSchedulesByIdResponses = {
  /**
   * training schedule updated
   */
  200: TrainingSchedule
}

export type PatchTrainingSchedulesByIdResponse =
  PatchTrainingSchedulesByIdResponses[keyof PatchTrainingSchedulesByIdResponses]

export type GetTrainingsData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    /**
     * Page number (100 per page)
     */
    page?: number
  }
  url: '/trainings'
}

export type GetTrainingsResponses = {
  /**
   * trainings found
   */
  200: Array<Training>
}

export type GetTrainingsResponse =
  GetTrainingsResponses[keyof GetTrainingsResponses]

export type PostTrainingsData = {
  body?: {
    training: {
      date: string
      /**
       * Local start time, "HH:MM" (24h)
       */
      start_time?: string
      location_name: string
      club_id?: number
      duration: number
      indoor: boolean
      surface: string
      focus?: Array<string>
      teacher_name?: string
      teacher_signed_id?: string
      /**
       * Personal note set on the author's Activity at creation time
       */
      activity_note?: string | null
      /**
       * Visibility of the author's Activity (default: true)
       */
      activity_visible?: boolean | null
      training_participations_attributes?: Array<{
        participant_name?: string
        participant_type?: string
        participant_id?: number
        id?: number
        _destroy?: boolean
      }>
    }
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/trainings'
}

export type PostTrainingsResponses = {
  /**
   * training created
   */
  200: {
    success?: boolean
    training?: Training
    activity_id?: number | null
  }
}

export type PostTrainingsResponse =
  PostTrainingsResponses[keyof PostTrainingsResponses]

export type DeleteTrainingsByIdData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path: {
    id: number
  }
  query?: never
  url: '/trainings/{id}'
}

export type DeleteTrainingsByIdResponses = {
  /**
   * training deleted
   */
  200: {
    success?: boolean
    training?: Training
  }
}

export type DeleteTrainingsByIdResponse =
  DeleteTrainingsByIdResponses[keyof DeleteTrainingsByIdResponses]

export type GetTrainingsByIdData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path: {
    id: number
  }
  query?: never
  url: '/trainings/{id}'
}

export type GetTrainingsByIdResponses = {
  /**
   * training found
   */
  200: {
    success?: boolean
    training?: Training
  }
}

export type GetTrainingsByIdResponse =
  GetTrainingsByIdResponses[keyof GetTrainingsByIdResponses]

export type PutTrainingsByIdData = {
  body?: {
    training: {
      date: string
      /**
       * Local start time, "HH:MM" (24h)
       */
      start_time?: string
      location_name: string
      club_id?: number
      duration: number
      indoor: boolean
      surface: string
      focus?: Array<string>
      teacher_signed_id?: string
      teacher_name?: string
      /**
       * Updates the author's Activity note (only applied if activities are resynced)
       */
      activity_note?: string | null
      /**
       * Updates the author's Activity visibility (only applied if activities are resynced)
       */
      activity_visible?: boolean | null
      training_participations_attributes?: Array<{
        id?: number
        participant_type?: string
        participant_id?: number
        _destroy?: boolean
      }>
    }
  }
  headers: {
    'X-Api-Key': string
  }
  path: {
    id: number
  }
  query?: never
  url: '/trainings/{id}'
}

export type PutTrainingsByIdErrors = {
  /**
   * training not found
   */
  404: unknown
}

export type PutTrainingsByIdResponses = {
  /**
   * training updated
   */
  200: {
    success?: boolean
    training?: Training
  }
}

export type PutTrainingsByIdResponse =
  PutTrainingsByIdResponses[keyof PutTrainingsByIdResponses]

export type GetUsersUpdatesCountersData = {
  body?: never
  path?: never
  query?: never
  url: '/users/updates/counters'
}

export type GetUsersUpdatesCountersErrors = {
  /**
   * senza X-Api-Key
   */
  401: unknown
}

export type GetUsersUpdatesCountersResponses = {
  /**
   * contatori
   */
  200: UpdatesCounters
}

export type GetUsersUpdatesCountersResponse =
  GetUsersUpdatesCountersResponses[keyof GetUsersUpdatesCountersResponses]

export type GetUsersUpdatesNotificationsData = {
  body?: never
  path?: never
  query?: {
    /**
     * `next_cursor` della pagina precedente
     */
    before_id?: number
    /**
     * Default 20, massimo 50
     */
    limit?: number
  }
  url: '/users/updates/notifications'
}

export type GetUsersUpdatesNotificationsResponses = {
  /**
   * ultima pagina
   */
  200: UpdateNotificationsPage
}

export type GetUsersUpdatesNotificationsResponse =
  GetUsersUpdatesNotificationsResponses[keyof GetUsersUpdatesNotificationsResponses]

export type PutUsersUpdatesNotificationsSeenData = {
  body?: never
  path?: never
  query?: never
  url: '/users/updates/notifications/seen'
}

export type PutUsersUpdatesNotificationsSeenResponses = {
  /**
   * notifiche viste
   */
  200: UpdatesCounters
}

export type PutUsersUpdatesNotificationsSeenResponse =
  PutUsersUpdatesNotificationsSeenResponses[keyof PutUsersUpdatesNotificationsSeenResponses]

export type PutUsersUpdatesNotificationsReadAllData = {
  body?: never
  path?: never
  query?: never
  url: '/users/updates/notifications/read_all'
}

export type PutUsersUpdatesNotificationsReadAllResponses = {
  /**
   * notifiche lette
   */
  200: UpdatesCounters
}

export type PutUsersUpdatesNotificationsReadAllResponse =
  PutUsersUpdatesNotificationsReadAllResponses[keyof PutUsersUpdatesNotificationsReadAllResponses]

export type PutUsersUpdatesNotificationsByIdReadData = {
  body?: never
  path: {
    id: number
  }
  query?: never
  url: '/users/updates/notifications/{id}/read'
}

export type PutUsersUpdatesNotificationsByIdReadErrors = {
  /**
   * notifica di un altro utente, eliminata o messaggio
   */
  404: ErrorResponse
}

export type PutUsersUpdatesNotificationsByIdReadError =
  PutUsersUpdatesNotificationsByIdReadErrors[keyof PutUsersUpdatesNotificationsByIdReadErrors]

export type PutUsersUpdatesNotificationsByIdReadResponses = {
  /**
   * notifica letta
   */
  200: UpdateNotificationWithCounters
}

export type PutUsersUpdatesNotificationsByIdReadResponse =
  PutUsersUpdatesNotificationsByIdReadResponses[keyof PutUsersUpdatesNotificationsByIdReadResponses]

export type PutUsersUpdatesNotificationsByIdUnreadData = {
  body?: never
  path: {
    id: number
  }
  query?: never
  url: '/users/updates/notifications/{id}/unread'
}

export type PutUsersUpdatesNotificationsByIdUnreadErrors = {
  /**
   * notifica non trovata
   */
  404: ErrorResponse
}

export type PutUsersUpdatesNotificationsByIdUnreadError =
  PutUsersUpdatesNotificationsByIdUnreadErrors[keyof PutUsersUpdatesNotificationsByIdUnreadErrors]

export type PutUsersUpdatesNotificationsByIdUnreadResponses = {
  /**
   * notifica non letta
   */
  200: UpdateNotificationWithCounters
}

export type PutUsersUpdatesNotificationsByIdUnreadResponse =
  PutUsersUpdatesNotificationsByIdUnreadResponses[keyof PutUsersUpdatesNotificationsByIdUnreadResponses]

export type PutUsersUpdatesNotificationsReadByDestinationData = {
  body: {
    destination_url: string
  }
  path?: never
  query?: never
  url: '/users/updates/notifications/read_by_destination'
}

export type PutUsersUpdatesNotificationsReadByDestinationResponses = {
  /**
   * notifiche lette
   */
  200: UpdatesCounters
}

export type PutUsersUpdatesNotificationsReadByDestinationResponse =
  PutUsersUpdatesNotificationsReadByDestinationResponses[keyof PutUsersUpdatesNotificationsReadByDestinationResponses]

export type DeleteUsersUpdatesNotificationsByIdData = {
  body?: never
  path: {
    id: number
  }
  query?: never
  url: '/users/updates/notifications/{id}'
}

export type DeleteUsersUpdatesNotificationsByIdErrors = {
  /**
   * notifica non trovata
   */
  404: ErrorResponse
}

export type DeleteUsersUpdatesNotificationsByIdError =
  DeleteUsersUpdatesNotificationsByIdErrors[keyof DeleteUsersUpdatesNotificationsByIdErrors]

export type DeleteUsersUpdatesNotificationsByIdResponses = {
  /**
   * notifica eliminata
   */
  200: UpdatesCounters
}

export type DeleteUsersUpdatesNotificationsByIdResponse =
  DeleteUsersUpdatesNotificationsByIdResponses[keyof DeleteUsersUpdatesNotificationsByIdResponses]

export type PutUsersUpdatesNotificationsByIdRestoreData = {
  body?: never
  path: {
    id: number
  }
  query?: never
  url: '/users/updates/notifications/{id}/restore'
}

export type PutUsersUpdatesNotificationsByIdRestoreErrors = {
  /**
   * notifica non trovata
   */
  404: ErrorResponse
}

export type PutUsersUpdatesNotificationsByIdRestoreError =
  PutUsersUpdatesNotificationsByIdRestoreErrors[keyof PutUsersUpdatesNotificationsByIdRestoreErrors]

export type PutUsersUpdatesNotificationsByIdRestoreResponses = {
  /**
   * notifica ripristinata
   */
  200: UpdatesCounters
}

export type PutUsersUpdatesNotificationsByIdRestoreResponse =
  PutUsersUpdatesNotificationsByIdRestoreResponses[keyof PutUsersUpdatesNotificationsByIdRestoreResponses]

export type PutUsersUpdatesNotificationsByIdMuteData = {
  body?: never
  path: {
    id: number
  }
  query?: never
  url: '/users/updates/notifications/{id}/mute'
}

export type PutUsersUpdatesNotificationsByIdMuteErrors = {
  /**
   * notifica non trovata
   */
  404: ErrorResponse
  /**
   * tipo senza preferenza
   */
  422: ErrorResponse
}

export type PutUsersUpdatesNotificationsByIdMuteError =
  PutUsersUpdatesNotificationsByIdMuteErrors[keyof PutUsersUpdatesNotificationsByIdMuteErrors]

export type PutUsersUpdatesNotificationsByIdMuteResponses = {
  /**
   * preferenza spenta
   */
  200: UpdateNotificationMuted
}

export type PutUsersUpdatesNotificationsByIdMuteResponse =
  PutUsersUpdatesNotificationsByIdMuteResponses[keyof PutUsersUpdatesNotificationsByIdMuteResponses]

export type GetUsersUpdatesConversationsData = {
  body?: never
  path?: never
  query?: {
    /**
     * `next_cursor` della pagina precedente
     */
    before?: number
    /**
     * Default 20, massimo 50
     */
    limit?: number
    /**
     * Con profile_id, counterpart_type e counterpart_id: solo quella conversazione (chat aperta da un link o da una push), anche se non è tra le più recenti
     */
    profile_type?: string
    profile_id?: number
    counterpart_type?: string
    counterpart_id?: number
  }
  url: '/users/updates/conversations'
}

export type GetUsersUpdatesConversationsResponses = {
  /**
   * conversazioni
   */
  200: UpdatesConversationsPage
}

export type GetUsersUpdatesConversationsResponse =
  GetUsersUpdatesConversationsResponses[keyof GetUsersUpdatesConversationsResponses]

export type DeleteUsersByIdData = {
  body?: never
  path: {
    id: string
  }
  query?: never
  url: '/users/{id}'
}

export type DeleteUsersByIdErrors = {
  /**
   * unauthorized - admin required
   */
  401: unknown
}

export type DeleteUsersByIdResponses = {
  /**
   * user deleted
   */
  200: {
    success: boolean
  }
}

export type DeleteUsersByIdResponse =
  DeleteUsersByIdResponses[keyof DeleteUsersByIdResponses]

export type GetUsersByIdData = {
  body?: never
  path: {
    id: string
  }
  query?: never
  url: '/users/{id}'
}

export type GetUsersByIdResponses = {
  /**
   * user found
   */
  200: UserWithProfile
}

export type GetUsersByIdResponse =
  GetUsersByIdResponses[keyof GetUsersByIdResponses]

export type PatchUsersByIdData = {
  body?: {
    user?: {
      first_name?: string
      last_name?: string
      email?: string
    }
  }
  path: {
    id: string
  }
  query?: never
  url: '/users/{id}'
}

export type PatchUsersByIdErrors = {
  /**
   * unauthorized - admin required
   */
  401: unknown
}

export type PatchUsersByIdResponses = {
  /**
   * user updated
   */
  200: UserUpdateResponse
}

export type PatchUsersByIdResponse =
  PatchUsersByIdResponses[keyof PatchUsersByIdResponses]

export type GetUsersData = {
  body?: never
  path?: never
  query?: {
    page?: number
  }
  url: '/users'
}

export type GetUsersErrors = {
  /**
   * unauthorized - admin required
   */
  401: unknown
}

export type GetUsersResponses = {
  /**
   * users listed
   */
  200: UsersIndexResponse
}

export type GetUsersResponse = GetUsersResponses[keyof GetUsersResponses]

export type GetUsersSearchData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    /**
     * Search term: email substring, name, or exact numeric id
     */
    q?: string
    /**
     * Page number (100 users per page)
     */
    page?: number
  }
  url: '/users/search'
}

export type GetUsersSearchErrors = {
  /**
   * unauthorized - admin required
   */
  401: ErrorResponse
}

export type GetUsersSearchError =
  GetUsersSearchErrors[keyof GetUsersSearchErrors]

export type GetUsersSearchResponses = {
  /**
   * blank query: bare empty array
   */
  200: UsersSearchResponse
}

export type GetUsersSearchResponse =
  GetUsersSearchResponses[keyof GetUsersSearchResponses]

export type PostUsersAdvancedSearchData = {
  body?: UserAdvancedSearchRequest
  path?: never
  query?: never
  url: '/users/advanced-search'
}

export type PostUsersAdvancedSearchErrors = {
  /**
   * missing X-Api-Key
   */
  401: unknown
  /**
   * admin filter used by a non-admin, or premium filter without membership
   */
  403: AccessDeniedResponse
}

export type PostUsersAdvancedSearchError =
  PostUsersAdvancedSearchErrors[keyof PostUsersAdvancedSearchErrors]

export type PostUsersAdvancedSearchResponses = {
  /**
   * admin search results with summary
   */
  200: UserAdvancedSearchResponse
}

export type PostUsersAdvancedSearchResponse =
  PostUsersAdvancedSearchResponses[keyof PostUsersAdvancedSearchResponses]

export type GetUsersAdvancedSearchFiltersData = {
  body?: never
  path?: never
  query?: never
  url: '/users/advanced-search/filters'
}

export type GetUsersAdvancedSearchFiltersErrors = {
  /**
   * missing X-Api-Key
   */
  401: unknown
}

export type GetUsersAdvancedSearchFiltersResponses = {
  /**
   * filter options
   */
  200: UserAdvancedSearchFilters
}

export type GetUsersAdvancedSearchFiltersResponse =
  GetUsersAdvancedSearchFiltersResponses[keyof GetUsersAdvancedSearchFiltersResponses]

export type GetUsersByIdAdminDetailsData = {
  body?: never
  path: {
    id: string
  }
  query?: never
  url: '/users/{id}/admin-details'
}

export type GetUsersByIdAdminDetailsErrors = {
  /**
   * missing X-Api-Key, or not an admin
   */
  401: unknown
  /**
   * user not found
   */
  404: unknown
}

export type GetUsersByIdAdminDetailsResponses = {
  /**
   * user without a FIT player profile
   */
  200: UserAdminDetailsResponse
}

export type GetUsersByIdAdminDetailsResponse =
  GetUsersByIdAdminDetailsResponses[keyof GetUsersByIdAdminDetailsResponses]

export type GetUsersByIdAdminNotificationsData = {
  body?: never
  path: {
    id: string
  }
  query?: {
    /**
     * `next_cursor` of the previous page.
     */
    before_id?: number
    /**
     * Only this notifier type, e.g. `UserMatchNotifier::Notification`.
     */
    type?: string
  }
  url: '/users/{id}/admin-notifications'
}

export type GetUsersByIdAdminNotificationsErrors = {
  /**
   * missing X-Api-Key, or not an admin
   */
  401: unknown
}

export type GetUsersByIdAdminNotificationsResponses = {
  /**
   * notifications
   */
  200: UserAdminNotificationsResponse
}

export type GetUsersByIdAdminNotificationsResponse =
  GetUsersByIdAdminNotificationsResponses[keyof GetUsersByIdAdminNotificationsResponses]

export type GetUsersByIdAdminTimelineData = {
  body?: never
  path: {
    id: string
  }
  query?: {
    /**
     * `next_before` of the previous page.
     */
    before?: string
    /**
     * Default 50, max 200.
     */
    limit?: number
  }
  url: '/users/{id}/admin-timeline'
}

export type GetUsersByIdAdminTimelineErrors = {
  /**
   * missing X-Api-Key, or not an admin
   */
  401: unknown
  /**
   * user not found
   */
  404: unknown
}

export type GetUsersByIdAdminTimelineResponses = {
  /**
   * timeline
   */
  200: UserTimelineResponse
}

export type GetUsersByIdAdminTimelineResponse =
  GetUsersByIdAdminTimelineResponses[keyof GetUsersByIdAdminTimelineResponses]

export type GetUsersNearbyData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query: {
    lat: number
    lng: number
    /**
     * Search radius in km (default 20)
     */
    radius?: number
  }
  url: '/users/nearby'
}

export type GetUsersNearbyErrors = {
  /**
   * missing lat/lng
   */
  400: {
    error?: string
  }
}

export type GetUsersNearbyError =
  GetUsersNearbyErrors[keyof GetUsersNearbyErrors]

export type GetUsersNearbyResponses = {
  /**
   * nearby users found
   */
  200: {
    search_point?: {
      lat?: number
      lng?: number
    }
    radius_km?: number
    users?: Array<{
      [key: string]: unknown
    }>
  }
}

export type GetUsersNearbyResponse =
  GetUsersNearbyResponses[keyof GetUsersNearbyResponses]

export type GetUsersByIdPostsData = {
  body?: never
  headers?: {
    'X-Api-Key'?: string
  }
  path: {
    id: string
  }
  query?: {
    page?: number
  }
  url: '/users/{id}/posts'
}

export type GetUsersByIdPostsResponses = {
  /**
   * user posts found
   */
  200: PostsResponse
}

export type GetUsersByIdPostsResponse =
  GetUsersByIdPostsResponses[keyof GetUsersByIdPostsResponses]

export type GetUsersByIdPreviewData = {
  body?: never
  path: {
    id: string
  }
  query?: never
  url: '/users/{id}/preview'
}

export type GetUsersByIdPreviewResponses = {
  /**
   * user preview found
   */
  200: UserPreview
}

export type GetUsersByIdPreviewResponse =
  GetUsersByIdPreviewResponses[keyof GetUsersByIdPreviewResponses]

export type GetUsersNotificationsData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    page?: number
  }
  url: '/users/notifications'
}

export type GetUsersNotificationsResponses = {
  /**
   * notifications found
   */
  200: {
    notifications: Array<Notification>
    page: number
    total_pages: number
  }
}

export type GetUsersNotificationsResponse =
  GetUsersNotificationsResponses[keyof GetUsersNotificationsResponses]

export type GetUsersNotificationsDropdownData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    page?: number
  }
  url: '/users/notifications_dropdown'
}

export type GetUsersNotificationsDropdownResponses = {
  /**
   * notifications dropdown found
   */
  200: {
    notifications: Array<Notification>
    page: number
    total_pages: number
  }
}

export type GetUsersNotificationsDropdownResponse =
  GetUsersNotificationsDropdownResponses[keyof GetUsersNotificationsDropdownResponses]

export type PutUsersClearNotificationsByTypeData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query: {
    /**
     * The exact class name of the notification (e.g., CommentNotifier::Notification)
     */
    type: string
  }
  url: '/users/clear_notifications_by_type'
}

export type PutUsersClearNotificationsByTypeErrors = {
  /**
   * invalid notification type
   */
  400: {
    success: boolean
    error: string
  }
}

export type PutUsersClearNotificationsByTypeError =
  PutUsersClearNotificationsByTypeErrors[keyof PutUsersClearNotificationsByTypeErrors]

export type PutUsersClearNotificationsByTypeResponses = {
  /**
   * notifications cleared successfully
   */
  200: {
    success: boolean
  }
}

export type PutUsersClearNotificationsByTypeResponse =
  PutUsersClearNotificationsByTypeResponses[keyof PutUsersClearNotificationsByTypeResponses]

export type PutUsersReadNotificationData = {
  body: {
    /**
     * id of one of the current user's notifications
     */
    notification_id: number
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/users/read_notification'
}

export type PutUsersReadNotificationErrors = {
  /**
   * notification not found
   */
  404: {
    success: boolean
    error: string
  }
}

export type PutUsersReadNotificationError =
  PutUsersReadNotificationErrors[keyof PutUsersReadNotificationErrors]

export type PutUsersReadNotificationResponses = {
  /**
   * notification marked as read
   */
  200: {
    success: boolean
  }
}

export type PutUsersReadNotificationResponse =
  PutUsersReadNotificationResponses[keyof PutUsersReadNotificationResponses]

export type GetUsersFollowersListByIdData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path: {
    id: string
  }
  query?: never
  url: '/users/followers_list/{id}'
}

export type GetUsersFollowersListByIdResponses = {
  /**
   * followers list found
   */
  200: FollowersListResponse
}

export type GetUsersFollowersListByIdResponse =
  GetUsersFollowersListByIdResponses[keyof GetUsersFollowersListByIdResponses]

export type GetUsersPreferredSurfacesData = {
  body?: never
  path?: never
  query?: never
  url: '/users/preferred_surfaces'
}

export type GetUsersPreferredSurfacesResponses = {
  /**
   * preferred surfaces found
   */
  200: Array<{
    id: string
    name: string
  }>
}

export type GetUsersPreferredSurfacesResponse =
  GetUsersPreferredSurfacesResponses[keyof GetUsersPreferredSurfacesResponses]

export type GetUsersTeacherQualificationsData = {
  body?: never
  path?: never
  query?: never
  url: '/users/teacher_qualifications'
}

export type GetUsersTeacherQualificationsResponses = {
  /**
   * teacher qualifications found
   */
  200: Array<{
    id: number
    label: string
  }>
}

export type GetUsersTeacherQualificationsResponse =
  GetUsersTeacherQualificationsResponses[keyof GetUsersTeacherQualificationsResponses]

export type GetUsersTrainerQualificationsData = {
  body?: never
  path?: never
  query?: never
  url: '/users/trainer_qualifications'
}

export type GetUsersTrainerQualificationsResponses = {
  /**
   * trainer qualifications found
   */
  200: Array<{
    id: number
    label: string
  }>
}

export type GetUsersTrainerQualificationsResponse =
  GetUsersTrainerQualificationsResponses[keyof GetUsersTrainerQualificationsResponses]

export type GetUsersFacebookInfoByTokenData = {
  body?: never
  path: {
    /**
     * The user's confirmation_token
     */
    token: string
  }
  query?: never
  url: '/users/facebook-info/{token}'
}

export type GetUsersFacebookInfoByTokenResponses = {
  /**
   * facebook info found
   */
  200: {
    first_name?: string
    last_name?: string
    photo?: string
  }
}

export type GetUsersFacebookInfoByTokenResponse =
  GetUsersFacebookInfoByTokenResponses[keyof GetUsersFacebookInfoByTokenResponses]

export type GetUsersConfirmAccountByTokenData = {
  body?: never
  path: {
    /**
     * The user's confirmation_token
     */
    token: string
  }
  query?: never
  url: '/users/confirm-account/{token}'
}

export type GetUsersConfirmAccountByTokenResponses = {
  /**
   * invalid token
   */
  200: {
    success?: boolean
    errors?: Array<string>
  }
  /**
   * account confirmed
   */
  201: {
    success?: boolean
    user?: UserSession
  }
}

export type GetUsersConfirmAccountByTokenResponse =
  GetUsersConfirmAccountByTokenResponses[keyof GetUsersConfirmAccountByTokenResponses]

export type PostUsersAccountConfirmationEmailData = {
  body?: never
  path?: never
  query: {
    email: string
  }
  url: '/users/account-confirmation-email'
}

export type PostUsersAccountConfirmationEmailResponses = {
  /**
   * confirmation email sent
   */
  200: {
    success: boolean
    /**
     * Present (with success: false) when no user has this email.
     */
    errors?: Array<string>
  }
}

export type PostUsersAccountConfirmationEmailResponse =
  PostUsersAccountConfirmationEmailResponses[keyof PostUsersAccountConfirmationEmailResponses]

export type GetUsersByTokenFitPlayerIdData = {
  body?: never
  path: {
    /**
     * The user's confirmation_token
     */
    token: string
  }
  query?: never
  url: '/users/{token}/fit-player-id'
}

export type GetUsersByTokenFitPlayerIdResponses = {
  /**
   * fit player id found
   */
  200: {
    fit_player_id?: string
  }
}

export type GetUsersByTokenFitPlayerIdResponse =
  GetUsersByTokenFitPlayerIdResponses[keyof GetUsersByTokenFitPlayerIdResponses]

export type GetUsersFeedData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    page?: number
    /**
     * "true" for a global feed instead of the personalized one
     */
    all?: string
    /**
     * Class name of the masqueraded identity ("User" or "FitPlayerProfile")
     */
    type?: string
    /**
     * id of the masqueraded identity named by `type`
     */
    id?: number
  }
  url: '/users/feed'
}

export type GetUsersFeedResponses = {
  /**
   * feed found
   */
  200: PostsResponse
}

export type GetUsersFeedResponse =
  GetUsersFeedResponses[keyof GetUsersFeedResponses]

export type GetUsersFeedPostsData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    page?: number
    /**
     * "true" for a global feed instead of the personalized one
     */
    all?: string
    /**
     * Class name of the masqueraded identity ("User" or "FitPlayerProfile")
     */
    type?: string
    /**
     * id of the masqueraded identity named by `type`
     */
    id?: number
  }
  url: '/users/feed-posts'
}

export type GetUsersFeedPostsResponses = {
  /**
   * feed posts found
   */
  200: PostsResponse
}

export type GetUsersFeedPostsResponse =
  GetUsersFeedPostsResponses[keyof GetUsersFeedPostsResponses]

export type GetUsersFeedMatchesData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    page?: number
    /**
     * "true" for every match instead of just followed ones
     */
    all?: string
    type?: string
    id?: number
  }
  url: '/users/feed-matches'
}

export type GetUsersFeedMatchesResponses = {
  /**
   * feed matches found
   */
  200: FeedMatchesResponse
}

export type GetUsersFeedMatchesResponse =
  GetUsersFeedMatchesResponses[keyof GetUsersFeedMatchesResponses]

export type GetUsersFeedOffersData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    page?: number
    /**
     * "true" for every offer instead of the personalized feed
     */
    all?: string
    /**
     * Class name of the masqueraded identity ("User" or "FitPlayerProfile")
     */
    type?: string
    /**
     * id of the masqueraded identity named by `type`
     */
    id?: number
  }
  url: '/users/feed-offers'
}

export type GetUsersFeedOffersResponses = {
  /**
   * feed offers found
   */
  200: FeedOffersResponse
}

export type GetUsersFeedOffersResponse =
  GetUsersFeedOffersResponses[keyof GetUsersFeedOffersResponses]

export type GetUsersFeedNewsData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    page?: number
  }
  url: '/users/feed-news'
}

export type GetUsersFeedNewsResponses = {
  /**
   * news feed found
   */
  200: PostsResponse
}

export type GetUsersFeedNewsResponse =
  GetUsersFeedNewsResponses[keyof GetUsersFeedNewsResponses]

export type PostUsersMarketData = {
  body?: {
    page?: number
    category_id?: number
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/users/market'
}

export type PostUsersMarketResponses = {
  /**
   * market offers found
   */
  200: {
    offers: Array<OfferSearch>
    page: number
    total_pages: number
  }
}

export type PostUsersMarketResponse =
  PostUsersMarketResponses[keyof PostUsersMarketResponses]

export type GetUsersUserMatchesData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    page?: number
    /**
     * :
     * * `pending`
     * * `confirmed`
     * * `rejected`
     *
     */
    confirmation?: string
    type?: string
    result?: string
    competitive?: boolean
    indoor?: boolean
    status?: string
    surface?: string
    player_id?: number
  }
  url: '/users/user-matches'
}

export type GetUsersUserMatchesResponses = {
  /**
   * user matches found
   */
  200: UserMatchListResponse
}

export type GetUsersUserMatchesResponse =
  GetUsersUserMatchesResponses[keyof GetUsersUserMatchesResponses]

export type PostUsersUserMatchesData = {
  body?: {
    opponent_id?: number | null
    opponent_name?: string | null
    opponent_rank_string?: string | null
    date: string
    result: string
    tournament?: string | null
    board?: string | null
    won?: boolean | null
    teammate_id?: number | null
    teammate_name?: string | null
    second_opponent_id?: number | null
    second_opponent_name?: string | null
    /**
     * 'User' or 'FitPlayerProfile'
     */
    teammateable_type?: string | null
    teammateable_id?: number | null
    /**
     * 'User' or 'FitPlayerProfile'
     */
    second_opponentable_type?: string | null
    second_opponentable_id?: number | null
    authorable_type?: string | null
    authorable_id?: number | null
    activity_note?: string | null
    activity_visible?: boolean | null
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/users/user-matches'
}

export type PostUsersUserMatchesResponses = {
  /**
   * user match created from user endpoint
   */
  200: {
    success: boolean
    user_match: {
      id?: number
      date?: string
      tournament?: string | null
      board?: string | null
      result?: string
      won?: boolean | null
      not_played?: boolean
      is_pending?: boolean
      is_confirmed?: boolean
      is_rejected?: boolean
      is_editable?: boolean
      is_played?: boolean
      type?: string
      opponent_rank?: string
      is_a_double_eligible_for_calculation?: boolean
      author?: {
        [key: string]: unknown
      }
      opponent?: {
        [key: string]: unknown
      }
      teammate?: {
        [key: string]: unknown
      } | null
      second_opponent?: {
        [key: string]: unknown
      } | null
    }
  }
}

export type PostUsersUserMatchesResponse =
  PostUsersUserMatchesResponses[keyof PostUsersUserMatchesResponses]

export type PutUsersMarketConditionAcceptedData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/users/market-condition-accepted'
}

export type PutUsersMarketConditionAcceptedResponses = {
  /**
   * market conditions accepted
   */
  200: {
    success?: boolean
  }
}

export type PutUsersMarketConditionAcceptedResponse =
  PutUsersMarketConditionAcceptedResponses[keyof PutUsersMarketConditionAcceptedResponses]

export type PostUsersCheckFitClubIdData = {
  body?: {
    fit_club_id?: string
  }
  headers?: {
    'X-Api-Key'?: string
  }
  path?: never
  query?: never
  url: '/users/check-fit-club-id'
}

export type PostUsersCheckFitClubIdResponses = {
  /**
   * fit club id checked
   */
  200: {
    success?: boolean
    error?: string | null
    reassign_old_fit_club_id?: boolean | null
  }
}

export type PostUsersCheckFitClubIdResponse =
  PostUsersCheckFitClubIdResponses[keyof PostUsersCheckFitClubIdResponses]

export type GetUsersCheckStatusData = {
  body?: never
  headers?: {
    'X-Api-Key'?: string
  }
  path?: never
  query?: never
  url: '/users/check-status'
}

export type GetUsersCheckStatusResponses = {
  /**
   * user sessions and status checked
   */
  200: {
    success?: boolean
  }
}

export type GetUsersCheckStatusResponse =
  GetUsersCheckStatusResponses[keyof GetUsersCheckStatusResponses]

export type PostUsersSyncContactsData = {
  body?: {
    /**
     * Phone numbers from the device address book
     */
    phones?: Array<string>
    /**
     * Email addresses from the device address book
     */
    emails?: Array<string>
  }
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: never
  url: '/users/sync_contacts'
}

export type PostUsersSyncContactsErrors = {
  /**
   * unauthorized without api key
   */
  401: unknown
}

export type PostUsersSyncContactsResponses = {
  /**
   * no valid contacts provided
   */
  200: {
    success: boolean
    followed: number
  }
}

export type PostUsersSyncContactsResponse =
  PostUsersSyncContactsResponses[keyof PostUsersSyncContactsResponses]

export type PostUsersByIdPremiumizeData = {
  body?: {
    /**
     * Redeemable code: random (case-insensitive) or, for older codes, "<TOKEN_SALT><premium_token_id>"
     */
    code?: string
  }
  path: {
    /**
     * Present in the URL but currently unused by the controller — the token always applies to the caller
     */
    id: string
  }
  query?: never
  url: '/users/{id}/premiumize'
}

export type PostUsersByIdPremiumizeResponses = {
  /**
   * invalid or already consumed token
   */
  200: {
    success: boolean
    premium_expiration?: string | null
    errors?: string | null
  }
}

export type PostUsersByIdPremiumizeResponse =
  PostUsersByIdPremiumizeResponses[keyof PostUsersByIdPremiumizeResponses]

export type PutUsersByIdBanData = {
  body?: never
  path: {
    id: string
  }
  query?: {
    /**
     * Internal moderation note explaining the ban
     */
    ban_notes?: string
  }
  url: '/users/{id}/ban'
}

export type PutUsersByIdBanErrors = {
  /**
   * unauthorized - admin required
   */
  401: unknown
}

export type PutUsersByIdBanResponses = {
  /**
   * user banned
   */
  200: {
    success: boolean
  }
}

export type PutUsersByIdBanResponse =
  PutUsersByIdBanResponses[keyof PutUsersByIdBanResponses]

export type PutUsersByIdUnbanData = {
  body?: never
  path: {
    id: string
  }
  query?: never
  url: '/users/{id}/unban'
}

export type PutUsersByIdUnbanErrors = {
  /**
   * unauthorized - admin required
   */
  401: unknown
}

export type PutUsersByIdUnbanResponses = {
  /**
   * user unbanned
   */
  200: {
    success: boolean
  }
}

export type PutUsersByIdUnbanResponse =
  PutUsersByIdUnbanResponses[keyof PutUsersByIdUnbanResponses]

export type PutUsersByIdMarkAsHiddenResultsData = {
  body?: never
  path: {
    id: string
  }
  query?: never
  url: '/users/{id}/mark-as-hidden-results'
}

export type PutUsersByIdMarkAsHiddenResultsErrors = {
  /**
   * unauthorized - admin required
   */
  401: unknown
}

export type PutUsersByIdMarkAsHiddenResultsResponses = {
  /**
   * hidden results toggled
   */
  200: {
    success: boolean
  }
}

export type PutUsersByIdMarkAsHiddenResultsResponse =
  PutUsersByIdMarkAsHiddenResultsResponses[keyof PutUsersByIdMarkAsHiddenResultsResponses]

export type PutUsersByIdMarkAsPremiumUserData = {
  body?: never
  path: {
    id: string
  }
  query?: never
  url: '/users/{id}/mark-as-premium-user'
}

export type PutUsersByIdMarkAsPremiumUserErrors = {
  /**
   * unauthorized - admin required
   */
  401: unknown
}

export type PutUsersByIdMarkAsPremiumUserResponses = {
  /**
   * premium status toggled
   */
  200: {
    success: boolean
  }
}

export type PutUsersByIdMarkAsPremiumUserResponse =
  PutUsersByIdMarkAsPremiumUserResponses[keyof PutUsersByIdMarkAsPremiumUserResponses]

export type PutUsersByIdMarkAsPremiumUserWithExpirationDateData = {
  body?: never
  path: {
    id: string
  }
  query: {
    /**
     * ISO8601 datetime when premium status expires
     */
    date: string
  }
  url: '/users/{id}/mark-as-premium-user-with-expiration-date'
}

export type PutUsersByIdMarkAsPremiumUserWithExpirationDateErrors = {
  /**
   * unauthorized - admin required
   */
  401: unknown
}

export type PutUsersByIdMarkAsPremiumUserWithExpirationDateResponses = {
  /**
   * user marked as premium with expiration date
   */
  200: {
    success: boolean
  }
}

export type PutUsersByIdMarkAsPremiumUserWithExpirationDateResponse =
  PutUsersByIdMarkAsPremiumUserWithExpirationDateResponses[keyof PutUsersByIdMarkAsPremiumUserWithExpirationDateResponses]

export type PutUsersByIdSetPremiumExpirationDateData = {
  body?: never
  path: {
    id: string
  }
  query: {
    /**
     * ISO8601 datetime when premium status expires
     */
    date: string
  }
  url: '/users/{id}/set-premium-expiration-date'
}

export type PutUsersByIdSetPremiumExpirationDateErrors = {
  /**
   * unauthorized - admin required
   */
  401: unknown
}

export type PutUsersByIdSetPremiumExpirationDateResponses = {
  /**
   * premium expiration date set
   */
  200: {
    success: boolean
  }
}

export type PutUsersByIdSetPremiumExpirationDateResponse =
  PutUsersByIdSetPremiumExpirationDateResponses[keyof PutUsersByIdSetPremiumExpirationDateResponses]

export type GetAppVersionData = {
  body?: never
  path?: never
  query?: never
  url: '/app-version'
}

export type GetAppVersionResponses = {
  /**
   * Minimum supported version, keyed by platform.
   */
  200: {
    ios?: {
      /**
       * Lowest semver (x.y.z) still allowed to run without a forced update.
       */
      min?: string
    }
    android?: {
      /**
       * Lowest semver (x.y.z) still allowed to run without a forced update.
       */
      min?: string
    }
  }
}

export type GetAppVersionResponse =
  GetAppVersionResponses[keyof GetAppVersionResponses]

export type GetBonusesData = {
  body?: never
  path?: never
  query?: {
    /**
     * Numero pagina
     */
    page?: number
  }
  url: '/bonuses'
}

export type GetBonusesErrors = {
  /**
   * profilo non trovato
   */
  404: unknown
}

export type GetBonusesResponses = {
  /**
   * lista bonus restituita
   */
  200: BonusListResponse
}

export type GetBonusesResponse = GetBonusesResponses[keyof GetBonusesResponses]

export type PostBonusesData = {
  body?: {
    bonus: {
      name?: string | null
      value: number
      year: number
    }
  }
  path?: never
  query?: never
  url: '/bonuses'
}

export type PostBonusesErrors = {
  /**
   * parametri non validi
   */
  404: unknown
}

export type PostBonusesResponses = {
  /**
   * bonus creato
   */
  200: BonusWithOutcomeResponse
}

export type PostBonusesResponse =
  PostBonusesResponses[keyof PostBonusesResponses]

export type DeleteBonusesByIdData = {
  body?: never
  path: {
    /**
     * ID del bonus
     */
    id: number
  }
  query?: never
  url: '/bonuses/{id}'
}

export type DeleteBonusesByIdErrors = {
  /**
   * bonus o profilo non trovato
   */
  404: unknown
}

export type DeleteBonusesByIdResponses = {
  /**
   * bonus eliminato
   */
  200: BonusWithOutcomeResponse
}

export type DeleteBonusesByIdResponse =
  DeleteBonusesByIdResponses[keyof DeleteBonusesByIdResponses]

export type PostPostsByPostIdCommentsData = {
  body?: {
    masqueraded_type: string
    masqueraded_id: number
    content: string
    parent_id?: number | null
  }
  path: {
    /**
     * ID of the post being commented on
     */
    post_id: number
  }
  query?: never
  url: '/posts/{post_id}/comments'
}

export type PostPostsByPostIdCommentsResponses = {
  /**
   * parametri non validi
   */
  200: CommentMutationResponse
}

export type PostPostsByPostIdCommentsResponse =
  PostPostsByPostIdCommentsResponses[keyof PostPostsByPostIdCommentsResponses]

export type DeletePostsByPostIdCommentsByIdData = {
  body?: never
  path: {
    post_id: number
    id: number
  }
  query?: never
  url: '/posts/{post_id}/comments/{id}'
}

export type DeletePostsByPostIdCommentsByIdErrors = {
  /**
   * commento non trovato
   */
  404: unknown
}

export type DeletePostsByPostIdCommentsByIdResponses = {
  /**
   * commento eliminato
   */
  200: CommentWithOutcomeResponse
}

export type DeletePostsByPostIdCommentsByIdResponse =
  DeletePostsByPostIdCommentsByIdResponses[keyof DeletePostsByPostIdCommentsByIdResponses]

export type PatchPostsByPostIdCommentsByIdData = {
  body?: {
    content: string
  }
  path: {
    post_id: number
    id: number
  }
  query?: never
  url: '/posts/{post_id}/comments/{id}'
}

export type PatchPostsByPostIdCommentsByIdResponses = {
  /**
   * parametri non validi
   */
  200: CommentMutationResponse
}

export type PatchPostsByPostIdCommentsByIdResponse =
  PatchPostsByPostIdCommentsByIdResponses[keyof PatchPostsByPostIdCommentsByIdResponses]

export type GetCommunityFeedData = {
  body?: never
  headers: {
    'X-Api-Key': string
  }
  path?: never
  query?: {
    /**
     * Ambito del feed: all (default), club, followers:
     * * `all`
     * * `club`
     * * `followers`
     *
     */
    scope?: string
    page?: number
  }
  url: '/community/feed'
}

export type GetCommunityFeedErrors = {
  /**
   * Non autorizzato
   */
  401: unknown
}

export type GetCommunityFeedResponses = {
  /**
   * Feed della community restituito
   */
  200: PostsResponse
}

export type GetCommunityFeedResponse =
  GetCommunityFeedResponses[keyof GetCommunityFeedResponses]

export type PostContactsData = {
  body: {
    /**
     * Support-request fields. May also be sent flat, at the top level.
     */
    info: {
      email: string
      name?: string
      player_name?: string
      problem?: string
      message?: string
      match?: string
      tournament_link?: string
    }
  }
  path?: never
  query?: never
  url: '/contacts'
}

export type PostContactsResponses = {
  /**
   * Parametri non validi
   */
  200: SuccessResponse
}

export type PostContactsResponse =
  PostContactsResponses[keyof PostContactsResponses]

export type GetCustomBannersAllData = {
  body?: never
  path?: never
  query?: never
  url: '/custom_banners/all'
}

export type GetCustomBannersAllResponses = {
  /**
   * Lista custom banners
   */
  200: Array<CustomBanner>
}

export type GetCustomBannersAllResponse =
  GetCustomBannersAllResponses[keyof GetCustomBannersAllResponses]

export type GetCustomBannersData = {
  body?: never
  path?: never
  query?: {
    /**
     * Numero pagina (default 1)
     */
    page?: number
  }
  url: '/custom_banners'
}

export type GetCustomBannersResponses = {
  /**
   * Lista paginata custom banners
   */
  200: CustomBannersIndexResponse
}

export type GetCustomBannersResponse =
  GetCustomBannersResponses[keyof GetCustomBannersResponses]

export type PostCustomBannersData = {
  body?: CustomBannerInput
  path?: never
  query?: never
  url: '/custom_banners'
}

export type PostCustomBannersErrors = {
  /**
   * Utente non staff
   */
  401: unknown
}

export type PostCustomBannersResponses = {
  /**
   * Parametri non validi
   */
  200: CustomBannerMutationResponse
}

export type PostCustomBannersResponse =
  PostCustomBannersResponses[keyof PostCustomBannersResponses]

export type DeleteCustomBannersByIdData = {
  body?: never
  path: {
    /**
     * ID del CustomBanner
     */
    id: number
  }
  query?: never
  url: '/custom_banners/{id}'
}

export type DeleteCustomBannersByIdErrors = {
  /**
   * Custom banner non trovato
   */
  404: unknown
}

export type DeleteCustomBannersByIdResponses = {
  /**
   * Custom banner eliminato
   */
  200: CustomBannerWithOutcomeResponse
}

export type DeleteCustomBannersByIdResponse =
  DeleteCustomBannersByIdResponses[keyof DeleteCustomBannersByIdResponses]

export type GetCustomBannersByIdData = {
  body?: never
  path: {
    /**
     * ID del CustomBanner
     */
    id: number
  }
  query?: never
  url: '/custom_banners/{id}'
}

export type GetCustomBannersByIdErrors = {
  /**
   * Custom banner non trovato
   */
  404: unknown
}

export type GetCustomBannersByIdResponses = {
  /**
   * Custom banner trovato
   */
  200: CustomBanner
}

export type GetCustomBannersByIdResponse =
  GetCustomBannersByIdResponses[keyof GetCustomBannersByIdResponses]

export type PatchCustomBannersByIdData = {
  body?: CustomBannerInput
  path: {
    /**
     * ID del CustomBanner
     */
    id: number
  }
  query?: never
  url: '/custom_banners/{id}'
}

export type PatchCustomBannersByIdErrors = {
  /**
   * Custom banner non trovato
   */
  404: unknown
}

export type PatchCustomBannersByIdResponses = {
  /**
   * Custom banner aggiornato
   */
  200: CustomBannerMutationResponse
}

export type PatchCustomBannersByIdResponse =
  PatchCustomBannersByIdResponses[keyof PatchCustomBannersByIdResponses]

export type PutCustomBannersByIdData = {
  body?: CustomBannerInput
  path: {
    /**
     * ID del CustomBanner
     */
    id: number
  }
  query?: never
  url: '/custom_banners/{id}'
}

export type PutCustomBannersByIdErrors = {
  /**
   * Custom banner non trovato
   */
  404: unknown
}

export type PutCustomBannersByIdResponses = {
  /**
   * Custom banner aggiornato
   */
  200: CustomBannerMutationResponse
}

export type PutCustomBannersByIdResponse =
  PutCustomBannersByIdResponses[keyof PutCustomBannersByIdResponses]

export type PostDebugSlackData = {
  body?: {
    /**
     * Canale Slack di destinazione (es. #debug-frontend)
     */
    channel: string
    /**
     * Qualsiasi oggetto JSON da ispezionare
     */
    payload: {
      [key: string]: unknown
    }
    /**
     * Tag opzionale per categorizzare il log
     */
    label?: string
  }
  path?: never
  query?: never
  url: '/debug/slack'
}

export type PostDebugSlackErrors = {
  /**
   * token mancante o non valido
   */
  401: unknown
  /**
   * errore Slack (es. canale non trovato o token mancante)
   */
  422: unknown
}

export type PostDebugSlackResponses = {
  /**
   * payload inviato con successo
   */
  200: unknown
}

export type PostEndorsementsData = {
  body?: {
    fit_player_profile_id: number
    endorsement_type_id: number
  }
  path?: never
  query?: never
  url: '/endorsements'
}

export type PostEndorsementsResponses = {
  /**
   * Parametri non validi
   */
  200: EndorsementMutationResponse
}

export type PostEndorsementsResponse =
  PostEndorsementsResponses[keyof PostEndorsementsResponses]

export type DeleteEndorsementsByIdData = {
  body?: never
  path: {
    /**
     * ID dell'endorsement
     */
    id: number
  }
  query?: never
  url: '/endorsements/{id}'
}

export type DeleteEndorsementsByIdErrors = {
  /**
   * Endorsement non trovato
   */
  404: unknown
}

export type DeleteEndorsementsByIdResponses = {
  /**
   * Endorsement eliminato
   */
  200: SuccessResponse
}

export type DeleteEndorsementsByIdResponse =
  DeleteEndorsementsByIdResponses[keyof DeleteEndorsementsByIdResponses]

export type GetFeedNewsData = {
  body?: never
  path?: never
  query?: {
    /**
     * Page number, 10 results per page (default 1)
     */
    page?: number
  }
  url: '/feed_news'
}

export type GetFeedNewsResponses = {
  /**
   * Feed news restituito
   */
  200: PostsResponse
}

export type GetFeedNewsResponse =
  GetFeedNewsResponses[keyof GetFeedNewsResponses]

export type GetFitPlayerProfileRequestsData = {
  body?: never
  path?: never
  query?: {
    /**
     * Numero pagina (100 richieste per pagina)
     */
    page?: number
    /**
     * Filtra per stato della richiesta:
     * * `pending`
     * * `approved`
     * * `rejected`
     *
     */
    status?: string
    /**
     * Ricerca testuale (case-insensitive) nelle note di approvazione/rifiuto
     */
    notes?: string
  }
  url: '/fit-player-profile-requests'
}

export type GetFitPlayerProfileRequestsErrors = {
  /**
   * Utente non autorizzato (non admin/banner_manager/censor_manager)
   */
  401: unknown
}

export type GetFitPlayerProfileRequestsResponses = {
  /**
   * Lista richieste restituita
   */
  200: FitPlayerProfileRequestsIndexResponse
}

export type GetFitPlayerProfileRequestsResponse =
  GetFitPlayerProfileRequestsResponses[keyof GetFitPlayerProfileRequestsResponses]

export type PutFitPlayerProfileRequestsByIdApproveData = {
  body?: {
    /**
     * Note di approvazione
     */
    notes?: string
  }
  path: {
    /**
     * ID della richiesta
     */
    id: number
  }
  query?: never
  url: '/fit-player-profile-requests/{id}/approve'
}

export type PutFitPlayerProfileRequestsByIdApproveErrors = {
  /**
   * Utente non autorizzato (non admin/banner_manager/censor_manager)
   */
  401: SimpleErrorResponse
  /**
   * Richiesta non trovata
   */
  404: unknown
}

export type PutFitPlayerProfileRequestsByIdApproveError =
  PutFitPlayerProfileRequestsByIdApproveErrors[keyof PutFitPlayerProfileRequestsByIdApproveErrors]

export type PutFitPlayerProfileRequestsByIdApproveResponses = {
  /**
   * Richiesta approvata
   */
  200: FitPlayerProfileRequest
}

export type PutFitPlayerProfileRequestsByIdApproveResponse =
  PutFitPlayerProfileRequestsByIdApproveResponses[keyof PutFitPlayerProfileRequestsByIdApproveResponses]

export type PutFitPlayerProfileRequestsByIdRejectData = {
  body?: {
    /**
     * Note di rifiuto
     */
    notes?: string
  }
  path: {
    /**
     * ID della richiesta
     */
    id: number
  }
  query?: never
  url: '/fit-player-profile-requests/{id}/reject'
}

export type PutFitPlayerProfileRequestsByIdRejectErrors = {
  /**
   * Utente non autorizzato (non admin/banner_manager/censor_manager)
   */
  401: SimpleErrorResponse
  /**
   * Richiesta non trovata
   */
  404: unknown
}

export type PutFitPlayerProfileRequestsByIdRejectError =
  PutFitPlayerProfileRequestsByIdRejectErrors[keyof PutFitPlayerProfileRequestsByIdRejectErrors]

export type PutFitPlayerProfileRequestsByIdRejectResponses = {
  /**
   * Richiesta rifiutata
   */
  200: FitPlayerProfileRequest
}

export type PutFitPlayerProfileRequestsByIdRejectResponse =
  PutFitPlayerProfileRequestsByIdRejectResponses[keyof PutFitPlayerProfileRequestsByIdRejectResponses]

export type GetFitPlayerProfilesByIdConnectionsData = {
  body?: never
  path: {
    /**
     * ID del giocatore target
     */
    id: number
  }
  query?: never
  url: '/fit-player-profiles/{id}/connections'
}

export type GetFitPlayerProfilesByIdConnectionsErrors = {
  /**
   * Accesso negato - Solo utenti premium
   */
  403: unknown
  /**
   * Profilo non trovato
   */
  404: unknown
}

export type GetFitPlayerProfilesByIdConnectionsResponses = {
  /**
   * Connessioni restituite
   */
  200: ConnectionsResponse
}

export type GetFitPlayerProfilesByIdConnectionsResponse =
  GetFitPlayerProfilesByIdConnectionsResponses[keyof GetFitPlayerProfilesByIdConnectionsResponses]

export type GetFitPlayerProfilesByIdConnectionsReducedData = {
  body?: never
  path: {
    /**
     * ID del giocatore target
     */
    id: number
  }
  query?: never
  url: '/fit-player-profiles/{id}/connections_reduced'
}

export type GetFitPlayerProfilesByIdConnectionsReducedErrors = {
  /**
   * Profilo non trovato
   */
  404: unknown
}

export type GetFitPlayerProfilesByIdConnectionsReducedResponses = {
  /**
   * Contatori restituiti con successo
   */
  200: ConnectionsReducedResponse
}

export type GetFitPlayerProfilesByIdConnectionsReducedResponse =
  GetFitPlayerProfilesByIdConnectionsReducedResponses[keyof GetFitPlayerProfilesByIdConnectionsReducedResponses]

export type GetFitPlayerProfilesByIdMatchSimulatorData = {
  body?: never
  path: {
    /**
     * ID del giocatore
     */
    id: number
  }
  query: {
    /**
     * ID dell'avversario
     */
    opponent_id: number
    /**
     * Superficie simulata (clay_court, hard_court, grass_court, etc.)
     */
    simulated_surface: string
    /**
     * Tipo di match (torneo, competizione_squadre)
     */
    simulated_competition_type: string
    /**
     * Tipo di campo (indoor, outdoor)
     */
    simulated_court_type: string
    /**
     * Anno per il calcolo delle statistiche
     */
    year?: number
  }
  url: '/fit-player-profiles/{id}/match-simulator'
}

export type GetFitPlayerProfilesByIdMatchSimulatorErrors = {
  /**
   * Accesso negato - Solo utenti premium
   */
  403: unknown
  /**
   * Avversario non trovato
   */
  404: unknown
}

export type GetFitPlayerProfilesByIdMatchSimulatorResponses = {
  /**
   * Simulazione match restituita
   */
  200: MatchSimulatorResponse
}

export type GetFitPlayerProfilesByIdMatchSimulatorResponse =
  GetFitPlayerProfilesByIdMatchSimulatorResponses[keyof GetFitPlayerProfilesByIdMatchSimulatorResponses]

export type GetFitPlayerProfilesSearchForMergeByQData = {
  body?: never
  path: {
    /**
     * Termine di ricerca
     */
    q: string
  }
  query?: never
  url: '/fit-player-profiles/search-for-merge/{q}'
}

export type GetFitPlayerProfilesSearchForMergeByQResponses = {
  /**
   * Risultati trovati
   */
  200: Array<PlayerMergeSearch>
}

export type GetFitPlayerProfilesSearchForMergeByQResponse =
  GetFitPlayerProfilesSearchForMergeByQResponses[keyof GetFitPlayerProfilesSearchForMergeByQResponses]

export type PostFollowsData = {
  body?: never
  path?: never
  query: {
    /**
     * ID dell'entità da seguire
     */
    followable_id: number
    /**
     * Classe del modello da seguire, es. "User" o "FitPlayerProfile"
     */
    followable_type: string
  }
  url: '/follows'
}

export type PostFollowsResponses = {
  /**
   * Parametri non validi
   */
  200: FollowResponse
}

export type PostFollowsResponse =
  PostFollowsResponses[keyof PostFollowsResponses]

export type PostRemoveFollowData = {
  body?: never
  path?: never
  query: {
    /**
     * ID dell'entità da smettere di seguire
     */
    followable_id: number
    /**
     * Classe del modello, es. "User" o "FitPlayerProfile"
     */
    followable_type: string
  }
  url: '/remove-follow'
}

export type PostRemoveFollowErrors = {
  /**
   * Parametri non validi
   */
  404: unknown
}

export type PostRemoveFollowResponses = {
  /**
   * Follow rimosso
   */
  200: FollowResponse
}

export type PostRemoveFollowResponse =
  PostRemoveFollowResponses[keyof PostRemoveFollowResponses]

export type GetMessagesConversationData = {
  body?: never
  path?: never
  query: {
    /**
     * Tipo profilo (User, FitPlayerProfile, ecc.)
     */
    type: string
    /**
     * ID profilo
     */
    id: number
    /**
     * Tipo dell'interlocutore, per filtrare a una singola conversazione
     */
    counterpart_type?: string
    /**
     * ID dell'interlocutore, per filtrare a una singola conversazione
     */
    counterpart_id?: number
    /**
     * Pagina Aggiornamenti: utente e giocatore della stessa persona sono una conversazione sola, da tutte e due le parti
     */
    merged?: boolean
  }
  url: '/messages/conversation'
}

export type GetMessagesConversationResponses = {
  /**
   * Conversazione restituita
   */
  200: MessagesConversationResponse
}

export type GetMessagesConversationResponse =
  GetMessagesConversationResponses[keyof GetMessagesConversationResponses]

export type PostMessagesData = {
  body?: {
    /**
     * 'User' (o 'Talker', normalizzato a 'User')
     */
    author_type: string
    author_id: number
    recipient_type: string
    recipient_id: number
    content: string
  }
  path?: never
  query?: never
  url: '/messages'
}

export type PostMessagesErrors = {
  /**
   * Destinatario: giocatore FIT non rivendicato
   */
  422: MessageCreateErrorResponse
}

export type PostMessagesError = PostMessagesErrors[keyof PostMessagesErrors]

export type PostMessagesResponses = {
  /**
   * Parametri non validi
   */
  200: {
    success?: boolean | null
    message?: MessagesConversationItem | null
  }
}

export type PostMessagesResponse =
  PostMessagesResponses[keyof PostMessagesResponses]

export type PutMessagesByIdBlockData = {
  body?: never
  path: {
    /**
     * ID del messaggio
     */
    id: number
  }
  query?: never
  url: '/messages/{id}/block'
}

export type PutMessagesByIdBlockResponses = {
  /**
   * Messaggio bloccato
   */
  200: MessagesBlockResponse
}

export type PutMessagesByIdBlockResponse =
  PutMessagesByIdBlockResponses[keyof PutMessagesByIdBlockResponses]

export type GetMessagesBackofficeData = {
  body?: never
  path?: never
  query?: {
    /**
     * Numero pagina (default 1)
     */
    page?: number
    /**
     * Risultati per pagina (default 50)
     */
    per_page?: number
  }
  url: '/messages/backoffice'
}

export type GetMessagesBackofficeErrors = {
  /**
   * Utente non admin
   */
  401: unknown
}

export type GetMessagesBackofficeResponses = {
  /**
   * Lista messaggi restituita
   */
  200: MessagesBackofficeResponse
}

export type GetMessagesBackofficeResponse =
  GetMessagesBackofficeResponses[keyof GetMessagesBackofficeResponses]

export type GetUsersMessagesCounterpartsData = {
  body?: never
  path?: never
  query: {
    /**
     * Filtra i counterpart per nome
     */
    name?: string
    /**
     * Tipo profilo (User, FitPlayerProfile, ecc.)
     */
    type: string
    /**
     * ID profilo
     */
    id: number
  }
  url: '/users/messages-counterparts'
}

export type GetUsersMessagesCounterpartsResponses = {
  /**
   * Lista counterpart restituita
   */
  200: MessagesCounterpartResponse
}

export type GetUsersMessagesCounterpartsResponse =
  GetUsersMessagesCounterpartsResponses[keyof GetUsersMessagesCounterpartsResponses]

export type GetPositionedBannersBySlotData = {
  body?: never
  path: {
    /**
     * Nome dello slot (es. home_top)
     */
    slot: string
  }
  query?: never
  url: '/positioned-banners/{slot}'
}

export type GetPositionedBannersBySlotErrors = {
  /**
   * Banner non trovato
   */
  404: unknown
}

export type GetPositionedBannersBySlotResponses = {
  /**
   * Banner posizionato trovato
   */
  200: PositionedBannerSlotResponse
}

export type GetPositionedBannersBySlotResponse =
  GetPositionedBannersBySlotResponses[keyof GetPositionedBannersBySlotResponses]

export type GetPositionedBannersBySlotAllData = {
  body?: never
  path: {
    /**
     * Nome dello slot (es. home_top)
     */
    slot: string
  }
  query?: never
  url: '/positioned-banners/{slot}/all'
}

export type GetPositionedBannersBySlotAllErrors = {
  /**
   * Banner non trovato
   */
  404: unknown
}

export type GetPositionedBannersBySlotAllResponses = {
  /**
   * Banners posizionati trovati
   */
  200: PositionedBannerAllResponse
}

export type GetPositionedBannersBySlotAllResponse =
  GetPositionedBannersBySlotAllResponses[keyof GetPositionedBannersBySlotAllResponses]

export type GetPositionedBannersData = {
  body?: never
  path?: never
  query?: {
    /**
     * Numero pagina (default 1)
     */
    page?: number
  }
  url: '/positioned-banners'
}

export type GetPositionedBannersResponses = {
  /**
   * Lista paginata banner posizionati
   */
  200: {
    data: Array<PositionedBannerResponse>
    page: number
    total_pages: number
  }
}

export type GetPositionedBannersResponse =
  GetPositionedBannersResponses[keyof GetPositionedBannersResponses]

export type PostPositionedBannersData = {
  body?: PositionedBannerInput
  path?: never
  query?: never
  url: '/positioned-banners'
}

export type PostPositionedBannersErrors = {
  /**
   * utente non staff
   */
  401: unknown
}

export type PostPositionedBannersResponses = {
  /**
   * Banner posizionato creato
   */
  200: {
    success: boolean
    positioned_banner: PositionedBannerResponse
  }
}

export type PostPositionedBannersResponse =
  PostPositionedBannersResponses[keyof PostPositionedBannersResponses]

export type GetPositionedBannersByIdByIdData = {
  body?: never
  path: {
    /**
     * ID del PositionedBanner
     */
    id: number
  }
  query?: never
  url: '/positioned-banners/by-id/{id}'
}

export type GetPositionedBannersByIdByIdErrors = {
  /**
   * Banner non trovato
   */
  404: unknown
}

export type GetPositionedBannersByIdByIdResponses = {
  /**
   * Banner posizionato trovato
   */
  200: PositionedBannerResponse
}

export type GetPositionedBannersByIdByIdResponse =
  GetPositionedBannersByIdByIdResponses[keyof GetPositionedBannersByIdByIdResponses]

export type DeletePositionedBannersByIdData = {
  body?: never
  path: {
    /**
     * ID del PositionedBanner
     */
    id: number
  }
  query?: never
  url: '/positioned-banners/{id}'
}

export type DeletePositionedBannersByIdResponses = {
  /**
   * Banner posizionato eliminato
   */
  200: {
    success: boolean
    positioned_banner: PositionedBannerResponse
  }
}

export type DeletePositionedBannersByIdResponse =
  DeletePositionedBannersByIdResponses[keyof DeletePositionedBannersByIdResponses]

export type PutPositionedBannersByIdData = {
  body?: PositionedBannerInput
  path: {
    /**
     * ID del PositionedBanner
     */
    id: number
  }
  query?: never
  url: '/positioned-banners/{id}'
}

export type PutPositionedBannersByIdResponses = {
  /**
   * Banner posizionato aggiornato
   */
  200: {
    success: boolean
    positioned_banner: PositionedBannerResponse
  }
}

export type PutPositionedBannersByIdResponse =
  PutPositionedBannersByIdResponses[keyof PutPositionedBannersByIdResponses]

export type GetPremiumTokensData = {
  body?: never
  path?: never
  query?: {
    /**
     * Numero pagina
     */
    page?: number
  }
  url: '/premium_tokens'
}

export type GetPremiumTokensErrors = {
  /**
   * X-Api-Key mancante o utente non staff
   */
  401: unknown
}

export type GetPremiumTokensResponses = {
  /**
   * lista premium tokens restituita
   */
  200: PremiumTokensIndexResponse
}

export type GetPremiumTokensResponse =
  GetPremiumTokensResponses[keyof GetPremiumTokensResponses]

export type PostPremiumTokensData = {
  body?: PremiumTokenInput
  path?: never
  query?: never
  url: '/premium_tokens'
}

export type PostPremiumTokensErrors = {
  /**
   * X-Api-Key mancante o utente non staff
   */
  401: unknown
}

export type PostPremiumTokensResponses = {
  /**
   * parametri non validi
   */
  200: PremiumTokenWithOutcomeResponse
}

export type PostPremiumTokensResponse =
  PostPremiumTokensResponses[keyof PostPremiumTokensResponses]

export type DeletePremiumTokensByIdData = {
  body?: never
  path: {
    id: number
  }
  query?: never
  url: '/premium_tokens/{id}'
}

export type DeletePremiumTokensByIdErrors = {
  /**
   * X-Api-Key mancante o utente non staff
   */
  401: unknown
  /**
   * premium token non trovato
   */
  404: unknown
}

export type DeletePremiumTokensByIdResponses = {
  /**
   * premium token eliminato
   */
  200: PremiumTokenWithOutcomeResponse
}

export type DeletePremiumTokensByIdResponse =
  DeletePremiumTokensByIdResponses[keyof DeletePremiumTokensByIdResponses]

export type GetPremiumTokensByIdData = {
  body?: never
  path: {
    id: number
  }
  query?: never
  url: '/premium_tokens/{id}'
}

export type GetPremiumTokensByIdErrors = {
  /**
   * X-Api-Key mancante o utente non staff
   */
  401: unknown
  /**
   * premium token non trovato
   */
  404: unknown
}

export type GetPremiumTokensByIdResponses = {
  /**
   * premium token restituito
   */
  200: PremiumToken
}

export type GetPremiumTokensByIdResponse =
  GetPremiumTokensByIdResponses[keyof GetPremiumTokensByIdResponses]

export type PutPremiumTokensByIdData = {
  body?: PremiumTokenInput
  path: {
    id: number
  }
  query?: never
  url: '/premium_tokens/{id}'
}

export type PutPremiumTokensByIdErrors = {
  /**
   * X-Api-Key mancante o utente non staff
   */
  401: unknown
  /**
   * premium token non trovato
   */
  404: unknown
}

export type PutPremiumTokensByIdResponses = {
  /**
   * premium token aggiornato
   */
  200: PremiumTokenWithOutcomeResponse
}

export type PutPremiumTokensByIdResponse =
  PutPremiumTokensByIdResponses[keyof PutPremiumTokensByIdResponses]

export type PostPremiumTokensMembershipEmailData = {
  body?: never
  path?: never
  query: {
    /**
     * Recipient email address
     */
    email: string
    /**
     * Premium token id used to build the activation link
     */
    id: string
  }
  url: '/premium_tokens/membership_email'
}

export type PostPremiumTokensMembershipEmailErrors = {
  /**
   * X-Api-Key mancante o utente non staff
   */
  401: unknown
}

export type PostPremiumTokensMembershipEmailResponses = {
  /**
   * parametri mancanti
   */
  200: MembershipEmailResponse
}

export type PostPremiumTokensMembershipEmailResponse =
  PostPremiumTokensMembershipEmailResponses[keyof PostPremiumTokensMembershipEmailResponses]

export type PostPremiumTokensCreateTicketForNotActivatedMembershipData = {
  body?: {
    /**
     * Email dell'utente la cui membership non risulta attivata
     */
    email: string
  }
  path?: never
  query?: never
  url: '/premium_tokens/create_ticket_for_not_activated_membership'
}

export type PostPremiumTokensCreateTicketForNotActivatedMembershipResponses = {
  /**
   * ticket accodato
   */
  200: SuccessResponse
}

export type PostPremiumTokensCreateTicketForNotActivatedMembershipResponse =
  PostPremiumTokensCreateTicketForNotActivatedMembershipResponses[keyof PostPremiumTokensCreateTicketForNotActivatedMembershipResponses]

export type GetCardinalityData = {
  body?: never
  path?: never
  query?: never
  url: '/cardinality'
}

export type GetCardinalityResponses = {
  /**
   * cardinalità restituita
   */
  200: CardinalityResponse
}

export type GetCardinalityResponse =
  GetCardinalityResponses[keyof GetCardinalityResponses]

export type PostCardinalitySaveData = {
  body?: never
  path?: never
  query: {
    /**
     * Anno del ranking
     */
    year: number
    /**
     * Cardinalità del ranking (vedi GET /cardinality per la prossima libera)
     */
    cardinality: number
  }
  url: '/cardinality/save'
}

export type PostCardinalitySaveErrors = {
  /**
   * utente non staff
   */
  401: unknown
}

export type PostCardinalitySaveResponses = {
  /**
   * ranking già esistente
   */
  200: SuccessResponse
}

export type PostCardinalitySaveResponse =
  PostCardinalitySaveResponses[keyof PostCardinalitySaveResponses]

export type PutResetPasswordRequestData = {
  body: {
    /**
     * Email dell'utente
     */
    email: string
  }
  path?: never
  query?: never
  url: '/reset-password/request'
}

export type PutResetPasswordRequestResponses = {
  /**
   * utente non trovato (ma sempre successo per sicurezza)
   */
  200: {
    success: boolean
  }
}

export type PutResetPasswordRequestResponse =
  PutResetPasswordRequestResponses[keyof PutResetPasswordRequestResponses]

export type PutResetPasswordData = {
  body: {
    /**
     * Token di reset password
     */
    token: string
    /**
     * Nuova password
     */
    password: string
  }
  path?: never
  query?: never
  url: '/reset-password'
}

export type PutResetPasswordResponses = {
  /**
   * token non valido (ma sempre successo per sicurezza)
   */
  200: {
    success: boolean
  }
}

export type PutResetPasswordResponse =
  PutResetPasswordResponses[keyof PutResetPasswordResponses]

export type DeleteSavedCompetitionSearchData = {
  body?: never
  path?: never
  query?: never
  url: '/saved-competition-search'
}

export type DeleteSavedCompetitionSearchErrors = {
  /**
   * ricerca salvata non trovata
   */
  404: SavedCompetitionSearchErrorResponse
}

export type DeleteSavedCompetitionSearchError =
  DeleteSavedCompetitionSearchErrors[keyof DeleteSavedCompetitionSearchErrors]

export type DeleteSavedCompetitionSearchResponses = {
  /**
   * ricerca salvata eliminata
   */
  200: SuccessResponse
}

export type DeleteSavedCompetitionSearchResponse =
  DeleteSavedCompetitionSearchResponses[keyof DeleteSavedCompetitionSearchResponses]

export type GetSavedCompetitionSearchData = {
  body?: never
  path?: never
  query?: never
  url: '/saved-competition-search'
}

export type GetSavedCompetitionSearchErrors = {
  /**
   * ricerca salvata non trovata
   */
  404: SavedCompetitionSearchErrorResponse
}

export type GetSavedCompetitionSearchError =
  GetSavedCompetitionSearchErrors[keyof GetSavedCompetitionSearchErrors]

export type GetSavedCompetitionSearchResponses = {
  /**
   * ricerca salvata restituita
   */
  200: SavedCompetitionSearch
}

export type GetSavedCompetitionSearchResponse =
  GetSavedCompetitionSearchResponses[keyof GetSavedCompetitionSearchResponses]

export type PostSavedCompetitionSearchData = {
  body?: {
    /**
     * e.g. 'open', 'under', 'over'
     */
    age_category?: string
    /**
     * e.g. 'open', 'seconda', 'terza', 'quarta'
     */
    rankings_category?: string
    /**
     * Region (FIT code), e.g. 'LAZ'
     */
    region?: string
    gender?: CompetitionGender | '0' | '1' | '2'
    /**
     * Match only championship competitions
     */
    championship?: boolean
    /**
     * Italian province codes to match against
     */
    province?: Array<string>
  }
  path?: never
  query?: never
  url: '/saved-competition-search'
}

export type PostSavedCompetitionSearchErrors = {
  /**
   * parametri non validi
   */
  422: ErrorResponse
}

export type PostSavedCompetitionSearchError =
  PostSavedCompetitionSearchErrors[keyof PostSavedCompetitionSearchErrors]

export type PostSavedCompetitionSearchResponses = {
  /**
   * ricerca salvata creata/aggiornata
   */
  200: SuccessResponse
}

export type PostSavedCompetitionSearchResponse =
  PostSavedCompetitionSearchResponses[keyof PostSavedCompetitionSearchResponses]

export type GetTweProductsData = {
  body?: never
  path?: never
  query?: {
    /**
     * Numero pagina (default 1)
     */
    page?: number
    /**
     * Risultati per pagina (default 24, massimo 100)
     */
    per?: number
    /**
     * Filtra per categoria (vedi GET /twe-product-categories)
     */
    category_id?: number
    /**
     * Filtra per marca, match esatto (es. Babolat)
     */
    brand?: string
    /**
     * Filtra per genere:
     * * `unisex`
     * * `uomo`
     * * `donna`
     *
     */
    gender?: string
    /**
     * Se true restituisce solo i prodotti in saldo
     */
    on_sale?: boolean
    /**
     * Se true restituisce solo i prodotti su cui e' applicabile il coupon premium (quelli con premium_price valorizzato). Indipendente da on_sale
     */
    discountable?: boolean
    /**
     * Prezzo minimo (confrontato con min_price del prodotto)
     */
    min_price?: number
    /**
     * Prezzo massimo (confrontato con min_price del prodotto)
     */
    max_price?: number
    /**
     * Ricerca testuale su titolo e marca
     */
    q?: string
    /**
     * Ordinamento (default: id crescente)
     */
    sort?: string
  }
  url: '/twe-products'
}

export type GetTweProductsResponses = {
  /**
   * lista prodotti restituita
   */
  200: TweProductsResponse
}

export type GetTweProductsResponse =
  GetTweProductsResponses[keyof GetTweProductsResponses]

export type GetTweProductsByIdData = {
  body?: never
  path: {
    /**
     * ID prodotto
     */
    id: number
  }
  query?: never
  url: '/twe-products/{id}'
}

export type GetTweProductsByIdErrors = {
  /**
   * prodotto inesistente o non piu' disponibile
   */
  404: unknown
}

export type GetTweProductsByIdResponses = {
  /**
   * prodotto trovato
   */
  200: TweProduct
}

export type GetTweProductsByIdResponse =
  GetTweProductsByIdResponses[keyof GetTweProductsByIdResponses]

export type GetTweProductsMostClickedData = {
  body?: never
  path?: never
  query?: {
    /**
     * Quanti prodotti restituire (default 10, massimo 100)
     */
    limit?: number
    /**
     * Conta solo i click da questa data inclusa (YYYY-MM-DD)
     */
    date_from?: string
    /**
     * Conta solo i click fino a questa data inclusa (YYYY-MM-DD)
     */
    date_to?: string
    /**
     * Filtra per categoria (vedi GET /twe-product-categories)
     */
    category_id?: number
    /**
     * Filtra per marca, match esatto (es. Babolat)
     */
    brand?: string
    /**
     * Filtra per genere:
     * * `unisex`
     * * `uomo`
     * * `donna`
     *
     */
    gender?: string
    /**
     * Se true considera solo i prodotti in saldo
     */
    on_sale?: boolean
    /**
     * Se true considera solo i prodotti scontabili col coupon premium
     */
    discountable?: boolean
    /**
     * Prezzo minimo (confrontato con min_price del prodotto)
     */
    min_price?: number
    /**
     * Prezzo massimo (confrontato con min_price del prodotto)
     */
    max_price?: number
    /**
     * Ricerca testuale su titolo e marca
     */
    q?: string
  }
  url: '/twe-products/most_clicked'
}

export type GetTweProductsMostClickedErrors = {
  /**
   * non autenticato o non amministratore
   */
  401: unknown
}

export type GetTweProductsMostClickedResponses = {
  /**
   * classifica restituita
   */
  200: TweMostClickedResponse
}

export type GetTweProductsMostClickedResponse =
  GetTweProductsMostClickedResponses[keyof GetTweProductsMostClickedResponses]

export type GetTweProductsForMeData = {
  body?: never
  path?: never
  query?: {
    /**
     * Numero massimo di prodotti (default 12, massimo 50)
     */
    limit?: number
  }
  url: '/twe-products/for_me'
}

export type GetTweProductsForMeResponses = {
  /**
   * carousel restituita
   */
  200: TweForMeResponse
}

export type GetTweProductsForMeResponse =
  GetTweProductsForMeResponses[keyof GetTweProductsForMeResponses]

export type GetTweProductsFeaturedData = {
  body?: never
  path?: never
  query?: {
    /**
     * Numero pagina (default 1)
     */
    page?: number
    /**
     * Risultati per pagina (default 24, massimo 100)
     */
    per?: number
  }
  url: '/twe-products/featured'
}

export type GetTweProductsFeaturedResponses = {
  /**
   * vetrina restituita
   */
  200: TweProductsResponse
}

export type GetTweProductsFeaturedResponse =
  GetTweProductsFeaturedResponses[keyof GetTweProductsFeaturedResponses]

export type PostTweProductsByIdClickData = {
  body?: never
  path: {
    /**
     * ID prodotto
     */
    id: number
  }
  query?: never
  url: '/twe-products/{id}/click'
}

export type PostTweProductsByIdClickErrors = {
  /**
   * prodotto inesistente
   */
  404: unknown
}

export type PostTweProductsByIdClickResponses = {
  /**
   * click registrato
   */
  200: unknown
}

export type GetTweProductsByIdClicksData = {
  body?: never
  path: {
    /**
     * ID prodotto
     */
    id: number
  }
  query?: never
  url: '/twe-products/{id}/clicks'
}

export type GetTweProductsByIdClicksResponses = {
  /**
   * conteggio restituito
   */
  200: {
    clicks_count: number
  }
}

export type GetTweProductsByIdClicksResponse =
  GetTweProductsByIdClicksResponses[keyof GetTweProductsByIdClicksResponses]

export type GetTweProductsByIdDailyClicksData = {
  body?: never
  path: {
    /**
     * ID prodotto
     */
    id: number
  }
  query?: {
    /**
     * Conta solo i click da questa data inclusa (YYYY-MM-DD)
     */
    date_from?: string
    /**
     * Conta solo i click fino a questa data inclusa (YYYY-MM-DD)
     */
    date_to?: string
  }
  url: '/twe-products/{id}/daily_clicks'
}

export type GetTweProductsByIdDailyClicksErrors = {
  /**
   * data non valida
   */
  400: unknown
  /**
   * non autenticato o non amministratore
   */
  401: unknown
  /**
   * prodotto non trovato
   */
  404: unknown
}

export type GetTweProductsByIdDailyClicksResponses = {
  /**
   * serie restituita
   */
  200: TweProductDailyClicksResponse
}

export type GetTweProductsByIdDailyClicksResponse =
  GetTweProductsByIdDailyClicksResponses[keyof GetTweProductsByIdDailyClicksResponses]

export type GetTweProductCategoriesData = {
  body?: never
  path?: never
  query?: never
  url: '/twe-product-categories'
}

export type GetTweProductCategoriesResponses = {
  /**
   * categorie restituite
   */
  200: TweCategoriesResponse
}

export type GetTweProductCategoriesResponse =
  GetTweProductCategoriesResponses[keyof GetTweProductCategoriesResponses]

export type GetTweProductBrandsData = {
  body?: never
  path?: never
  query?: {
    /**
     * Filtra per categoria (vedi GET /twe-product-categories)
     */
    category_id?: number
    /**
     * Filtra per genere:
     * * `unisex`
     * * `uomo`
     * * `donna`
     *
     */
    gender?: string
    /**
     * Se true conta solo i prodotti in saldo
     */
    on_sale?: boolean
    /**
     * Se true conta solo i prodotti su cui e' applicabile il coupon premium. Indipendente da on_sale
     */
    discountable?: boolean
    /**
     * Prezzo minimo (confrontato con min_price del prodotto)
     */
    min_price?: number
    /**
     * Prezzo massimo (confrontato con min_price del prodotto)
     */
    max_price?: number
    /**
     * Ricerca testuale su titolo e marca
     */
    q?: string
  }
  url: '/twe-product-brands'
}

export type GetTweProductBrandsResponses = {
  /**
   * marchi restituiti
   */
  200: TweBrandsResponse
}

export type GetTweProductBrandsResponse =
  GetTweProductBrandsResponses[keyof GetTweProductBrandsResponses]

export type GetUpdateTasksData = {
  body?: never
  path?: never
  query?: {
    /**
     * Numero pagina (default 1, 20 per pagina)
     */
    page?: number
  }
  url: '/update-tasks'
}

export type GetUpdateTasksResponses = {
  /**
   * lista update tasks restituita
   */
  200: UpdateTasksIndexResponse
}

export type GetUpdateTasksResponse =
  GetUpdateTasksResponses[keyof GetUpdateTasksResponses]

export type PostUpdateTasksData = {
  body?: {
    /**
     * File da processare, come data URI base64 (`data:<mime>;base64,...`)
     */
    file?: string
    /**
     * Nome originale del file
     */
    filename?: string
  }
  path?: never
  query?: {
    /**
     * Esegui il task dopo il salvataggio
     */
    run_after_save?: boolean
  }
  url: '/update-tasks'
}

export type PostUpdateTasksResponses = {
  /**
   * parametri non validi
   */
  200: UpdateTaskCreateResponse
}

export type PostUpdateTasksResponse =
  PostUpdateTasksResponses[keyof PostUpdateTasksResponses]

export type PutUpdateTasksByIdRunData = {
  body?: {
    /**
     * Evita di inviare l'email di esito
     */
    avoid_send_mail?: boolean
  }
  path: {
    /**
     * ID update task
     */
    id: number
  }
  query?: never
  url: '/update-tasks/{id}/run'
}

export type PutUpdateTasksByIdRunResponses = {
  /**
   * update task non trovato o non in pending
   */
  200: SuccessResponse
}

export type PutUpdateTasksByIdRunResponse =
  PutUpdateTasksByIdRunResponses[keyof PutUpdateTasksByIdRunResponses]

export type PostUpdateTasksByIdRerunData = {
  body?: {
    /**
     * Solo per i task di import: non inviare mail e notifiche ai giocatori
     */
    avoid_send_mail?: boolean
  }
  path: {
    /**
     * ID update task
     */
    id: number
  }
  query?: never
  url: '/update-tasks/{id}/rerun'
}

export type PostUpdateTasksByIdRerunErrors = {
  /**
   * update task non trovato (risposta senza body)
   */
  404: unknown
  /**
   * update task senza job ne file da rielaborare
   */
  422: SimpleErrorResponse
}

export type PostUpdateTasksByIdRerunError =
  PostUpdateTasksByIdRerunErrors[keyof PostUpdateTasksByIdRerunErrors]

export type PostUpdateTasksByIdRerunResponses = {
  /**
   * job riaccodato con gli stessi argomenti
   */
  200: SuccessResponse
}

export type PostUpdateTasksByIdRerunResponse =
  PostUpdateTasksByIdRerunResponses[keyof PostUpdateTasksByIdRerunResponses]

export type PutUpdateTasksByIdSetPendingData = {
  body?: never
  path: {
    /**
     * ID update task
     */
    id: number
  }
  query?: never
  url: '/update-tasks/{id}/set_pending'
}

export type PutUpdateTasksByIdSetPendingResponses = {
  /**
   * update task impostato come pending
   */
  200: SuccessResponse
}

export type PutUpdateTasksByIdSetPendingResponse =
  PutUpdateTasksByIdSetPendingResponses[keyof PutUpdateTasksByIdSetPendingResponses]

export type GetUpdateTasksByIdTweProductsData = {
  body?: never
  path: {
    /**
     * ID update task
     */
    id: number
  }
  query?: {
    /**
     * Numero pagina
     */
    page?: number
  }
  url: '/update-tasks/{id}/twe-products'
}

export type GetUpdateTasksByIdTweProductsResponses = {
  /**
   * prodotti TWE aggiornati dal task restituiti
   */
  200: UpdateTaskTweProductsResponse
}

export type GetUpdateTasksByIdTweProductsResponse =
  GetUpdateTasksByIdTweProductsResponses[keyof GetUpdateTasksByIdTweProductsResponses]

export type DeleteUpdateTasksByIdData = {
  body?: never
  path: {
    /**
     * ID update task
     */
    id: number
  }
  query?: never
  url: '/update-tasks/{id}'
}

export type DeleteUpdateTasksByIdErrors = {
  /**
   * update task non trovato
   */
  404: SimpleErrorResponse
}

export type DeleteUpdateTasksByIdError =
  DeleteUpdateTasksByIdErrors[keyof DeleteUpdateTasksByIdErrors]

export type DeleteUpdateTasksByIdResponses = {
  /**
   * update task eliminato
   */
  200: SuccessResponse
}

export type DeleteUpdateTasksByIdResponse =
  DeleteUpdateTasksByIdResponses[keyof DeleteUpdateTasksByIdResponses]

export type PostUpdateTasksCacheRefreshData = {
  body?: never
  path?: never
  query?: never
  url: '/update-tasks/cache_refresh'
}

export type PostUpdateTasksCacheRefreshResponses = {
  /**
   * cache aggiornata
   */
  200: SuccessResponse
}

export type PostUpdateTasksCacheRefreshResponse =
  PostUpdateTasksCacheRefreshResponses[keyof PostUpdateTasksCacheRefreshResponses]

export type PostUpdateTasksCacheFlushAllData = {
  body?: never
  path?: never
  query?: never
  url: '/update-tasks/cache_flush_all'
}

export type PostUpdateTasksCacheFlushAllErrors = {
  /**
   * non admin
   */
  401: unknown
}

export type PostUpdateTasksCacheFlushAllResponses = {
  /**
   * cache svuotata
   */
  200: SuccessResponse
}

export type PostUpdateTasksCacheFlushAllResponse =
  PostUpdateTasksCacheFlushAllResponses[keyof PostUpdateTasksCacheFlushAllResponses]

export type PostUpdateTasksRestartServersData = {
  body?: never
  path?: never
  query?: never
  url: '/update-tasks/restart_servers'
}

export type PostUpdateTasksRestartServersResponses = {
  /**
   * riavvio server avviato
   */
  200: SuccessResponse
}

export type PostUpdateTasksRestartServersResponse =
  PostUpdateTasksRestartServersResponses[keyof PostUpdateTasksRestartServersResponses]

export type PostUpdateTasksRunRestoreDumpData = {
  body?: {
    /**
     * Nome del file dump da ripristinare
     */
    dump_filename: string
  }
  path?: never
  query?: never
  url: '/update-tasks/run-restore-dump'
}

export type PostUpdateTasksRunRestoreDumpResponses = {
  /**
   * ripristino dump avviato
   */
  200: SuccessResponse
}

export type PostUpdateTasksRunRestoreDumpResponse =
  PostUpdateTasksRunRestoreDumpResponses[keyof PostUpdateTasksRunRestoreDumpResponses]

export type PostUpdateTasksRunAsyncJobData = {
  body?: {
    /**
     * Record da passare come argomento al job (opzionale)
     */
    model_param?: {
      /**
       * Nome del modello
       */
      _model: string
      /**
       * ID del record del modello
       */
      id: number
    }
  }
  path?: never
  query: {
    /**
     * Nome della classe del job da eseguire
     */
    job_name: string
    /**
     * Parametro da passare al job
     */
    param?: string
  }
  url: '/update-tasks/run_async_job'
}

export type PostUpdateTasksRunAsyncJobErrors = {
  /**
   * nome job mancante
   */
  400: SimpleErrorResponse
  /**
   * job non trovato
   */
  404: SimpleErrorResponse
}

export type PostUpdateTasksRunAsyncJobError =
  PostUpdateTasksRunAsyncJobErrors[keyof PostUpdateTasksRunAsyncJobErrors]

export type PostUpdateTasksRunAsyncJobResponses = {
  /**
   * job eseguito con successo
   */
  200: JobEnqueuedResponse
}

export type PostUpdateTasksRunAsyncJobResponse =
  PostUpdateTasksRunAsyncJobResponses[keyof PostUpdateTasksRunAsyncJobResponses]

export type GetUserMatchesData = {
  body?: never
  path?: never
  query?: {
    /**
     * Numero pagina
     */
    page?: number
    /**
     * Stato di conferma del match:
     * * `pending`
     * * `confirmed`
     * * `rejected`
     *
     */
    confirmation?: string
    /**
     * Singolare o doppio
     */
    type?: string
    /**
     * Esito del match
     */
    result?: string
    /**
     * Match agonistico (true) o amichevole (false)
     */
    competitive?: boolean
    /**
     * Match indoor
     */
    indoor?: boolean
    /**
     * Stato del match
     */
    status?: string
    /**
     * Superficie di gioco
     */
    surface?: string
    /**
     * Id del FitPlayerProfile coinvolto nel match
     */
    player_id?: number
  }
  url: '/user-matches'
}

export type GetUserMatchesResponses = {
  /**
   * lista match utente restituita
   */
  200: UserMatchListResponse
}

export type GetUserMatchesResponse =
  GetUserMatchesResponses[keyof GetUserMatchesResponses]

export type PostUserMatchesData = {
  /**
   * `date` and `result` are required for a played match; a match to organise (open/proposed) needs `starts_at` instead.
   */
  body?: {
    opponent_id?: number | null
    opponent_name?: string | null
    opponent_rank_string?: string | null
    date?: string
    result?: string
    won?: boolean | null
    not_played?: boolean | null
    tournament?: string | null
    board?: string | null
    teammate_id?: number | null
    teammate_name?: string | null
    second_opponent_id?: number | null
    second_opponent_name?: string | null
    /**
     * 'User' or 'FitPlayerProfile'
     */
    teammateable_type?: string | null
    teammateable_id?: number | null
    /**
     * 'User' or 'FitPlayerProfile'
     */
    second_opponentable_type?: string | null
    second_opponentable_id?: number | null
    is_a_double_eligible_for_calculation?: boolean | null
    /**
     * 'User' or 'FitPlayerProfile'
     */
    authorable_type?: string | null
    authorable_id?: number | null
    /**
     * 'User' or 'FitPlayerProfile'
     */
    opponentable_type?: string | null
    opponentable_id?: number | null
    /**
     * Personal note set on the author's Activity at creation time
     */
    activity_note?: string | null
    /**
     * Visibility of the author's Activity (default: true)
     */
    activity_visible?: boolean | null
    competitive?: boolean | null
    location_name?: string | null
    /**
     * Duration in minutes
     */
    duration?: number | null
    /**
     * Default: false
     */
    indoor?: boolean | null
    surface?:
      | 'clay_court'
      | 'synthetic'
      | 'grass_court'
      | 'synthetic_grass'
      | 'hard_court'
      | 'synthetic_clay'
      | 'other'
      | null
    /**
     * Default played (a result). open: looking for players. proposed: a challenge to `opponentable_*`. Only settable on create.
     */
    status?: 'played' | 'open' | 'proposed' | null
    /**
     * Required for open/proposed; must be in the future. Derives `date`.
     */
    starts_at?: string | null
    /**
     * After starts_at.
     */
    ends_at?: string | null
    time_type?: 'exact' | 'band' | null
    doubles?: boolean | null
    court_booked?: boolean | null
    /**
     * Latitude of the place.
     */
    lat_input?: number | null
    /**
     * Longitude of the place.
     */
    lng_input?: number | null
    /**
     * The place as typed by the user.
     */
    address_user_input?: string | null
    /**
     * Free text from the organiser, shown on the match; ignored on a played match.
     */
    notes?: string | null
  }
  path?: never
  query?: never
  url: '/user-matches'
}

export type PostUserMatchesErrors = {
  /**
   * profilo incompleto per pubblicare una partita aperta
   */
  422: {
    success: boolean
    error: 'profile_incomplete'
    missing: Array<'city' | 'gender' | 'level' | 'frequency' | 'visibility'>
  }
}

export type PostUserMatchesError =
  PostUserMatchesErrors[keyof PostUserMatchesErrors]

export type PostUserMatchesResponses = {
  /**
   * parametri non validi
   */
  200: UserMatchCreateResponse
}

export type PostUserMatchesResponse =
  PostUserMatchesResponses[keyof PostUserMatchesResponses]

export type DeleteUserMatchesByIdData = {
  body?: never
  path: {
    /**
     * ID UserMatch
     */
    id: number
  }
  query?: never
  url: '/user-matches/{id}'
}

export type DeleteUserMatchesByIdErrors = {
  /**
   * match utente non trovato
   */
  404: unknown
}

export type DeleteUserMatchesByIdResponses = {
  /**
   * match utente eliminato
   */
  200: UserMatchWithOutcomeResponse
}

export type DeleteUserMatchesByIdResponse =
  DeleteUserMatchesByIdResponses[keyof DeleteUserMatchesByIdResponses]

export type GetUserMatchesByIdData = {
  body?: never
  path: {
    /**
     * ID UserMatch
     */
    id: number
  }
  query?: never
  url: '/user-matches/{id}'
}

export type GetUserMatchesByIdErrors = {
  /**
   * match utente non trovato
   */
  404: unknown
}

export type GetUserMatchesByIdResponses = {
  /**
   * partita da organizzare, vista da un visitatore
   */
  200: UserMatchResponse
}

export type GetUserMatchesByIdResponse =
  GetUserMatchesByIdResponses[keyof GetUserMatchesByIdResponses]

export type PutUserMatchesByIdData = {
  /**
   * `status` is ignored here: it moves only through request/accept/decline/cancel/result.
   */
  body?: {
    opponent_id?: number | null
    opponent_name?: string | null
    opponent_rank_string?: string | null
    date?: string
    result?: string
    won?: boolean | null
    not_played?: boolean | null
    tournament?: string | null
    board?: string | null
    teammate_id?: number | null
    teammate_name?: string | null
    second_opponent_id?: number | null
    second_opponent_name?: string | null
    /**
     * 'User' or 'FitPlayerProfile'
     */
    teammateable_type?: string | null
    teammateable_id?: number | null
    /**
     * 'User' or 'FitPlayerProfile'
     */
    second_opponentable_type?: string | null
    second_opponentable_id?: number | null
    is_a_double_eligible_for_calculation?: boolean | null
    /**
     * 'User' or 'FitPlayerProfile'
     */
    authorable_type?: string | null
    authorable_id?: number | null
    /**
     * 'User' or 'FitPlayerProfile'
     */
    opponentable_type?: string | null
    opponentable_id?: number | null
    activity_note?: string | null
    activity_visible?: boolean | null
    competitive?: boolean | null
    location_name?: string | null
    /**
     * Duration in minutes
     */
    duration?: number | null
    indoor?: boolean | null
    surface?:
      | 'clay_court'
      | 'synthetic'
      | 'grass_court'
      | 'synthetic_grass'
      | 'hard_court'
      | 'synthetic_clay'
      | 'other'
      | null
    /**
     * Required for open/proposed; must be in the future. Derives `date`.
     */
    starts_at?: string | null
    /**
     * After starts_at.
     */
    ends_at?: string | null
    time_type?: 'exact' | 'band' | null
    doubles?: boolean | null
    court_booked?: boolean | null
    /**
     * Latitude of the place.
     */
    lat_input?: number | null
    /**
     * Longitude of the place.
     */
    lng_input?: number | null
    /**
     * The place as typed by the user.
     */
    address_user_input?: string | null
    /**
     * Free text from the organiser, shown on the match; ignored on a played match.
     */
    notes?: string | null
  }
  path: {
    /**
     * ID UserMatch
     */
    id: number
  }
  query?: never
  url: '/user-matches/{id}'
}

export type PutUserMatchesByIdErrors = {
  /**
   * solo chi ha creato la partita organizzata puo modificarla (forbidden)
   */
  403: UserMatchActionError
  /**
   * match utente non trovato
   */
  404: unknown
  /**
   * posto gia preso o con la richiesta in attesa (spot_taken|request_pending)
   */
  409: UserMatchActionError
  /**
   * partita organizzata non piu modificabile (not_editable)
   */
  422: UserMatchActionError
}

export type PutUserMatchesByIdError =
  PutUserMatchesByIdErrors[keyof PutUserMatchesByIdErrors]

export type PutUserMatchesByIdResponses = {
  /**
   * match utente aggiornato
   */
  200: UserMatchWithOutcomeResponse
}

export type PutUserMatchesByIdResponse =
  PutUserMatchesByIdResponses[keyof PutUserMatchesByIdResponses]

export type PutUserMatchesByIdConfirmData = {
  body?: {
    /**
     * Partita organizzata, obbligatorio: `match_finder.result_submitted_at` cosi' come lo si e' ricevuto (con i microsecondi). Mancante o illeggibile: 422 `invalid_params`.
     */
    result_submitted_at?: string | null
  }
  path: {
    /**
     * ID UserMatch
     */
    id: number
  }
  query?: never
  url: '/user-matches/{id}/confirm'
}

export type PutUserMatchesByIdConfirmErrors = {
  /**
   * match utente non trovato
   */
  404: {
    success: boolean
  }
}

export type PutUserMatchesByIdConfirmError =
  PutUserMatchesByIdConfirmErrors[keyof PutUserMatchesByIdConfirmErrors]

export type PutUserMatchesByIdConfirmResponses = {
  /**
   * match utente confermato
   */
  200: UserMatchWithOutcomeResponse
}

export type PutUserMatchesByIdConfirmResponse =
  PutUserMatchesByIdConfirmResponses[keyof PutUserMatchesByIdConfirmResponses]

export type PutUserMatchesByIdRejectData = {
  body?: {
    /**
     * Partita organizzata, obbligatorio: `match_finder.result_submitted_at` cosi' come lo si e' ricevuto (con i microsecondi). Mancante o illeggibile: 422 `invalid_params`.
     */
    result_submitted_at?: string | null
  }
  path: {
    /**
     * ID UserMatch
     */
    id: number
  }
  query?: never
  url: '/user-matches/{id}/reject'
}

export type PutUserMatchesByIdRejectErrors = {
  /**
   * match utente non trovato
   */
  404: {
    success: boolean
  }
}

export type PutUserMatchesByIdRejectError =
  PutUserMatchesByIdRejectErrors[keyof PutUserMatchesByIdRejectErrors]

export type PutUserMatchesByIdRejectResponses = {
  /**
   * match utente rifiutato
   */
  200: UserMatchWithOutcomeResponse
}

export type PutUserMatchesByIdRejectResponse =
  PutUserMatchesByIdRejectResponses[keyof PutUserMatchesByIdRejectResponses]

export type PutUserMatchesByIdResultData = {
  body: {
    /**
     * Risultato del match (es. 6-4 6-2)
     */
    result: string
    /**
     * Durata in minuti, salvata solo insieme a `result`
     */
    duration?: number
    /**
     * Superficie, salvata solo insieme a `result`
     */
    surface?:
      | 'clay_court'
      | 'synthetic'
      | 'grass_court'
      | 'synthetic_grass'
      | 'hard_court'
      | 'synthetic_clay'
      | 'other'
    /**
     * Al coperto, salvato solo insieme a `result`
     */
    indoor?: boolean
    /**
     * Partita organizzata: vince chi crea (dal suo lato). Se manca lo ricava il server dai set; va mandato quando il punteggio non lo dice (ritiro, walkover).
     */
    won?: boolean | null
    /**
     * Titolo dell'attivita' di chi salva, salvato solo insieme a `result`
     */
    activity_title?: string
    /**
     * Come e' andata: nota dell'attivita' di chi salva, solo insieme a `result`
     */
    activity_note?: string
    /**
     * Visibilita' dell'attivita' di chi salva (false = Solo io), solo insieme a `result`
     */
    activity_visible?: boolean
  }
  path: {
    /**
     * ID UserMatch
     */
    id: number
  }
  query?: never
  url: '/user-matches/{id}/result'
}

export type PutUserMatchesByIdResultErrors = {
  /**
   * solo chi gioca la partita organizzata inserisce il risultato (forbidden)
   */
  403: UserMatchActionError
  /**
   * match utente non trovato
   */
  404: unknown
  /**
   * partita non ancora giocabile
   */
  422: UserMatchActionError
}

export type PutUserMatchesByIdResultError =
  PutUserMatchesByIdResultErrors[keyof PutUserMatchesByIdResultErrors]

export type PutUserMatchesByIdResultResponses = {
  /**
   * risultato match utente aggiornato
   */
  200: UserMatchWithOutcomeResponse
}

export type PutUserMatchesByIdResultResponse =
  PutUserMatchesByIdResultResponses[keyof PutUserMatchesByIdResultResponses]

export type GetUserMatchesOpenData = {
  body?: never
  path?: never
  query?: {
    lat?: number
    lng?: number
    /**
     * Default 25, solo con lat/lng
     */
    radius_km?: number
    date_from?: string
    date_to?: string
    /**
     * "HH:MM" Europe/Rome: matches whose time window (or band) ends after it
     */
    time_from?: string
    /**
     * "HH:MM" Europe/Rome: matches whose time window (or band) starts before it
     */
    time_to?: string
    doubles?: boolean
    court_booked?: boolean
    indoor?: boolean
    /**
     * :
     * * `clay_court`
     * * `synthetic`
     * * `grass_court`
     * * `synthetic_grass`
     * * `hard_court`
     * * `synthetic_clay`
     * * `other`
     *
     */
    surface?: string
    page?: number
    /**
     * Solo admin: tutte le partite in cui si puo' entrare, ignorando genere, raggio, blocchi, sovrapposizioni, filtri e profilo incompleto. Per gli altri e' ignorato.
     */
    show_all?: boolean
  }
  url: '/user-matches/open'
}

export type GetUserMatchesOpenErrors = {
  /**
   * profilo incompleto per il Match Finder
   */
  422: {
    success: boolean
    error: 'profile_incomplete'
    missing: Array<'city' | 'gender' | 'level' | 'frequency' | 'visibility'>
  }
}

export type GetUserMatchesOpenError =
  GetUserMatchesOpenErrors[keyof GetUserMatchesOpenErrors]

export type GetUserMatchesOpenResponses = {
  /**
   * partite aperte
   */
  200: UserMatchOpenListResponse
}

export type GetUserMatchesOpenResponse =
  GetUserMatchesOpenResponses[keyof GetUserMatchesOpenResponses]

export type DeleteUserMatchesByIdRequestData = {
  body?: never
  path: {
    /**
     * ID UserMatch
     */
    id: number
  }
  query?: never
  url: '/user-matches/{id}/request'
}

export type DeleteUserMatchesByIdRequestErrors = {
  /**
   * non sei chi ha fatto la richiesta (not_requester)
   */
  422: UserMatchActionError
}

export type DeleteUserMatchesByIdRequestError =
  DeleteUserMatchesByIdRequestErrors[keyof DeleteUserMatchesByIdRequestErrors]

export type DeleteUserMatchesByIdRequestResponses = {
  /**
   * richiesta ritirata
   */
  200: UserMatchWithOutcomeResponse
}

export type DeleteUserMatchesByIdRequestResponse =
  DeleteUserMatchesByIdRequestResponses[keyof DeleteUserMatchesByIdRequestResponses]

export type PostUserMatchesByIdRequestData = {
  body: {
    /**
     * Uno dei `free_seats` della partita
     */
    seat: 'teammate' | 'opponent' | 'second_opponent'
    starts_at?: string | null
    ends_at?: string | null
    /**
     * Nota per chi organizza
     */
    note?: string | null
  }
  path: {
    /**
     * ID UserMatch
     */
    id: number
  }
  query?: never
  url: '/user-matches/{id}/request'
}

export type PostUserMatchesByIdRequestErrors = {
  /**
   * partita propria, persona bloccata o genere diverso
   */
  403: UserMatchActionError
  /**
   * partita non aperta, posto preso o richiesta altrui in corso
   */
  409: UserMatchActionError
  /**
   * iscrizioni chiuse (expired)
   */
  410: UserMatchActionError
  /**
   * posto inesistente, fascia non valida, profilo incompleto o posto gia' tuo
   */
  422: UserMatchActionError
}

export type PostUserMatchesByIdRequestError =
  PostUserMatchesByIdRequestErrors[keyof PostUserMatchesByIdRequestErrors]

export type PostUserMatchesByIdRequestResponses = {
  /**
   * richiesta registrata
   */
  200: UserMatchWithOutcomeResponse
}

export type PostUserMatchesByIdRequestResponse =
  PostUserMatchesByIdRequestResponses[keyof PostUserMatchesByIdRequestResponses]

export type PostUserMatchesByIdRequestAcceptData = {
  /**
   * La richiesta che chi organizza ha visto, da `match_finder.request`.
   */
  body: {
    /**
     * `match_finder.request.player.user_id`
     */
    request_user_id: number
    /**
     * `match_finder.request.requested_at`, con i microsecondi, invariato
     */
    requested_at: string
  }
  path: {
    /**
     * ID UserMatch
     */
    id: number
  }
  query?: never
  url: '/user-matches/{id}/request/accept'
}

export type PostUserMatchesByIdRequestAcceptErrors = {
  /**
   * non sei l'organizzatore (forbidden)
   */
  403: UserMatchActionError
  /**
   * posto preso (spot_taken) o richiesta cambiata (request_changed)
   */
  409: UserMatchActionError
  /**
   * richiesta scaduta (expired)
   */
  410: UserMatchActionError
  /**
   * nessuna richiesta (no_request)
   */
  422: UserMatchActionError
}

export type PostUserMatchesByIdRequestAcceptError =
  PostUserMatchesByIdRequestAcceptErrors[keyof PostUserMatchesByIdRequestAcceptErrors]

export type PostUserMatchesByIdRequestAcceptResponses = {
  /**
   * richiesta accettata
   */
  200: UserMatchWithOutcomeResponse
}

export type PostUserMatchesByIdRequestAcceptResponse =
  PostUserMatchesByIdRequestAcceptResponses[keyof PostUserMatchesByIdRequestAcceptResponses]

export type PostUserMatchesByIdRequestRejectData = {
  /**
   * La richiesta che chi organizza ha visto, da `match_finder.request`.
   */
  body: {
    /**
     * `match_finder.request.player.user_id`
     */
    request_user_id: number
    /**
     * `match_finder.request.requested_at`, con i microsecondi, invariato
     */
    requested_at: string
  }
  path: {
    /**
     * ID UserMatch
     */
    id: number
  }
  query?: never
  url: '/user-matches/{id}/request/reject'
}

export type PostUserMatchesByIdRequestRejectErrors = {
  /**
   * non sei l'organizzatore (forbidden)
   */
  403: UserMatchActionError
  /**
   * la richiesta e' cambiata nel frattempo (request_changed)
   */
  409: UserMatchActionError
  /**
   * nessuna richiesta (no_request)
   */
  422: UserMatchActionError
}

export type PostUserMatchesByIdRequestRejectError =
  PostUserMatchesByIdRequestRejectErrors[keyof PostUserMatchesByIdRequestRejectErrors]

export type PostUserMatchesByIdRequestRejectResponses = {
  /**
   * richiesta rifiutata
   */
  200: UserMatchWithOutcomeResponse
}

export type PostUserMatchesByIdRequestRejectResponse =
  PostUserMatchesByIdRequestRejectResponses[keyof PostUserMatchesByIdRequestRejectResponses]

export type DeleteUserMatchesByIdSeatData = {
  body?: never
  path: {
    /**
     * ID UserMatch
     */
    id: number
  }
  query?: never
  url: '/user-matches/{id}/seat'
}

export type DeleteUserMatchesByIdSeatErrors = {
  /**
   * nessun posto da lasciare (not_seated) o partita iniziata (started)
   */
  422: UserMatchActionError
}

export type DeleteUserMatchesByIdSeatError =
  DeleteUserMatchesByIdSeatErrors[keyof DeleteUserMatchesByIdSeatErrors]

export type DeleteUserMatchesByIdSeatResponses = {
  /**
   * posto liberato
   */
  200: UserMatchWithOutcomeResponse
}

export type DeleteUserMatchesByIdSeatResponse =
  DeleteUserMatchesByIdSeatResponses[keyof DeleteUserMatchesByIdSeatResponses]

export type PostUserMatchesByIdCancelData = {
  body?: {
    /**
     * Motivo, mostrato ai giocatori
     */
    reason?: string | null
  }
  path: {
    /**
     * ID UserMatch
     */
    id: number
  }
  query?: never
  url: '/user-matches/{id}/cancel'
}

export type PostUserMatchesByIdCancelErrors = {
  /**
   * non sei l'organizzatore (forbidden)
   */
  403: UserMatchActionError
  /**
   * partita gia' giocata
   */
  422: ValidationErrorResponse
}

export type PostUserMatchesByIdCancelError =
  PostUserMatchesByIdCancelErrors[keyof PostUserMatchesByIdCancelErrors]

export type PostUserMatchesByIdCancelResponses = {
  /**
   * partita annullata
   */
  200: UserMatchWithOutcomeResponse
}

export type PostUserMatchesByIdCancelResponse =
  PostUserMatchesByIdCancelResponses[keyof PostUserMatchesByIdCancelResponses]

export type GetUserMatchesMineData = {
  body?: never
  path?: never
  query?: never
  url: '/user-matches/mine'
}

export type GetUserMatchesMineResponses = {
  /**
   * le mie partite
   */
  200: UserMatchMineResponse
}

export type GetUserMatchesMineResponse =
  GetUserMatchesMineResponses[keyof GetUserMatchesMineResponses]

export type GetUserMatchesPlayersNearbyData = {
  body?: never
  path?: never
  query?: {
    lat?: number
    lng?: number
    /**
     * Default 25
     */
    radius_km?: number
  }
  url: '/user-matches/players-nearby'
}

export type GetUserMatchesPlayersNearbyResponses = {
  /**
   * giocatori vicini
   */
  200: NearbyPlayersResponse
}

export type GetUserMatchesPlayersNearbyResponse =
  GetUserMatchesPlayersNearbyResponses[keyof GetUserMatchesPlayersNearbyResponses]

export type PutUserMatchesByIdReminderData = {
  body: {
    enabled: boolean
  }
  path: {
    /**
     * ID UserMatch
     */
    id: number
  }
  query?: never
  url: '/user-matches/{id}/reminder'
}

export type PutUserMatchesByIdReminderErrors = {
  /**
   * non partecipi (forbidden)
   */
  403: UserMatchActionError
  /**
   * `enabled` mancante (invalid_params)
   */
  422: UserMatchActionError
}

export type PutUserMatchesByIdReminderError =
  PutUserMatchesByIdReminderErrors[keyof PutUserMatchesByIdReminderErrors]

export type PutUserMatchesByIdReminderResponses = {
  /**
   * promemoria aggiornato
   */
  200: UserMatchWithOutcomeResponse
}

export type PutUserMatchesByIdReminderResponse =
  PutUserMatchesByIdReminderResponses[keyof PutUserMatchesByIdReminderResponses]

export type PostUserMatchesByIdCelebratedData = {
  body?: never
  path: {
    /**
     * ID UserMatch
     */
    id: number
  }
  query?: never
  url: '/user-matches/{id}/celebrated'
}

export type PostUserMatchesByIdCelebratedErrors = {
  /**
   * non partecipi (forbidden)
   */
  403: UserMatchActionError
}

export type PostUserMatchesByIdCelebratedError =
  PostUserMatchesByIdCelebratedErrors[keyof PostUserMatchesByIdCelebratedErrors]

export type PostUserMatchesByIdCelebratedResponses = {
  /**
   * festeggiata
   */
  200: UserMatchWithOutcomeResponse
}

export type PostUserMatchesByIdCelebratedResponse =
  PostUserMatchesByIdCelebratedResponses[keyof PostUserMatchesByIdCelebratedResponses]

export type PostUserMatchesByIdAcceptData = {
  body?: never
  path: {
    /**
     * ID UserMatch
     */
    id: number
  }
  query?: never
  url: '/user-matches/{id}/accept'
}

export type PostUserMatchesByIdAcceptErrors = {
  /**
   * non sei lo sfidato
   */
  403: SuccessResponse
  /**
   * la sfida non e' piu' proposed (not_open)
   */
  409: UserMatchActionError
  /**
   * la partita e' gia' iniziata (started)
   */
  422: UserMatchActionError
}

export type PostUserMatchesByIdAcceptError =
  PostUserMatchesByIdAcceptErrors[keyof PostUserMatchesByIdAcceptErrors]

export type PostUserMatchesByIdAcceptResponses = {
  /**
   * sfida: full
   */
  200: UserMatchWithOutcomeResponse
}

export type PostUserMatchesByIdAcceptResponse =
  PostUserMatchesByIdAcceptResponses[keyof PostUserMatchesByIdAcceptResponses]

export type PostUserMatchesByIdDeclineData = {
  body?: never
  path: {
    /**
     * ID UserMatch
     */
    id: number
  }
  query?: never
  url: '/user-matches/{id}/decline'
}

export type PostUserMatchesByIdDeclineErrors = {
  /**
   * non sei lo sfidato
   */
  403: SuccessResponse
  /**
   * la sfida non e' piu' proposed (not_open)
   */
  409: UserMatchActionError
  /**
   * la partita e' gia' iniziata (started)
   */
  422: UserMatchActionError
}

export type PostUserMatchesByIdDeclineError =
  PostUserMatchesByIdDeclineErrors[keyof PostUserMatchesByIdDeclineErrors]

export type PostUserMatchesByIdDeclineResponses = {
  /**
   * sfida: cancelled
   */
  200: UserMatchWithOutcomeResponse
}

export type PostUserMatchesByIdDeclineResponse =
  PostUserMatchesByIdDeclineResponses[keyof PostUserMatchesByIdDeclineResponses]
